import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { fetchApplicationDetails, uploadFormFile, saveFormData } from '../../../utils/apiUtils';
import styled, { keyframes } from 'styled-components';
import { useUserProfile } from '../../../contexts/UserProfileContext';
import FilePreviewModal from '../../common/FilePreviewModal';
import { getSecureFileUrl, isPublicStaticFile, isUploadedFile } from '../../../utils/secureFileUtils';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import FormPageNavigator from '../../common/FormPageNavigator';
import SessionTimer from '../../common/SessionTimer';
import ApplicationIdDisplay from '../../common/ApplicationIdDisplay';
import ModernDatePicker from '../../common/ModernDatePicker';
import { useActivityTracker } from '../../../hooks/useActivityTracker';
import { useFormProgress } from '../../../hooks/useFormProgress';
// Import all form pages for PDF generation
import ApplicationFormPage2 from './ApplicationFormPage2';
import ApplicationFormPage3 from './ApplicationFormPage3';
import ApplicationFormPage4 from './ApplicationFormPage4';
import ApplicationFormPage5 from './ApplicationFormPage5';
import ApplicationFormPage6 from './ApplicationFormPage6';
import ApplicationFormPage7 from './ApplicationFormPage7';
import ApplicationFormPage8 from './ApplicationFormPage8';
import ApplicationFormPage9 from './ApplicationFormPage9';
import ApplicationFormPage10 from './ApplicationFormPage10';
import ApplicationFormPage11 from './ApplicationFormPage11';
import ApplicationFormPage12 from './ApplicationFormPage12';
import ApplicationFormPage13 from './ApplicationFormPage13';
import ApplicationFormPage14 from './ApplicationFormPage14';

// Browsers hard-cap canvas dimensions (~32,767px per side, ~268M pixels total).
// html2canvas silently renders BLANK content beyond those caps, which clipped
// partner/additional-applicant sections on very tall pages when captured at a
// fixed scale. Clamp the capture scale so the canvas always stays within limits
// (full quality for normal pages, slightly lower DPI for very tall ones).
const getSafeCanvasScale = (el: HTMLElement, preferredScale: number): number => {
  const MAX_CANVAS_DIM = 30000;
  const MAX_CANVAS_AREA = 250000000;
  const w = Math.max(1, el.scrollWidth);
  const h = Math.max(1, el.scrollHeight);
  return Math.min(
    preferredScale,
    MAX_CANVAS_DIM / w,
    MAX_CANVAS_DIM / h,
    Math.sqrt(MAX_CANVAS_AREA / (w * h))
  );
};

// Find the bottom-most row of visible (non-near-white) content in a captured
// canvas. Hidden buttons and trailing padding otherwise inflate captures with
// blank space that turns into empty PDF pages at the end of each section.
// Samples every 4th row / every 4th pixel for speed.
const findCanvasContentBottom = (canvas: HTMLCanvasElement): number => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas.height;
  const { width, height } = canvas;
  const ROW_STEP = 4;
  for (let y = height - 1; y >= 0; y -= ROW_STEP) {
    const row = ctx.getImageData(0, y, width, 1).data;
    for (let x = 0; x < row.length; x += 16) {
      const a = row[x + 3];
      if (a > 16 && (row[x] < 250 || row[x + 1] < 250 || row[x + 2] < 250)) {
        // Found content — keep a small margin below it
        return Math.min(height, y + ROW_STEP + 20);
      }
    }
  }
  return Math.min(height, 40);
};

// API URL configuration
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;

// Helper function to generate a unique application ID
const generateApplicationId = (): string => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
  const year = String(now.getFullYear());
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
  const randomChar = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 32)]; // Excludes confusing chars I/O/0/1
  return `CNG-${day}${month}${year}${hours}${minutes}${seconds}-${milliseconds}${randomChar}`;
};

// Helper to derive Application Date (DD/MM/YYYY, Day) from the application ID pattern CNG-DDMMYYYYHHMMSS
const formatApplicationDateFromId = (applicationId: string): string | null => {
  if (!applicationId) return null;

  // Extract the numeric part after the prefix (if present)
  const match = applicationId.match(/CNG-(\d{14})/);
  let numeric = match ? match[1] : applicationId.replace(/[^0-9]/g, '');

  if (numeric.length < 8) return null;

  const dayNum = parseInt(numeric.slice(0, 2), 10);
  const monthNum = parseInt(numeric.slice(2, 4), 10);
  const yearNum = parseInt(numeric.slice(4, 8), 10);

  if (!dayNum || !monthNum || !yearNum) return null;

  const date = new Date(yearNum, monthNum - 1, dayNum);
  if (isNaN(date.getTime())) return null;

  const day = String(dayNum).padStart(2, '0');
  const month = String(monthNum).padStart(2, '0');
  const year = String(yearNum);

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayLabel = dayNames[date.getDay()];

  // Example: 28/12/2025, Sun
  return `${day}/${month}/${year}, ${dayLabel}`;
};

const calculateAgeFromDob = (dobString: string): number | null => {
  if (!dobString) return null;
  const dob = new Date(dobString);
  if (isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
};

// A4 Dimensions (approximate for screen, maintaining aspect ratio)
// 210mm / 297mm = 0.707
const A4_WIDTH_PX = 794; // Approx 210mm at 96 DPI

// Skeleton animation keyframes
const skeletonPulse = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

// Countdown modal animations
const countdownPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const advertLocationGlow = keyframes`
  0%, 100% { box-shadow: 0 0 4px rgba(37, 99, 235, 0.3), 0 0 12px rgba(37, 99, 235, 0.1); }
  50% { box-shadow: 0 0 8px rgba(37, 99, 235, 0.5), 0 0 20px rgba(37, 99, 235, 0.2); }
`;

const srNoGuideEnter = keyframes`
  from { opacity: 0; transform: scale(0.92) translateY(8px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const srNoOverlayFade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const SkeletonBox = styled.div`
  width: 120px;
  height: 80px;
  border-radius: 4px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: ${skeletonPulse} 1.5s ease-in-out infinite;
`;

// Hidden container for rendering pages for PDF generation
const HiddenPageContainer = styled.div`
  position: absolute;
  left: -9999px;
  top: -9999px;
  width: ${A4_WIDTH_PX}px;
  height: auto;
  overflow: hidden;
`;

const FileUploadProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background-color: #e9ecef;
  border-radius: 3px;
  margin-top: 5px;
  overflow: hidden;
`;

const FileUploadProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => `${props.progress}%`};
  background-color: #6cb288;
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const FileUploadButton = styled.button<{ disabled?: boolean }>`
  background-color: ${props => props.disabled ? '#f0f0f0' : '#6cb288'};
  color: ${props => props.disabled ? '#999999' : 'white'};
  padding: 10px 18px;
  border: none;
  border-radius: 6px;
  font-size: 0.9em;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background-color: ${props => props.disabled ? '#f0f0f0' : '#4d9e70'};
  }
`;

const FileUploadStatus = styled.div`
  font-size: 0.85em;
  margin-top: 6px;
  font-weight: 500;
`;

const ClickableFileName = styled.span`
  color: #007bff;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: #0056b3;
  }
`;

const FileUploadSuccess = styled.div`
  color: #28a745;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.85em;
  margin-top: 6px;
`;

const UploadErrorModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const UploadErrorContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 450px;
  width: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  text-align: center;
`;

const UploadSpinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #ffffff;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ShakeAnimation = styled.div<{ $isShaking: boolean }>`
  animation: ${props => props.$isShaking ? 'shake 0.5s ease-in-out' : 'none'};
  border-radius: 8px;
  transition: box-shadow 0.3s ease;
  ${props => props.$isShaking && `
    box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.4);
  `}
  
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
    20%, 40%, 60%, 80% { transform: translateX(8px); }
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 5px;
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const FadeInContainer = styled.div`
  animation: fadeInSmooth 0.3s ease-out forwards;
  
  @keyframes fadeInSmooth {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SlideDownContainer = styled.div`
  animation: slideDown 0.5s ease-out forwards;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
      max-height: 0;
    }
    to {
      opacity: 1;
      transform: translateY(0);
      max-height: 10000px;
    }
  }
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  gap: 6px;
  
  input[type="radio"] {
    appearance: none;
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    border: 2px solid #6cb288;
    border-radius: 50%;
    outline: none;
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;
    
    &:checked {
      border-color: #6cb288;
      background-color: #f0f9f4;
      
      &::after {
        content: "";
        position: absolute;
        top: 3px;
        left: 3px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #6cb288;
      }
    }
  }
  
  &:hover input[type="radio"]:not(:disabled) {
    border-color: #4d9e70;
    box-shadow: 0 0 0 1px rgba(108, 178, 136, 0.2);
  }
  
  /* Disabled state styling */
  &:has(input[type="radio"]:disabled) {
    cursor: not-allowed;
    opacity: 0.6;
    color: #9ca3af;
  }
  
  input[type="radio"]:disabled {
    cursor: not-allowed;
    border-color: #d1d5db;
    background-color: #f3f4f6;
    
    &:checked {
      border-color: #9ca3af;
      background-color: #e5e7eb;
      
      &::after {
        background-color: #9ca3af;
      }
    }
  }
`;

const LocationCard = styled.div`
  margin-top: 15px;
  padding: 18px 20px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  background: linear-gradient(135deg, #f9fafb, #e3fcef);
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
`;

const FloatingBackButton = styled.button`
  position: fixed;
  bottom: 30px;
  left: 30px;
  background: linear-gradient(145deg, #4a6cf7, #5e54e2);
  color: white;
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(74, 108, 247, 0.3), 0 3px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  -webkit-tap-highlight-color: transparent;
  
  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
    bottom: 20px;
    left: 20px;
    font-size: 18px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 10px 20px rgba(74, 108, 247, 0.4), 0 6px 6px rgba(0, 0, 0, 0.1);
    
    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0) scale(0.98);
    box-shadow: 0 3px 8px rgba(74, 108, 247, 0.2), 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &::after {
    content: 'Back';
    position: absolute;
    font-size: 12px;
    bottom: 7px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::after {
    opacity: 1;
  }
`;

// Mobile toggle button to hide/show floating elements
const MobileFloatingToggle = styled.button<{ isHidden: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    bottom: 18px;
    right: 18px;
    width: 48px;
    height: 48px;
    border-radius: 14px;
    border: none;
    background: ${props => props.isHidden 
      ? 'linear-gradient(145deg, #a8e6cf 0%, #88d4ab 50%, #6bc293 100%)' 
      : 'linear-gradient(145deg, #ffd6d6 0%, #ffb8b8 50%, #ff9999 100%)'};
    color: ${props => props.isHidden ? '#2d6a4f' : '#c92a2a'};
    font-size: 20px;
    cursor: pointer;
    z-index: 9999;
    align-items: center;
    justify-content: center;
    box-shadow: ${props => props.isHidden 
      ? '0 4px 16px rgba(107, 194, 147, 0.35), 0 2px 6px rgba(0, 0, 0, 0.08)' 
      : '0 4px 16px rgba(255, 153, 153, 0.4), 0 2px 6px rgba(0, 0, 0, 0.08)'};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(8px);
    
    svg {
      width: 22px;
      height: 22px;
      stroke-width: 2;
    }
    
    &:active {
      transform: scale(0.92);
    }
    
    &:hover {
      box-shadow: ${props => props.isHidden 
        ? '0 6px 20px rgba(107, 194, 147, 0.45), 0 3px 8px rgba(0, 0, 0, 0.1)' 
        : '0 6px 20px rgba(255, 153, 153, 0.5), 0 3px 8px rgba(0, 0, 0, 0.1)'};
    }
  }
`;

const A4_HEIGHT_PX = 1123; // Approx 297mm at 96 DPI

const TopRightButtonContainer = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 10;
`;

const ActionButtonsContainer = styled.div`
  margin-top: 30px;
  width: 100%;
`;

const CenteredButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 15px; 
`;

const RowButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 20px;
`;

// Progress bar at top of page
const ProgressBarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => `${props.progress}%`};
  background-color: #6cb288;
  transition: width 0.5s ease-in-out;
`;

const ProgressLabel = styled.div`
  position: fixed;
  top: 10px;
  right: 10px;
  background-color: #f8fcfa;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 1000;
`;

// Zoom controls
const ZoomControlsContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
  
  @media (max-width: 850px) {
    display: none; /* Hide zoom controls on mobile - form is already responsive */
  }
`;

const ZoomButton = styled.button`
  width: 30px;
  height: 30px;
  background-color: white;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  padding: 0;
  
  &:hover {
    background-color: #f8fcfa;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const OfflineIndicator = styled.div`
  background-color: #dc3545; /* Red for offline */
  color: white;
  text-align: center;
  padding: 10px;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2000; /* Ensure it's above other elements */
  font-size: 0.9em;
`;

const PageWrapper = styled.div<{ scale: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 20px;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  background-color: #f5f5f5;
  transform: scale(${props => props.scale});
  transform-origin: center top;
  transition: transform 0.3s ease;
  overflow-x: hidden;
  
  @media (max-width: 850px) {
    padding: 10px;
    transform: none !important;
  }
  
  @media (max-width: 480px) {
    padding: 5px;
  }
`;

const A4PageContainer = styled.div`
  width: ${A4_WIDTH_PX}px;
  max-width: 100%;
  min-height: ${A4_HEIGHT_PX}px;
  background-color: white;
  padding: 50px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07);
  box-sizing: border-box;
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  color: #212529;
  font-size: 13px;
  line-height: 1.6;
  
  @media (max-width: 850px) {
    width: 100%;
    min-height: auto;
    padding: 20px;
    border-radius: 8px;
    margin: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    margin: 5px;
    font-size: 12px;
  }
`;

const LogoImage = styled.img`
  height: 75px; /* Increased by 25% from 60px */
  margin-bottom: 10px;
`;

const SubTitle = styled.p`
  font-size: 0.96em; /* 70% of original 0.8em */
  font-style: italic;
  color: #333; /* Darker text */
  text-align: center;
  margin-bottom: 20px;
  font-weight: 500;
`;

const TopSectionLayout = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  
  @media (max-width: 600px) {
    flex-direction: column-reverse;
    align-items: center;
    gap: 15px;
  }
`;

const LeftFields = styled.div`
  flex: 3;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 600px) {
    width: 100%;
  }
`;

const RightPhotoPlaceholder = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-left: 20px;
  
  @media (max-width: 600px) {
    padding-left: 0;
    margin-bottom: 15px;
  }
`;

const ApplicationDateBox = styled.div`
  width: 150px;
  min-height: 24px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  background-color: #f8f9fa;
  font-size: 1em;
  font-weight: 600;
  color: #333;
`;

const ApplicationDateLabel = styled.div`
  width: 150px;
  margin-bottom: 4px;
  font-size: 0.85em;
  font-weight: 500;
  color: #333;
  text-align: center;
`;

const PhotoBox = styled.div<{ disabled?: boolean }>`
  width: 150px; /* As per form image */
  height: 180px; /* As per form image */
  border: 3px solid transparent;
  border-radius: 8px;
  background: linear-gradient(#f9f9f9, #f9f9f9) padding-box,
              linear-gradient(135deg, #1a5276, #3498db, #1a5276, #2980b9) border-box;
  box-shadow: 0 4px 15px rgba(26, 82, 118, 0.25), 
              inset 0 0 0 1px rgba(255, 255, 255, 0.5);
  display: flex;
  flex-direction: column; 
  justify-content: center;
  align-items: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  background-color: #f9f9f9;
  text-align: center; /* For placeholder text */
  position: relative; /* For positioning the animated image */
  overflow: hidden;  /* To clip the image during animation */
  transition: box-shadow 0.3s ease, transform 0.2s ease;
  
  &:hover {
    box-shadow: 0 6px 20px rgba(26, 82, 118, 0.35),
                inset 0 0 0 1px rgba(255, 255, 255, 0.6);
    transform: ${({ disabled }) => (disabled ? 'none' : 'translateY(-2px)')};
  }
`;

const PhotoBoxText = styled.p`
  font-size: 0.8em;
  color: #555;
  margin: 5px 0 0 0;
  padding: 5px;
`;

const AnimatedPhotoImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  clip-path: inset(100% 0 0 0); /* Initial: hidden, revealed from bottom (top edge 100% clipped) */
  transition: clip-path 2s ease-in-out; /* Animation duration 2 seconds */

  &.reveal {
    clip-path: inset(0 0 0 0); /* Final: fully revealed */
  }
`;

const UploadConfirmationText = styled.p`
  font-size: 0.8em;
  color: green;
  margin-top: 8px;
  text-align: center;
  font-weight: bold;
  height: 1.2em; /* Reserve space to prevent layout shift */
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 30px; /* Increased spacing */
  border-bottom: 1px solid #dee2e6; /* Softer border */
  padding-bottom: 20px;
`;



const FormTitle = styled.h1`
  font-size: 1.5em; /* Larger title */
  font-weight: 600;
  text-align: center;
  margin: 0 0 15px 0; /* Adjusted margin */
  text-transform: uppercase;
  letter-spacing: 0.5px; /* Adjusted for a tighter look */
  color: #004085; /* Dark blue for title */
`;



const FormContentContainer = styled.div`
  /* font-size and line-height will now be inherited from A4PageContainer or overridden by specific elements */
`;

const DownloadButton = styled.button`
  padding: 8px 12px;
  margin-left: 10px;
  font-size: 0.9em;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;







const FormFieldRow = styled.div`
  display: flex;
  align-items: baseline; /* Better for aligning text labels with input text */
  margin-bottom: 12px; /* Increased spacing */
`;

const AppNoRowContainer = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 12px;
  position: relative; /* For positioning message if needed */
`;

const GenerateAppNoButton = styled.button`
  padding: 8px 12px;
  margin-left: 10px;
  font-size: 0.9em;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const AppNoMessageText = styled.p`
  font-size: 0.8em;
  color: green;
  margin-top: -8px; /* Adjust to position nicely below the row */
  margin-bottom: 8px;
  min-height: 1.2em; /* Reserve space */
  text-align: left; /* Align with label */
  padding-left: calc( /* Approximate padding to align with input start if label width is known or fixed */
    1em /* Label font-size approx */ * 
    20 /* Avg chars in 'Application No. (for office use):' */ * 
    0.6 /* Avg char width factor */ + 
    8px /* Label margin-right */
  );
`;

const FormFieldFullWidth = styled.div`
  display: flex;
  flex-direction: column; 
  margin-bottom: 12px; /* Increased spacing */
`;

const Label = styled.label`
  margin-right: 8px;
  white-space: nowrap;
  font-weight: 500; /* Semi-bold for better emphasis */
  color: #495057; /* Standard label color */
  font-size: 0.9em; /* Slightly smaller than input text if base is 13px */
  margin-bottom: 4px; /* Space if label is on its own line above input */
`;

const LabelInline = styled.label`
  margin: 0 8px 0 4px;
  white-space: nowrap;
  font-weight: 500;
  color: #495057;
  font-size: 0.9em;
`;

const StyledInput = styled.input`
  flex-grow: 1;
  border: 1px solid #a8d0b9;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 16px; /* 16px prevents iOS zoom on focus */
  font-family: inherit;
  background-color: #f8fcfa;
  color: #495057;
  line-height: 1.5;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  -webkit-appearance: none;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #6cb288;
    box-shadow: 0 0 0 0.2rem rgba(108, 178, 136, 0.25);
  }

  &:hover {
    border-color: #8ac0a0;
  }

  &::placeholder {
    color: #7ea38c;
    opacity: 1; 
  }

  &[type="date"],
  &[type="datetime-local"],
  &[type="month"],
  &[type="time"] {
    padding-right: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
    font-size: 16px;
  }
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  border: 1px solid #a8d0b9;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 16px; /* 16px prevents iOS zoom */
  font-family: inherit;
  background-color: #f8fcfa;
  color: #495057;
  line-height: 1.5;
  resize: vertical;
  min-height: 40px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  -webkit-appearance: none;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #6cb288;
    box-shadow: 0 0 0 0.2rem rgba(108, 178, 136, 0.25);
  }

  &:hover {
    border-color: #8ac0a0;
  }

  &::placeholder {
    color: #7ea38c;
    opacity: 1; 
  }
  
  @media (max-width: 480px) {
    padding: 10px;
    font-size: 16px;
    min-height: 60px;
  }
`;



const InstructionText = styled.p`
  font-size: 9px;
  font-style: italic;
  color: #555;
  margin: 10px 0;
  text-align: left;
`;

const SectionTitle = styled.h2`
  font-size: 1.25em; /* Relative to base font size */
  font-weight: 600;
  color: #0056b3; /* A darker primary blue */
  margin-top: 35px;
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
`;

const IndividualFields = styled.div`
  /* Styles for the individual fields section if needed */
`;

// Styling for Radio Buttons
const RadioGroupContainer = styled.div`
  display: flex;
  flex-wrap: wrap; /* Allow wrapping if too many options */
  align-items: center;
  margin-top: 5px; /* Space above radio group */
  margin-bottom: 5px; /* Space below radio group */
`;

const RadioOptionWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: 20px; /* Space between radio options */
  margin-bottom: 5px; /* Space for wrapped items */

  input[type="radio"] {
    margin-right: 6px;
    width: 16px; /* Custom size */
    height: 16px; /* Custom size */
    accent-color: #007bff; /* Modern way to color radio/checkbox */
    cursor: pointer;
  }

  label {
    font-weight: normal; /* Normal weight for radio labels */
    font-size: 1em;
    color: #495057;
    cursor: pointer;
  }
`;

// Styling for Select (Dropdown)
const StyledSelect = styled.select`
  flex-grow: 1;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 1em;
  font-family: inherit;
  background-color: #fff;
  color: #495057;
  line-height: 1.5;
  appearance: none; /* Remove default system appearance */
  background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20fill%3D%22%236c757d%22%20d%3D%22M7.41%208.59L12%2013.17l4.59-4.58L18%2010l-6%206-6-6%201.41-1.41z%22%2F%3E%3C%2Fsvg%3E');
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px 16px;
  padding-right: 36px; /* Make space for the arrow */
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }

  option {
    font-weight: normal;
  }
`;


const SaveDraftButton = styled.button`
  background-color: #ffc107; /* Amber color for draft */
  color: #212529; /* Dark text for contrast */
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 15px; /* Space from submit button or other elements */
  margin-right: 10px; /* Space from submit button if on same line */
  display: inline-block; /* Allow side-by-side with submit if desired */
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #e0a800; /* Darker amber on hover */
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #c69500;
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }

  &:disabled {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
  }
`;

const PayNowButton = styled.button`
  background-color: #007bff;
  color: white;
  padding: 10px 25px;
  border: none;
  border-radius: 6px; /* Slightly larger radius */
  font-size: 1em; /* Relative to base */
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 30px; /* More space above */
  display: block; 
  margin-left: auto;
  margin-right: auto;
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #0069d9; /* Darker shade on hover */
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #0056b3;
    transform: translateY(1px); /* Slight press effect */
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }
`;

const PageNumber = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  font-size: 0.8em;
  color: #777;
`;

const NextPageButton = styled.button`
  position: absolute;
  bottom: 15px;
  right: 60px;
  background-color: #6cb288;
  color: white;
  padding: 8px 25px;
  border: none;
  border-radius: 4px;
  font-size: 1em;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #4d9e70;
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const SaveIndicator = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 50%;
  left: 20px;
  transform: translateY(-50%);
  background-color: #f8fcfa;
  color: #6cb288;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #a8d0b9;
  font-size: 0.8em;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 10000;
`;

// PDF Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999; /* Ensure it is on top of all other content */
  backdrop-filter: blur(3px);
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  width: 400px;
  max-width: 90%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
  font-weight: 600;
`;

const ProgressContainer = styled.div`
  width: 100%;
  background-color: #f0f0f0;
  border-radius: 10px;
  height: 10px;
  margin: 20px 0;
  overflow: hidden;
`;

const ProgressBar = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => props.progress}%;
  background: linear-gradient(90deg, #4c6ef5, #3b5bdb);
  border-radius: 10px;
  transition: width 0.3s ease-in-out;
`;

const ProgressText = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
`;

const DeclarationText = styled.p`
  font-size: 0.9em;
  margin-top: 25px;
  margin-bottom: 15px;
  line-height: 1.5;
  text-align: justify;
`;

// Confirmation Modal Components
const ConfirmModalContent = styled(ModalContent)`
  width: 380px;
  animation: modalSlideIn 0.3s ease-out;
  
  @keyframes modalSlideIn {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ConfirmModalIcon = styled.div`
  font-size: 3.5rem;
  margin-bottom: 1rem;
`;

const ConfirmModalTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #2d3748;
  font-size: 1.3rem;
  font-weight: 600;
`;

const ConfirmModalText = styled.p`
  margin: 0 0 1.5rem 0;
  color: #4a5568;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const ConfirmModalButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  width: 100%;
`;

const ConfirmButton = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ConfirmButtonPrimary = styled(ConfirmButton)`
  background-color: #667eea;
  color: white;
  
  &:hover {
    background-color: #5a67d8;
  }
`;

const ConfirmButtonSecondary = styled(ConfirmButton)`
  background-color: #e2e8f0;
  color: #4a5568;
  
  &:hover {
    background-color: #cbd5e0;
  }
`;

const ConfirmButtonDanger = styled(ConfirmButton)`
  background-color: #f56565;
  color: white;
  
  &:hover {
    background-color: #e53e3e;
  }
`;

// Partner Selection Modal Components
const PartnerSelectionModal = styled(ModalContent)`
  width: 450px;
  max-width: 90vw;
  animation: modalSlideIn 0.3s ease-out;
`;

const PartnerSelectionTitle = styled.h2`
  margin: 0 0 0.5rem 0;
  color: #2d3748;
  font-size: 1.4rem;
  font-weight: 700;
  text-align: center;
`;

const PartnerSelectionSubtitle = styled.p`
  margin: 0 0 1.5rem 0;
  color: #718096;
  font-size: 0.9rem;
  text-align: center;
`;

const PartnerOptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 400px;
  overflow-y: auto;
`;

const PartnerOption = styled.button<{ isMain?: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: ${props => props.isMain ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f7fafc'};
  color: ${props => props.isMain ? 'white' : '#2d3748'};
  border: 2px solid ${props => props.isMain ? 'transparent' : '#e2e8f0'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: ${props => props.isMain ? 'transparent' : '#667eea'};
  }
`;

const PartnerOptionIcon = styled.div`
  font-size: 2rem;
`;

const PartnerOptionInfo = styled.div`
  flex: 1;
`;

const PartnerOptionName = styled.div`
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.25rem;
`;

const PartnerOptionRole = styled.div<{ isMain?: boolean }>`
  font-size: 0.8rem;
  color: ${props => props.isMain ? 'rgba(255,255,255,0.8)' : '#718096'};
`;

const LockedSectionBanner = styled.div`
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
  color: #92400e;
`;

// Partner Setup Modal Components - Horizontal Loveable Style
const PartnerSetupModalContent = styled(ModalContent)`
  width: auto;
  min-width: 400px;
  max-width: min(95vw, 900px);
  max-height: 85vh;
  overflow-y: auto;
  animation: modalSlideIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  padding: 1.25rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);

  @keyframes cardFadeIn {
    from {
      opacity: 0;
      transform: translateY(8px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const PartnerSetupTitle = styled.h2`
  margin: 0 0 0.15rem 0;
  color: #1d1d1f;
  font-size: 1.1rem;
  font-weight: 600;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  letter-spacing: -0.02em;
`;

const PartnerSetupSubtitle = styled.p`
  margin: 0 0 0.75rem 0;
  color: #86868b;
  font-size: 0.75rem;
  text-align: center;
  font-weight: 400;
`;

const PartnerSetupCardsContainer = styled.div`
  display: flex;
  gap: 0.625rem;
  overflow-x: auto;
  padding: 0.25rem 0.125rem 0.375rem;
  scroll-behavior: smooth;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d1d6;
    border-radius: 2px;
  }
`;

const PartnerSetupRow = styled.div<{ isMain?: boolean; index?: number }>`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.625rem;
  background: ${props => props.isMain 
    ? 'linear-gradient(180deg, #f0f5ff 0%, #e8f0fe 100%)' 
    : '#fafafa'};
  border-radius: 10px;
  border: 1px solid ${props => props.isMain ? '#007aff' : '#e5e5ea'};
  min-width: 220px;
  max-width: 240px;
  flex-shrink: 0;
  animation: cardFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${props => (props.index || 0) * 0.05}s;
  opacity: 0;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${props => props.isMain ? '#007aff' : '#c7c7cc'};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

const PartnerSetupRowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.125rem;
`;

const PartnerSetupRowTitle = styled.div`
  font-weight: 500;
  color: #1d1d1f;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const PartnerSetupInputRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const PartnerSetupInputGroup = styled.div<{ hasError?: boolean }>`
  label {
    display: block;
    font-size: 0.6rem;
    color: #86868b;
    margin-bottom: 0.1rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }
  
  input {
    width: 100%;
    padding: 0.4rem 0.5rem;
    border: 1px solid ${props => props.hasError ? '#ff3b30' : '#d1d1d6'};
    border-radius: 6px;
    font-size: 0.7rem;
    transition: all 0.15s ease;
    background: #fff;
    
    &:focus {
      outline: none;
      border-color: ${props => props.hasError ? '#ff3b30' : '#007aff'};
      box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(255, 59, 48, 0.12)' : 'rgba(0, 122, 255, 0.12)'};
    }
    
    &:disabled {
      background: #f5f5f7;
      cursor: not-allowed;
    }
    
    &::placeholder {
      color: #aeaeb2;
    }
  }
`;

const PartnerSetupUploadBtn = styled.label<{ hasFile?: boolean; isUploading?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.3rem 0.5rem;
  background: ${props => props.hasFile ? '#34c759' : '#007aff'};
  color: white;
  border-radius: 5px;
  font-size: 0.65rem;
  font-weight: 500;
  cursor: ${props => props.isUploading ? 'wait' : 'pointer'};
  opacity: ${props => props.isUploading ? 0.7 : 1};
  transition: all 0.15s ease;
  white-space: nowrap;
  
  &:hover {
    filter: brightness(1.05);
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  input {
    display: none;
  }
`;

const PartnerSetupDetectedName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.4rem;
  background: #f0fdf4;
  border: 1px solid #34c759;
  border-radius: 4px;
  font-size: 0.6rem;
  color: #166534;
`;

const PartnerSetupCountSelector = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.625rem;
  padding: 0.4rem 0.75rem;
  background: #f5f5f7;
  border-radius: 8px;
  border: none;
`;

const PartnerCountBtn = styled.button<{ disabled?: boolean }>`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  background: ${props => props.disabled ? '#d1d1d6' : '#007aff'};
  color: white;
  font-size: 1rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    filter: brightness(1.1);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

const PartnerCountDisplay = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1d1d1f;
  min-width: 32px;
  text-align: center;
`;

const PartnerSetupActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding-top: 0.625rem;
  border-top: 1px solid #e5e5ea;
  justify-content: flex-end;
`;

const PartnerSetupBtn = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.45rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  
  background: ${props => props.variant === 'primary' ? '#007aff' : '#e5e5ea'};
  color: ${props => props.variant === 'primary' ? 'white' : '#1d1d1f'};
  
  &:hover {
    filter: brightness(${props => props.variant === 'primary' ? '1.05' : '0.95'});
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SignatureSection = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 40px;
  align-items: flex-end; /* Align items to the bottom for varying heights */
`;

const SignatureField = styled.div`
  flex-basis: 45%; // Adjust as needed
  display: flex;
  flex-direction: column;

  span { // For the "Date:" and "Signature of Applicant:" text
    margin-bottom: 8px;
    font-weight: 500;
    color: #495057;
    font-size: 0.9em;
  }

  .signature-line {
    border-bottom: 1px solid #495057;
    height: 30px; // Space for signature
    /* User will sign here, so it's not an input */
  }
`;

// Interface for partner/director details (for LLP, Pvt Ltd, Public Ltd, etc.)
interface PartnerDirectorDetail {
  id: string; // Unique identifier for each partner/director
  photo?: File | string | null; // Profile photo for partner/director
  nameInBlocks: string;
  fatherOrHusbandName: string;
  dateOfBirth: string;
  panCardFile?: File | string | null;
  dobProofFile?: File | string | null;
  isMinimumEducationMet: string;
  qualification: string;
  qualificationProofFile?: File | string | null;
  currentAddress: string;
  permanentAddress: string;
  permanentAddressSameAsCurrent: boolean;
  addressProofFile?: File | string | null;
  personalMobile: string;
  secondaryMobile: string;
  emailId: string;
  nationality: string;
  gender: string;
  maritalStatus: string;
  maritalStatusOther: string;
  // Identification & Statutory Details
  aadhaarNumber: string;
  aadhaarVerified: string;
  aadhaarFile: File | string | null;
  panNumberStatutory: string;
  panVerified: string;
  panStatutoryFile: File | string | null;
  gstNumber: string;
  gstVerified: string;
  gstFile: File | string | null;
  identityProofVoterId: boolean;
  identityProofPassport: boolean;
  identityProofDrivingLicense: boolean;
  identityDocumentNumber: string;
  voterIdNumber: string;
  passportNumber: string;
  drivingLicenseNumber: string;
  voterIdFile: File | string | null;
  passportFile: File | string | null;
  drivingLicenseFile: File | string | null;
  // Educational Qualification
  eduSscBoard: string;
  eduSscMarksObtained: string;
  eduSscOutOfMarks: string;
  eduSscPercentage: string;
  eduSscYearOfPassing: string;
  eduSscCertificateFile: File | string | null;
  eduHscBoard: string;
  eduHscMarksObtained: string;
  eduHscOutOfMarks: string;
  eduHscPercentage: string;
  eduHscYearOfPassing: string;
  eduHscCertificateFile: File | string | null;
  eduGraduateBoard: string;
  eduGraduateMarksObtained: string;
  eduGraduateOutOfMarks: string;
  eduGraduatePercentage: string;
  eduGraduateYearOfPassing: string;
  eduGraduateCertificateFile: File | string | null;
  eduOthersBoard: string;
  eduOthersMarksObtained: string;
  eduOthersOutOfMarks: string;
  eduOthersPercentage: string;
  eduOthersYearOfPassing: string;
  eduOthersCertificateFile: File | string | null;
  // Business / Employment Details
  presentOccupation: string;
  businessEmploymentDetails: string;
  totalWorkExperienceYears: string;
  relevantIndustryExperience: string;
  // Financial Capability
  annualIncome: string;
  netWorthAtApplication: string;
  sourceOfInvestment: string;
  abilityToMeetRequiredInvestment: string;
  // Financial Capability Document Checklist
  finDocProjectReport: string;
  finDocProjectReportFile: File | string | null;
  finDocBankStatement: string;
  finDocBankStatementFile: File | string | null;
  finDocFdTdReceipts: string;
  finDocFdTdReceiptsFile: File | string | null;
  finDocInvestmentCertificates: string;
  finDocInvestmentCertificatesFile: File | string | null;
  finDocMutualFundStatement: string;
  finDocMutualFundStatementFile: File | string | null;
  finDocDematStatement: string;
  finDocDematStatementFile: File | string | null;
  finDocCaCertificate: string;
  finDocCaCertificateFile: File | string | null;
  finDocDpCertificate: string;
  finDocDpCertificateFile: File | string | null;
  finDocAttestedFinancials: string;
  finDocAttestedFinancialsFile: File | string | null;
  finDocNotarizedAffidavit: string;
  finDocNotarizedAffidavitFile: File | string | null;
  finDocBankLetter: string;
  finDocBankLetterFile: File | string | null;
  finDocOwnershipDocs: string;
  finDocOwnershipDocsFile: File | string | null;
  // Appendix Files
  affidavitFile?: File | string | null;
  appendixBFile?: File | string | null;
  appendixCFile?: File | string | null;
  appendixDFile?: File | string | null;
  appendixEFile?: File | string | null;
  appendixFFile?: File | string | null;
}

interface FormData {
  applicationNoOfficeUse: string;
  location: string;
  locationDistrict: string;
  locationState: string;
  locationStretchSrNoArea: string;
  locationLocality: string;
  locationAreaStretch: string;

  // New detailed location fields mapped from admin XLSX
  locationSrNo: string; // Sr.No
  locationCA: string; // CA
  locationArea: string; // Area
  locationStretchSide: string; // Strech Side(LHS/RHS)
  locationFrom: string;
  locationFromLatLong: string;
  locationTo: string;
  locationToLatLong: string;
  locationMinPlotFrontage: string; // Minimum Plot Size Required (Frontage in Meter)
  hasSufficientFrontage: string; // Does applicant have >= required frontage
  locationRoadType: string; // Road NH/STATE/CITY
  locationMinPlotDepth: string; // Minimum Plot Size Required(Depth in Meter)
  hasSufficientDepth: string; // Does applicant have >= required depth

  advertisedOn: string; // Date
  advertisedIn: string; // Newspaper/Medium
  advertisementSource: string; // New field for how they found out about the ad
  dealershipType: string; // New field: Type of Applicant (Individual, Proprietorship, etc.)
  entityName: string; // Partnership/Pvt Ltd/Public Ltd name
  numberOfPartners: number; // Number of partners/directors for Partnership, Pvt Ltd, Public Ltd
  partnerDirectorDetails: PartnerDirectorDetail[]; // Details of additional partners/directors
  nameInBlocks: string;
  fatherOrHusbandName: string;
  spouseEmploymentStatus: string; // New field: Husband/Spouse Employment Status
  spouseEmploymentDetails: string; // New field: Husband/Spouse Employment Details (if applicable)
  dateOfBirth: string;
  panCardFile?: File | string | null;
  dobProofFile?: File | string | null;
  isMinimumEducationMet: string; // 'Yes' or 'No' - Are you Class 10 (High School) pass?
  qualification: string;
  qualificationProofFile?: File | string | null;
  currentAddress: string;
  permanentAddress: string;
  permanentAddressSameAsCurrent: boolean;
  addressProofFile?: File | string | null;
  personalMobile: string;
  secondaryMobile: string;
  emailId: string;
  nationality: string;
  gender: string;
  maritalStatus: string;
  maritalStatusOther: string;
  errors?: {
    personalMobile?: string;
    secondaryMobile?: string;
    emailId?: string;
  };
  
  // Identification & Statutory Details
  aadhaarNumber: string;
  aadhaarVerified: string;
  aadhaarFile?: File | string | null;
  panNumberStatutory: string;
  panVerified: string;
  panStatutoryFile?: File | string | null;
  gstNumber: string;
  gstVerified: string;
  gstFile?: File | string | null;
  identityProofVoterId: boolean;
  identityProofPassport: boolean;
  identityProofDrivingLicense: boolean;
  identityDocumentNumber: string;
  voterIdNumber: string;
  passportNumber: string;
  drivingLicenseNumber: string;
  voterIdFile?: File | string | null;
  passportFile?: File | string | null;
  drivingLicenseFile?: File | string | null;
  
  // Educational Qualification
  eduSscBoard: string;
  eduSscMarksObtained: string;
  eduSscOutOfMarks: string;
  eduSscPercentage: string;
  eduSscYearOfPassing: string;
  eduSscCertificateFile?: File | string | null;
  eduHscBoard: string;
  eduHscMarksObtained: string;
  eduHscOutOfMarks: string;
  eduHscPercentage: string;
  eduHscYearOfPassing: string;
  eduHscCertificateFile?: File | string | null;
  eduGraduateBoard: string;
  eduGraduateMarksObtained: string;
  eduGraduateOutOfMarks: string;
  eduGraduatePercentage: string;
  eduGraduateYearOfPassing: string;
  eduGraduateCertificateFile?: File | string | null;
  eduOthersBoard: string;
  eduOthersMarksObtained: string;
  eduOthersOutOfMarks: string;
  eduOthersPercentage: string;
  eduOthersYearOfPassing: string;
  eduOthersCertificateFile?: File | string | null;
  
  // Appendix-A Affidavit (for Individual/OPC, Proprietorship, LLP, Pvt Ltd, Public Ltd)
  affidavitFile?: File | string | null;
  
  // Appendix-B Affidavit (for Non Individual applicant entity, Other)
  affidavitBFile?: File | string | null;
  
  // Non Individual Applicant Entity fields
  niTypeOfApplicant: string; // Registered Society/Company/Charitable Trust/Govt. Org.
  niNameOfEntity: string;
  niDateOfRegistrationIncorporation: string;
  niCompanyRegisteredInIndia: string; // Yes/No
  niNameOfAuthorizedSignatory: string;
  niMobilePrimary: string;
  niMobileAlternate: string;
  niPhoneResidential: string;
  niPhoneOfficial: string;
  niFax: string;
  niEmailId: string;
  // Profit/Loss for 3 Financial Years
  niFinancialYear1: string;
  niProfitAfterTax1: string;
  niLoss1: string;
  niFinancialYear2: string;
  niProfitAfterTax2: string;
  niLoss2: string;
  niFinancialYear3: string;
  niProfitAfterTax3: string;
  niLoss3: string;
  // Current Address
  niCurrentFlatHouseNo: string;
  niCurrentBuildingName: string;
  niCurrentPlotNo: string;
  niCurrentStreetName: string;
  niCurrentLocality: string;
  niCurrentLandmark: string;
  niCurrentCityTown: string;
  niCurrentDistrict: string;
  niCurrentState: string;
  niCurrentPinCode: string;
  niCurrentMobilePrimary: string;
  niCurrentMobileAlternate: string;
  niCurrentPhoneResidential: string;
  niCurrentPhoneOfficial: string;
  niCurrentFax: string;
  niCurrentEmailId: string;
  // Permanent Address
  niPermanentAddressSameAsCurrent: boolean;
  niPermanentFlatHouseNo: string;
  niPermanentBuildingName: string;
  niPermanentPlotNo: string;
  niPermanentStreetName: string;
  niPermanentLocality: string;
  niPermanentLandmark: string;
  niPermanentCityTown: string;
  niPermanentDistrict: string;
  niPermanentState: string;
  niPermanentPinCode: string;
  niPermanentMobilePrimary: string;
  niPermanentMobileAlternate: string;
  niPermanentPhoneResidential: string;
  
  // BUSINESS / EMPLOYMENT DETAILS
  presentOccupation: string;
  businessEmploymentDetails: string;
  totalWorkExperienceYears: string;
  relevantIndustryExperience: string;
  
  // FINANCIAL CAPABILITY
  annualIncome: string;
  netWorthAtApplication: string;
  sourceOfInvestment: string;
  abilityToMeetRequiredInvestment: string;
  
  // FINANCIAL CAPABILITY – DOCUMENT CHECKLIST
  finDocProjectReport: string;
  finDocProjectReportFile?: File | string | null;
  finDocBankStatement: string;
  finDocBankStatementFile?: File | string | null;
  finDocFdTdReceipts: string;
  finDocFdTdReceiptsFile?: File | string | null;
  finDocInvestmentCertificates: string;
  finDocInvestmentCertificatesFile?: File | string | null;
  finDocMutualFundStatement: string;
  finDocMutualFundStatementFile?: File | string | null;
  finDocDematStatement: string;
  finDocDematStatementFile?: File | string | null;
  finDocCaCertificate: string;
  finDocCaCertificateFile?: File | string | null;
  finDocDpCertificate: string;
  finDocDpCertificateFile?: File | string | null;
  finDocAttestedFinancials: string;
  finDocAttestedFinancialsFile?: File | string | null;
  finDocNotarizedAffidavit: string;
  finDocNotarizedAffidavitFile?: File | string | null;
  finDocBankConfirmationLetter: string;
  finDocBankConfirmationLetterFile?: File | string | null;
  finDocPropertyDocuments: string;
  finDocPropertyDocumentsFile?: File | string | null;
  
  // LAND DETAILS (MANDATORY)
  landOwnershipStatus: string;
  landRegistrationDate: string;
  landCompleteAddress: string;
  landLocationCoordinates: string;
  landPlotDrawingFile?: File | string | null;
  landSurveyNo: string;
  landGataNo: string;
  landKhataNo: string;
  landKhatauniNo: string;
  landFerfarNo: string;
  landVargaNo: string;
  landAreaSqMtrs: string;
  landFrontageMtrs: string;
  landDepthMtrs: string;
  landApproachRoadWidthMtrs: string;
  landPlotLevelAboveMtr: string;
  landPlotLevelBelowMtr: string;
  // Land To be offered to MNGL
  mnglLandAreaSqMtrs: string;
  mnglLandFrontageMtrs: string;
  mnglLandDepthMtrs: string;
  mnglTotalAreaSqMtrs: string;
  // Detail of surroundings
  surroundingsEast: string;
  surroundingsWest: string;
  surroundingsNorth: string;
  surroundingsSouth: string;
  landUse: string;
  landConvertedFromAgriculture: string;
  landConvertedFromAgricultureDetails: string;
  landTitleClear: string;
  landTitleClearDetails: string;
  landLitigationFree: string;
  landLitigationFreeDetails: string;
  landPowerConnectionAvailable: string;
  landWaterConnectionAvailable: string;
  landEncroachment: string;
  landEncroachmentDetails: string;
  landGovtAcquisitionPlan: string;
  landGovtAcquisitionPlanDetails: string;
  // OTHER INFORMATION
  nearestFuelStationAvailable: string;
  nearestFuelStationDistance: string;
  nearestPoliceStationName: string;
  nearestFireStationName: string;
  highVoltageTransmissionLine: string;
  highVoltageTransmissionLineDistance: string;
  landAbuttingRoadBoundary: string;
  landAbuttingRoadBoundaryDistance: string;
  
  // Appendix Files
  appendixBFile?: File | string | null;
  appendixCFile?: File | string | null;
  appendixDFile?: File | string | null;
  appendixEFile?: File | string | null;
  appendixFFile?: File | string | null;
}

interface ApplicationFormPage1Props {
  isViewMode?: boolean;
}

const ApplicationFormPage1: React.FC<ApplicationFormPage1Props> = ({ isViewMode = false }) => {
  const { updateProfileDetails, processImageUpload, startImageUploadAnimation, fullName: contextFullName, phoneNumber: contextPhoneNumber, profileImageUrl: contextProfileImageUrl } = useUserProfile();
  
  // Track user activity for admin visibility
  useActivityTracker({ page: 'Application Form - Page 1', activity: 'Filling Form' });
  
  const formRef = useRef<HTMLDivElement>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  // Refs for all form pages for PDF generation
  const page1Ref = useRef<HTMLDivElement>(null);
  const page2Ref = useRef<HTMLDivElement>(null);
  const page3Ref = useRef<HTMLDivElement>(null);
  const page4Ref = useRef<HTMLDivElement>(null);
  const page5Ref = useRef<HTMLDivElement>(null);
  const page6Ref = useRef<HTMLDivElement>(null);
  const page7Ref = useRef<HTMLDivElement>(null);
  const page8Ref = useRef<HTMLDivElement>(null);
  const page9Ref = useRef<HTMLDivElement>(null);
  const page10Ref = useRef<HTMLDivElement>(null);
  const page11Ref = useRef<HTMLDivElement>(null);
  const page12Ref = useRef<HTMLDivElement>(null);
  const page13Ref = useRef<HTMLDivElement>(null);
  const page14Ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { email: userEmail } = useUserProfile();

  const [applicationId, setApplicationId] = useState<string>('');
  
  // Application lock status (for paid/manually locked applications)
  const [isApplicationLocked, setIsApplicationLocked] = useState<boolean>(false);
  
  // Mobile floating elements visibility toggle
  const [mobileFloatingHidden, setMobileFloatingHidden] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Detect mobile device on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const [photo, setPhoto] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const isNewlyGeneratedIdRef = useRef<boolean>(false);
  const isPhotoFreshlyUploadedRef = useRef<boolean>(false);
  const dobFileInputRef = useRef<HTMLInputElement>(null);
  const panFileInputRef = useRef<HTMLInputElement>(null);
  const [isRevealing, setIsRevealing] = useState<boolean>(false);
  const [photoUploadMessage, setPhotoUploadMessage] = useState<string>('');
  const [isAdminMode, setIsAdminMode] = useState(false);
  
  // File upload states
  const [dobFileProgress, setDobFileProgress] = useState<number>(0);
  const [isDobFileUploading, setIsDobFileUploading] = useState<boolean>(false);
  const [dobFileUploadMessage, setDobFileUploadMessage] = useState<string>('');
  const [panFileProgress, setPanFileProgress] = useState<number>(0);
  const [isPanFileUploading, setIsPanFileUploading] = useState<boolean>(false);
  const [panFileUploadMessage, setPanFileUploadMessage] = useState<string>('');
  
  // Upload error modal state
  const [uploadErrorModal, setUploadErrorModal] = useState<{ show: boolean; message: string; fieldName: string }>({ show: false, message: '', fieldName: '' });
  
  // Sr.No guidance modal state — shown when user enters invalid/random Sr.No
  const [showSrNoGuide, setShowSrNoGuide] = useState(false);
  
  // Class 10 question validation ref and shake state
  const class10QuestionRef = useRef<HTMLDivElement>(null);
  const [isClass10Shaking, setIsClass10Shaking] = useState(false);
  
  // Partner/Director Class 10 question shake states - indexed by partner index
  const partnerClass10Refs = useRef<{[key: number]: HTMLDivElement | null}>({});
  const [partnerClass10Shaking, setPartnerClass10Shaking] = useState<{[key: number]: boolean}>({});
  
  // Partner/Director mandatory fields shake states - indexed by partner index
  const partnerPhotoRefs = useRef<{[key: number]: HTMLDivElement | null}>({});
  const [partnerPhotoShaking, setPartnerPhotoShaking] = useState<{[key: number]: boolean}>({});
  const partnerNationalityRefs = useRef<{[key: number]: HTMLDivElement | null}>({});
  const [partnerNationalityShaking, setPartnerNationalityShaking] = useState<{[key: number]: boolean}>({});
  const partnerGenderRefs = useRef<{[key: number]: HTMLDivElement | null}>({});
  const [partnerGenderShaking, setPartnerGenderShaking] = useState<{[key: number]: boolean}>({});
  const partnerMaritalStatusRefs = useRef<{[key: number]: HTMLDivElement | null}>({});
  const [partnerMaritalStatusShaking, setPartnerMaritalStatusShaking] = useState<{[key: number]: boolean}>({});
  const partnerKycRefs = useRef<{[key: number]: HTMLDivElement | null}>({});
  const [partnerKycShaking, setPartnerKycShaking] = useState<{[key: number]: boolean}>({});
  const partnerEduQualRefs = useRef<{[key: number]: HTMLDivElement | null}>({});
  const [partnerEduQualShaking, setPartnerEduQualShaking] = useState<{[key: number]: boolean}>({});
  const partnerBusinessDetailsRefs = useRef<{[key: number]: HTMLDivElement | null}>({});
  const [partnerBusinessDetailsShaking, setPartnerBusinessDetailsShaking] = useState<{[key: number]: boolean}>({});
  const partnerFinancialCapabilityRefs = useRef<{[key: number]: HTMLDivElement | null}>({});
  const [partnerFinancialCapabilityShaking, setPartnerFinancialCapabilityShaking] = useState<{[key: number]: boolean}>({});
  const partnerAppendixARefs = useRef<{[key: number]: HTMLDivElement | null}>({});
  const [partnerAppendixAShaking, setPartnerAppendixAShaking] = useState<{[key: number]: boolean}>({});
  const partnerAppendixBRefs = useRef<{[key: number]: HTMLDivElement | null}>({});
  const [partnerAppendixBShaking, setPartnerAppendixBShaking] = useState<{[key: number]: boolean}>({});
  const partnerAppendixCRefs = useRef<{[key: number]: HTMLDivElement | null}>({});
  const [partnerAppendixCShaking, setPartnerAppendixCShaking] = useState<{[key: number]: boolean}>({});
  const partnerAppendixDRefs = useRef<{[key: number]: HTMLDivElement | null}>({});
  const [partnerAppendixDShaking, setPartnerAppendixDShaking] = useState<{[key: number]: boolean}>({});
  const partnerAppendixERefs = useRef<{[key: number]: HTMLDivElement | null}>({});
  const [partnerAppendixEShaking, setPartnerAppendixEShaking] = useState<{[key: number]: boolean}>({});
  const partnerAppendixFRefs = useRef<{[key: number]: HTMLDivElement | null}>({});
  const [partnerAppendixFShaking, setPartnerAppendixFShaking] = useState<{[key: number]: boolean}>({});
  
  // Mandatory fields shake states and refs
  const profilePhotoRef = useRef<HTMLDivElement>(null);
  const [isProfilePhotoShaking, setIsProfilePhotoShaking] = useState(false);
  const advertisementSourceRef = useRef<HTMLDivElement>(null);
  const [isAdvertisementSourceShaking, setIsAdvertisementSourceShaking] = useState(false);
  const cngLocationRef = useRef<HTMLDivElement>(null);
  const [isCngLocationShaking, setIsCngLocationShaking] = useState(false);
  const frontageQuestionRef = useRef<HTMLDivElement>(null);
  const [isFrontageQuestionShaking, setIsFrontageQuestionShaking] = useState(false);
  const applicationTypeRef = useRef<HTMLDivElement>(null);
  const [isApplicationTypeShaking, setIsApplicationTypeShaking] = useState(false);
  const panCardUploadRef = useRef<HTMLDivElement>(null);
  const [isPanCardUploadShaking, setIsPanCardUploadShaking] = useState(false);
  const currentAddressRef = useRef<HTMLDivElement>(null);
  const [isCurrentAddressShaking, setIsCurrentAddressShaking] = useState(false);
  const personalMobileRef = useRef<HTMLDivElement>(null);
  const [isPersonalMobileShaking, setIsPersonalMobileShaking] = useState(false);
  const nationalityRef = useRef<HTMLDivElement>(null);
  const [isNationalityShaking, setIsNationalityShaking] = useState(false);
  const maritalStatusRef = useRef<HTMLDivElement>(null);
  const [isMaritalStatusShaking, setIsMaritalStatusShaking] = useState(false);
  const genderRef = useRef<HTMLDivElement>(null);
  const [isGenderShaking, setIsGenderShaking] = useState(false);
  const kycDetailsRef = useRef<HTMLDivElement>(null);
  const [isKycDetailsShaking, setIsKycDetailsShaking] = useState(false);
  const educationalQualRef = useRef<HTMLDivElement>(null);
  const [isEducationalQualShaking, setIsEducationalQualShaking] = useState(false);
  const businessDetailsRef = useRef<HTMLDivElement>(null);
  const [isBusinessDetailsShaking, setIsBusinessDetailsShaking] = useState(false);
  const financialCapabilityRef = useRef<HTMLDivElement>(null);
  const [isFinancialCapabilityShaking, setIsFinancialCapabilityShaking] = useState(false);
  const financialDocChecklistRef = useRef<HTMLDivElement>(null);
  const [isFinancialDocChecklistShaking, setIsFinancialDocChecklistShaking] = useState(false);
  const appendixCardsRef = useRef<HTMLDivElement>(null);
  const [isAppendixCardsShaking, setIsAppendixCardsShaking] = useState(false);
  
  // Financial document upload states
  type FinDocField = 'finDocProjectReportFile' | 'finDocBankStatementFile' | 'finDocFdTdReceiptsFile' | 'finDocInvestmentCertificatesFile' | 'finDocMutualFundStatementFile' | 'finDocDematStatementFile' | 'finDocCaCertificateFile' | 'finDocDpCertificateFile' | 'finDocAttestedFinancialsFile' | 'finDocNotarizedAffidavitFile' | 'finDocBankConfirmationLetterFile' | 'finDocPropertyDocumentsFile';
  const [finDocUploadStatus, setFinDocUploadStatus] = useState<Partial<Record<FinDocField, 'idle' | 'uploading' | 'success' | 'error'>>>({});
  const [finDocUploadProgress, setFinDocUploadProgress] = useState<Partial<Record<FinDocField, number>>>({});
  
  // Financial document display names for error modal
  const FIN_DOC_DISPLAY_NAMES: Record<FinDocField, string> = {
    finDocProjectReportFile: 'Project Report / Business Plan',
    finDocBankStatementFile: 'Bank Statement',
    finDocFdTdReceiptsFile: 'FD/TD Receipts',
    finDocInvestmentCertificatesFile: 'Investment Certificates',
    finDocMutualFundStatementFile: 'Mutual Fund Statement',
    finDocDematStatementFile: 'Demat Statement',
    finDocCaCertificateFile: 'CA Certificate',
    finDocDpCertificateFile: 'DP Certificate',
    finDocAttestedFinancialsFile: 'Attested Financials',
    finDocNotarizedAffidavitFile: 'Notarized Affidavit',
    finDocBankConfirmationLetterFile: 'Bank Confirmation Letter',
    finDocPropertyDocumentsFile: 'Property Documents'
  };
  
  // Collapsible section states for discouraging manual entry
  const [showPanFields, setShowPanFields] = useState<boolean>(false);
  const [showAddressFields, setShowAddressFields] = useState<boolean>(false);
  
  // AI detection status for IDENTIFICATION & STATUTORY DETAILS section
  const [aiDetectionField, setAiDetectionField] = useState<string | null>(null);
  
  // AI detection status for Partner IDENTIFICATION & STATUTORY DETAILS section
  // Format: { [partnerIndex_fieldType]: boolean } e.g., { '0_aadhaar': true, '1_pan': false }
  const [partnerAiDetection, setPartnerAiDetection] = useState<{[key: string]: boolean}>({});
  
  // Partner education certificate upload tracking
  // Format: { [partnerIndex_qualType]: boolean } e.g., { '0_Ssc': true, '1_Hsc': false }
  const [partnerEduCertUploading, setPartnerEduCertUploading] = useState<{[key: string]: boolean}>({});
  
  // Partner photo menu state - tracks which partner's photo menu is open
  const [partnerPhotoMenuOpen, setPartnerPhotoMenuOpen] = useState<number | null>(null);
  
  // Primary applicant photo menu state
  const [primaryPhotoMenuOpen, setPrimaryPhotoMenuOpen] = useState(false);
  
  // Webcam selfie capture states
  const [showWebcamModal, setShowWebcamModal] = useState(false);
  const [webcamStream, setWebcamStream] = useState<MediaStream | null>(null);
  const [webcamError, setWebcamError] = useState<string | null>(null);
  const [webcamTarget, setWebcamTarget] = useState<'primary' | number>('primary'); // 'primary' or partner index
  const webcamVideoRef = useRef<HTMLVideoElement>(null);
  const webcamCanvasRef = useRef<HTMLCanvasElement>(null);
  const [faceDetected, setFaceDetected] = useState(false);
  const [faceInFrame, setFaceInFrame] = useState(false);
  const [autoCapturing, setAutoCapturing] = useState(false);
  const [captureCountdown, setCaptureCountdown] = useState<number | null>(null);
  const [faceDetectionSupported, setFaceDetectionSupported] = useState(true);
  const faceDetectorRef = useRef<any>(null);
  const detectionIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const stableFaceCountRef = useRef<number>(0);
  const autoCapturingRef = useRef<boolean>(false);
  
  // Cleanup webcam stream on unmount or navigation
  useEffect(() => {
    return () => {
      if (webcamStream) {
        webcamStream.getTracks().forEach(track => track.stop());
      }
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [webcamStream]);

  const qualificationFileInputRef = useRef<HTMLInputElement>(null);
  const [qualificationFileProgress, setQualificationFileProgress] = useState<number>(0);
  const [isQualificationFileUploading, setIsQualificationFileUploading] = useState<boolean>(false);
  const [qualificationFileUploadMessage, setQualificationFileUploadMessage] = useState<string>('');
  
  const addressFileInputRef = useRef<HTMLInputElement>(null);
  const [addressFileProgress, setAddressFileProgress] = useState<number>(0);
  const [isAddressFileUploading, setIsAddressFileUploading] = useState(false);
  const [addressFileUploadMessage, setAddressFileUploadMessage] = useState('');
  
  // Affidavit file upload state (Appendix-A)
  const affidavitFileInputRef = useRef<HTMLInputElement>(null);
  const [affidavitFileProgress, setAffidavitFileProgress] = useState<number>(0);
  const [isAffidavitFileUploading, setIsAffidavitFileUploading] = useState(false);
  const [affidavitFileUploadMessage, setAffidavitFileUploadMessage] = useState('');
  
  // Affidavit B file upload state (Appendix-B for Non Individual/Other)
  const affidavitBFileInputRef = useRef<HTMLInputElement>(null);
  const [affidavitBFileProgress, setAffidavitBFileProgress] = useState<number>(0);
  const [isAffidavitBFileUploading, setIsAffidavitBFileUploading] = useState(false);
  const [affidavitBFileUploadMessage, setAffidavitBFileUploadMessage] = useState('');
  
  // Appendix C file upload state
  const [appendixCFileProgress, setAppendixCFileProgress] = useState<number>(0);
  const [isAppendixCFileUploading, setIsAppendixCFileUploading] = useState(false);
  const [appendixCFileUploadMessage, setAppendixCFileUploadMessage] = useState('');
  
  // Appendix D file upload state
  const [appendixDFileProgress, setAppendixDFileProgress] = useState<number>(0);
  const [isAppendixDFileUploading, setIsAppendixDFileUploading] = useState(false);
  const [appendixDFileUploadMessage, setAppendixDFileUploadMessage] = useState('');
  
  // Appendix E file upload state
  const [appendixEFileProgress, setAppendixEFileProgress] = useState<number>(0);
  const [isAppendixEFileUploading, setIsAppendixEFileUploading] = useState(false);
  const [appendixEFileUploadMessage, setAppendixEFileUploadMessage] = useState('');
  
  // Appendix F file upload state
  const [appendixFFileProgress, setAppendixFFileProgress] = useState<number>(0);
  const [isAppendixFFileUploading, setIsAppendixFFileUploading] = useState(false);
  const [appendixFFileUploadMessage, setAppendixFFileUploadMessage] = useState('');
  
  // Zoom state
  const [zoomScale, setZoomScale] = useState<number>(1.0);

  // Application form heading/subheading (configurable via admin settings)
  const [formHeading, setFormHeading] = useState<string>('APPLICATION FOR OFFER OF OPEN PLOTS TO SET UP A CNG STATION BY WAY OF LONG-TERM LEASE');
  const [formSubheading, setFormSubheading] = useState<string>('(Landowners shall be awarded Dealership of the CNG STATION based on their suitability)');
  
  // Check if Fill Dummy Data button should be shown (controlled from Admin Controls, default OFF)
  const [showDummyDataButton, setShowDummyDataButton] = useState<boolean>(false);
  // Control visibility of Joint Application (Linked Applications) section from admin setting
  const [showLinkedApplicationsNav, setShowLinkedApplicationsNav] = useState<boolean>(true);
  
  // Mandatory Fields Settings (fetched from admin settings, all default to true = mandatory)
  const [mandatoryFields, setMandatoryFields] = useState<{
    profilePhoto: boolean;
    advertisementSource: boolean;
    cngStationLocation: boolean;
    applicationType: boolean;
    panCardUpload: boolean;
    minimumEducation: boolean;
    currentAddress: boolean;
    personalMobile: boolean;
    nationality: boolean;
    maritalStatus: boolean;
    gender: boolean;
    kycAadhaarPan: boolean;
    educationalQualification: boolean;
    businessEmploymentDetails: boolean;
    financialCapability: boolean;
    financialCapabilityDocChecklist: boolean;
    appendixCards: boolean;
  }>({
    profilePhoto: true,
    advertisementSource: true,
    cngStationLocation: true,
    applicationType: true,
    panCardUpload: true,
    minimumEducation: true,
    currentAddress: true,
    personalMobile: true,
    nationality: true,
    maritalStatus: true,
    gender: true,
    kycAadhaarPan: true,
    educationalQualification: true,
    businessEmploymentDetails: true,
    financialCapability: true,
    financialCapabilityDocChecklist: true,
    appendixCards: true,
  });
  
  // Partner Selection Modal - tracks which user is editing the form
  // 'main' = Main Applicant (full access), 0 = Partner index 0, 1 = Partner index 1, etc.
  // null = not yet selected (show modal if partners exist)
  // Initialize from sessionStorage if available (persists across page navigation)
  const [editingUser, setEditingUser] = useState<'main' | number | null>(() => {
    const stored = sessionStorage.getItem('editingUser');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  });
  const [showPartnerSelectionModal, setShowPartnerSelectionModal] = useState<boolean>(false);
  
  // Partner Setup Modal - shown when non-Individual type is first selected
  interface PartnerSetupData {
    name: string;
    phone: string;
    email: string;
    documentFile: File | null;
    documentType: 'pan' | 'aadhaar' | null;
    detectedName: string;
    isUploading: boolean;
  }
  const [showPartnerSetupModal, setShowPartnerSetupModal] = useState<boolean>(false);
  const [partnerSetupCount, setPartnerSetupCount] = useState<number>(2);
  const [partnerSetupData, setPartnerSetupData] = useState<PartnerSetupData[]>([]);
  const [partnerSetupType, setPartnerSetupType] = useState<string>(''); // 'Partnership', 'Pvt Ltd', etc.
  const [isPartnerSetupSubmitting, setIsPartnerSetupSubmitting] = useState<boolean>(false);
  
  // Appendix Logic - which appendixes are visible for the selected Application Type
  const [visibleAppendixes, setVisibleAppendixes] = useState<string[]>(['A', 'B', 'D', 'E', 'F']);
  
  // Load available locations from Admin Controls
  interface LocationItem {
    id: string;
    name: string;
    enabled: boolean;
  }
  
  const [availableLocations, setAvailableLocations] = useState<LocationItem[]>([]);

  interface LocationStretchItem {
    district: string;
    state: string;
    stretchSrNoArea: string;
    location: string;
    areaStretch: string;

    // New detailed fields corresponding to admin XLSX columns
    srNo: string;
    ca: string;
    area: string;
    stretchSide: string;
    from: string;
    fromLatLong: string;
    to: string;
    toLatLong: string;
    minPlotFrontageMeters: string;
    roadType: string;
    minPlotDepthMeters: string;
  }

  const [locationStretches, setLocationStretches] = useState<LocationStretchItem[]>([]);
  
  useEffect(() => {
    const fetchFormHeadingAndControlsSettings = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const API_BASE = API_URL === '/api' ? '' : API_URL;

        // Fetch settings for headings, toggles and available CNG station locations
        const settingsResponse = await fetch(`${API_BASE}/api/public/settings`);
        if (!settingsResponse.ok) {
          console.error('Failed to fetch application form heading settings:', settingsResponse.status, settingsResponse.statusText);
        } else {
          const data = await settingsResponse.json();
          const settings = data.settings || data;

          if (settings && typeof settings.formHeading === 'string' && settings.formHeading.trim() !== '') {
            setFormHeading(settings.formHeading);
          }
          if (settings && typeof settings.formSubheading === 'string' && settings.formSubheading.trim() !== '') {
            setFormSubheading(settings.formSubheading);
          }
          if (settings && typeof settings.showDummyDataButton === 'boolean') {
            setShowDummyDataButton(settings.showDummyDataButton);
          }
          if (settings && typeof settings.showLinkedApplicationsNav === 'boolean') {
            setShowLinkedApplicationsNav(settings.showLinkedApplicationsNav);
          }
          // Fetch mandatory fields settings
          if (settings && settings.mandatoryFields) {
            setMandatoryFields(prev => ({
              ...prev,
              ...settings.mandatoryFields
            }));
          }
          if (settings && Array.isArray(settings.cngStationLocations)) {
            const enabledLocations = settings.cngStationLocations.filter((loc: LocationItem) => loc.enabled);
            if (enabledLocations.length > 0) {
              setAvailableLocations(enabledLocations);
            } else {
              setAvailableLocations([{ id: '1', name: 'Pune', enabled: true }]);
            }
          } else {
            setAvailableLocations([{ id: '1', name: 'Pune', enabled: true }]);
          }

          // If settings already has locationStretches, keep them as a fallback source
          if (settings && Array.isArray(settings.locationStretches) && settings.locationStretches.length > 0) {
            const stretches: LocationStretchItem[] = settings.locationStretches
              .filter((row: any) =>
                row && (row.location || row.area || row.areaStretch || row.district || row.state || row.stretchSrNoArea || row.srNo)
              )
              .map((row: any) => {
                const srNo = row.srNo != null
                  ? String(row.srNo).trim()
                  : row.stretchSrNoArea != null
                    ? String(row.stretchSrNoArea).trim()
                    : '';
                const location = row.location ? String(row.location).trim() : '';
                const area = row.area
                  ? String(row.area).trim()
                  : row.areaStretch
                    ? String(row.areaStretch).trim()
                    : '';

                return {
                  // Legacy fields kept for compatibility
                  district: row.district ? String(row.district).trim() : '',
                  state: row.state ? String(row.state).trim() : '',
                  stretchSrNoArea: srNo,
                  location,
                  areaStretch: area,

                  // New detailed fields
                  srNo,
                  ca: row.ca ? String(row.ca).trim() : '',
                  area,
                  stretchSide: row.stretchSide ? String(row.stretchSide).trim() : '',
                  from: row.from ? String(row.from).trim() : '',
                  fromLatLong: row.fromLatLong ? String(row.fromLatLong).trim() : '',
                  to: row.to ? String(row.to).trim() : '',
                  toLatLong: row.toLatLong ? String(row.toLatLong).trim() : '',
                  minPlotFrontageMeters: row.minPlotFrontageMeters ? String(row.minPlotFrontageMeters).trim() : '',
                  roadType: row.roadType ? String(row.roadType).trim() : '',
                  minPlotDepthMeters: row.minPlotDepthMeters ? String(row.minPlotDepthMeters).trim() : '',
                };
              });
            setLocationStretches(stretches);
          }
        }

        // Fetch locations from dedicated locations collection
        try {
          const locationsResponse = await fetch(`${API_BASE}/api/locations`);
          if (locationsResponse.ok) {
            const json = await locationsResponse.json();
            const locationsArray = Array.isArray(json.locations) ? json.locations : [];

            if (locationsArray.length > 0) {
              const stretchesFromLocations: LocationStretchItem[] = locationsArray
                .filter((row: any) =>
                  row && (row.location || row.area || row.areaStretch || row.district || row.state || row.stretchSrNoArea || row.srNo)
                )
                .map((row: any) => {
                  const srNo = row.srNo != null
                    ? String(row.srNo).trim()
                    : row.stretchSrNoArea != null
                      ? String(row.stretchSrNoArea).trim()
                      : '';
                  const location = row.location ? String(row.location).trim() : '';
                  const area = row.area
                    ? String(row.area).trim()
                    : row.areaStretch
                      ? String(row.areaStretch).trim()
                      : '';

                  return {
                    // Legacy fields kept for compatibility
                    district: row.district ? String(row.district).trim() : '',
                    state: row.state ? String(row.state).trim() : '',
                    stretchSrNoArea: srNo,
                    location,
                    areaStretch: area,

                    // New detailed fields
                    srNo,
                    ca: row.ca ? String(row.ca).trim() : '',
                    area,
                    stretchSide: row.stretchSide ? String(row.stretchSide).trim() : '',
                    from: row.from ? String(row.from).trim() : '',
                    fromLatLong: row.fromLatLong ? String(row.fromLatLong).trim() : '',
                    to: row.to ? String(row.to).trim() : '',
                    toLatLong: row.toLatLong ? String(row.toLatLong).trim() : '',
                    minPlotFrontageMeters: row.minPlotFrontageMeters ? String(row.minPlotFrontageMeters).trim() : '',
                    roadType: row.roadType ? String(row.roadType).trim() : '',
                    minPlotDepthMeters: row.minPlotDepthMeters ? String(row.minPlotDepthMeters).trim() : '',
                  };
                });

              setLocationStretches(stretchesFromLocations);
            }
          }
        } catch (locErr) {
          console.error('Error fetching locations from /api/locations:', locErr);
          // If this fails, we still have any stretches loaded from settings above
        }
      } catch (error) {
        console.error('Error fetching application form heading settings:', error);
        // Fallback to default location if settings fetch fails
        setAvailableLocations([{ id: '1', name: 'Pune', enabled: true }]);
      }
    };

    fetchFormHeadingAndControlsSettings();
  }, []);
  
  // Joint Application / Linked Applications State
  interface LinkedApplication {
    applicationId: string;
    isPrimaryApplicant: boolean;
    status: string;
    createdAt: string;
  }
  
  const [linkedApplications, setLinkedApplications] = useState<LinkedApplication[]>([]);
  const [linkInputValue, setLinkInputValue] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [linkMessage, setLinkMessage] = useState('');
  const [showLinkConfirm, setShowLinkConfirm] = useState(false);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [showBrochureModal, setShowBrochureModal] = useState(false);
  const [showAadhaarSampleModal, setShowAadhaarSampleModal] = useState(false);
  
  // Fetch linked applications
  const fetchLinkedApplications = useCallback(async () => {
    if (!applicationId) return;
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const API_BASE = API_URL === '/api' ? '' : API_URL;
      
      const response = await fetch(`${API_BASE}/api/applications/${applicationId}/linked`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setLinkedApplications(data.applications || data.linked || []);
      }
    } catch (error) {
      console.error('Error fetching linked applications:', error);
    }
  }, [applicationId]);
  
  // Show link confirmation modal
  const handleLinkApplication = () => {
    if (!linkInputValue.trim()) {
      setLinkMessage('Please enter an Application ID');
      return;
    }
    setShowLinkConfirm(true);
  };
  
  // Actually perform the link after confirmation
  const confirmLinkApplication = async () => {
    setShowLinkConfirm(false);
    setIsLinking(true);
    setLinkMessage('');
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const API_BASE = API_URL === '/api' ? '' : API_URL;
      
      const response = await fetch(`${API_BASE}/api/applications/${applicationId}/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ targetApplicationId: linkInputValue.trim() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setLinkMessage('✓ Applications linked successfully');
        setLinkInputValue('');
        fetchLinkedApplications();
      } else {
        setLinkMessage(`✗ ${data.message || 'Failed to link applications'}`);
      }
    } catch (error) {
      console.error('Error linking application:', error);
      setLinkMessage('✗ Error linking applications');
    } finally {
      setIsLinking(false);
      setTimeout(() => setLinkMessage(''), 5000);
    }
  };
  
  // Show unlink confirmation modal
  const handleUnlinkApplication = () => {
    setShowUnlinkConfirm(true);
  };
  
  // Actually perform the unlink after confirmation
  const confirmUnlinkApplication = async () => {
    setShowUnlinkConfirm(false);
    setIsLinking(true);
    setLinkMessage('');
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const API_BASE = API_URL === '/api' ? '' : API_URL;
      
      const response = await fetch(`${API_BASE}/api/applications/${applicationId}/unlink`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setLinkMessage('✓ Application unlinked successfully');
        fetchLinkedApplications();
      } else {
        setLinkMessage(`✗ ${data.message || 'Failed to unlink application'}`);
      }
    } catch (error) {
      console.error('Error unlinking application:', error);
      setLinkMessage('✗ Error unlinking application');
    } finally {
      setIsLinking(false);
      setTimeout(() => setLinkMessage(''), 5000);
    }
  };
  
  // Load linked applications when applicationId is available
  useEffect(() => {
    if (applicationId) {
      fetchLinkedApplications();
    }
  }, [applicationId, fetchLinkedApplications]);
  
  // Form data state
  const [formData, setFormData] = useState<FormData>({
    // Initialize with the new applicationId if available
    applicationNoOfficeUse: '', // This will be set in useEffect
    location: '',
    locationDistrict: '',
    locationState: '',
    locationStretchSrNoArea: '',
    locationLocality: '',
    locationAreaStretch: '',

    // New detailed location fields (start empty)
    locationSrNo: '',
    locationCA: '',
    locationArea: '',
    locationStretchSide: '',
    locationFrom: '',
    locationFromLatLong: '',
    locationTo: '',
    locationToLatLong: '',
    locationMinPlotFrontage: '',
    hasSufficientFrontage: '',
    locationRoadType: '',
    locationMinPlotDepth: '',
    hasSufficientDepth: '',
    advertisedOn: '',
    advertisedIn: '',
    advertisementSource: '',
    dealershipType: '',
    entityName: '',
    numberOfPartners: 0,
    partnerDirectorDetails: [],
    nameInBlocks: '',
    fatherOrHusbandName: '',
    spouseEmploymentStatus: '',
    spouseEmploymentDetails: '',
    dateOfBirth: '',
    panCardFile: null,
    dobProofFile: null,
    isMinimumEducationMet: '',
    qualification: '',
    qualificationProofFile: null,
    currentAddress: '',
    permanentAddress: '',
    permanentAddressSameAsCurrent: false,
    addressProofFile: null,
    personalMobile: '',
    secondaryMobile: '',
    emailId: '',
    nationality: '',
    gender: '',
    maritalStatus: '',
    maritalStatusOther: '',
    errors: {},
    // Identification & Statutory Details
    aadhaarNumber: '',
    aadhaarVerified: '',
    aadhaarFile: null,
    panNumberStatutory: '',
    panVerified: '',
    panStatutoryFile: null,
    gstNumber: '',
    gstVerified: '',
    gstFile: null,
    identityProofVoterId: false,
    identityProofPassport: false,
    identityProofDrivingLicense: false,
    identityDocumentNumber: '',
    voterIdNumber: '',
    passportNumber: '',
    drivingLicenseNumber: '',
    voterIdFile: null,
    passportFile: null,
    drivingLicenseFile: null,
    // Educational Qualification
    eduSscBoard: '',
    eduSscMarksObtained: '',
    eduSscOutOfMarks: '',
    eduSscPercentage: '',
    eduSscYearOfPassing: '',
    eduSscCertificateFile: null,
    eduHscBoard: '',
    eduHscMarksObtained: '',
    eduHscOutOfMarks: '',
    eduHscPercentage: '',
    eduHscYearOfPassing: '',
    eduHscCertificateFile: null,
    eduGraduateBoard: '',
    eduGraduateMarksObtained: '',
    eduGraduateOutOfMarks: '',
    eduGraduatePercentage: '',
    eduGraduateYearOfPassing: '',
    eduGraduateCertificateFile: null,
    eduOthersBoard: '',
    eduOthersMarksObtained: '',
    eduOthersOutOfMarks: '',
    eduOthersPercentage: '',
    eduOthersYearOfPassing: '',
    eduOthersCertificateFile: null,
    // Appendix-A Affidavit
    affidavitFile: null,
    // Appendix-B Affidavit
    affidavitBFile: null,
    // Non Individual Applicant Entity fields
    niTypeOfApplicant: '',
    niNameOfEntity: '',
    niDateOfRegistrationIncorporation: '',
    niCompanyRegisteredInIndia: '',
    niNameOfAuthorizedSignatory: '',
    niMobilePrimary: '',
    niMobileAlternate: '',
    niPhoneResidential: '',
    niPhoneOfficial: '',
    niFax: '',
    niEmailId: '',
    niFinancialYear1: '',
    niProfitAfterTax1: '',
    niLoss1: '',
    niFinancialYear2: '',
    niProfitAfterTax2: '',
    niLoss2: '',
    niFinancialYear3: '',
    niProfitAfterTax3: '',
    niLoss3: '',
    niCurrentFlatHouseNo: '',
    niCurrentBuildingName: '',
    niCurrentPlotNo: '',
    niCurrentStreetName: '',
    niCurrentLocality: '',
    niCurrentLandmark: '',
    niCurrentCityTown: '',
    niCurrentDistrict: '',
    niCurrentState: '',
    niCurrentPinCode: '',
    niCurrentMobilePrimary: '',
    niCurrentMobileAlternate: '',
    niCurrentPhoneResidential: '',
    niCurrentPhoneOfficial: '',
    niCurrentFax: '',
    niCurrentEmailId: '',
    niPermanentAddressSameAsCurrent: false,
    niPermanentFlatHouseNo: '',
    niPermanentBuildingName: '',
    niPermanentPlotNo: '',
    niPermanentStreetName: '',
    niPermanentLocality: '',
    niPermanentLandmark: '',
    niPermanentCityTown: '',
    niPermanentDistrict: '',
    niPermanentState: '',
    niPermanentPinCode: '',
    niPermanentMobilePrimary: '',
    niPermanentMobileAlternate: '',
    niPermanentPhoneResidential: '',
    // BUSINESS / EMPLOYMENT DETAILS
    presentOccupation: '',
    businessEmploymentDetails: '',
    totalWorkExperienceYears: '',
    relevantIndustryExperience: '',
    // FINANCIAL CAPABILITY
    annualIncome: '',
    netWorthAtApplication: '',
    sourceOfInvestment: '',
    abilityToMeetRequiredInvestment: '',
    // FINANCIAL CAPABILITY – DOCUMENT CHECKLIST
    finDocProjectReport: '',
    finDocProjectReportFile: null,
    finDocBankStatement: '',
    finDocBankStatementFile: null,
    finDocFdTdReceipts: '',
    finDocFdTdReceiptsFile: null,
    finDocInvestmentCertificates: '',
    finDocInvestmentCertificatesFile: null,
    finDocMutualFundStatement: '',
    finDocMutualFundStatementFile: null,
    finDocDematStatement: '',
    finDocDematStatementFile: null,
    finDocCaCertificate: '',
    finDocCaCertificateFile: null,
    finDocDpCertificate: '',
    finDocDpCertificateFile: null,
    finDocAttestedFinancials: '',
    finDocAttestedFinancialsFile: null,
    finDocNotarizedAffidavit: '',
    finDocNotarizedAffidavitFile: null,
    finDocBankConfirmationLetter: '',
    finDocBankConfirmationLetterFile: null,
    finDocPropertyDocuments: '',
    finDocPropertyDocumentsFile: null,
    // LAND DETAILS (MANDATORY)
    landOwnershipStatus: '',
    landRegistrationDate: '',
    landCompleteAddress: '',
    landLocationCoordinates: '',
    landPlotDrawingFile: null,
    landSurveyNo: '',
    landGataNo: '',
    landKhataNo: '',
    landKhatauniNo: '',
    landFerfarNo: '',
    landVargaNo: '',
    landAreaSqMtrs: '',
    landFrontageMtrs: '',
    landDepthMtrs: '',
    landApproachRoadWidthMtrs: '',
    landPlotLevelAboveMtr: '',
    landPlotLevelBelowMtr: '',
    // Land To be offered to MNGL
    mnglLandAreaSqMtrs: '',
    mnglLandFrontageMtrs: '',
    mnglLandDepthMtrs: '',
    mnglTotalAreaSqMtrs: '',
    // Detail of surroundings
    surroundingsEast: '',
    surroundingsWest: '',
    surroundingsNorth: '',
    surroundingsSouth: '',
    landUse: '',
    landConvertedFromAgriculture: '',
    landConvertedFromAgricultureDetails: '',
    landTitleClear: '',
    landTitleClearDetails: '',
    landLitigationFree: '',
    landLitigationFreeDetails: '',
    landPowerConnectionAvailable: '',
    landWaterConnectionAvailable: '',
    landEncroachment: '',
    landEncroachmentDetails: '',
    landGovtAcquisitionPlan: '',
    landGovtAcquisitionPlanDetails: '',
    // OTHER INFORMATION
    nearestFuelStationAvailable: '',
    nearestFuelStationDistance: '',
    nearestPoliceStationName: '',
    nearestFireStationName: '',
    highVoltageTransmissionLine: '',
    highVoltageTransmissionLineDistance: '',
    landAbuttingRoadBoundary: '',
    landAbuttingRoadBoundaryDistance: ''
  });

  // State for save indicator
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<string>('Saved');
  const [showSaveIndicator, setShowSaveIndicator] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  
  // State for tracking data loading from backend (prevents race conditions)
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const [showPlotSizeModal, setShowPlotSizeModal] = useState<boolean>(false);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [dobAge, setDobAge] = useState<number | null>(null);
  const [dobAgeError, setDobAgeError] = useState<string | null>(null);
  
  // State for PDF generation modal
  const [isPdfGenerating, setIsPdfGenerating] = useState<boolean>(false);
  const [compressionProgress, setCompressionProgress] = useState<number>(0);
  const [isFullPdfGenerating, setIsFullPdfGenerating] = useState<boolean>(false);
  const [fullPdfProgress, setFullPdfProgress] = useState<number>(0);
  const [currentPageGenerating, setCurrentPageGenerating] = useState<number>(0);
  const [pdfFormData, setPdfFormData] = useState<any | null>(null);
  const [pdfRenderKey, setPdfRenderKey] = useState<number>(0);
  
  // Countdown modal state for auto-download
  const [showCountdownModal, setShowCountdownModal] = useState<boolean>(false);
  const [countdownValue, setCountdownValue] = useState<number>(3);
  
  // Minimum loading display time state
  const [showLoadingOverlay, setShowLoadingOverlay] = useState<boolean>(false);
  const loadingStartTimeRef = useRef<number>(0);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalFileUrl, setModalFileUrl] = useState<string | null>(null);
  const [modalFileName, setModalFileName] = useState<string | null>(null);
  const [modalFileType, setModalFileType] = useState<string | null>(null);
  const [modalFieldName, setModalFieldName] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<'document' | 'profile' | null>(null);
  
  // Appendix PDF preview modal state
  const [appendixPdfModal, setAppendixPdfModal] = useState<{ isOpen: boolean; pdfUrl: string; title: string }>({
    isOpen: false,
    pdfUrl: '',
    title: ''
  });

  // Global progress bar (across Page1+2+3+4) — PageA and Page5 are info-only
  const formProgress = useFormProgress(formData);

  // ESC key handler for appendix PDF modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && appendixPdfModal.isOpen) {
        setAppendixPdfModal({ isOpen: false, pdfUrl: '', title: '' });
      }
    };
    if (appendixPdfModal.isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [appendixPdfModal.isOpen]);
  
  // Image quality error modal state
  const [imageQualityError, setImageQualityError] = useState<{ show: boolean; reason: string; fieldName: string; isAadhaarError?: boolean }>({
    show: false,
    reason: '',
    fieldName: '',
    isAadhaarError: false,
  });
  
  // Document mismatch error modal state
  const [documentMismatchError, setDocumentMismatchError] = useState<{ show: boolean; reason: string; fieldName: string; detectedType: string; expectedType: string }>({
    show: false,
    reason: '',
    fieldName: '',
    detectedType: '',
    expectedType: '',
  });
  
  // Name mismatch modal state (for PAN vs Aadhaar name validation)
  const [nameMismatchModal, setNameMismatchModal] = useState<{ 
    show: boolean; 
    aadhaarName: string; 
    panName: string; 
    reason: string;
    partnerIndex?: number;
  }>({
    show: false,
    aadhaarName: '',
    panName: '',
    reason: '',
  });
  
  // Extracted names from documents (for cross-validation)
  const [extractedNames, setExtractedNames] = useState<{
    aadhaarName?: string;
    panName?: string;
    partners?: { [key: number]: { aadhaarName?: string; panName?: string } };
  }>({});
  
  // Education certificate upload state
  const [eduCertUploading, setEduCertUploading] = useState<{ [key: string]: boolean }>({});
  
  // Education auto-detection info modal state
  const [eduAutoDetectInfo, setEduAutoDetectInfo] = useState<{ show: boolean; detectedType: string; detectedLabel: string; partnerIndex?: number }>({
    show: false,
    detectedType: '',
    detectedLabel: '',
  });
  
  // Disabled tooltip state - shows tiny "Disabled" bubble on click of disabled elements
  const [disabledTooltip, setDisabledTooltip] = useState<{ show: boolean; x: number; y: number }>({ show: false, x: 0, y: 0 });
  
  // Function to validate if PAN and Aadhaar names match
  const validateDocumentNames = useCallback(async (aadhaarName: string, panName: string, partnerIndex?: number) => {
    if (!aadhaarName || !panName) return; // Need both names to validate
    
    try {
      console.log(`[Name Validation] Validating names - Aadhaar: "${aadhaarName}", PAN: "${panName}"`);
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const apiBase = API_URL === '/api' ? '' : API_URL;
      
      const response = await fetch(`${apiBase}/api/validate-document-names`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ aadhaarName, panName })
      });
      
      const data = await response.json();
      
      if (data.success && !data.isMatch) {
        // Names don't match - show modal
        setNameMismatchModal({
          show: true,
          aadhaarName: data.aadhaarName,
          panName: data.panName,
          reason: data.reason || 'The names on your Aadhaar card and PAN card do not match.',
          partnerIndex
        });
      }
    } catch (error) {
      console.error('[Name Validation] Error:', error);
      // Don't block the user if validation fails - just log the error
    }
  }, []);

  // Function to check for duplicate PAN/Aadhaar numbers across main applicant and partners
  const checkDuplicateDocument = useCallback((
    documentType: 'aadhaar' | 'pan',
    newValue: string,
    currentOwner: 'main' | { partnerIndex: number }
  ): { isDuplicate: boolean; duplicateOwner?: string } => {
    if (!newValue || newValue.trim() === '') return { isDuplicate: false };
    
    // Normalize the value (remove spaces for comparison)
    const normalizedNew = newValue.replace(/\s/g, '').toUpperCase();
    
    // Get all existing values to check against
    const existingValues: Array<{ value: string; owner: string }> = [];
    
    // Add main applicant's value
    if (documentType === 'aadhaar' && formData.aadhaarNumber) {
      existingValues.push({ 
        value: formData.aadhaarNumber.replace(/\s/g, '').toUpperCase(), 
        owner: 'Main Applicant' 
      });
    }
    if (documentType === 'pan' && formData.panNumberStatutory) {
      existingValues.push({ 
        value: formData.panNumberStatutory.replace(/\s/g, '').toUpperCase(), 
        owner: 'Main Applicant' 
      });
    }
    
    // Add all partners' values
    if (formData.partnerDirectorDetails && formData.partnerDirectorDetails.length > 0) {
      formData.partnerDirectorDetails.forEach((partner, idx) => {
        if (documentType === 'aadhaar' && partner.aadhaarNumber) {
          existingValues.push({ 
            value: partner.aadhaarNumber.replace(/\s/g, '').toUpperCase(), 
            owner: `${(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #${idx + 2}${partner.nameInBlocks ? ` (${partner.nameInBlocks})` : ''}` 
          });
        }
        if (documentType === 'pan' && partner.panNumberStatutory) {
          existingValues.push({ 
            value: partner.panNumberStatutory.replace(/\s/g, '').toUpperCase(), 
            owner: `${(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #${idx + 2}${partner.nameInBlocks ? ` (${partner.nameInBlocks})` : ''}` 
          });
        }
      });
    }
    
    // Filter out current owner's existing value (allow updating same person's document)
    const valuesToCheck = existingValues.filter(item => {
      if (currentOwner === 'main') {
        return item.owner !== 'Main Applicant';
      } else {
        const partnerOwnerPrefix = `${(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #${currentOwner.partnerIndex + 2}`;
        return !item.owner.startsWith(partnerOwnerPrefix);
      }
    });
    
    // Check for duplicates
    const duplicate = valuesToCheck.find(item => item.value === normalizedNew);
    if (duplicate) {
      return { isDuplicate: true, duplicateOwner: duplicate.owner };
    }
    
    return { isDuplicate: false };
  }, [formData.aadhaarNumber, formData.panNumberStatutory, formData.partnerDirectorDetails]);
  
  // ESC key handler for education auto-detect modal
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && eduAutoDetectInfo.show) {
        setEduAutoDetectInfo({ show: false, detectedType: '', detectedLabel: '' });
      }
    };
    
    if (eduAutoDetectInfo.show) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [eduAutoDetectInfo.show]);
  
  // Click-outside handler to close photo menus
  useEffect(() => {
    const handleClickOutside = () => {
      if (partnerPhotoMenuOpen !== null) {
        setPartnerPhotoMenuOpen(null);
      }
      if (primaryPhotoMenuOpen) {
        setPrimaryPhotoMenuOpen(false);
      }
    };
    
    if (partnerPhotoMenuOpen !== null || primaryPhotoMenuOpen) {
      // Add with a small delay to avoid immediately closing when menu opens
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [partnerPhotoMenuOpen, primaryPhotoMenuOpen]);
  
  // Computed: Check if ANY education certificate is being uploaded (to disable entire table)
  const isAnyEduCertUploading = Object.values(eduCertUploading).some(v => v);
  
  // Computed: Check if ANY statutory document is being uploaded (to disable entire IDENTIFICATION table)
  const isAnyStatutoryUploading = aiDetectionField !== null;
  
  // Save timeout reference for debouncing
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Typewriter animation for AI autofill - smooth character-by-character animation
  const typewriterFill = useCallback(async (
    fieldName: keyof FormData,
    targetValue: string,
    charDelay: number = 35 // milliseconds per character
  ): Promise<void> => {
    if (!targetValue || targetValue.length === 0) return;
    
    for (let i = 1; i <= targetValue.length; i++) {
      const partialValue = targetValue.substring(0, i);
      setFormData(prev => ({
        ...prev,
        [fieldName]: partialValue,
      }));
      await new Promise(resolve => setTimeout(resolve, charDelay));
    }
  }, []);

  // Typewriter for partner details - animates character by character
  const typewriterFillPartner = useCallback(async (
    partnerIndex: number,
    fieldName: string,
    targetValue: string,
    charDelay: number = 35
  ): Promise<void> => {
    if (!targetValue || targetValue.length === 0) return;
    
    for (let i = 1; i <= targetValue.length; i++) {
      const partialValue = targetValue.substring(0, i);
      setFormData(prev => {
        const updatedDetails = [...(prev.partnerDirectorDetails || [])];
        if (updatedDetails[partnerIndex]) {
          updatedDetails[partnerIndex] = {
            ...updatedDetails[partnerIndex],
            [fieldName]: partialValue,
          };
        }
        return { ...prev, partnerDirectorDetails: updatedDetails };
      });
      await new Promise(resolve => setTimeout(resolve, charDelay));
    }
  }, []);

  // Set Pune as default location when availableLocations is loaded and no location is selected
  useEffect(() => {
    if (availableLocations.length > 0 && !formData.location) {
      const puneLocation = availableLocations.find(loc => loc.name === 'Pune');
      if (puneLocation) {
        setFormData(prev => ({
          ...prev,
          location: 'Pune'
        }));
      }
    }
  }, [availableLocations, formData.location]);

  // Populate emailId from userEmail context when available (for validation purposes)
  useEffect(() => {
    if (userEmail && (!formData.emailId || formData.emailId.trim() === '')) {
      setFormData(prev => ({
        ...prev,
        emailId: userEmail
      }));
    }
  }, [userEmail, formData.emailId]);

  // Auto-select "Yes" for frontage when "All Roads" is selected
  useEffect(() => {
    if (formData.locationFrom === 'All Roads' && formData.locationTo === 'All Roads') {
      if (formData.hasSufficientFrontage !== 'Yes') {
        setFormData(prev => ({ ...prev, hasSufficientFrontage: 'Yes' }));
      }
    }
  }, [formData.locationFrom, formData.locationTo, formData.hasSufficientFrontage]);

  // Check if plot size requirements are not met - blocks rest of form
  const isPlotSizeInsufficient = formData.hasSufficientFrontage === 'No';
  
  // Check if frontage question needs to be answered before enabling Application Type
  const isFrontageUnanswered = formData.locationMinPlotFrontage && formData.hasSufficientFrontage !== 'Yes';

  // Function to save data to database - returns true on success, false on failure
  const saveToDatabaseCallback = useCallback(async (data: typeof formData, isDraft: boolean, anId?: string): Promise<boolean> => {
    const idToUse = anId || applicationId;

    if (!idToUse) {
      console.error('Application ID is missing, cannot save.');
      setSaveMessage('Error: Application ID missing.');
      setIsSaving(false);
      // Potentially show a more user-friendly error or guide them
      return false;
    }

    if (!isOnline) {
      // TODO: Implement offline queuing if desired. For now, just prevent save.
      console.warn('Offline: Save operation skipped for application ID:', idToUse);
      setSaveMessage('Offline: Changes will be saved when back online.'); // Placeholder message
      // setTimeout(() => setShowSaveIndicator(false), 3000); // Hide after a bit
      return false; // Do not proceed with saving if offline
    }

    console.log(`[SaveToDB] Attempting to save. ID: ${idToUse}, isDraft: ${isDraft}`);
    setIsSaving(true);
    const currentSaveMessage = isDraft ? 'Saving Draft...' : 'Submitting Application...';
    setSaveMessage(currentSaveMessage);
    setShowSaveIndicator(true);
    
    try {
      // If photo is a base64 data URL, upload it via the dedicated file endpoint first
      // to avoid sending very large JSON payloads which can fail server limits.
      let photoValue = photo;
      if (photo && typeof photo === 'string' && photo.startsWith('data:image')) {
        try {
          const parts = photo.split(',');
          const mimeMatch = parts[0].match(/:(.*?);/);
          const mime = (mimeMatch && mimeMatch[1]) ? mimeMatch[1] : 'image/jpeg';
          const bstr = atob(parts[1]);
          const len = bstr.length;
          const u8arr = new Uint8Array(len);
          for (let i = 0; i < len; i++) {
            u8arr[i] = bstr.charCodeAt(i);
          }
          const file = new File([u8arr], 'profile-photo.jpg', { type: mime });
          const uploadRes = await uploadFormFile(idToUse, file, 'photo', () => {});
          if (uploadRes && uploadRes.success && uploadRes.filePath) {
            photoValue = uploadRes.filePath; // store server path instead of base64 for backend persistence
            // Keep local preview unchanged (base64) to avoid cross-origin loading issues in dev
          }
        } catch (e) {
          console.error('[SaveToDB] Photo upload failed, proceeding without photo update:', e);
        }
      }

      // Only send data that can be serialized (remove File objects)
      // If a field is a File object (freshly uploaded, not yet saved via upload endpoint),
      // fall back to its name. If it's a string path (from server), keep it.
      const serializeFile = (val: any) => val instanceof File ? val.name : val;
      const dataToSave = {
        ...data, // data is the current formData state
        photo: photoValue, // Use uploaded path if available
        panCardFile: serializeFile(data.panCardFile),
        dobProofFile: serializeFile(data.dobProofFile),
        qualificationProofFile: serializeFile(data.qualificationProofFile),
        addressProofFile: serializeFile(data.addressProofFile),
        aadhaarFile: serializeFile(data.aadhaarFile),
        voterIdFile: serializeFile(data.voterIdFile),
        passportFile: serializeFile(data.passportFile),
        drivingLicenseFile: serializeFile(data.drivingLicenseFile),
        // Education certificate files
        eduSscCertificateFile: serializeFile(data.eduSscCertificateFile),
        eduHscCertificateFile: serializeFile(data.eduHscCertificateFile),
        eduGraduateCertificateFile: serializeFile(data.eduGraduateCertificateFile),
        eduOthersCertificateFile: serializeFile(data.eduOthersCertificateFile),
      };
      
      const response = await saveFormData(idToUse, dataToSave, isDraft);
      
      if (response.success) {
        const successMsg = isDraft ? 'Draft saved successfully' : 'Application submitted successfully';
        console.log(`[SaveToDB] Success for ID: ${idToUse}. Message: ${successMsg}`);
        setSaveMessage(successMsg);
        if (isDraft && isNewlyGeneratedIdRef.current) {
          isNewlyGeneratedIdRef.current = false; // Reset flag after successful initial draft save
          console.log('[SaveToDB] Reset isNewlyGeneratedIdRef for ID:', idToUse);
        }
        setTimeout(() => setShowSaveIndicator(false), 2000);
        return true;
      } else {
        setSaveMessage('Error saving');
        setTimeout(() => setShowSaveIndicator(false), 3000);
        return false;
      }
    } catch (error) {
      console.error('Error saving form data:', error);
      setSaveMessage('Error saving');
      setTimeout(() => setShowSaveIndicator(false), 3000);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [applicationId, isOnline, formData, photo]); // Added dependencies: applicationId, isOnline, formData, photo.

  const debouncedSave = useCallback((newData: FormData) => {
    console.log('[DebouncedSave] Invoked. Clearing previous timeout if any.');
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      console.log('[DebouncedSave] Timeout executed. Checking conditions to save.');
      // Save to database if we have an email (used as identifier) and a valid application ID
      // All auto-saves should be drafts until explicit submission
      if (applicationId && newData.emailId && newData.emailId.trim() !== '') {
        console.log(`[DebouncedSave] Conditions met. ApplicationId: ${applicationId}, Email: ${newData.emailId}. Proceeding to save.`);
        console.log(`[DebouncedSave] Triggering auto-save (as draft) for ID: ${applicationId}`);
        saveToDatabaseCallback({ ...newData, applicationNoOfficeUse: applicationId }, true, applicationId);
      } else {
        console.log(`[DebouncedSave] Conditions NOT met. Skipping save. ApplicationId: ${applicationId}, Email: ${newData.emailId ? newData.emailId.trim() : 'undefined/empty'}`);
        // Hide the save indicator if conditions aren't met
        setSaveMessage('');
        setShowSaveIndicator(false);
      }
    }, 1000); // 1 second delay
  }, [saveToDatabaseCallback, applicationId]);

  // Removed interval-based auto-save - using navigation-based save modal instead

  // Check if we're in admin edit mode
  useEffect(() => {
    const adminEditMode = sessionStorage.getItem('adminEditMode');
    setIsAdminMode(adminEditMode === 'true');
  }, []);

  // Effect to initialize or generate applicationId
  useEffect(() => {
    let currentApplicationId = location.state?.applicationId || sessionStorage.getItem('currentApplicationId');
    let source = '';

    if (currentApplicationId) {
      source = location.state?.applicationId ? 'location.state' : 'sessionStorage';
      console.log(`[AppFormPage1 Effect InitID] Found existing Application ID: ${currentApplicationId} from ${source}`);
      setApplicationId(currentApplicationId);
      // TODO: If loading an existing draft, consider pre-filling formData by fetching from backend
      // For now, just ensure formData has the ID if it's different
      if (formData.applicationNoOfficeUse !== currentApplicationId) {
        setFormData(prev => ({ ...prev, applicationNoOfficeUse: currentApplicationId }));
      }
    } else {
      const newId = generateApplicationId();
      console.log(`[AppFormPage1 Effect InitID] No existing ID found. Generated new Application ID: ${newId}`);
      setApplicationId(newId);
      sessionStorage.setItem('currentApplicationId', newId);
      isNewlyGeneratedIdRef.current = true; // Mark as newly generated for initial auto-save
      setFormData(prev => ({ ...prev, applicationNoOfficeUse: newId }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]); // Only re-run if location.state changes (e.g., navigating with new state)

  // Effect to load existing application data when resuming a draft
  useEffect(() => {
    const loadDraftData = async () => {
      if (applicationId && !isNewlyGeneratedIdRef.current && !isViewMode) {
        console.log(`[AppFormPage1 LoadDraft] Attempting to load draft data for ID: ${applicationId}`);
        setIsDataLoading(true);
        setIsDataLoaded(false);
        setShowLoadingOverlay(true);
        loadingStartTimeRef.current = Date.now();
        try {
          // Assuming fetchApplicationDetails returns an object with a formData property
          // and formData itself contains a 'photo' field (base64 string)
          const appData = await fetchApplicationDetails(applicationId);
          // fetchApplicationDetails now directly returns ApplicationDetails (which includes formData)
          if (appData && appData.formData) {
            // Ensure that file proof fields are treated as strings (filenames) if they exist
            const loadedFormData = {
              ...appData.formData,
              dobProofFile: appData.formData.dobProofFile || null, // Expecting filename or null
              qualificationProofFile: appData.formData.qualificationProofFile || null,
              addressProofFile: appData.formData.addressProofFile || null,
              voterIdFile: appData.formData.voterIdFile || null,
              passportFile: appData.formData.passportFile || null,
              drivingLicenseFile: appData.formData.drivingLicenseFile || null,
            };
            
            // Map top-level partner file paths into partnerDirectorDetails array (backward compatibility)
            if (loadedFormData.partnerDirectorDetails && Array.isArray(loadedFormData.partnerDirectorDetails)) {
              const partnerFileFields = ['photo', 'aadhaarFile', 'panStatutoryFile', 'gstFile', 'voterIdFile', 'passportFile', 'drivingLicenseFile', 'addressProofFile', 'eduSscCertificateFile', 'eduHscCertificateFile', 'eduGraduateCertificateFile', 'eduOthersCertificateFile', 'panCardFile', 'dobProofFile', 'qualificationProofFile'];
              loadedFormData.partnerDirectorDetails = loadedFormData.partnerDirectorDetails.map((partner: any, index: number) => {
                const updatedPartner = { ...partner };
                partnerFileFields.forEach(field => {
                  const topLevelKey = `partner_${index}_${field}`;
                  // If top-level has value but partner object doesn't, copy it over
                  if (appData.formData[topLevelKey] && !updatedPartner[field]) {
                    updatedPartner[field] = appData.formData[topLevelKey];
                    console.log(`[LoadDraft] Mapped ${topLevelKey} to partner[${index}].${field}`);
                  }
                });
                return updatedPartner;
              });
            }
            console.log('[AppFormPage1 LoadDraft] appData.formData.dobProofFile:', appData.formData.dobProofFile);
            console.log('[AppFormPage1 LoadDraft] appData.formData.qualificationProofFile:', appData.formData.qualificationProofFile);
            console.log('[AppFormPage1 LoadDraft] appData.formData.addressProofFile:', appData.formData.addressProofFile);
            console.log('[AppFormPage1 LoadDraft] appData.formData.voterIdFile:', appData.formData.voterIdFile);
            console.log('[AppFormPage1 LoadDraft] appData.formData.passportFile:', appData.formData.passportFile);
            console.log('[AppFormPage1 LoadDraft] appData.formData.drivingLicenseFile:', appData.formData.drivingLicenseFile);
            try { console.log('[AppFormPage1 LoadDraft] Constructed loadedFormData before setFormData:', JSON.stringify(loadedFormData, null, 2)); } catch { console.log('[AppFormPage1 LoadDraft] loadedFormData constructed (not serializable for logging)'); }
            // Format Aadhaar numbers with spaces on load (XXXX XXXX XXXX)
            if (loadedFormData.aadhaarNumber && typeof loadedFormData.aadhaarNumber === 'string') {
              const digits = loadedFormData.aadhaarNumber.replace(/\D/g, '').slice(0, 12);
              if (digits.length > 0) {
                loadedFormData.aadhaarNumber = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
              }
            }
            // Format partner Aadhaar numbers too
            if (loadedFormData.partnerDirectorDetails && Array.isArray(loadedFormData.partnerDirectorDetails)) {
              loadedFormData.partnerDirectorDetails = loadedFormData.partnerDirectorDetails.map((p: any) => {
                if (p && p.aadhaarNumber && typeof p.aadhaarNumber === 'string') {
                  const digits = p.aadhaarNumber.replace(/\D/g, '').slice(0, 12);
                  if (digits.length > 0) {
                    return { ...p, aadhaarNumber: digits.replace(/(\d{4})(?=\d)/g, '$1 ') };
                  }
                }
                return p;
              });
            }
            setFormData(loadedFormData);
            if (appData.formData.photo) {
              setPhoto(appData.formData.photo); // Set the photo state
            }
            console.log(`[AppFormPage1 LoadDraft] Successfully loaded draft data for ID: ${applicationId}`, appData);
            setIsDataLoaded(true);
          } else {
            console.warn(`[AppFormPage1 LoadDraft] No application data or formData found for ID: ${applicationId}`, appData);
            setIsDataLoaded(true); // Still mark as loaded even if no data (new application)
          }
        } catch (error) {
          console.error(`[AppFormPage1 LoadDraft] Error loading draft data for ${applicationId}:`, error);
          setIsDataLoaded(true); // Mark as loaded to allow user to proceed
        } finally {
          setIsDataLoading(false);
          // Ensure loading overlay stays for at least 2 seconds
          const elapsed = Date.now() - loadingStartTimeRef.current;
          const minDisplayTime = 2000; // 2 seconds minimum
          const remainingTime = Math.max(0, minDisplayTime - elapsed);
          setTimeout(() => {
            setShowLoadingOverlay(false);
          }, remainingTime);
        }
      } else if (applicationId && isNewlyGeneratedIdRef.current) {
        // New application - mark as loaded immediately
        setIsDataLoaded(true);
        setIsDataLoading(false);
        setShowLoadingOverlay(false);
      } else if (!applicationId) {
        // No applicationId yet - ensure we're not in a frozen state
        // This can happen briefly before the ID is generated
        setIsDataLoading(false);
        setShowLoadingOverlay(false);
      }
    };

    loadDraftData();
    
    // Safety timeout: If isDataLoaded is still false after 3 seconds, force it to true
    // This prevents the form from being stuck in a frozen state
    const safetyTimeout = setTimeout(() => {
      setIsDataLoaded(prev => {
        if (!prev) {
          console.warn('[AppFormPage1] Safety timeout: forcing isDataLoaded to true');
          return true;
        }
        return prev;
      });
      setIsDataLoading(false);
    }, 3000);
    
    return () => clearTimeout(safetyTimeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, isViewMode]); // Run when applicationId or isViewMode changes

  // Effect to check if application is locked (after payment or manually locked by admin)
  useEffect(() => {
    const checkLockStatus = async () => {
      if (!applicationId) return;
      try {
        const response = await axios.get(`${API_BASE}/api/settings/application-lock-status/${applicationId}`);
        if (response.data.success && response.data.isLocked) {
          setIsApplicationLocked(true);
          console.log(`[AppFormPage1] Application ${applicationId} is locked: ${response.data.reason}`);
        } else {
          setIsApplicationLocked(false);
        }
      } catch (error) {
        console.error('[AppFormPage1] Error checking application lock status:', error);
      }
    };
    checkLockStatus();
  }, [applicationId]);

  // Effect to auto-trigger full PDF download when navigating with autoDownloadPdf flag in sessionStorage
  useEffect(() => {
    const autoDownloadFlag = sessionStorage.getItem('autoDownloadPdf');
    const searchParams = new URLSearchParams(location.search);
    const shouldAutoDownload = autoDownloadFlag === 'true' || searchParams.get('download') === 'true' || location.state?.autoDownload === true;
    
    // Only trigger download after data is fully loaded (prevents blank PDF race condition)
    if (shouldAutoDownload && applicationId && !isFullPdfGenerating && isDataLoaded && !showCountdownModal) {
      console.log('[AutoDownload] Data loaded, starting countdown before PDF generation...');
      
      // Clear the flag immediately to prevent re-triggering
      sessionStorage.removeItem('autoDownloadPdf');
      
      // Show countdown modal instead of immediately generating PDF
      setShowCountdownModal(true);
      setCountdownValue(3);
      
      // Clean up the URL to remove the query param if present
      if (searchParams.get('download') === 'true') {
        window.history.replaceState({}, '', '/form/page1');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, location.search, location.state?.autoDownload, isDataLoaded]);
  
  // Effect to handle countdown timer
  useEffect(() => {
    if (!showCountdownModal) return;
    
    if (countdownValue > 0) {
      const timer = setTimeout(() => {
        setCountdownValue(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Countdown finished, trigger PDF generation
      console.log('[AutoDownload] Countdown complete, triggering PDF generation...');
      setShowCountdownModal(false);
      handleDownloadFullApplicationPdf();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCountdownModal, countdownValue]);

  // Effect to show Partner Selection Modal when partners exist and user hasn't selected who they are
  useEffect(() => {
    // Only show modal if:
    // 1. Data is loaded
    // 2. Dealership type requires multiple partners
    // 3. Partners exist in the form
    // 4. User hasn't selected who they are yet
    // 5. Not in view mode
    const requiresPartners = ['Partnership', 'Pvt Ltd', 'Public Ltd', 'LLP', 'Non Individual applicant entity'].includes(formData.dealershipType);
    const hasPartners = formData.partnerDirectorDetails && formData.partnerDirectorDetails.length > 0;
    
    if (isDataLoaded && requiresPartners && hasPartners && editingUser === null && !isViewMode) {
      setShowPartnerSelectionModal(true);
    }
  }, [isDataLoaded, formData.dealershipType, formData.partnerDirectorDetails, editingUser, isViewMode]);

  // Handler for partner selection
  const handleSelectEditingUser = (selection: 'main' | number) => {
    setEditingUser(selection);
    // Persist to sessionStorage so other pages can read it
    sessionStorage.setItem('editingUser', JSON.stringify(selection));
    setShowPartnerSelectionModal(false);
  };

  // Check if a section should be locked (read-only) based on current editing user
  const isMainApplicantSectionLocked = editingUser !== null && editingUser !== 'main';
  // Helper: Combines all conditions that should disable main applicant fields
  // Now also includes isApplicationLocked for paid/manually locked applications
  const isMainFieldDisabled = !isOnline || isViewMode || isMainApplicantSectionLocked || isApplicationLocked;
  
  // Handler to show disabled tooltip on click of disabled elements
  const showDisabledTooltip = (e: React.MouseEvent) => {
    if (!isMainApplicantSectionLocked) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setDisabledTooltip({ show: true, x: rect.left + rect.width / 2, y: rect.top - 5 });
    setTimeout(() => setDisabledTooltip({ show: false, x: 0, y: 0 }), 1500);
  };
  const isPartnerSectionLocked = (partnerIndex: number) => {
    if (editingUser === null) return false; // No selection yet, allow editing
    if (editingUser === 'main') return false; // Main applicant can edit all
    return editingUser !== partnerIndex; // Only the selected partner can edit their section
  };

  // Partner Setup Modal Handlers
  const initializePartnerSetupModal = (dealershipType: string) => {
    const minPartners = dealershipType === 'Public Ltd' ? 3 : 2;
    setPartnerSetupType(dealershipType);
    setPartnerSetupCount(minPartners);
    // Initialize partner data array (minPartners - 1 because main applicant is #1)
    const initialData: PartnerSetupData[] = Array(minPartners - 1).fill(null).map(() => ({
      name: '',
      phone: '',
      email: '',
      documentFile: null,
      documentType: null,
      detectedName: '',
      isUploading: false
    }));
    setPartnerSetupData(initialData);
    setShowPartnerSetupModal(true);
  };

  // Open partner setup modal with existing data (for when partner count changes)
  const openPartnerSetupModalWithCount = (newCount: number) => {
    const dealershipType = formData.dealershipType;
    const minPartners = dealershipType === 'Public Ltd' ? 3 : 2;
    const validCount = Math.max(minPartners, newCount);
    
    setPartnerSetupType(dealershipType);
    setPartnerSetupCount(validCount);
    
    // Pre-fill with existing partner data
    const existingDetails = formData.partnerDirectorDetails || [];
    const neededPartners = validCount - 1; // -1 for main applicant
    
    const setupData: PartnerSetupData[] = Array(neededPartners).fill(null).map((_, idx) => {
      const existing = existingDetails[idx];
      return {
        name: existing?.nameInBlocks || '',
        phone: existing?.personalMobile || '',
        email: existing?.emailId || '',
        documentFile: null,
        documentType: null,
        detectedName: existing?.nameInBlocks || '',
        isUploading: false
      };
    });
    
    setPartnerSetupData(setupData);
    setShowPartnerSetupModal(true);
  };

  const handlePartnerSetupCountChange = (delta: number) => {
    const minPartners = partnerSetupType === 'Public Ltd' ? 3 : 2;
    const newCount = Math.max(minPartners, Math.min(20, partnerSetupCount + delta));
    setPartnerSetupCount(newCount);
    
    // Adjust partner data array
    const currentData = [...partnerSetupData];
    const neededPartners = newCount - 1; // -1 for main applicant
    
    if (neededPartners > currentData.length) {
      // Add more partners
      const additionalPartners = Array(neededPartners - currentData.length).fill(null).map(() => ({
        name: '',
        phone: '',
        email: '',
        documentFile: null,
        documentType: null,
        detectedName: '',
        isUploading: false
      }));
      setPartnerSetupData([...currentData, ...additionalPartners]);
    } else if (neededPartners < currentData.length) {
      // Remove extra partners
      setPartnerSetupData(currentData.slice(0, neededPartners));
    }
  };

  const handlePartnerSetupInputChange = (index: number, field: 'name' | 'phone' | 'email', value: string) => {
    const updatedData = [...partnerSetupData];
    if (field === 'name') {
      updatedData[index].name = value.toUpperCase();
    } else if (field === 'phone') {
      // Phone - only allow digits
      updatedData[index].phone = value.replace(/\D/g, '').slice(0, 10);
    } else if (field === 'email') {
      updatedData[index].email = value.toLowerCase().trim();
    }
    setPartnerSetupData(updatedData);
  };

  // Email validation helper
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Indian mobile number validation (must start with 6, 7, 8, or 9 and be 10 digits)
  const isValidIndianMobile = (phone: string): boolean => {
    if (phone.length !== 10) return false;
    return /^[6-9]\d{9}$/.test(phone);
  };

  // Check for duplicate mobile numbers in partner setup
  const hasDuplicateMobiles = (): boolean => {
    const phones = partnerSetupData.map(p => p.phone).filter(p => p.length === 10);
    return phones.length !== new Set(phones).size;
  };

  // Get duplicate mobile numbers for display
  const getDuplicateMobiles = (): string[] => {
    const phones = partnerSetupData.map(p => p.phone).filter(p => p.length === 10);
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    phones.forEach(p => {
      if (seen.has(p)) duplicates.add(p);
      seen.add(p);
    });
    return Array.from(duplicates);
  };

  const handlePartnerSetupDocumentUpload = async (index: number, file: File) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      alert('Only JPEG, JPG, or PNG images are accepted.');
      return;
    }

    const updatedData = [...partnerSetupData];
    updatedData[index].documentFile = file;
    updatedData[index].documentType = null; // Will be auto-detected
    updatedData[index].isUploading = true;
    setPartnerSetupData(updatedData);

    try {
      // Upload and extract name using AI with auto-detection
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API_BASE_URL}/api/extract-id-name`, {
        method: 'POST',
        body: formDataUpload,
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        const extractedName = result.extractedName || result.fullName || '';
        const detectedType = result.documentType || 'ID';
        
        const finalData = [...partnerSetupData];
        finalData[index].isUploading = false;
        finalData[index].detectedName = extractedName;
        finalData[index].documentType = detectedType === 'pan' ? 'pan' : detectedType === 'aadhaar' ? 'aadhaar' : null;
        if (extractedName) {
          finalData[index].name = extractedName.toUpperCase();
        }
        setPartnerSetupData(finalData);
      } else {
        throw new Error('Failed to extract document info');
      }
    } catch (error) {
      console.error('Error extracting document info:', error);
      const finalData = [...partnerSetupData];
      finalData[index].isUploading = false;
      setPartnerSetupData(finalData);
      alert('Failed to extract name from document. Please enter manually.');
    }
  };

  const handlePartnerSetupSubmit = () => {
    // Validate all partners have valid emails
    const invalidEmails = partnerSetupData.filter((p, idx) => !isValidEmail(p.email));
    if (invalidEmails.length > 0) {
      alert(`Please enter valid email addresses for all ${partnerSetupType === 'Partnership' ? 'partners' : 'directors'}.`);
      return;
    }

    // Validate Indian mobile numbers
    const invalidMobiles = partnerSetupData.filter(p => p.phone && !isValidIndianMobile(p.phone));
    if (invalidMobiles.length > 0) {
      alert('Please enter valid Indian mobile numbers (10 digits starting with 6, 7, 8, or 9).');
      return;
    }

    // Check for duplicate mobile numbers
    if (hasDuplicateMobiles()) {
      const dupes = getDuplicateMobiles();
      alert(`Duplicate mobile numbers found: ${dupes.join(', ')}. Each partner must have a unique mobile number.`);
      return;
    }
    
    setIsPartnerSetupSubmitting(true);
    
    // Create partner details from setup data
    const newPartnerDetails: PartnerDirectorDetail[] = partnerSetupData.map((data, idx) => ({
      id: `partner_${Date.now()}_${idx}_${Math.random().toString(36).substr(2, 9)}`,
      photo: null,
      nameInBlocks: data.name,
      fatherOrHusbandName: '',
      dateOfBirth: '',
      panCardFile: null,
      dobProofFile: null,
      isMinimumEducationMet: '',
      qualification: '',
      qualificationProofFile: null,
      currentAddress: '',
      permanentAddress: '',
      permanentAddressSameAsCurrent: false,
      addressProofFile: null,
      personalMobile: data.phone,
      secondaryMobile: '',
      emailId: data.email,
      nationality: 'Indian',
      gender: '',
      maritalStatus: '',
      maritalStatusOther: '',
      aadhaarNumber: '',
      aadhaarVerified: '',
      aadhaarFile: null,
      panNumberStatutory: '',
      panVerified: '',
      panStatutoryFile: null,
      gstNumber: '',
      gstVerified: '',
      gstFile: null,
      identityProofVoterId: false,
      identityProofPassport: false,
      identityProofDrivingLicense: false,
      identityDocumentNumber: '',
      voterIdNumber: '',
      passportNumber: '',
      drivingLicenseNumber: '',
      voterIdFile: null,
      passportFile: null,
      drivingLicenseFile: null,
      eduSscBoard: '',
      eduSscMarksObtained: '',
      eduSscOutOfMarks: '',
      eduSscPercentage: '',
      eduSscYearOfPassing: '',
      eduSscCertificateFile: null,
      eduHscBoard: '',
      eduHscMarksObtained: '',
      eduHscOutOfMarks: '',
      eduHscPercentage: '',
      eduHscYearOfPassing: '',
      eduHscCertificateFile: null,
      eduGraduateBoard: '',
      eduGraduateMarksObtained: '',
      eduGraduateOutOfMarks: '',
      eduGraduatePercentage: '',
      eduGraduateYearOfPassing: '',
      eduGraduateCertificateFile: null,
      eduOthersBoard: '',
      eduOthersMarksObtained: '',
      eduOthersOutOfMarks: '',
      eduOthersPercentage: '',
      eduOthersYearOfPassing: '',
      eduOthersCertificateFile: null,
      presentOccupation: '',
      businessEmploymentDetails: '',
      totalWorkExperienceYears: '',
      relevantIndustryExperience: '',
      annualIncome: '',
      netWorthAtApplication: '',
      sourceOfInvestment: '',
      abilityToMeetRequiredInvestment: '',
      finDocProjectReport: '',
      finDocProjectReportFile: null,
      finDocBankStatement: '',
      finDocBankStatementFile: null,
      finDocFdTdReceipts: '',
      finDocFdTdReceiptsFile: null,
      finDocInvestmentCertificates: '',
      finDocInvestmentCertificatesFile: null,
      finDocMutualFundStatement: '',
      finDocMutualFundStatementFile: null,
      finDocDematStatement: '',
      finDocDematStatementFile: null,
      finDocCaCertificate: '',
      finDocCaCertificateFile: null,
      finDocDpCertificate: '',
      finDocDpCertificateFile: null,
      finDocAttestedFinancials: '',
      finDocAttestedFinancialsFile: null,
      finDocNotarizedAffidavit: '',
      finDocNotarizedAffidavitFile: null,
      finDocBankLetter: '',
      finDocBankLetterFile: null,
      finDocOwnershipDocs: '',
      finDocOwnershipDocsFile: null,
      affidavitFile: null,
      appendixBFile: null,
      appendixCFile: null,
      appendixDFile: null,
      appendixEFile: null,
      appendixFFile: null
    }));

    // Update form data with dealership type and partner details
    setFormData(prev => ({
      ...prev,
      dealershipType: partnerSetupType,
      numberOfPartners: partnerSetupCount,
      partnerDirectorDetails: newPartnerDetails
    }));

    setShowPartnerSetupModal(false);
    setIsPartnerSetupSubmitting(false);
    
    // Set editing user to main applicant after setup
    setEditingUser('main');
  };

  const handlePartnerSetupCancel = () => {
    setShowPartnerSetupModal(false);
    // Reset dealership type if cancelled
    setFormData(prev => ({
      ...prev,
      dealershipType: ''
    }));
  };

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handleOpenFilePreview = async (file: File | string | null, defaultFileName: string = 'file') => {
    console.log('[handleOpenFilePreview] Called with file:', file, 'defaultFileName:', defaultFileName);
    if (!file) {
      console.log('[handleOpenFilePreview] File is null, returning.');
      return;
    }

    console.log('[handleOpenFilePreview] Current API_BASE_URL:', API_BASE_URL);

    let url: string | null = null;
    let name: string | null = null;
    let type: string | null = null;

    if (typeof file === 'string') {
      // 'file' is the path from the DB, e.g., '/uploads/...'
      console.log('[handleOpenFilePreview] File is a string (path):', file);
      
      // Normalize file to a path (strip any accidental origin and ensure leading slash)
      let normalizedPath = file;
      if (file.includes('://')) {
        try {
          normalizedPath = new URL(file).pathname;
        } catch {
          normalizedPath = file;
        }
      } else if (!file.startsWith('/')) {
        normalizedPath = `/${file}`;
      }

      // Extract filename for display
      name = file.split('/').pop() || defaultFileName;

      // Infer mime type from extension
      if (file.toLowerCase().endsWith('.pdf')) {
        type = 'application/pdf';
      } else if (file.match(/\.(jpeg|jpg|gif|png)$/i)) {
        const ext = file.split('.').pop()?.toLowerCase();
        type = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      } else {
        type = 'application/octet-stream'; // Fallback
      }

      // Use secure file URL utility - handles both public and private files
      // Public files (templates) get direct URLs, private files get signed URLs
      try {
        url = await getSecureFileUrl(normalizedPath, applicationId);
        if (!url) {
          console.error('[handleOpenFilePreview] Failed to get secure URL for:', normalizedPath);
          alert('Unable to access file. Please try again or contact support.');
          return;
        }
      } catch (error) {
        console.error('[handleOpenFilePreview] Error getting secure URL:', error);
        alert('Unable to access file. Please try again.');
        return;
      }

      console.log(`[handleOpenFilePreview] Secure URL: ${url}, Name: ${name}, Type: ${type}`);
    } else {
      console.log('[handleOpenFilePreview] File is a File object:', file);
      // File is a File object (newly uploaded)
      url = URL.createObjectURL(file);
      name = file.name;
      type = file.type;
      console.log('[handleOpenFilePreview] File object - Constructed URL:', url, 'Name:', name, 'Type:', type);
    }

    setModalMode('document'); // Ensure document mode so crop/save works with applicationId & fieldName
    setModalFileUrl(url);
    setModalFileName(name);
    setModalFileType(type);
    setModalFieldName(defaultFileName);
    setIsModalOpen(true);
    console.log('[handleOpenFilePreview] Modal state set. mode: document, isOpen:', true, 'Url:', url);
  };

  const handleDownloadPdf = async () => {
    setIsPdfGenerating(true);
    setCompressionProgress(0);
    const formElement = formRef.current;
    if (!formElement) {
      alert('Form content is not available for PDF export.');
      setIsPdfGenerating(false);
      return;
    }

    const formClone = formElement.cloneNode(true) as HTMLElement;

    try {
      formClone.style.position = 'absolute';
      formClone.style.left = '-9999px';
      formClone.style.top = '0px';
      formClone.style.width = `${formElement.offsetWidth}px`;
      document.body.appendChild(formClone);

      // Cloned nodes RESTART their CSS animations from frame 0 — e.g.
      // SlideDownContainer's slideDown begins at max-height:0/opacity:0, which
      // collapsed partner/additional-applicant sections and left blank bands in
      // the captured PDF. Neutralize all animations/transitions in the clone so
      // every section renders in its natural, fully-expanded state.
      formClone.querySelectorAll('*').forEach(el => {
        (el as HTMLElement).style.animation = 'none';
        (el as HTMLElement).style.transition = 'none';
      });
      formClone.style.animation = 'none';
      formClone.style.transition = 'none';

      const inputs = formClone.querySelectorAll('input[type="text"], input[type="number"], textarea');
      inputs.forEach(input => {
        const typedInput = input as HTMLInputElement | HTMLTextAreaElement;
        const value = typedInput.value;
        if (value) {
          const span = document.createElement('span');
          span.textContent = value;
          span.style.borderBottom = '1px solid #333';
          span.style.padding = '2px 5px';
          span.style.margin = '0 5px';
          span.style.fontSize = 'inherit';
          span.style.fontFamily = 'inherit';
          span.style.display = 'inline-block';
          span.style.whiteSpace = 'pre-wrap';
          span.style.wordBreak = 'break-word';
          span.style.color = window.getComputedStyle(typedInput).color;
          typedInput.parentNode?.replaceChild(span, typedInput);
        }
      });

      const checkboxes = formClone.querySelectorAll('input[type="checkbox"], input[type="radio"]');
      checkboxes.forEach(checkbox => {
        const typedCheckbox = checkbox as HTMLInputElement;
        const span = document.createElement('span');
        span.textContent = typedCheckbox.checked ? '☑' : '☐';
        span.style.fontSize = 'inherit';
        span.style.fontFamily = 'inherit';
        span.style.display = 'inline-block';
        span.style.margin = '0 5px';
        typedCheckbox.parentNode?.replaceChild(span, typedCheckbox);
      });

      const buttons = formClone.querySelectorAll('button');
      buttons.forEach(btn => (btn.style.visibility = 'hidden'));

      const canvas = await html2canvas(formClone, {
        scale: getSafeCanvasScale(formClone, 1.9),
        useCORS: true,
        width: formClone.scrollWidth,
        height: formClone.scrollHeight,
        windowWidth: formClone.scrollWidth,
        windowHeight: formClone.scrollHeight,
      });

      // Trim trailing blank area (hidden buttons/bottom padding)
      const contentBottom = findCanvasContentBottom(canvas);
      let outCanvas: HTMLCanvasElement = canvas;
      if (contentBottom < canvas.height - 10) {
        const cropped = document.createElement('canvas');
        cropped.width = canvas.width;
        cropped.height = contentBottom;
        cropped.getContext('2d')?.drawImage(canvas, 0, 0, canvas.width, contentBottom, 0, 0, canvas.width, contentBottom);
        outCanvas = cropped;
      }

      const imgData = outCanvas.toDataURL('image/jpeg', 0.5); // Use aggressive JPEG compression
      const pdf = new jsPDF({
        orientation: outCanvas.width > outCanvas.height ? 'l' : 'p',
        unit: 'px',
        format: [outCanvas.width, outCanvas.height],
      });

      pdf.addImage(imgData, 'JPEG', 0, 0, outCanvas.width, outCanvas.height);
      
      // Simulate compression process for visual feedback
      for (let i = 0; i <= 100; i += 10) {
        setCompressionProgress(i);
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      pdf.save(`application_form_page1_${applicationId || 'draft'}.pdf`);
    } catch (error) {
      console.error('PDF Generation Error:', error);
      alert(`Error generating PDF for Page 1. Please check the console for details. Error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      if (formClone.parentNode === document.body) {
        document.body.removeChild(formClone);
      }
      setIsPdfGenerating(false);
      setCompressionProgress(0);
    }
  };

  const handlePanCardUpload = async (file: File) => {
    if (!applicationId) {
      alert('Application ID is not available. Please save the draft first.');
      return;
    }

    // Validate file type - only accept images (no PDFs)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      setUploadErrorModal({
        show: true,
        message: 'Please upload an image file only (JPG, JPEG, or PNG). PDF files are not accepted for PAN Card upload.',
        fieldName: 'PAN Card'
      });
      return;
    }

    // Additional check: reject PDFs explicitly
    if (file.type === 'application/pdf' || fileExtension === '.pdf') {
      setUploadErrorModal({
        show: true,
        message: 'PDF files are not accepted for PAN Card upload. Please upload a clear image (JPG, JPEG, or PNG) of your PAN Card.',
        fieldName: 'PAN Card'
      });
      return;
    }

    setIsPanFileUploading(true);
    setPanFileProgress(0);
    setPanFileUploadMessage('');

    try {
      const response = await uploadFormFile(
        applicationId,
        file,
        'panCardFile',
        (progressEvent) => {
          if (!progressEvent.total) return;
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setPanFileProgress(percentCompleted);
        }
      );

      if (response && response.success) {
        const filePath = (response as any).filePath || file;
        const extracted = (response as any).extractedFields;
        const panNumber = extracted?.panNumber || '';
        
        // Update stored PAN file path AND auto-link to IDENTIFICATION table
        setFormData(prev => ({
          ...prev,
          panCardFile: filePath,
          // Auto-link to IDENTIFICATION table
          panStatutoryFile: filePath,
          ...(panNumber ? { panNumberStatutory: panNumber } : {}),
        }));

        if (extracted) {
          let { fullName, fatherName, dateOfBirth } = extracted as {
            fullName?: string;
            fatherName?: string;
            dateOfBirth?: string;
          };

          fullName = fullName && typeof fullName === 'string' ? fullName.trim() : '';
          fatherName = fatherName && typeof fatherName === 'string' ? fatherName.trim() : '';
          dateOfBirth = dateOfBirth && typeof dateOfBirth === 'string' ? dateOfBirth.trim() : '';

          // Normalize DOB to YYYY-MM-DD if it looks like DD/MM/YYYY or similar
          let normalizedDob = dateOfBirth;
          if (dateOfBirth && /\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4}/.test(dateOfBirth)) {
            const parts = dateOfBirth.replace(/-/g, '/').split('/');
            if (parts.length === 3) {
              const [dd, mm, yy] = parts;
              const day = dd.padStart(2, '0');
              const month = mm.padStart(2, '0');
              let year = yy;
              if (year.length === 2) {
                const intYear = parseInt(year, 10);
                year = intYear >= 50 ? `19${year}` : `20${year}`;
              }
              normalizedDob = `${year}-${month}-${day}`;
            }
          }

          // Update file path with panNumber if extracted
          setFormData(prev => ({
            ...prev,
            panCardFile: filePath,
            panStatutoryFile: filePath,
            ...(panNumber ? { panNumberStatutory: panNumber } : {}),
          }));

          // Typewriter animation for AI-extracted fields (sequential for smooth effect)
          const nameToFill = fullName && fullName.length > 0 ? fullName.toUpperCase() : '';
          const fatherToFill = fatherName && fatherName.length > 0 ? fatherName.toUpperCase() : '';
          const dobToFill = normalizedDob && normalizedDob.length > 0 ? normalizedDob : '';

          // Auto-expand the PAN fields section with smooth animation
          setShowPanFields(true);
          
          if (nameToFill) await typewriterFill('nameInBlocks', nameToFill);
          if (fatherToFill) await typewriterFill('fatherOrHusbandName', fatherToFill);
          if (dobToFill) await typewriterFill('dateOfBirth', dobToFill);

          // Trigger save after AI autofill completes to persist extracted data
          setFormData(currentData => {
            debouncedSave(currentData);
            return currentData;
          });

          setPanFileUploadMessage('PAN card uploaded and details extracted. Please review and edit if needed.');
        } else {
          setPanFileUploadMessage('PAN card uploaded. AI extraction not available or returned no data.');
        }
      } else {
        throw new Error('PAN card upload failed on server.');
      }
    } catch (error: any) {
      console.error('Error uploading PAN card:', error);
      // Check for image quality error from backend
      if (error.response?.data?.imageQualityError) {
        setImageQualityError({
          show: true,
          reason: error.response.data.reason || 'The uploaded image is blurry, smudged, or unreadable.',
          fieldName: 'PAN Card',
        });
        setPanFileUploadMessage('');
      } else {
        // Show error modal for AI validation errors or other upload failures
        const errorMessage = error.message || 'Failed to upload PAN card.';
        setUploadErrorModal({
          show: true,
          message: errorMessage,
          fieldName: 'PAN Card'
        });
        setPanFileUploadMessage('');
      }
    } finally {
      setIsPanFileUploading(false);
    }
  };

  // Handler for educational certificate file upload with AI extraction and auto-detection
  const handleEduCertificateUpload = async (
    file: File,
    clickedQualificationType: 'Ssc' | 'Hsc' | 'Graduate' | 'Others',
    fieldPrefix: string
  ) => {
    // Use the clicked button's type for upload, but AI will auto-detect the actual type
    setEduCertUploading(prev => ({ ...prev, [clickedQualificationType]: true }));

    try {
      const response = await uploadFormFile(
        applicationId,
        file,
        `edu${clickedQualificationType}CertificateFile`,
        () => {}
      );

      if (response && response.success) {
        const extracted = (response as any).extractedFields;
        
        // Determine the actual qualification type from AI detection
        let actualQualificationType: 'Ssc' | 'Hsc' | 'Graduate' | 'Others' = clickedQualificationType;
        let detectedLabel = fieldPrefix;
        
        if (extracted && extracted.detectedQualificationType) {
          actualQualificationType = extracted.detectedQualificationType as 'Ssc' | 'Hsc' | 'Graduate' | 'Others';
          detectedLabel = extracted.detectedQualificationLabel || fieldPrefix;
          
          // Show info modal if detected type differs from clicked type
          if (actualQualificationType !== clickedQualificationType) {
            setEduAutoDetectInfo({
              show: true,
              detectedType: actualQualificationType,
              detectedLabel: detectedLabel,
            });
          }
        }
        
        // Use the AI-detected qualification type for ALL field names
        // Server also remaps the file to the detected type's field in the DB
        const fileFieldName = `edu${actualQualificationType}CertificateFile` as keyof FormData;
        const boardFieldName = `edu${actualQualificationType}Board` as keyof FormData;
        const marksFieldName = `edu${actualQualificationType}MarksObtained` as keyof FormData;
        const outOfFieldName = `edu${actualQualificationType}OutOfMarks` as keyof FormData;
        const percentageFieldName = `edu${actualQualificationType}Percentage` as keyof FormData;
        const yearFieldName = `edu${actualQualificationType}YearOfPassing` as keyof FormData;
        const clickedFileFieldName = `edu${clickedQualificationType}CertificateFile` as keyof FormData;

        // Store file under the DETECTED type only; clear clicked type if different
        const filePath = (response as any).filePath || file;
        setFormData(prev => ({
          ...prev,
          [fileFieldName]: filePath,
          ...(fileFieldName !== clickedFileFieldName ? { [clickedFileFieldName]: null } : {}),
        }));

        // Then apply typewriter animation for extracted fields
        if (extracted) {
          if (extracted.board) {
            await typewriterFill(boardFieldName, extracted.board);
          }
          if (extracted.marksObtained) {
            await typewriterFill(marksFieldName, extracted.marksObtained);
          }
          if (extracted.outOfMarks) {
            await typewriterFill(outOfFieldName, extracted.outOfMarks);
          }
          if (extracted.percentage) {
            await typewriterFill(percentageFieldName, extracted.percentage);
          }
          if (extracted.yearOfPassing) {
            await typewriterFill(yearFieldName, extracted.yearOfPassing);
          }

          // Trigger save after autofill completes
          setFormData(currentData => {
            debouncedSave(currentData);
            return currentData;
          });
        }
      }
    } catch (error: any) {
      console.error('Error uploading education certificate:', error);
      if (error.response?.data?.imageQualityError) {
        setImageQualityError({
          show: true,
          reason: error.response.data.reason || 'The uploaded image is blurry, smudged, or unreadable.',
          fieldName: `${fieldPrefix} Certificate`,
        });
      } else {
        setUploadErrorModal({
          show: true,
          message: error.response?.data?.reason || error.message || 'Upload failed. Please try again.',
          fieldName: `${fieldPrefix} Certificate`
        });
      }
    } finally {
      setEduCertUploading(prev => ({ ...prev, [clickedQualificationType]: false }));
    }
  };

  // Handler for partner educational certificate file upload with AI extraction and auto-detection
  const handlePartnerEduCertificateUpload = async (
    partnerIndex: number,
    file: File,
    clickedQualificationType: 'Ssc' | 'Hsc' | 'Graduate' | 'Others',
    fieldPrefix: string
  ) => {
    // Use the clicked button's type for upload, but AI will auto-detect the actual type
    const uploadKey = `${partnerIndex}_${clickedQualificationType}`;
    setPartnerEduCertUploading(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const response = await uploadFormFile(
        applicationId,
        file,
        `partner_${partnerIndex}_edu${clickedQualificationType}CertificateFile`,
        () => {}
      );

      if (response && response.success) {
        const extracted = (response as any).extractedFields;
        
        // Determine the actual qualification type from AI detection
        let actualQualificationType: 'Ssc' | 'Hsc' | 'Graduate' | 'Others' = clickedQualificationType;
        let detectedLabel = fieldPrefix;
        
        if (extracted && extracted.detectedQualificationType) {
          actualQualificationType = extracted.detectedQualificationType as 'Ssc' | 'Hsc' | 'Graduate' | 'Others';
          detectedLabel = extracted.detectedQualificationLabel || fieldPrefix;
          
          // Show info modal if detected type differs from clicked type
          if (actualQualificationType !== clickedQualificationType) {
            setEduAutoDetectInfo({
              show: true,
              detectedType: actualQualificationType,
              detectedLabel: detectedLabel,
              partnerIndex: partnerIndex,
            });
          }
        }
        
        // Use the detected qualification type for field names (marks, board, etc.)
        const fileFieldName = `edu${actualQualificationType}CertificateFile`;
        const boardFieldName = `edu${actualQualificationType}Board`;
        const marksFieldName = `edu${actualQualificationType}MarksObtained`;
        const outOfFieldName = `edu${actualQualificationType}OutOfMarks`;
        const percentageFieldName = `edu${actualQualificationType}Percentage`;
        const yearFieldName = `edu${actualQualificationType}YearOfPassing`;

        // Store file under the DETECTED type only; clear clicked type if different
        // Server also remaps the file to the detected type's field in the DB
        const clickedFileFieldName = `edu${clickedQualificationType}CertificateFile`;
        const filePath = (response as any).filePath || file;

        setFormData(prevFormData => {
          const updatedDetails = [...(prevFormData.partnerDirectorDetails || [])];
          if (updatedDetails[partnerIndex]) {
            updatedDetails[partnerIndex] = {
              ...updatedDetails[partnerIndex],
              [fileFieldName]: filePath,
              ...(fileFieldName !== clickedFileFieldName ? { [clickedFileFieldName]: null } : {}),
            };
          }
          return { ...prevFormData, partnerDirectorDetails: updatedDetails };
        });
        
        // Then apply typewriter animation for extracted fields
        if (extracted) {
          console.log(`[Partner ${partnerIndex + 1} Edu] AI extracted (detected as ${actualQualificationType}):`, extracted);
          
          if (extracted.board) {
            await typewriterFillPartner(partnerIndex, boardFieldName, extracted.board);
          }
          if (extracted.marksObtained) {
            await typewriterFillPartner(partnerIndex, marksFieldName, extracted.marksObtained);
          }
          if (extracted.outOfMarks) {
            await typewriterFillPartner(partnerIndex, outOfFieldName, extracted.outOfMarks);
          }
          if (extracted.percentage) {
            await typewriterFillPartner(partnerIndex, percentageFieldName, extracted.percentage);
          }
          if (extracted.yearOfPassing) {
            await typewriterFillPartner(partnerIndex, yearFieldName, extracted.yearOfPassing);
          }
          
          // Trigger save after AI autofill completes
          setFormData(currentData => {
            debouncedSave(currentData);
            return currentData;
          });
        }
      }
    } catch (error: any) {
      console.error('Error uploading partner education certificate:', error);
      if (error.response?.data?.imageQualityError) {
        setImageQualityError({
          show: true,
          reason: error.response.data.reason || 'The uploaded image is blurry, smudged, or unreadable.',
          fieldName: `Partner ${partnerIndex + 2} ${fieldPrefix} Certificate`,
        });
      } else {
        setUploadErrorModal({
          show: true,
          message: error.response?.data?.reason || error.message || 'Upload failed. Please try again.',
          fieldName: `Partner ${partnerIndex + 2} ${fieldPrefix} Certificate`
        });
      }
    } finally {
      setPartnerEduCertUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleDownloadFullApplicationPdf = async () => {
    setIsFullPdfGenerating(true);
    setFullPdfProgress(0);
    setCurrentPageGenerating(1);
    
    // Initialize jsPDF lazily based on the first rendered page's dimensions
    let pdf: any = null;
    let pdfCursorY = 0; // vertical position (pt) already used on the current PDF page
    
    // Load complete formData for hidden pages to ensure filled values for all pages
    try {
      const idForLoad = applicationId || formData.applicationNoOfficeUse || '';
      if (idForLoad) {
        const appData = await fetchApplicationDetails(idForLoad);
        if (appData && appData.formData) {
          setPdfFormData(appData.formData);
          // Allow React to render hidden pages with the populated data before capture
          setPdfRenderKey(prev => prev + 1);
          await new Promise(resolve => setTimeout(resolve, 350));
        }
      }
    } catch (e) {
      console.error('[FullPDF] Failed to load full form data for PDF generation:', e);
    }
    
    try {
      // Only include the 5 actual pages used in the application (1, 2, 3, 4, 14)
      const totalPages = 5;
      const pageRefs = [
        formRef, // Page 1 - current page
        page2Ref, // Page 2
        page3Ref, // Page 3
        page4Ref, // Page 4
        page14Ref, // Page 14 (displayed as Page 5)
      ];

      // Wait a moment for all page components to fully render
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Process each page one by one with the same exact styling as individual page PDFs
      for (let i = 0; i < pageRefs.length; i++) {
        const pageRef = pageRefs[i];
        const pageNum = i + 1;
        setCurrentPageGenerating(pageNum);
        
        // Calculate progress percentage
        const progress = Math.round((pageNum / totalPages) * 100);
        setFullPdfProgress(progress);
        
        if (!pageRef.current) {
          console.warn(`Page ${pageNum} ref is not available, skipping...`);
          continue;
        }
        
        console.log(`Processing page ${pageNum}...`);
        console.log(`Page ${pageNum} element:`, pageRef.current);
        console.log(`Page ${pageNum} dimensions:`, {
          width: pageRef.current?.offsetWidth,
          height: pageRef.current?.offsetHeight
        });
        
        // Validate that the page has content to capture
        let pageElement = pageRef.current;
        
        if (!pageElement || pageElement.offsetHeight === 0 || pageElement.offsetWidth === 0) {
          console.warn(`Page ${pageNum} has no visible content, skipping...`);
          continue;
        }
        
        // For pages 2-14, we might need to find the actual form content within the page component
        if (i > 0) {
          // Try to find a more specific form container if available
          const formElements = pageElement.querySelectorAll('.form-container, [data-testid="form-content"], .page-content');
          if (formElements.length > 0 && (formElements[0] as HTMLElement).offsetHeight > 0) {
            pageElement = formElements[0] as HTMLDivElement;
            console.log(`Using form container for page ${pageNum}`);
          }
        }
        
        // Clone the page to prepare it for capture - using exact same technique as handleDownloadPdf
        const pageClone = pageElement.cloneNode(true) as HTMLElement;
        pageClone.style.position = 'absolute';
        pageClone.style.left = '-9999px';
        pageClone.style.top = '0px';
        pageClone.style.width = `${pageElement.offsetWidth}px`;
        document.body.appendChild(pageClone);

        // Cloned nodes RESTART their CSS animations from frame 0 — e.g.
        // SlideDownContainer's slideDown begins at max-height:0/opacity:0, which
        // collapsed partner/additional-applicant sections and left blank bands in
        // the captured PDF. Neutralize all animations/transitions in the clone so
        // every section renders in its natural, fully-expanded state.
        pageClone.querySelectorAll('*').forEach(el => {
          (el as HTMLElement).style.animation = 'none';
          (el as HTMLElement).style.transition = 'none';
        });
        pageClone.style.animation = 'none';
        pageClone.style.transition = 'none';

        // Helper to fetch value from loaded application data when DOM clone lacks it
        const getPdfValueFor = (el: Element): string | null => {
          const n = el.getAttribute('name');
          if (!n) return null;
          const v = (pdfFormData as any)?.[n];
          return v === undefined || v === null ? null : String(v);
        };

        // Convert text/number inputs and textareas to static spans, with fallback to pdfFormData
        const inputs = pageClone.querySelectorAll('input[type="text"], input[type="number"], textarea');
        inputs.forEach(input => {
          const typedInput = input as HTMLInputElement | HTMLTextAreaElement;
          const domVal = typedInput.value;
          const fallbackVal = getPdfValueFor(typedInput) || '';
          const finalVal = (domVal && domVal.toString().trim() !== '') ? domVal : fallbackVal;
          if (finalVal && finalVal.toString().trim() !== '') {
            const span = document.createElement('span');
            span.textContent = String(finalVal);
            span.style.borderBottom = '1px solid #333';
            span.style.padding = '2px 5px';
            span.style.margin = '0 5px';
            span.style.fontSize = 'inherit';
            span.style.fontFamily = 'inherit';
            span.style.display = 'inline-block';
            span.style.whiteSpace = 'pre-wrap';
            span.style.wordBreak = 'break-word';
            span.style.color = window.getComputedStyle(typedInput).color;
            typedInput.parentNode?.replaceChild(span, typedInput);
          }
        });

        // Convert selects to static text using selected option or pdfFormData fallback
        const selects = pageClone.querySelectorAll('select');
        selects.forEach(select => {
          const sel = select as HTMLSelectElement;
          let selectedText = '';
          if (sel.options && sel.selectedIndex >= 0) {
            selectedText = sel.options[sel.selectedIndex]?.text || '';
          }
          if (!selectedText) {
            const fb = getPdfValueFor(sel);
            if (fb) {
              const match = Array.from(sel.options).find(o => o.value === fb || o.text === fb);
              selectedText = match ? match.text : fb;
            }
          }
          const span = document.createElement('span');
          span.textContent = selectedText || '';
          span.style.borderBottom = '1px solid #333';
          span.style.padding = '2px 5px';
          span.style.margin = '0 5px';
          span.style.fontSize = 'inherit';
          span.style.fontFamily = 'inherit';
          span.style.display = 'inline-block';
          span.style.whiteSpace = 'pre-wrap';
          span.style.wordBreak = 'break-word';
          span.style.color = window.getComputedStyle(sel).color;
          sel.parentNode?.replaceChild(span, sel);
        });

        // Convert checkboxes and radios to symbols, with fallback based on pdfFormData
        const checkboxes = pageClone.querySelectorAll('input[type="checkbox"], input[type="radio"]');
        checkboxes.forEach(checkbox => {
          const typedCheckbox = checkbox as HTMLInputElement;
          const nameAttr = typedCheckbox.getAttribute('name');
          let isChecked = typedCheckbox.checked;
          if (!isChecked && nameAttr && (pdfFormData as any) && (pdfFormData as any)[nameAttr] !== undefined) {
            const dataVal = (pdfFormData as any)[nameAttr];
            if (typedCheckbox.type === 'radio') {
              isChecked = String(dataVal) === typedCheckbox.value;
            } else {
              isChecked = Boolean(dataVal);
            }
          }
          const span = document.createElement('span');
          span.textContent = isChecked ? '☑' : '☐';
          span.style.fontSize = 'inherit';
          span.style.fontFamily = 'inherit';
          span.style.display = 'inline-block';
          span.style.margin = '0 5px';
          typedCheckbox.parentNode?.replaceChild(span, typedCheckbox);
        });

        const buttons = pageClone.querySelectorAll('button');
        buttons.forEach(btn => (btn.style.visibility = 'hidden'));
        
        // Generate canvas using identical settings to handleDownloadPdf
        const canvas = await html2canvas(pageClone, {
          scale: getSafeCanvasScale(pageClone, 1.9),
          useCORS: true,
          width: pageClone.scrollWidth,
          height: pageClone.scrollHeight,
          windowWidth: pageClone.scrollWidth,
          windowHeight: pageClone.scrollHeight,
        });

        // A4 dimensions in points (72 DPI): 595.28 x 841.89
        const A4_WIDTH_PT = 595.28;
        const A4_HEIGHT_PT = 841.89;

        // Trim trailing blank area of this capture (hidden buttons and bottom
        // padding otherwise turn into fully blank PDF pages after each section)
        const contentBottom = findCanvasContentBottom(canvas);

        // Calculate scale factor to fit canvas width to A4 width
        const scaleFactor = A4_WIDTH_PT / canvas.width;
        const scaledHeight = contentBottom * scaleFactor;

        console.log(`Page ${pageNum}: canvas ${canvas.width}x${canvas.height} (content up to ${contentBottom}px), scaled height: ${scaledHeight.toFixed(0)}`);

        // Flow this section's content continuously into the PDF: fill the
        // remaining space on the current A4 page before starting a new one, so
        // sections join seamlessly instead of each starting a fresh page with a
        // blank tail on the previous one.
        let sourceY = 0;
        while (sourceY < contentBottom) {
          if (!pdf) {
            // Initialize jsPDF with A4 format (portrait)
            pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
            pdfCursorY = 0;
          } else if (pdfCursorY >= A4_HEIGHT_PT - 4) {
            pdf.addPage('a4', 'p');
            pdfCursorY = 0;
          }

          // How much of this canvas fits into the space left on the current page
          const availablePt = A4_HEIGHT_PT - pdfCursorY;
          const sourceHeight = Math.min(availablePt / scaleFactor, contentBottom - sourceY);
          if (sourceHeight < 1) {
            pdf.addPage('a4', 'p');
            pdfCursorY = 0;
            continue;
          }

          // Create a temporary canvas for this slice
          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = Math.ceil(sourceHeight);
          const sliceCtx = sliceCanvas.getContext('2d');

          if (sliceCtx) {
            // White background so sub-pixel rounding never shows as black in JPEG
            sliceCtx.fillStyle = '#ffffff';
            sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
            // Draw the slice from the original canvas
            sliceCtx.drawImage(
              canvas,
              0, sourceY,                          // Source x, y
              canvas.width, sourceHeight,          // Source width, height
              0, 0,                                // Destination x, y
              canvas.width, sourceHeight           // Destination width, height
            );

            const sliceImgData = sliceCanvas.toDataURL('image/jpeg', 0.85);
            const destHeight = sourceHeight * scaleFactor;
            pdf.addImage(sliceImgData, 'JPEG', 0, pdfCursorY, A4_WIDTH_PT, destHeight);
            pdfCursorY += destHeight;
          }

          sourceY += sourceHeight;
        }

        // Small visual gap before the next section starts
        pdfCursorY += 14;
        
        // Remove the clone from DOM
        if (pageClone.parentNode === document.body) {
          document.body.removeChild(pageClone);
        }
        
        // Small delay for better UI feedback
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // Save the complete PDF with a better filename format (matching individual page PDFs)
      if (pdf) {
        pdf.save(`${applicationId || 'CNG-draft'}_full_application.pdf`);
        console.log('Full application PDF saved successfully with A4 page splitting');
      } else {
        alert('Unable to generate full application PDF because no pages could be rendered.');
      }
      
    } catch (error) {
      console.error('Full PDF Generation Error:', error);
      alert(`Error generating full application PDF. Please check the console for details. Error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsFullPdfGenerating(false);
      setFullPdfProgress(0);
      setCurrentPageGenerating(0);
      setPdfFormData(null);
    }
  };

  const applyLocationStretchSelection = (
    match: LocationStretchItem,
    overrides?: { srNo?: string; locationName?: string }
  ) => {
    const srNo = overrides?.srNo ?? match.srNo ?? match.stretchSrNoArea ?? '';
    const locationName = overrides?.locationName ?? match.location ?? '';
    const area = match.area || match.areaStretch || '';

    const updatedFormData: FormData = {
      ...formData,

      // New detailed fields
      locationSrNo: srNo,
      locationLocality: locationName,
      locationCA: match.ca || '',
      locationArea: area,
      locationStretchSide: match.stretchSide || '',
      locationFrom: match.from || '',
      locationFromLatLong: match.fromLatLong || '',
      locationTo: match.to || '',
      locationToLatLong: match.toLatLong || '',
      locationMinPlotFrontage: match.minPlotFrontageMeters || '',
      locationRoadType: match.roadType || '',
      locationMinPlotDepth: match.minPlotDepthMeters || '',

      // Legacy aliases kept in sync for backward compatibility
      locationStretchSrNoArea: srNo,
      locationAreaStretch: area,
      locationDistrict: match.district || formData.locationDistrict,
      locationState: match.state || formData.locationState,
    };

    setFormData(updatedFormData);
  };

  const commitLocationSelectionBySrNo = (enteredValue: string) => {
    const srNoValue = enteredValue.trim();
    if (!srNoValue) return;

    const match = locationStretches.find((row) =>
      (row.srNo || row.stretchSrNoArea || '').trim().toLowerCase() === srNoValue.toLowerCase()
    );

    if (!match) {
      setShowSrNoGuide(true);
      return;
    }

    applyLocationStretchSelection(match, { srNo: srNoValue, locationName: match.location });
  };

  const handleLocationSrNoKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitLocationSelectionBySrNo(e.currentTarget.value);
    }
  };

  const handleLocationSrNoBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    commitLocationSelectionBySrNo(e.currentTarget.value);
  };

  const handleLocationNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const enteredValue = e.currentTarget.value.trim();
    if (!enteredValue) return;

    const match = locationStretches.find((row) =>
      (row.location || '').trim().toLowerCase() === enteredValue.toLowerCase()
    );

    if (!match) {
      return;
    }

    applyLocationStretchSelection(match, { srNo: match.srNo || match.stretchSrNoArea, locationName: enteredValue });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let newErrors = { ...formData.errors };
    let updatedFormData;
    
    // Format Aadhaar number with spaces after every 4 digits
    if (name === 'aadhaarNumber') {
      // Remove all non-digits and spaces
      const digitsOnly = value.replace(/\D/g, '');
      // Limit to 12 digits
      const truncated = digitsOnly.slice(0, 12);
      // Format with spaces: XXXX XXXX XXXX
      const formatted = truncated.replace(/(\d{4})(?=\d)/g, '$1 ');
      updatedFormData = { ...formData, [name]: formatted };
      setFormData(updatedFormData);
      return;
    }
    // Auto convert name fields to uppercase
    if (name === 'nameInBlocks' || name === 'fatherOrHusbandName') {
      updatedFormData = { ...formData, [name]: value.toUpperCase() };
      setFormData(updatedFormData);
    } 
    // Validate mobile numbers - Indian 10-digit format (starts with 6, 7, 8, or 9)
    else if (name === 'personalMobile' || name === 'secondaryMobile') {
      const numericValue = value.replace(/\D/g, '');
      const indianMobileRegex = /^[6-9]\d{9}$/;
      if (numericValue !== '' && numericValue.length !== 10) {
        newErrors[name] = 'Must be exactly 10 digits';
      } else if (numericValue !== '' && !indianMobileRegex.test(numericValue)) {
        newErrors[name] = 'Must start with 6, 7, 8, or 9';
      } else {
        delete newErrors[name];
      }
      updatedFormData = { 
        ...formData, 
        [name]: numericValue,
        errors: newErrors
      };
      setFormData(updatedFormData);
    }
    // Email validation
    else if (name === 'emailId') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value !== '' && !emailRegex.test(value)) {
        newErrors.emailId = 'Please enter a valid email address';
      } else {
        delete newErrors.emailId;
      }
      updatedFormData = { 
        ...formData, 
        [name]: value,
        errors: newErrors
      };
      setFormData(updatedFormData);
    }
    // Auto-fill 'Advertised in' for certain advertisement sources
    else if (name === 'advertisementSource') {
      const autoFillSources = ['Newspaper', 'Website', 'Social Media'];
      const shouldAutoFill = autoFillSources.includes(value);
      updatedFormData = {
        ...formData,
        advertisementSource: value,
        advertisedIn: shouldAutoFill ? value : formData.advertisedIn,
      };
      setFormData(updatedFormData);
    }
    else {
      updatedFormData = { ...formData, [name]: value };
      setFormData(updatedFormData);
    }
    // Removed auto-save on every keystroke - use SAVE DRAFT button or navigation modal instead
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const objectUrl = URL.createObjectURL(file);
      startImageUploadAnimation(objectUrl); // Start animation with a temporary URL

      // Pre-read base64 for preview (only set on success)
      const reader = new FileReader();
      let base64Result: string | null = null;
      reader.onload = (event) => {
        base64Result = event.target?.result as string;
      };
      reader.readAsDataURL(file);

      // Call context function to upload and validate profile image
      // When admin is in User View mode, pass the target user's email so the server updates the right user
      const adminTargetEmail = isAdminMode ? formData.emailId : undefined;
      processImageUpload(file, objectUrl, adminTargetEmail).then((success) => {
        if (success) {
          console.log('[AppFormPage1 handlePhotoChange] Profile image processed by context.');
          // Only set the local preview AFTER the upload (including AI validation) succeeds
          if (base64Result) {
            isPhotoFreshlyUploadedRef.current = true;
            setPhoto(base64Result);
          }
        } else {
          console.error('[AppFormPage1 handlePhotoChange] Context rejected image.');
          // Show the AI validation rejection modal
          setUploadErrorModal({
            show: true,
            message: 'This does not appear to be a valid photograph of a person. Please upload a clear, real photo of yourself.',
            fieldName: 'Profile Photo'
          });
          // Reset the file input so user can try again
          if (e.target) e.target.value = '';
        }
      }).catch(err => {
        console.error('[AppFormPage1 handlePhotoChange] Error calling processImageUpload from context:', err);
        setUploadErrorModal({
          show: true,
          message: 'Photo upload failed. Please try again.',
          fieldName: 'Profile Photo'
        });
        if (e.target) e.target.value = '';
      });
    }
  };

  const handleProofFileUpload = async (file: File, fieldName: 'dobProofFile' | 'qualificationProofFile' | 'addressProofFile' | 'affidavitFile' | 'affidavitBFile' | 'appendixBFile' | 'appendixCFile' | 'appendixDFile' | 'appendixEFile' | 'appendixFFile' | 'finDocProjectReportFile' | 'finDocBankStatementFile' | 'finDocFdTdReceiptsFile' | 'finDocInvestmentCertificatesFile' | 'finDocMutualFundStatementFile' | 'finDocDematStatementFile' | 'finDocCaCertificateFile' | 'finDocDpCertificateFile' | 'finDocAttestedFinancialsFile' | 'finDocNotarizedAffidavitFile' | 'finDocBankConfirmationLetterFile' | 'finDocPropertyDocumentsFile') => {
    if (!applicationId) {
      alert('Application ID is not available. Please save the draft first.');
      return;
    }

    // Global file type validation - block unsupported file types
    const blockedExtensions = ['.doc', '.docx', '.xlsx', '.xls', '.txt', '.bmp'];
    const blockedMimeTypes = [
      'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'image/bmp'
    ];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (blockedExtensions.includes(fileExtension) || blockedMimeTypes.includes(file.type)) {
      setUploadErrorModal({
        show: true,
        message: `File type "${fileExtension}" is not supported. Please upload only JPG, JPEG, PNG, or PDF files.`,
        fieldName: 'Document Upload'
      });
      return;
    }

    // For Address Proof uploads, only accept images (no PDFs)
    if (fieldName === 'addressProofFile') {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const allowedExtensions = ['.jpg', '.jpeg', '.png'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        setUploadErrorModal({
          show: true,
          message: 'Please upload an image file only (JPG, JPEG, or PNG). PDF files are not accepted for Address Proof upload.',
          fieldName: 'Address Proof'
        });
        return;
      }
      if (file.type === 'application/pdf' || fileExtension === '.pdf') {
        setUploadErrorModal({
          show: true,
          message: 'PDF files are not accepted for Address Proof upload. Please upload a clear image (JPG, JPEG, or PNG) of your Address Proof document.',
          fieldName: 'Address Proof'
        });
        return;
      }
    }

    const stateSetters: Record<string, { setIsUploading: (v: boolean) => void; setProgress: (v: number) => void; setMessage: (v: string) => void }> = {
      dobProofFile: {
        setIsUploading: setIsDobFileUploading,
        setProgress: setDobFileProgress,
        setMessage: setDobFileUploadMessage,
      },
      qualificationProofFile: {
        setIsUploading: setIsQualificationFileUploading,
        setProgress: setQualificationFileProgress,
        setMessage: setQualificationFileUploadMessage,
      },
      addressProofFile: {
        setIsUploading: setIsAddressFileUploading,
        setProgress: setAddressFileProgress,
        setMessage: setAddressFileUploadMessage,
      },
      affidavitFile: {
        setIsUploading: setIsAffidavitFileUploading,
        setProgress: setAffidavitFileProgress,
        setMessage: setAffidavitFileUploadMessage,
      },
      affidavitBFile: {
        setIsUploading: setIsAffidavitBFileUploading,
        setProgress: setAffidavitBFileProgress,
        setMessage: setAffidavitBFileUploadMessage,
      },
      appendixCFile: {
        setIsUploading: setIsAppendixCFileUploading,
        setProgress: setAppendixCFileProgress,
        setMessage: setAppendixCFileUploadMessage,
      },
      appendixDFile: {
        setIsUploading: setIsAppendixDFileUploading,
        setProgress: setAppendixDFileProgress,
        setMessage: setAppendixDFileUploadMessage,
      },
      appendixEFile: {
        setIsUploading: setIsAppendixEFileUploading,
        setProgress: setAppendixEFileProgress,
        setMessage: setAppendixEFileUploadMessage,
      },
      appendixFFile: {
        setIsUploading: setIsAppendixFFileUploading,
        setProgress: setAppendixFFileProgress,
        setMessage: setAppendixFFileUploadMessage,
      },
    };

    // Default no-op setters for non-financial document files
    const defaultSetters = {
      setIsUploading: () => {},
      setProgress: () => {},
      setMessage: () => {},
    };

    // Check if this is a financial document field
    const isFinDocField = fieldName.startsWith('finDoc');
    const finDocFieldName = fieldName as FinDocField;

    const { setIsUploading, setProgress, setMessage } = stateSetters[fieldName] || defaultSetters;

    // Set uploading status for financial documents
    if (isFinDocField) {
      setFinDocUploadStatus(prev => ({ ...prev, [finDocFieldName]: 'uploading' }));
      setFinDocUploadProgress(prev => ({ ...prev, [finDocFieldName]: 0 }));
    } else {
      setIsUploading(true);
      setProgress(0);
      setMessage('');
    }

    try {
      const response = await uploadFormFile(
        applicationId,
        file,
        fieldName,
        (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (isFinDocField) {
            setFinDocUploadProgress(prev => ({ ...prev, [finDocFieldName]: percentCompleted }));
          } else {
            setProgress(percentCompleted);
          }
        }
      );

      if (response.success) {
        // Handle AI-extracted address for address proof
        if (fieldName === 'addressProofFile' && (response as any).extractedFields) {
          const extracted = (response as any).extractedFields;
          let { address, aadhaarNumber, documentType } = extracted as { address?: string; aadhaarNumber?: string; documentType?: string };
          address = address && typeof address === 'string' ? address.trim() : '';
          aadhaarNumber = aadhaarNumber && typeof aadhaarNumber === 'string' ? aadhaarNumber.trim() : '';
          // Format Aadhaar with spaces after every 4 digits (e.g., "1234 5678 9012")
          if (aadhaarNumber) {
            aadhaarNumber = aadhaarNumber.replace(/\D/g, '').slice(0, 12).replace(/(\d{4})(?=\d)/g, '$1 ');
          }
          documentType = documentType && typeof documentType === 'string' ? documentType.toLowerCase().trim() : '';

          const filePath = (response as any).filePath || response.filePath;
          
          // Only link to aadhaarFile if the AI confirmed this is an Aadhaar card
          const isAadhaarCard = documentType === 'aadhaar' || documentType === 'aadhaar_card';
          const shouldLinkAadhaar = isAadhaarCard && aadhaarNumber && aadhaarNumber.length >= 12;

          if (address && address.length > 0) {
            // First set the file path AND auto-link to aadhaarFile ONLY if document is Aadhaar card
            setFormData(prev => ({
              ...prev,
              [fieldName]: filePath,
              // Auto-link to IDENTIFICATION table ONLY if AI confirmed this is an Aadhaar card
              ...(shouldLinkAadhaar ? { 
                aadhaarFile: filePath,
                aadhaarNumber: aadhaarNumber 
              } : {}),
            }));

            // Auto-expand the address fields section with smooth animation
            setShowAddressFields(true);

            // Typewriter animation for address
            await typewriterFill('currentAddress', address);
            
            // Also fill permanent address if same as current
            const shouldFillPermanent = formData.permanentAddressSameAsCurrent;
            if (shouldFillPermanent) {
              await typewriterFill('permanentAddress', address);
            }

            // Trigger save after AI autofill completes to persist extracted data
            setFormData(currentData => {
              debouncedSave(currentData);
              return currentData;
            });

            const msg = shouldLinkAadhaar 
              ? 'Aadhaar uploaded. Address extracted and linked to IDENTIFICATION table.'
              : `Address proof (${documentType || 'document'}) uploaded and address extracted. Please review and edit if needed.`;
            setMessage(msg);
          } else {
            setFormData(prev => ({
              ...prev,
              [fieldName]: filePath,
              // Auto-link to IDENTIFICATION table ONLY if AI confirmed this is an Aadhaar card
              ...(shouldLinkAadhaar ? { 
                aadhaarFile: filePath,
                aadhaarNumber: aadhaarNumber 
              } : {}),
            }));
            setMessage(shouldLinkAadhaar ? 'Aadhaar linked to IDENTIFICATION table.' : 'Address proof uploaded. AI extraction not available or returned no data.');
          }
        } else {
          setFormData(prev => ({
            ...prev,
            [fieldName]: response.filePath,
          }));
          // Set success status for financial documents
          if (isFinDocField) {
            setFinDocUploadStatus(prev => ({ ...prev, [finDocFieldName]: 'success' }));
          } else {
            setMessage('File uploaded successfully!');
            setTimeout(() => setMessage(''), 700);
          }
        }
      } else {
        throw new Error('File upload failed on server.');
      }
    } catch (error: any) {
      // Set error status for financial documents
      if (isFinDocField) {
        setFinDocUploadStatus(prev => ({ ...prev, [finDocFieldName]: 'error' }));
        setFinDocUploadProgress(prev => ({ ...prev, [finDocFieldName]: 0 }));
      }
      // Check for image quality error from backend (including incomplete Aadhaar, rejected documents like Masked Aadhaar)
      if (error.response?.data?.imageQualityError) {
        const isIncompleteAadhaar = error.response.data.incompleteAadhaar;
        const isDocumentRejected = error.response.data.documentRejected;
        const isAadhaarField = (fieldName as string) === 'aadhaarFile' || fieldName.includes('_aadhaarFile') || fieldName === 'addressProofFile';
        
        // Use upload error modal for rejected documents (Masked Aadhaar, etc.)
        if (isDocumentRejected) {
          setUploadErrorModal({
            show: true,
            message: error.response.data.reason || 'This document type is not accepted.',
            fieldName: fieldName === 'addressProofFile' ? 'Address Proof' : 'Document',
          });
        } else {
          // Use image quality error modal for blurry/incomplete images
          setImageQualityError({
            show: true,
            reason: error.response.data.reason || (isIncompleteAadhaar 
              ? 'Please upload an image containing BOTH front and back sides of your Aadhaar card in a single image.'
              : 'The uploaded image is blurry, smudged, or unreadable.'),
            fieldName: isIncompleteAadhaar ? 'Aadhaar Card' : (fieldName === 'addressProofFile' ? 'Address Proof' : 'Document'),
            isAadhaarError: isIncompleteAadhaar && isAadhaarField,
          });
        }
        setMessage('');
        return;
      } else {
        // Show error modal for AI validation errors
        const errorMessage = error.message || 'File upload failed. Please try again.';
        // Get display name - check financial docs first, then other known fields
        let displayFieldName = 'Document';
        if (isFinDocField && FIN_DOC_DISPLAY_NAMES[finDocFieldName]) {
          displayFieldName = FIN_DOC_DISPLAY_NAMES[finDocFieldName];
        } else if (fieldName === 'addressProofFile') {
          displayFieldName = 'Address Proof';
        } else if (fieldName === 'dobProofFile') {
          displayFieldName = 'Date of Birth Proof';
        } else if (fieldName === 'qualificationProofFile') {
          displayFieldName = 'Qualification Proof';
        } else if (fieldName === 'affidavitFile') {
          displayFieldName = 'Affidavit';
        } else if (fieldName === 'affidavitBFile') {
          displayFieldName = 'Affidavit B';
        }
        setUploadErrorModal({
          show: true,
          message: errorMessage,
          fieldName: displayFieldName
        });
        if (!isFinDocField) {
          setMessage('');
        }
      }
    } finally {
      if (isFinDocField) {
        // Clear progress after a delay for financial documents
        setTimeout(() => {
          setFinDocUploadProgress(prev => ({ ...prev, [finDocFieldName]: 0 }));
        }, 500);
      } else {
        setIsUploading(false);
      }
    }
  };

  useEffect(() => {
    let animationFrameId: number;
    let messageTimerId: NodeJS.Timeout;
    let clearMessageTimerId: NodeJS.Timeout;

    if (photo && isPhotoFreshlyUploadedRef.current) {
      // Only show animation and message for freshly uploaded photos (not on page load/refresh)
      setIsRevealing(false); 
      setPhotoUploadMessage(''); 

      animationFrameId = requestAnimationFrame(() => {
        setIsRevealing(true); // Start animation
        messageTimerId = setTimeout(() => {
          setPhotoUploadMessage('Photo uploaded successfully!');
          clearMessageTimerId = setTimeout(() => {
            setPhotoUploadMessage('');
            isPhotoFreshlyUploadedRef.current = false; // Reset after animation completes
          }, 3000); // Clear message 3 seconds after it appears
        }, 2000); // Message appears after 2s animation (matching animation duration)
      });
    } else if (photo) {
      // Photo loaded from database - show immediately without animation
      setIsRevealing(true);
      setPhotoUploadMessage('');
    } else {
      setIsRevealing(false);
      setPhotoUploadMessage('');
    }

    return () => { // Cleanup function
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (messageTimerId) clearTimeout(messageTimerId);
      if (clearMessageTimerId) clearTimeout(clearMessageTimerId);
    };
  }, [photo]);

  useEffect(() => {
    const currentEditingAppId = sessionStorage.getItem('currentApplicationId');
    const newAppIdFromState = location.state?.newApplicationId as string;

    let determinedAppId = '';
    if (currentEditingAppId) {
      determinedAppId = currentEditingAppId;
    } else if (newAppIdFromState) {
      determinedAppId = newAppIdFromState;
    }

    if (determinedAppId) {
      setApplicationId(determinedAppId);
      setFormData(prevData => ({
        ...prevData,
        applicationNoOfficeUse: determinedAppId
      }));
    } else {
      // Handle case where no ID is available (e.g., direct navigation to form, might be an error or new flow)
      console.warn('Application ID not found in session storage or location state.');
      // Optionally, navigate away or show an error
    }
  }, [location.state]);

  // The following useEffect was made redundant by the one at ~line 972 and has been removed.
  // It was causing double initial save attempts.
  // useEffect(() => {
  //   if (isNewlyGeneratedIdRef.current && applicationId && !isSaving && isOnline) {
  //     console.log(`Auto-saving newly generated application ${applicationId} as draft.`);
  //     const initialDraftData = { 
  //       ...formData, 
  //       applicationNoOfficeUse: applicationId, 
  //       emailId: formData.emailId
  //     };
  //     saveToDatabaseCallback(initialDraftData, true, applicationId);
  //     isNewlyGeneratedIdRef.current = false;
  //   }
  // }, [applicationId, formData.emailId, isNewlyGeneratedIdRef.current, isSaving, isOnline, saveToDatabaseCallback]);

  const triggerPhotoUpload = () => {
    photoInputRef.current?.click();
  };

  // Webcam selfie functions with AI-powered face detection using OpenRouter
  const [aiGuidance, setAiGuidance] = useState<string>('Initializing camera...');
  const [detectionStatus, setDetectionStatus] = useState<{
    isFrontal: boolean;
    isCentered: boolean;
    sizeOk: boolean;
    lightingOk: boolean;
  }>({ isFrontal: false, isCentered: false, sizeOk: false, lightingOk: false });
  const [stableFaceCount, setStableFaceCount] = useState(0);
  const isDetectingRef = useRef(false);

  const captureFrameAsBase64 = (): string | null => {
    try {
      if (!webcamVideoRef.current || !webcamCanvasRef.current) return null;

      const video = webcamVideoRef.current;
      const canvas = webcamCanvasRef.current;

      // Use smaller resolution for faster API calls
      canvas.width = 320;
      canvas.height = 240;

      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      // Mirror the image
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      return canvas.toDataURL('image/jpeg', 0.7);
    } catch (err) {
      console.warn('[Webcam] Frame capture failed:', err instanceof Error ? err.message : err);
      return null;
    }
  };

  const startFaceDetection = () => {
    if (!webcamVideoRef.current) return;

    setFaceDetectionSupported(true);
    setAiGuidance('Looking for your face...');

    // Use AI-based detection with OpenRouter API
    detectionIntervalRef.current = setInterval(() => {
      if (isDetectingRef.current) return; // Skip if previous detection still running

      const video = webcamVideoRef.current;
      if (!video || video.readyState !== 4) return;

      isDetectingRef.current = true;

      // Wrap the entire async work in a self-contained promise that always resolves
      // to prevent unhandled promise rejections from crashing Sentry serialization
      (async () => {
        try {
          const imageData = captureFrameAsBase64();
          if (!imageData) {
            return; // finally block will reset isDetectingRef
          }

          const response = await fetch('/api/detect-face', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imageData })
          });

          // Guard: if modal was closed while fetch was in-flight, bail out
          if (!webcamVideoRef.current) return;

          if (!response.ok) {
            console.warn('[FaceDetection] API returned', response.status);
            return;
          }

          const result = await response.json();

          // Guard again after second await
          if (!webcamVideoRef.current) return;

          if (result.fallbackToManual) {
            setFaceDetectionSupported(false);
            setAiGuidance('AI detection unavailable - use manual capture');
            if (detectionIntervalRef.current) {
              clearInterval(detectionIntervalRef.current);
              detectionIntervalRef.current = null;
            }
            return;
          }

          if (result.success) {
            setFaceDetected(result.faceDetected);
            setDetectionStatus({
              isFrontal: result.isFrontal,
              isCentered: result.isCentered,
              sizeOk: result.sizeOk,
              lightingOk: result.lightingOk
            });
            setAiGuidance(result.guidance || 'Position your face in the frame');

            if (result.ready) {
              setFaceInFrame(true);
              stableFaceCountRef.current += 1;
              setStableFaceCount(stableFaceCountRef.current);

              // Capture immediately when all validations pass (use ref to check current state, not stale closure)
              if (!autoCapturingRef.current) {
                startAutoCapture();
              }
            } else {
              setFaceInFrame(false);
              stableFaceCountRef.current = 0;
              setStableFaceCount(0);
              cancelAutoCapture();
            }
          } else {
            setFaceDetected(false);
            setFaceInFrame(false);
            stableFaceCountRef.current = 0;
            cancelAutoCapture();
          }
        } catch (err) {
          // Catch ALL errors to prevent unhandled promise rejections
          console.warn('[FaceDetection] Detection cycle error:', err instanceof Error ? err.message : err);
        } finally {
          isDetectingRef.current = false;
        }
      })();
    }, 1500); // Check every 1.5 seconds (API calls take time)
  };

  const startAutoCapture = () => {
    autoCapturingRef.current = true;
    setAutoCapturing(true);
    setCaptureCountdown(3);
    
    let count = 3;
    countdownIntervalRef.current = setInterval(() => {
      count -= 1;
      setCaptureCountdown(count);
      
      if (count <= 0) {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        captureWebcamPhoto();
      }
    }, 1000);
  };

  const cancelAutoCapture = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    autoCapturingRef.current = false;
    setAutoCapturing(false);
    setCaptureCountdown(null);
  };

  const openWebcamModal = async (target: 'primary' | number) => {
    setWebcamTarget(target);
    setWebcamError(null);
    setFaceDetected(false);
    setFaceInFrame(false);
    autoCapturingRef.current = false;
    setAutoCapturing(false);
    setCaptureCountdown(null);
    setAiGuidance('Initializing camera...');
    setDetectionStatus({ isFrontal: false, isCentered: false, sizeOk: false, lightingOk: false });
    stableFaceCountRef.current = 0;
    isDetectingRef.current = false;
    setShowWebcamModal(true);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      setWebcamStream(stream);

      // Wait for video to be ready then start AI detection
      setTimeout(() => {
        try {
          if (webcamVideoRef.current) {
            webcamVideoRef.current.srcObject = stream;
            webcamVideoRef.current.onloadedmetadata = () => {
              startFaceDetection();
            };
          }
        } catch (timeoutErr) {
          console.warn('[Webcam] Error attaching stream to video element:', timeoutErr instanceof Error ? timeoutErr.message : timeoutErr);
        }
      }, 100);
    } catch (err: any) {
      console.error('Webcam error:', err);
      setWebcamError(err.name === 'NotAllowedError'
        ? 'Camera access denied. Please allow camera permissions.'
        : err.name === 'NotFoundError'
        ? 'No camera found. Please connect a webcam.'
        : 'Failed to access camera. Please try again.');
    }
  };

  const closeWebcamModal = () => {
    // Stop face detection
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
    cancelAutoCapture();
    isDetectingRef.current = false;
    
    // Stop stream from state if available
    if (webcamStream) {
      webcamStream.getTracks().forEach(track => track.stop());
      setWebcamStream(null);
    }
    
    // Also stop stream directly from video element (handles stale closure issue)
    if (webcamVideoRef.current && webcamVideoRef.current.srcObject) {
      const stream = webcamVideoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      webcamVideoRef.current.srcObject = null;
    }
    
    setShowWebcamModal(false);
    setWebcamError(null);
    setFaceDetected(false);
    setFaceInFrame(false);
    setAiGuidance('Initializing camera...');
    setDetectionStatus({ isFrontal: false, isCentered: false, sizeOk: false, lightingOk: false });
    stableFaceCountRef.current = 0;
    setStableFaceCount(0);
  };

  const captureWebcamPhoto = async () => {
    if (!webcamVideoRef.current || !webcamCanvasRef.current) return;
    
    // Stop detection during capture
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
    
    const video = webcamVideoRef.current;
    const canvas = webcamCanvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Mirror the image for natural selfie look
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    
    // Close webcam first
    closeWebcamModal();
    
    if (webcamTarget === 'primary') {
      // Set primary applicant photo
      isPhotoFreshlyUploadedRef.current = true;
      setPhoto(dataUrl);
      
      // Also process through context for backend upload
      if (processImageUpload) {
        try {
          const selfieTargetEmail = isAdminMode ? formData.emailId : undefined;
          await processImageUpload(
            new File([await (await fetch(dataUrl)).blob()], 'selfie.jpg', { type: 'image/jpeg' }),
            dataUrl,
            selfieTargetEmail
          );
        } catch (err) {
          console.error('Error uploading selfie:', err);
        }
      }
    } else {
      // Set partner photo
      const partnerIndex = webcamTarget as number;
      handlePartnerDetailChange(partnerIndex, 'photo', dataUrl);
      
      // Upload to server
      if (applicationId) {
        try {
          const blob = await (await fetch(dataUrl)).blob();
          const file = new File([blob], 'partner-selfie.jpg', { type: 'image/jpeg' });
          const response = await uploadFormFile(applicationId, file, `partnerPhoto_${partnerIndex}`, () => {});
          if (response?.success && response.filePath) {
            handlePartnerDetailChange(partnerIndex, 'photo', response.filePath);
          }
        } catch (err) {
          console.error('Error uploading partner selfie:', err);
        }
      }
    }
  };

  // Open profile photo editor on double-click
  const handleProfilePhotoDoubleClick = () => {
    if (!photo || isViewMode || !isOnline) return;

    // Use existing local preview (data URL or server path)
    let url = photo as string;
    let type = 'image/jpeg';

    if (typeof photo === 'string') {
      if (photo.startsWith('data:image')) {
        // Data URL already includes mime
        const mimeMatch = photo.match(/^data:(.*?);/);
        if (mimeMatch && mimeMatch[1]) {
          type = mimeMatch[1];
        }
      } else {
        // Server path or relative URL – let browser infer type as image
        type = 'image/*';
      }
    }

    setModalMode('profile');
    setModalFileUrl(url);
    setModalFileName('Profile Photo');
    setModalFileType(type);
    setModalFieldName(null);
    setIsModalOpen(true);
  };

  // Delete primary applicant photo
  const handlePrimaryPhotoDelete = async () => {
    if (!photo) return;
    
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      // If it's a server path, delete from backend
      if (typeof photo === 'string' && !photo.startsWith('data:')) {
        await fetch(`${API_BASE_URL}/api/applications/${applicationId}/delete-file`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ fieldName: 'profilePhoto', filePath: photo })
        });
      }
      // Clear from state
      setPhoto(null);
      setFormData(prev => ({ ...prev, profilePhoto: null }));
      setPrimaryPhotoMenuOpen(false);
    } catch (err) {
      console.error('Error deleting primary photo:', err);
      alert('Failed to delete photo. Please try again.');
    }
  };

  // Open partner photo editor modal
  const handlePartnerPhotoEdit = (partnerIndex: number) => {
    const partner = formData.partnerDirectorDetails?.[partnerIndex];
    if (!partner?.photo || isViewMode || !isOnline) return;

    let url = partner.photo as string;
    let type = 'image/jpeg';

    if (typeof partner.photo === 'string') {
      if (partner.photo.startsWith('data:image')) {
        const mimeMatch = partner.photo.match(/^data:(.*?);/);
        if (mimeMatch && mimeMatch[1]) {
          type = mimeMatch[1];
        }
      } else {
        type = 'image/*';
      }
    }

    setModalMode('document');
    setModalFileUrl(url);
    setModalFileName(`${(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #${partnerIndex + 2} Photo`);
    setModalFileType(type);
    setModalFieldName(`partner_${partnerIndex}_photo`);
    setIsModalOpen(true);
  };

  // Delete partner photo from backend and state
  const handlePartnerPhotoDelete = async (partnerIndex: number) => {
    const partner = formData.partnerDirectorDetails?.[partnerIndex];
    if (!partner?.photo) return;
    
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      // If it's a server path, delete from backend
      if (typeof partner.photo === 'string' && !partner.photo.startsWith('data:')) {
        await fetch(`${API_BASE_URL}/api/applications/${applicationId}/delete-file`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ fieldName: `partner_${partnerIndex}_photo`, filePath: partner.photo })
        });
      }
      // Clear from state
      handlePartnerDetailChange(partnerIndex, 'photo', null);
      setPartnerPhotoMenuOpen(null);
    } catch (err) {
      console.error('Error deleting partner photo:', err);
      alert('Failed to delete photo. Please try again.');
    }
  };

  const handleZoomIn = () => {
    setZoomScale(prev => Math.min(prev + 0.1, 1.5));
  };
  
  const handleZoomOut = () => {
    setZoomScale(prev => Math.max(prev - 0.1, 0.6));
  };

  // Helper to trigger shake animation - shared by Next button and NavBar
  const triggerShake = (ref: React.RefObject<HTMLDivElement>, setShaking: React.Dispatch<React.SetStateAction<boolean>>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setShaking(true);
    setTimeout(() => setShaking(false), 600);
  };

  // Validate mandatory fields - returns true if all valid, false if validation failed (shake triggered)
  const validateMandatoryFields = (): boolean => {
    // 1. Profile Photo
    if (mandatoryFields.profilePhoto && !photo) {
      triggerShake(profilePhotoRef, setIsProfilePhotoShaking);
      return false;
    }
    
    // 2. Advertisement Source
    if (mandatoryFields.advertisementSource && !formData.advertisementSource) {
      triggerShake(advertisementSourceRef, setIsAdvertisementSourceShaking);
      return false;
    }
    
    // 3. CNG Station Location
    if (mandatoryFields.cngStationLocation && !formData.location) {
      triggerShake(cngLocationRef, setIsCngLocationShaking);
      return false;
    }
    
    // 3.5. Frontage Question - must be answered before proceeding
    if (formData.locationMinPlotFrontage && !formData.hasSufficientFrontage) {
      triggerShake(frontageQuestionRef, setIsFrontageQuestionShaking);
      return false;
    }
    
    // 4. Application Type
    if (mandatoryFields.applicationType && !formData.dealershipType) {
      triggerShake(applicationTypeRef, setIsApplicationTypeShaking);
      return false;
    }
    
    // 5. PAN Card Upload
    if (mandatoryFields.panCardUpload && !formData.panCardFile) {
      triggerShake(panCardUploadRef, setIsPanCardUploadShaking);
      return false;
    }
    
    // Check primary applicant age
    if (dobAgeError) {
      alert(dobAgeError);
      return false;
    }
    
    // 6. Minimum Education (Class 10)
    if (mandatoryFields.minimumEducation && !formData.isMinimumEducationMet) {
      triggerShake(class10QuestionRef, setIsClass10Shaking);
      return false;
    }
    if (formData.isMinimumEducationMet === 'No') {
      alert('You cannot proceed further without minimum Intermediate (Class 10) qualification.');
      return false;
    }
    
    // 7. Current Address
    if (mandatoryFields.currentAddress && !formData.currentAddress && !formData.addressProofFile) {
      triggerShake(currentAddressRef, setIsCurrentAddressShaking);
      return false;
    }
    
    // 8. Personal Mobile
    if (mandatoryFields.personalMobile && (!formData.personalMobile || formData.personalMobile.length !== 10)) {
      triggerShake(personalMobileRef, setIsPersonalMobileShaking);
      return false;
    }
    
    // 9. Nationality
    if (mandatoryFields.nationality && !formData.nationality) {
      triggerShake(nationalityRef, setIsNationalityShaking);
      return false;
    }
    
    // 10. Marital Status
    if (mandatoryFields.maritalStatus && !formData.maritalStatus) {
      triggerShake(maritalStatusRef, setIsMaritalStatusShaking);
      return false;
    }
    
    // 11. Gender
    if (mandatoryFields.gender && !formData.gender) {
      triggerShake(genderRef, setIsGenderShaking);
      return false;
    }
    
    // 12. KYC Aadhaar & PAN
    if (mandatoryFields.kycAadhaarPan && (!formData.aadhaarNumber || !formData.panNumberStatutory)) {
      triggerShake(kycDetailsRef, setIsKycDetailsShaking);
      return false;
    }
    
    // 13. Educational Qualification
    const hasMainApplicantEduDoc = 
      (formData.eduSscCertificateFile && formData.eduSscBoard) ||
      (formData.eduHscCertificateFile && formData.eduHscBoard) ||
      (formData.eduGraduateCertificateFile && formData.eduGraduateBoard) ||
      (formData.eduOthersCertificateFile && formData.eduOthersBoard);
    
    if (mandatoryFields.educationalQualification && !hasMainApplicantEduDoc) {
      triggerShake(educationalQualRef, setIsEducationalQualShaking);
      return false;
    }
    
    // 14. Business/Employment Details
    if (mandatoryFields.businessEmploymentDetails && (!formData.presentOccupation || !formData.businessEmploymentDetails || !formData.totalWorkExperienceYears)) {
      triggerShake(businessDetailsRef, setIsBusinessDetailsShaking);
      return false;
    }
    
    // 15. Financial Capability
    if (mandatoryFields.financialCapability && (!formData.annualIncome || !formData.netWorthAtApplication || !formData.sourceOfInvestment || !formData.abilityToMeetRequiredInvestment)) {
      triggerShake(financialCapabilityRef, setIsFinancialCapabilityShaking);
      return false;
    }
    
    // 16. Financial Capability Document Checklist
    const hasFinancialDoc = formData.finDocProjectReportFile || formData.finDocBankStatementFile || 
      formData.finDocFdTdReceiptsFile || formData.finDocInvestmentCertificatesFile ||
      formData.finDocMutualFundStatementFile || formData.finDocDematStatementFile ||
      formData.finDocCaCertificateFile || formData.finDocDpCertificateFile ||
      formData.finDocAttestedFinancialsFile || formData.finDocNotarizedAffidavitFile ||
      formData.finDocBankConfirmationLetterFile || formData.finDocPropertyDocumentsFile;
    
    if (mandatoryFields.financialCapabilityDocChecklist && !hasFinancialDoc) {
      triggerShake(financialDocChecklistRef, setIsFinancialDocChecklistShaking);
      return false;
    }
    
    // 17. Appendix Cards - check if any required appendix is missing
    if (mandatoryFields.appendixCards) {
      const missingAppendix = 
        (visibleAppendixes.includes('A') && !formData.affidavitFile) ||
        (visibleAppendixes.includes('B') && !formData.appendixBFile) ||
        (visibleAppendixes.includes('C') && !formData.appendixCFile) ||
        (visibleAppendixes.includes('D') && !formData.appendixDFile) ||
        (visibleAppendixes.includes('E') && !formData.appendixEFile) ||
        (visibleAppendixes.includes('F') && !formData.appendixFFile);
      
      if (missingAppendix) {
        triggerShake(appendixCardsRef, setIsAppendixCardsShaking);
        return false;
      }
    }
    
    // NOTE: Partner/Director validation is NOT done here for navigation purposes.
    // Main applicant can navigate to other pages even if partners haven't completed their sections.
    // Partner validation is only enforced on Page 5 for the Payment button.
    
    return true; // All validations passed
  };

  // Validate partner mandatory fields when a partner is editing (editingUser is a number)
  const validatePartnerMandatoryFields = (partnerIndex: number): boolean => {
    const partner = formData.partnerDirectorDetails?.[partnerIndex];
    if (!partner) return true;

    // Helper to trigger shake for partner fields
    const triggerPartnerShake = (
      refs: React.MutableRefObject<{[key: number]: HTMLDivElement | null}>,
      setShaking: React.Dispatch<React.SetStateAction<{[key: number]: boolean}>>,
      index: number
    ) => {
      setShaking(prev => ({ ...prev, [index]: true }));
      setTimeout(() => setShaking(prev => ({ ...prev, [index]: false })), 500);
      refs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // 1. Partner Photo
    if (mandatoryFields.profilePhoto && !partner.photo) {
      triggerPartnerShake(partnerPhotoRefs, setPartnerPhotoShaking, partnerIndex);
      return false;
    }

    // 2. Class 10 (High School) pass question
    if (!partner.isMinimumEducationMet) {
      triggerPartnerShake(partnerClass10Refs, setPartnerClass10Shaking, partnerIndex);
      return false;
    }
    if (partner.isMinimumEducationMet === 'No') {
      alert('You cannot proceed further without minimum Intermediate (Class 10) qualification.');
      return false;
    }

    // 3. Nationality
    if (mandatoryFields.nationality && !partner.nationality) {
      triggerPartnerShake(partnerNationalityRefs, setPartnerNationalityShaking, partnerIndex);
      return false;
    }

    // 4. Gender
    if (mandatoryFields.gender && !partner.gender) {
      triggerPartnerShake(partnerGenderRefs, setPartnerGenderShaking, partnerIndex);
      return false;
    }

    // 5. Marital Status
    if (mandatoryFields.maritalStatus && !partner.maritalStatus) {
      triggerPartnerShake(partnerMaritalStatusRefs, setPartnerMaritalStatusShaking, partnerIndex);
      return false;
    }

    // 6. KYC Details (Aadhaar & PAN)
    if (mandatoryFields.kycAadhaarPan && (!partner.aadhaarNumber || !partner.panNumberStatutory)) {
      triggerPartnerShake(partnerKycRefs, setPartnerKycShaking, partnerIndex);
      return false;
    }

    // 7. Educational Qualification - at least one certificate uploaded
    const hasPartnerEduDoc = 
      (partner.eduSscCertificateFile && partner.eduSscBoard) ||
      (partner.eduHscCertificateFile && partner.eduHscBoard) ||
      (partner.eduGraduateCertificateFile && partner.eduGraduateBoard) ||
      (partner.eduOthersCertificateFile && partner.eduOthersBoard);
    
    if (mandatoryFields.educationalQualification && !hasPartnerEduDoc) {
      triggerPartnerShake(partnerEduQualRefs, setPartnerEduQualShaking, partnerIndex);
      return false;
    }

    // 8. Business/Employment Details
    if (mandatoryFields.businessEmploymentDetails && (!partner.presentOccupation || !partner.businessEmploymentDetails || !partner.totalWorkExperienceYears)) {
      triggerPartnerShake(partnerBusinessDetailsRefs, setPartnerBusinessDetailsShaking, partnerIndex);
      return false;
    }

    // 9. Financial Capability
    if (mandatoryFields.financialCapability && (!partner.annualIncome || !partner.netWorthAtApplication || !partner.sourceOfInvestment || !partner.abilityToMeetRequiredInvestment)) {
      triggerPartnerShake(partnerFinancialCapabilityRefs, setPartnerFinancialCapabilityShaking, partnerIndex);
      return false;
    }

    // 10. Financial Capability Document Checklist - at least one document
    const hasPartnerFinDoc = partner.finDocProjectReportFile || partner.finDocBankStatementFile ||
      partner.finDocFdTdReceiptsFile || partner.finDocInvestmentCertificatesFile ||
      partner.finDocMutualFundStatementFile || partner.finDocDematStatementFile ||
      partner.finDocCaCertificateFile || partner.finDocOwnershipDocsFile;
    if (mandatoryFields.financialCapabilityDocChecklist && !hasPartnerFinDoc) {
      triggerPartnerShake(partnerFinancialCapabilityRefs, setPartnerFinancialCapabilityShaking, partnerIndex);
      return false;
    }

    // 11. Appendix A - Affidavit
    if (visibleAppendixes.includes('A') && !partner.affidavitFile) {
      triggerPartnerShake(partnerAppendixARefs, setPartnerAppendixAShaking, partnerIndex);
      return false;
    }

    // 12. Appendix B
    if (visibleAppendixes.includes('B') && !partner.appendixBFile) {
      triggerPartnerShake(partnerAppendixBRefs, setPartnerAppendixBShaking, partnerIndex);
      return false;
    }

    // 13. Appendix C
    if (visibleAppendixes.includes('C') && !partner.appendixCFile) {
      triggerPartnerShake(partnerAppendixCRefs, setPartnerAppendixCShaking, partnerIndex);
      return false;
    }

    // 14. Appendix D
    if (visibleAppendixes.includes('D') && !partner.appendixDFile) {
      triggerPartnerShake(partnerAppendixDRefs, setPartnerAppendixDShaking, partnerIndex);
      return false;
    }

    // 15. Appendix E
    if (visibleAppendixes.includes('E') && !partner.appendixEFile) {
      triggerPartnerShake(partnerAppendixERefs, setPartnerAppendixEShaking, partnerIndex);
      return false;
    }

    // 16. Appendix F
    if (visibleAppendixes.includes('F') && !partner.appendixFFile) {
      triggerPartnerShake(partnerAppendixFRefs, setPartnerAppendixFShaking, partnerIndex);
      return false;
    }

    return true; // All partner validations passed
  };

  const handleNextButtonClick = () => {
    // If a partner is editing (editingUser is a number), validate their fields
    if (typeof editingUser === 'number') {
      if (!validatePartnerMandatoryFields(editingUser)) {
        return;
      }
    } else {
      // Main applicant validation
      if (!validateMandatoryFields()) {
        return;
      }
    }
    
    // Show save modal before navigation (skip if application is locked)
    if (applicationId && formData.emailId && formData.emailId.trim() !== '' && isOnline && !isApplicationLocked) {
      setPendingNavigation('/form/page2');
      setShowSaveModal(true);
    } else {
      if (applicationId && applicationId !== formData.applicationNoOfficeUse) {
        setFormData(prev => ({
          ...prev,
          applicationNoOfficeUse: applicationId
        }));
      }
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate('/form/page2', { state: { applicationId } });
    }
  };

  // Handle save modal actions
  const handleSaveAndNavigate = async () => {
    setShowSaveModal(false);
    
    if (applicationId && isOnline) {
      console.log('[Navigation] Saving draft before navigating');
      const saveSuccess = await saveToDatabaseCallback(formData, true, applicationId);
      console.log('[Navigation] Save result:', saveSuccess ? 'SUCCESS' : 'FAILED');
      
      // Wait 1.5 seconds so user can see the "Draft saved successfully" message
      await new Promise(resolve => setTimeout(resolve, 1500));
    }
    
    if (pendingNavigation) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleSkipSaveAndNavigate = () => {
    setShowSaveModal(false);
    if (pendingNavigation) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  // Cancel save modal - just close without navigating
  const handleCancelSaveModal = useCallback(() => {
    setShowSaveModal(false);
    setPendingNavigation(null);
  }, []);

  // ESC key handler for save modal
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showSaveModal) {
        handleCancelSaveModal();
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [showSaveModal, handleCancelSaveModal]);

  // Handle navigation from vertical nav bar - validate mandatory fields first, then show save modal
  const handleNavBarNavigation = (targetPath: string) => {
    // If a partner is editing, validate their fields before allowing navigation
    if (typeof editingUser === 'number') {
      if (!validatePartnerMandatoryFields(editingUser)) {
        return; // Partner validation failed, shake animation triggered
      }
    } else {
      // Main applicant validation
      if (!validateMandatoryFields()) {
        return; // Validation failed, shake animation triggered
      }
    }
    
    // Skip save modal if application is locked
    if (applicationId && isOnline && !isApplicationLocked) {
      setPendingNavigation(targetPath);
      setShowSaveModal(true);
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate(targetPath, { state: { applicationId } });
    }
  };

  // Form progress is now calculated by the shared useFormProgress hook (global across Page1+2+3)

  const handleNameBlur = () => {
    if (formData.nameInBlocks && formData.nameInBlocks.trim() !== '' && formData.nameInBlocks !== contextFullName) {
      console.log(`[AppFormPage1 handleNameBlur] Name changed. Updating profile: ${formData.nameInBlocks}`);
      const targetEmail = isAdminMode ? formData.emailId : undefined;
      updateProfileDetails({ fullName: formData.nameInBlocks }, targetEmail);
    }
  };

  const handlePhoneBlur = () => {
    if (formData.personalMobile && formData.personalMobile.trim() !== '' && formData.personalMobile.length === 10 && formData.personalMobile !== contextPhoneNumber) {
      console.log(`[AppFormPage1 handlePhoneBlur] Phone changed. Updating profile: ${formData.personalMobile}`);
      const targetEmail = isAdminMode ? formData.emailId : undefined;
      updateProfileDetails({ phoneNumber: formData.personalMobile }, targetEmail);
    }
  };

  const isIndividualOrProprietorship =
    formData.dealershipType === 'Individual' || formData.dealershipType === 'OPC' || formData.dealershipType === 'Proprietorship';

  // Check if dealership type requires multiple partners/directors
  const requiresMultiplePartners = ['Partnership', 'Pvt Ltd', 'Public Ltd', 'Non Individual applicant entity'].includes(formData.dealershipType);
  
  // Check if dealership type requires company registration fields (Pvt Ltd, Public Ltd, Non Individual)
  const requiresCompanyRegistration = ['Pvt Ltd', 'Public Ltd', 'Non Individual applicant entity'].includes(formData.dealershipType);
  
  // Check if Non-Individual requires sub-option selection
  const isNonIndividualWithoutSubOption = formData.dealershipType === 'Non Individual applicant entity' && !formData.niTypeOfApplicant;
  
  // Check if company is at least 3 years old from registration date
  const isCompanyTooYoung = useMemo(() => {
    if (!formData.niDateOfRegistrationIncorporation || !requiresCompanyRegistration) return false;
    
    const registrationDate = new Date(formData.niDateOfRegistrationIncorporation);
    const today = new Date();
    
    // Calculate the date 3 years ago from today
    const threeYearsAgo = new Date(today);
    threeYearsAgo.setFullYear(today.getFullYear() - 3);
    
    // Company must be registered ON OR BEFORE 3 years ago (strictly older than 3 years)
    return registrationDate > threeYearsAgo;
  }, [formData.niDateOfRegistrationIncorporation, requiresCompanyRegistration]);
  
  // Check if company is not registered in India
  const isCompanyNotInIndia = requiresCompanyRegistration && formData.niCompanyRegisteredInIndia === 'No';
  
  // Combined form freeze condition - freeze form if company is too young OR not registered in India OR Non-Individual without sub-option
  const isFormFrozen = isCompanyTooYoung || isCompanyNotInIndia || isNonIndividualWithoutSubOption;
  
  // Check if Pvt Ltd/Public Ltd form sections should be visible (both conditions must be met)
  // For Pvt Ltd/Public Ltd: Company must be registered in India AND at least 3 years old
  const isPvtLtdFormVisible = useMemo(() => {
    if (!['Pvt Ltd', 'Public Ltd'].includes(formData.dealershipType)) return true; // Not applicable for other types
    
    // Must have answered "Yes" to Company Registered in India
    if (formData.niCompanyRegisteredInIndia !== 'Yes') return false;
    
    // Must have a valid date that's at least 3 years old
    if (!formData.niDateOfRegistrationIncorporation) return false;
    
    const registrationDate = new Date(formData.niDateOfRegistrationIncorporation);
    const today = new Date();
    const threeYearsAgo = new Date(today);
    threeYearsAgo.setFullYear(today.getFullYear() - 3);
    
    // Date must be valid and at least 3 years old
    return registrationDate <= threeYearsAgo;
  }, [formData.dealershipType, formData.niCompanyRegisteredInIndia, formData.niDateOfRegistrationIncorporation]);
  
  // Get minimum partners required based on dealership type
  const getMinimumPartners = useCallback((type: string): number => {
    if (type === 'Public Ltd') return 3;
    if (['Partnership', 'Pvt Ltd', 'Non Individual applicant entity'].includes(type)) return 2;
    return 0;
  }, []);

  // Create empty partner/director detail object
  const createEmptyPartnerDetail = useCallback((): PartnerDirectorDetail => ({
    id: `partner_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    photo: null,
    nameInBlocks: '',
    fatherOrHusbandName: '',
    dateOfBirth: '',
    panCardFile: null,
    dobProofFile: null,
    isMinimumEducationMet: '',
    qualification: '',
    qualificationProofFile: null,
    currentAddress: '',
    permanentAddress: '',
    permanentAddressSameAsCurrent: false,
    addressProofFile: null,
    personalMobile: '',
    secondaryMobile: '',
    emailId: '',
    nationality: 'Indian',
    gender: '',
    maritalStatus: '',
    maritalStatusOther: '',
    // Identification & Statutory Details
    aadhaarNumber: '',
    aadhaarVerified: '',
    aadhaarFile: null,
    panNumberStatutory: '',
    panVerified: '',
    panStatutoryFile: null,
    gstNumber: '',
    gstVerified: '',
    gstFile: null,
    identityProofVoterId: false,
    identityProofPassport: false,
    identityProofDrivingLicense: false,
    identityDocumentNumber: '',
    voterIdNumber: '',
    passportNumber: '',
    drivingLicenseNumber: '',
    voterIdFile: null,
    passportFile: null,
    drivingLicenseFile: null,
    // Educational Qualification
    eduSscBoard: '',
    eduSscMarksObtained: '',
    eduSscOutOfMarks: '',
    eduSscPercentage: '',
    eduSscYearOfPassing: '',
    eduSscCertificateFile: null,
    eduHscBoard: '',
    eduHscMarksObtained: '',
    eduHscOutOfMarks: '',
    eduHscPercentage: '',
    eduHscYearOfPassing: '',
    eduHscCertificateFile: null,
    eduGraduateBoard: '',
    eduGraduateMarksObtained: '',
    eduGraduateOutOfMarks: '',
    eduGraduatePercentage: '',
    eduGraduateYearOfPassing: '',
    eduGraduateCertificateFile: null,
    eduOthersBoard: '',
    eduOthersMarksObtained: '',
    eduOthersOutOfMarks: '',
    eduOthersPercentage: '',
    eduOthersYearOfPassing: '',
    eduOthersCertificateFile: null,
    // Business / Employment Details
    presentOccupation: '',
    businessEmploymentDetails: '',
    totalWorkExperienceYears: '',
    relevantIndustryExperience: '',
    // Financial Capability
    annualIncome: '',
    netWorthAtApplication: '',
    sourceOfInvestment: '',
    abilityToMeetRequiredInvestment: '',
    // Financial Capability Document Checklist
    finDocProjectReport: '',
    finDocProjectReportFile: null,
    finDocBankStatement: '',
    finDocBankStatementFile: null,
    finDocFdTdReceipts: '',
    finDocFdTdReceiptsFile: null,
    finDocInvestmentCertificates: '',
    finDocInvestmentCertificatesFile: null,
    finDocMutualFundStatement: '',
    finDocMutualFundStatementFile: null,
    finDocDematStatement: '',
    finDocDematStatementFile: null,
    finDocCaCertificate: '',
    finDocCaCertificateFile: null,
    finDocDpCertificate: '',
    finDocDpCertificateFile: null,
    finDocAttestedFinancials: '',
    finDocAttestedFinancialsFile: null,
    finDocNotarizedAffidavit: '',
    finDocNotarizedAffidavitFile: null,
    finDocBankLetter: '',
    finDocBankLetterFile: null,
    finDocOwnershipDocs: '',
    finDocOwnershipDocsFile: null,
    // Appendix-A Affidavit
    affidavitFile: null,
  }), []);

  // Auto-initialize partner details when dealership type requiring partners is selected
  useEffect(() => {
    const multiPartnerTypes = ['Partnership', 'Pvt Ltd', 'Public Ltd', 'Non Individual applicant entity'];
    if (multiPartnerTypes.includes(formData.dealershipType)) {
      const minRequired = getMinimumPartners(formData.dealershipType);
      const additionalPartnersNeeded = minRequired - 1; // Subtract 1 for primary applicant
      const currentDetails = formData.partnerDirectorDetails || [];
      
      // Only initialize if numberOfPartners is not set or partnerDirectorDetails is empty
      if (formData.numberOfPartners < minRequired || currentDetails.length < additionalPartnersNeeded) {
        const newPartners = Array(Math.max(0, additionalPartnersNeeded - currentDetails.length))
          .fill(null)
          .map(() => createEmptyPartnerDetail());
        
        setFormData(prev => ({
          ...prev,
          numberOfPartners: Math.max(minRequired, prev.numberOfPartners || minRequired),
          partnerDirectorDetails: [...currentDetails, ...newPartners],
        }));
      }
    }
  }, [formData.dealershipType, getMinimumPartners, createEmptyPartnerDetail]);

  // Handle number of partners change
  // Note: numberOfPartners represents TOTAL partners including the primary applicant
  // So partnerDirectorDetails array should have (numberOfPartners - 1) items for additional partners
  const handleNumberOfPartnersChange = (newCount: number) => {
    const minRequired = getMinimumPartners(formData.dealershipType);
    const validCount = Math.max(minRequired, newCount);
    
    // Open the partner setup modal with the new count to let user configure partners
    openPartnerSetupModalWithCount(validCount);
  };

  // Handle partner detail field change
  const handlePartnerDetailChange = (index: number, field: keyof PartnerDirectorDetail, value: any) => {
    const updatedDetails = [...(formData.partnerDirectorDetails || [])];
    if (updatedDetails[index]) {
      // Format Aadhaar number with spaces after every 4 digits
      let formattedValue = value;
      if (field === 'aadhaarNumber' && typeof value === 'string') {
        const digitsOnly = value.replace(/\D/g, '');
        const truncated = digitsOnly.slice(0, 12);
        formattedValue = truncated.replace(/(\d{4})(?=\d)/g, '$1 ');
      }
      updatedDetails[index] = { ...updatedDetails[index], [field]: formattedValue };
      
      // Auto-fill permanent address if checkbox is checked
      if (field === 'currentAddress' && updatedDetails[index].permanentAddressSameAsCurrent) {
        updatedDetails[index].permanentAddress = value;
      }
      if (field === 'permanentAddressSameAsCurrent' && value === true) {
        updatedDetails[index].permanentAddress = updatedDetails[index].currentAddress;
      }
      
      const updatedFormData = { ...formData, partnerDirectorDetails: updatedDetails };
      setFormData(updatedFormData);
    }
  };

  // State for tracking partner file upload progress
  const [partnerFileUploading, setPartnerFileUploading] = useState<{[key: string]: boolean}>({});
  const [partnerFileProgress, setPartnerFileProgress] = useState<{[key: string]: number}>({});
  const [partnerFileMessage, setPartnerFileMessage] = useState<{[key: string]: string}>({});

  // Handle partner file uploads (PAN Card, Qualification Proof, Address Proof, Affidavit, Education Certificates)
  const handlePartnerFileUpload = async (
    partnerIndex: number, 
    file: File, 
    fieldName: 'panCardFile' | 'qualificationProofFile' | 'addressProofFile' | 'dobProofFile' | 'affidavitFile' | 'eduSscCertificateFile' | 'eduHscCertificateFile' | 'eduGraduateCertificateFile' | 'eduOthersCertificateFile'
  ) => {
    if (!applicationId) {
      alert('Application ID is not available. Please save the draft first.');
      return;
    }

    // Global file type validation - block unsupported file types
    const blockedExtensions = ['.doc', '.docx', '.xlsx', '.xls', '.txt', '.bmp'];
    const blockedMimeTypes = [
      'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'image/bmp'
    ];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (blockedExtensions.includes(fileExt) || blockedMimeTypes.includes(file.type)) {
      setUploadErrorModal({
        show: true,
        message: `File type "${fileExt}" is not supported. Please upload only JPG, JPEG, PNG, or PDF files.`,
        fieldName: `Partner ${partnerIndex + 2} Document Upload`
      });
      return;
    }

    // For PAN Card and Address Proof uploads, only accept images (no PDFs)
    if (fieldName === 'panCardFile' || fieldName === 'addressProofFile') {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const allowedExtensions = ['.jpg', '.jpeg', '.png'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      const fieldDisplayName = fieldName === 'panCardFile' ? 'PAN Card' : 'Address Proof';
      
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        setUploadErrorModal({
          show: true,
          message: `Please upload an image file only (JPG, JPEG, or PNG). PDF files are not accepted for ${fieldDisplayName} upload.`,
          fieldName: `Partner ${partnerIndex + 2} ${fieldDisplayName}`
        });
        return;
      }
      if (file.type === 'application/pdf' || fileExtension === '.pdf') {
        setUploadErrorModal({
          show: true,
          message: `PDF files are not accepted for ${fieldDisplayName} upload. Please upload a clear image (JPG, JPEG, or PNG).`,
          fieldName: `Partner ${partnerIndex + 2} ${fieldDisplayName}`
        });
        return;
      }
    }

    const uploadKey = `${partnerIndex}_${fieldName}`;
    setPartnerFileUploading(prev => ({ ...prev, [uploadKey]: true }));
    setPartnerFileProgress(prev => ({ ...prev, [uploadKey]: 0 }));
    setPartnerFileMessage(prev => ({ ...prev, [uploadKey]: '' }));

    try {
      // Use a unique field name for partner files to distinguish from main applicant files
      const partnerFieldName = `partner_${partnerIndex}_${fieldName}`;
      
      const response = await uploadFormFile(
        applicationId,
        file,
        partnerFieldName,
        (progressEvent) => {
          if (!progressEvent.total) return;
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setPartnerFileProgress(prev => ({ ...prev, [uploadKey]: percentCompleted }));
        }
      );

      if (response && response.success) {
        // Check if partner exists
        if (formData.partnerDirectorDetails?.[partnerIndex]) {
          // Handle AI extraction for PAN card
          if (fieldName === 'panCardFile' && (response as any).extractedFields) {
            const extracted = (response as any).extractedFields;
            let { fullName, fatherName, dateOfBirth } = extracted as {
              fullName?: string;
              fatherName?: string;
              dateOfBirth?: string;
            };

            fullName = fullName && typeof fullName === 'string' ? fullName.trim() : '';
            fatherName = fatherName && typeof fatherName === 'string' ? fatherName.trim() : '';
            dateOfBirth = dateOfBirth && typeof dateOfBirth === 'string' ? dateOfBirth.trim() : '';

            // Normalize DOB to YYYY-MM-DD
            let normalizedDob = dateOfBirth;
            if (dateOfBirth && /\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4}/.test(dateOfBirth)) {
              const parts = dateOfBirth.replace(/-/g, '/').split('/');
              if (parts.length === 3) {
                const [dd, mm, yy] = parts;
                const day = dd.padStart(2, '0');
                const month = mm.padStart(2, '0');
                let year = yy;
                if (year.length === 2) {
                  const intYear = parseInt(year, 10);
                  year = intYear >= 50 ? `19${year}` : `20${year}`;
                }
                normalizedDob = `${year}-${month}-${day}`;
              }
            }

            // Typewriter animation for partner PAN card extracted fields
            const nameToFill = fullName ? fullName.toUpperCase() : '';
            const fatherToFill = fatherName ? fatherName.toUpperCase() : '';
            const dobToFill = normalizedDob || '';

            // Update file path first, then animate
            // Also auto-link panCardFile to panStatutoryFile in IDENTIFICATION table
            const filePath = (response as any).filePath || file.name;
            const panNumber = (extracted as any).panNumber || '';
            setFormData(prev => {
              const details = [...(prev.partnerDirectorDetails || [])];
              if (details[partnerIndex]) {
                details[partnerIndex] = { 
                  ...details[partnerIndex], 
                  [fieldName]: filePath,
                  // Auto-link to IDENTIFICATION table
                  panStatutoryFile: filePath,
                  ...(panNumber ? { panNumberStatutory: panNumber } : {})
                };
              }
              return { ...prev, partnerDirectorDetails: details };
            });

            if (nameToFill) await typewriterFillPartner(partnerIndex, 'nameInBlocks', nameToFill);
            if (fatherToFill) await typewriterFillPartner(partnerIndex, 'fatherOrHusbandName', fatherToFill);
            if (dobToFill) await typewriterFillPartner(partnerIndex, 'dateOfBirth', dobToFill);

            setPartnerFileMessage(prev => ({ 
              ...prev, 
              [uploadKey]: 'PAN card uploaded and details extracted. Also linked to IDENTIFICATION table.' 
            }));
          }
          // Handle AI extraction for Address proof
          else if (fieldName === 'addressProofFile' && (response as any).extractedFields) {
            const extracted = (response as any).extractedFields;
            let { address, aadhaarNumber, documentType } = extracted as { address?: string; aadhaarNumber?: string; documentType?: string };
            address = address && typeof address === 'string' ? address.trim() : '';
            aadhaarNumber = aadhaarNumber && typeof aadhaarNumber === 'string' ? aadhaarNumber.trim() : '';
            // Format Aadhaar with spaces after every 4 digits (e.g., "1234 5678 9012")
            if (aadhaarNumber) {
              aadhaarNumber = aadhaarNumber.replace(/\D/g, '').slice(0, 12).replace(/(\d{4})(?=\d)/g, '$1 ');
            }
            documentType = documentType && typeof documentType === 'string' ? documentType.toLowerCase().trim() : '';

            const filePath = (response as any).filePath || file.name;
            
            // Only link to aadhaarFile if the AI confirmed this is an Aadhaar card
            const isAadhaarCard = documentType === 'aadhaar' || documentType === 'aadhaar_card';
            const shouldLinkAadhaar = isAadhaarCard && aadhaarNumber && aadhaarNumber.length >= 12;

            if (address && address.length > 0) {
              // Update file path first
              // Also auto-link to aadhaarFile ONLY if AI confirmed this is an Aadhaar card
              setFormData(prev => {
                const details = [...(prev.partnerDirectorDetails || [])];
                if (details[partnerIndex]) {
                  details[partnerIndex] = { 
                    ...details[partnerIndex], 
                    [fieldName]: filePath,
                    // Auto-link to IDENTIFICATION table ONLY if AI confirmed this is an Aadhaar card
                    ...(shouldLinkAadhaar ? { 
                      aadhaarFile: filePath,
                      aadhaarNumber: aadhaarNumber 
                    } : {})
                  };
                }
                return { ...prev, partnerDirectorDetails: details };
              });

              // Typewriter animation for partner address
              await typewriterFillPartner(partnerIndex, 'currentAddress', address);
              
              // Also fill permanent address if same as current
              const shouldFillPermanent = formData.partnerDirectorDetails?.[partnerIndex]?.permanentAddressSameAsCurrent;
              if (shouldFillPermanent) {
                await typewriterFillPartner(partnerIndex, 'permanentAddress', address);
              }

              const message = shouldLinkAadhaar 
                ? 'Aadhaar uploaded. Address extracted and linked to IDENTIFICATION table.' 
                : `Address proof (${documentType || 'document'}) uploaded and address extracted. Please review.`;
              setPartnerFileMessage(prev => ({ 
                ...prev, 
                [uploadKey]: message 
              }));
            } else {
              // Still update file path and link ONLY if Aadhaar card confirmed
              setFormData(prev => {
                const details = [...(prev.partnerDirectorDetails || [])];
                if (details[partnerIndex]) {
                  details[partnerIndex] = { 
                    ...details[partnerIndex], 
                    [fieldName]: filePath,
                    ...(shouldLinkAadhaar ? { 
                      aadhaarFile: filePath,
                      aadhaarNumber: aadhaarNumber 
                    } : {})
                  };
                }
                return { ...prev, partnerDirectorDetails: details };
              });
              setPartnerFileMessage(prev => ({ 
                ...prev, 
                [uploadKey]: shouldLinkAadhaar ? 'Aadhaar linked to IDENTIFICATION table.' : 'File uploaded successfully!' 
              }));
            }
          }
          else {
            // For non-AI-extracted files, update normally
            setFormData(prev => {
              const details = [...(prev.partnerDirectorDetails || [])];
              if (details[partnerIndex]) {
                details[partnerIndex] = { ...details[partnerIndex], [fieldName]: (response as any).filePath || file.name };
              }
              return { ...prev, partnerDirectorDetails: details };
            });
            setPartnerFileMessage(prev => ({ ...prev, [uploadKey]: 'File uploaded successfully!' }));
          }

        }
      } else {
        throw new Error('File upload failed on server.');
      }
    } catch (error: any) {
      console.error('Error uploading partner file:', error);
      // Check for incomplete Aadhaar or image quality error
      if (error.response?.data?.imageQualityError) {
        const isIncompleteAadhaar = error.response.data.incompleteAadhaar;
        const isAadhaarField = (fieldName as string) === 'aadhaarFile' || fieldName.includes('aadhaarFile') || fieldName === 'addressProofFile';
        setImageQualityError({
          show: true,
          reason: error.response.data.reason || (isIncompleteAadhaar 
            ? 'Please upload an image containing BOTH front and back sides of your Aadhaar card in a single image.'
            : 'The uploaded image is blurry, smudged, or unreadable.'),
          fieldName: isIncompleteAadhaar ? 'Aadhaar Card' : 'Address Proof',
          isAadhaarError: isIncompleteAadhaar && isAadhaarField,
        });
      } else {
        setPartnerFileMessage(prev => ({ ...prev, [uploadKey]: `Error: ${error.message || 'Upload failed'}` }));
      }
    } finally {
      setPartnerFileUploading(prev => ({ ...prev, [uploadKey]: false }));
      setTimeout(() => {
        setPartnerFileMessage(prev => ({ ...prev, [uploadKey]: '' }));
      }, 4000);
    }
  };

  useEffect(() => {
    if (!formData.dateOfBirth) {
      setDobAge(null);
      setDobAgeError(null);
      return;
    }

    const age = calculateAgeFromDob(formData.dateOfBirth);
    setDobAge(age);

    if (age === null) {
      setDobAgeError('Please enter a valid Date of Birth.');
      return;
    }

    // Age validation applies to all dealership types - primary applicant must be 21-65 years
    if (age < 21 || age > 65) {
      setDobAgeError(
        `Current age as of today is ${age} years. Eligibility criteria is 21 to 65 years. You cannot proceed further.`
      );
    } else {
      setDobAgeError(null);
    }
  }, [formData.dateOfBirth]);

  // Auto-calculate percentage for educational qualifications
  useEffect(() => {
    const calculatePercentage = (marks: string, outOf: string): string => {
      const marksNum = parseFloat(marks);
      const outOfNum = parseFloat(outOf);
      if (!isNaN(marksNum) && !isNaN(outOfNum) && outOfNum > 0) {
        return ((marksNum / outOfNum) * 100).toFixed(2);
      }
      return '';
    };

    const updates: Partial<FormData> = {};

    // SSC
    const sscPercentage = calculatePercentage(formData.eduSscMarksObtained, formData.eduSscOutOfMarks);
    if (sscPercentage && formData.eduSscPercentage !== sscPercentage) {
      updates.eduSscPercentage = sscPercentage;
    }

    // HSC
    const hscPercentage = calculatePercentage(formData.eduHscMarksObtained, formData.eduHscOutOfMarks);
    if (hscPercentage && formData.eduHscPercentage !== hscPercentage) {
      updates.eduHscPercentage = hscPercentage;
    }

    // Graduate
    const graduatePercentage = calculatePercentage(formData.eduGraduateMarksObtained, formData.eduGraduateOutOfMarks);
    if (graduatePercentage && formData.eduGraduatePercentage !== graduatePercentage) {
      updates.eduGraduatePercentage = graduatePercentage;
    }

    // Others
    const othersPercentage = calculatePercentage(formData.eduOthersMarksObtained, formData.eduOthersOutOfMarks);
    if (othersPercentage && formData.eduOthersPercentage !== othersPercentage) {
      updates.eduOthersPercentage = othersPercentage;
    }

    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
  }, [
    formData.eduSscMarksObtained, formData.eduSscOutOfMarks,
    formData.eduHscMarksObtained, formData.eduHscOutOfMarks,
    formData.eduGraduateMarksObtained, formData.eduGraduateOutOfMarks,
    formData.eduOthersMarksObtained, formData.eduOthersOutOfMarks,
  ]);

  // Effect for online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Effect for initial auto-save of a newly generated ID
  useEffect(() => {
    if (applicationId && isNewlyGeneratedIdRef.current && isOnline && Object.keys(formData).some(key => formData[key as keyof FormData] !== '' && key !== 'errors' && key !== 'applicationNoOfficeUse')) {
      console.log(`[AppFormPage1 Effect AutoSave] Application ID ${applicationId} is newly generated. Attempting to save as draft.`);
      saveToDatabaseCallback(formData, true, applicationId); // true for isDraft
      // isNewlyGeneratedIdRef.current = false; // Moved reset to saveToDatabaseCallback on success
    }
  }, [applicationId, formData, saveToDatabaseCallback, isOnline, isNewlyGeneratedIdRef]);

  // Effect to auto-initialize partner details when dealership type changes to multi-partner type
  useEffect(() => {
    const requiresPartners = ['LLP', 'Pvt Ltd', 'Public Ltd', 'Other'].includes(formData.dealershipType);
    
    if (requiresPartners) {
      const minRequired = formData.dealershipType === 'Public Ltd' ? 3 : 2;
      const currentPartnerCount = formData.numberOfPartners || 0;
      const currentDetails = formData.partnerDirectorDetails || [];
      
      // Only auto-initialize if numberOfPartners is 0 or less than minimum
      if (currentPartnerCount < minRequired) {
        const additionalPartnersNeeded = minRequired - 1; // -1 for primary applicant
        
        // Create new partner details if needed
        const newDetails = [...currentDetails];
        while (newDetails.length < additionalPartnersNeeded) {
          newDetails.push({
            id: `partner_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            nameInBlocks: '',
            fatherOrHusbandName: '',
            dateOfBirth: '',
            panCardFile: null,
            dobProofFile: null,
            isMinimumEducationMet: '',
            qualification: '',
            qualificationProofFile: null,
            currentAddress: '',
            permanentAddress: '',
            permanentAddressSameAsCurrent: false,
            addressProofFile: null,
            personalMobile: '',
            secondaryMobile: '',
            emailId: '',
            nationality: 'Indian',
            gender: '',
            maritalStatus: '',
            maritalStatusOther: '',
            aadhaarNumber: '',
            aadhaarVerified: '',
            aadhaarFile: null,
            panNumberStatutory: '',
            panVerified: '',
            panStatutoryFile: null,
            gstNumber: '',
            gstVerified: '',
            gstFile: null,
            identityProofVoterId: false,
            identityProofPassport: false,
            identityProofDrivingLicense: false,
            identityDocumentNumber: '',
            voterIdNumber: '',
            passportNumber: '',
            drivingLicenseNumber: '',
            voterIdFile: null,
            passportFile: null,
            drivingLicenseFile: null,
            eduSscBoard: '',
            eduSscMarksObtained: '',
            eduSscOutOfMarks: '',
            eduSscPercentage: '',
            eduSscYearOfPassing: '',
            eduSscCertificateFile: null,
            eduHscBoard: '',
            eduHscMarksObtained: '',
            eduHscOutOfMarks: '',
            eduHscPercentage: '',
            eduHscYearOfPassing: '',
            eduHscCertificateFile: null,
            eduGraduateBoard: '',
            eduGraduateMarksObtained: '',
            eduGraduateOutOfMarks: '',
            eduGraduatePercentage: '',
            eduGraduateYearOfPassing: '',
            eduGraduateCertificateFile: null,
            eduOthersBoard: '',
            eduOthersMarksObtained: '',
            eduOthersOutOfMarks: '',
            eduOthersPercentage: '',
            eduOthersYearOfPassing: '',
            eduOthersCertificateFile: null,
            // Business / Employment Details
            presentOccupation: '',
            businessEmploymentDetails: '',
            totalWorkExperienceYears: '',
            relevantIndustryExperience: '',
            // Financial Capability
            annualIncome: '',
            netWorthAtApplication: '',
            sourceOfInvestment: '',
            abilityToMeetRequiredInvestment: '',
            // Financial Capability Document Checklist
            finDocProjectReport: '',
            finDocProjectReportFile: null,
            finDocBankStatement: '',
            finDocBankStatementFile: null,
            finDocFdTdReceipts: '',
            finDocFdTdReceiptsFile: null,
            finDocInvestmentCertificates: '',
            finDocInvestmentCertificatesFile: null,
            finDocMutualFundStatement: '',
            finDocMutualFundStatementFile: null,
            finDocDematStatement: '',
            finDocDematStatementFile: null,
            finDocCaCertificate: '',
            finDocCaCertificateFile: null,
            finDocDpCertificate: '',
            finDocDpCertificateFile: null,
            finDocAttestedFinancials: '',
            finDocAttestedFinancialsFile: null,
            finDocNotarizedAffidavit: '',
            finDocNotarizedAffidavitFile: null,
            finDocBankLetter: '',
            finDocBankLetterFile: null,
            finDocOwnershipDocs: '',
            finDocOwnershipDocsFile: null,
            affidavitFile: null,
          });
        }
        
        const updatedFormData = {
          ...formData,
          numberOfPartners: minRequired,
          partnerDirectorDetails: newDetails,
        };
        setFormData(updatedFormData);
      }
    }
  }, [formData.dealershipType]);

  // Fetch appendix logic when dealershipType changes
  useEffect(() => {
    const fetchAppendixLogic = async () => {
      console.log('[Page1 AppendixLogic] dealershipType:', formData.dealershipType);
      if (!formData.dealershipType) {
        setVisibleAppendixes(['A', 'B', 'D', 'E', 'F']); // Default
        return;
      }
      
      try {
        const RAW_API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://backend.mngldodo.site' : 'http://localhost:5000');
        const API_BASE = RAW_API_URL.endsWith('/api') ? RAW_API_URL.slice(0, -4) : RAW_API_URL;
        const url = `${API_BASE}/api/appendix-logic/${encodeURIComponent(formData.dealershipType)}`;
        console.log('[Page1 AppendixLogic] Fetching from:', url);
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          console.log('[Page1 AppendixLogic] Response:', data);
          if (data.success && data.visibleAppendixes) {
            setVisibleAppendixes(data.visibleAppendixes);
            console.log('[Page1 AppendixLogic] Set visibleAppendixes to:', data.visibleAppendixes);
          }
        }
      } catch (error) {
        console.error('Error fetching appendix logic:', error);
      }
    };
    fetchAppendixLogic();
  }, [formData.dealershipType]);

  const handleFillDummyData = () => {
    console.log('[AppFormPage1 FillDummyData] Filling form with dummy data.');
    setFormData(prevData => ({
      ...prevData,
      // Top section
      location: prevData.location || 'Pune',
      advertisedOn: prevData.advertisedOn || '2024-04-15',
      advertisedIn: prevData.advertisedIn || 'Times of India',
      advertisementSource: prevData.advertisementSource || 'Website',

      // Personal details
      nameInBlocks: prevData.nameInBlocks || 'John Doe',
      fatherOrHusbandName: prevData.fatherOrHusbandName || 'Richard Doe',
      dateOfBirth: prevData.dateOfBirth || '1990-01-15',
      qualification: prevData.qualification || 'Graduate',
      currentAddress: prevData.currentAddress || '123, MG Road, Pune, Maharashtra – 411001',
      permanentAddress: prevData.permanentAddress || '123, MG Road, Pune, Maharashtra – 411001',
      permanentAddressSameAsCurrent: prevData.permanentAddressSameAsCurrent !== undefined ? prevData.permanentAddressSameAsCurrent : true,
      personalMobile: prevData.personalMobile || '9876543210',
      secondaryMobile: prevData.secondaryMobile || '9876543211',
      nationality: prevData.nationality || 'Indian',
      gender: prevData.gender || 'Male',
      maritalStatus: prevData.maritalStatus || 'Married',

      // IMPORTANT: Do not auto-fill emailId or photo per requirement
      // emailId: prevData.emailId,
      // photo: prevData.photo,

      // Keep file proofs unchanged
      applicationNoOfficeUse: prevData.applicationNoOfficeUse || applicationId || '',
      errors: {}
    }));
    // Optionally, trigger a save or visual feedback
    setSaveMessage('Dummy data filled. Save to persist.');
    setShowSaveIndicator(true);
    setTimeout(() => {
      setShowSaveIndicator(false);
      // setSaveMessage(''); // Optionally reset message if needed after timeout
    }, 3000);
  };

  // Pre-compute Application Date label from applicationId for display in the top-right box
  const applicationDateDisplay = formatApplicationDateFromId(applicationId);

  const districtSuggestions = Array.from(
    new Set(
      locationStretches
        .map((row) => (row.district || '').trim())
        .filter((value) => value.length > 0)
    )
  );

  const stateSuggestions = Array.from(
    new Set(
      locationStretches
        .map((row) => (row.state || '').trim())
        .filter((value) => value.length > 0)
    )
  );

  const stretchSuggestions = Array.from(
    new Set(
      locationStretches
        .map((row) => ((row.srNo || row.stretchSrNoArea) || '').trim())
        .filter((value) => value.length > 0)
    )
  );

  const locationSuggestions = Array.from(
    new Set(
      locationStretches
        .map((row) => (row.location || '').trim())
        .filter((value) => value.length > 0)
    )
  );

  const areaStretchSuggestions = Array.from(
    new Set(
      locationStretches
        .map((row) => (row.areaStretch || '').trim())
        .filter((value) => value.length > 0)
    )
  );

  return (
    <>
      {/* Loading overlay - shows while application data is being fetched from backend (minimum 2 seconds) */}
      {showLoadingOverlay && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '40px',
            borderRadius: '16px',
            backgroundColor: 'white',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            maxWidth: '400px'
          }}>
            {/* Animated loader */}
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #e5e7eb',
              borderTop: '4px solid #4A6CF7',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px auto'
            }} />
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            
            <h3 style={{ 
              margin: '0 0 12px 0', 
              color: '#1e40af', 
              fontSize: '1.3em',
              fontWeight: 600 
            }}>
              Loading Application Data
            </h3>
            <p style={{ 
              margin: 0, 
              color: '#6b7280', 
              fontSize: '0.95em',
              lineHeight: 1.5
            }}>
              Please wait while we retrieve your application details...
            </p>
            <div style={{
              marginTop: '20px',
              padding: '10px 16px',
              backgroundColor: '#fef3c7',
              borderRadius: '8px',
              border: '1px solid #f59e0b'
            }}>
              <p style={{ 
                margin: 0, 
                color: '#92400e', 
                fontSize: '0.85em',
                fontWeight: 500
              }}>
                ⚠️ Do not close or refresh this page
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Progress bar - hidden on mobile when toggle is active */}
      {!(isMobile && mobileFloatingHidden) && (
        <>
          <ProgressBarContainer>
            <ProgressBarFill progress={formProgress} />
          </ProgressBarContainer>
          <ProgressLabel>Page 1/5 • {Math.round(formProgress)}% complete</ProgressLabel>
        </>
      )}
      
      {/* Zoom controls */}
      <ZoomControlsContainer>
        <ZoomButton onClick={handleZoomIn}>+</ZoomButton>
        <ZoomButton onClick={handleZoomOut}>−</ZoomButton>
      </ZoomControlsContainer>
      
      {/* Save indicator */}
      <SaveIndicator visible={showSaveIndicator}>
        {saveMessage}
      </SaveIndicator>

      {!isOnline && (
        <OfflineIndicator>
          You are currently offline. Changes will not be saved until you reconnect.
        </OfflineIndicator>
      )}
      {/* Application Locked Banner */}
      {isApplicationLocked && !isViewMode && (
        <div style={{
          position: 'fixed',
          top: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(254, 243, 199, 0.92)',
          backdropFilter: 'blur(8px)',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          padding: '10px 20px',
          zIndex: 9998,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 4px 20px rgba(245, 158, 11, 0.25)',
          maxWidth: '90%'
        }}>
          <span style={{ fontSize: '1.3rem' }}>🔒</span>
          <div>
            <div style={{ fontWeight: 600, color: '#92400e', fontSize: '0.9rem' }}>
              Application Locked
            </div>
            <div style={{ color: '#78350f', fontSize: '0.75rem' }}>
              This application is locked. You can view data but cannot make changes.
            </div>
          </div>
        </div>
      )}
      {/* Floating Back Button - hidden on Page 1 (no previous page) */}
      
      {/* Download & Brochure buttons - hidden on mobile when toggle is active */}
      {!(isMobile && mobileFloatingHidden) && (
        <div style={{ position: 'fixed', top: '80px', left: '30px', zIndex: 1001, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button 
            onClick={handleDownloadFullApplicationPdf} 
            disabled={isFullPdfGenerating || isDataLoading || !isDataLoaded}
            style={{ 
              backgroundColor: '#4caf50', 
              color: '#ffffff', 
              borderRadius: '4px', 
              padding: '8px 15px',
              border: '1px solid #388e3c',
              fontWeight: 'bold',
              cursor: (isFullPdfGenerating || isDataLoading || !isDataLoaded) ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              opacity: (isFullPdfGenerating || isDataLoading || !isDataLoaded) ? 0.7 : 1
            }}
          >
            {isDataLoading ? 'Loading Data...' : 'Download Full Application'}
          </button>
          <button 
            onClick={() => setShowBrochureModal(true)}
            style={{ 
              backgroundColor: '#2196f3', 
              color: '#ffffff', 
              borderRadius: '4px', 
              padding: '8px 15px',
              border: '1px solid #1976d2',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            📄 View Brochure
          </button>
        </div>
      )}
      {showDummyDataButton && (
        <div style={{ position: 'fixed', top: '80px', right: '30px', zIndex: 1001, display: 'block' }}>
          <button 
            onClick={handleFillDummyData} 
            style={{ 
              backgroundColor: '#ffc107', 
              color: '#212529', 
              borderRadius: '4px', 
              padding: '8px 15px',
              border: '1px solid #e0a800',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
            }}
          >
            Fill Dummy Data
          </button>
        </div>
      )}
      {/* FormPageNavigator - hidden on mobile when toggle is active */}
      {!appendixPdfModal.isOpen && !(isMobile && mobileFloatingHidden) && <FormPageNavigator currentPage={1} totalPages={14} targetFormRef={formRef} zoomScale={zoomScale} onNavigate={handleNavBarNavigation} disabled={
        !!dobAgeError || 
        formData.isMinimumEducationMet === 'No' ||
        isFormFrozen ||
        (requiresMultiplePartners && formData.partnerDirectorDetails && formData.partnerDirectorDetails.some(partner => {
          const partnerAge = partner.dateOfBirth ? calculateAgeFromDob(partner.dateOfBirth) : null;
          const isAgeInvalid = partnerAge !== null && (partnerAge < 21 || partnerAge > 65);
          return isAgeInvalid || partner.isMinimumEducationMet === 'No';
        }))
      } />}
      {/* NextPageButton - hidden on mobile when toggle is active */}
      {!appendixPdfModal.isOpen && !(isMobile && mobileFloatingHidden) && <div style={{ position: 'fixed', right: '-25px', top: '605px', zIndex: 999 }}>
        <NextPageButton 
          onClick={handleNextButtonClick}
          disabled={
            !!dobAgeError || 
            formData.isMinimumEducationMet === 'No' ||
            isPlotSizeInsufficient ||
            isFormFrozen ||
            (requiresMultiplePartners && formData.partnerDirectorDetails && formData.partnerDirectorDetails.some(partner => {
              const partnerAge = partner.dateOfBirth ? calculateAgeFromDob(partner.dateOfBirth) : null;
              const isAgeInvalid = partnerAge !== null && (partnerAge < 21 || partnerAge > 65);
              return isAgeInvalid || partner.isMinimumEducationMet === 'No';
            }))
          }
          style={{ 
            height: '35px', 
            width: '140px', 
            borderRadius: '4px', 
            whiteSpace: 'nowrap',
            fontSize: '12.9px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '0 10px',
            opacity: (dobAgeError || formData.isMinimumEducationMet === 'No' || isPlotSizeInsufficient || isFormFrozen) ? 0.5 : 1,
            cursor: (dobAgeError || formData.isMinimumEducationMet === 'No' || isPlotSizeInsufficient || isFormFrozen) ? 'not-allowed' : 'pointer'
          }}
        >
          Next Page →
        </NextPageButton>
      </div>}
      {/* WhatsApp Help Button - hidden on mobile when toggle is active */}
      {!(isMobile && mobileFloatingHidden) && (
        <div style={{ position: 'fixed', right: '20px', bottom: '100px', zIndex: 999, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#25D366', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>Need Help!</span>
          <a 
            href="https://web.whatsapp.com/send?phone=919971874909&text=Hi%2C%20Need%20Help%20filling%20MNGL%20DODO%20form"
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'block', cursor: 'pointer', transition: 'transform 0.2s ease' }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            title="Chat with us on WhatsApp"
          >
            <img src="/assets/images/whatsapp.png" alt="WhatsApp Help" style={{ }} />
          </a>
        </div>
      )}
      <PageWrapper scale={zoomScale}>
      {/* Floating Disabled Tooltip - appears on click of disabled elements */}
      {disabledTooltip.show && (
        <div style={{
          position: 'fixed',
          left: disabledTooltip.x,
          top: disabledTooltip.y,
          transform: 'translate(-50%, -100%)',
          backgroundColor: '#dc2626',
          color: 'white',
          padding: '4px 10px',
          borderRadius: '6px',
          fontSize: '0.75rem',
          fontWeight: 600,
          zIndex: 9999,
          boxShadow: '0 2px 8px rgba(220, 38, 38, 0.4)',
          animation: 'fadeInOut 1.5s ease-in-out',
          pointerEvents: 'none'
        }}>
          🔒 Disabled
        </div>
      )}
      {/* Unified Partner Editing Banner - combines user indicator and lock notice */}
      {editingUser !== null && editingUser !== 'main' && (
        <div style={{
          position: 'sticky',
          top: '10px',
          zIndex: 100,
          margin: '0 auto 15px auto',
          padding: '16px 24px',
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          border: '2px solid #f59e0b',
          borderRadius: '16px',
          boxShadow: '0 6px 20px rgba(245, 158, 11, 0.25)',
          maxWidth: '850px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '15px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 3px 8px rgba(217, 119, 6, 0.4)'
              }}>
                <span style={{ fontSize: '1.4rem' }}>👤</span>
              </div>
              <div>
                <div style={{ fontWeight: 700, color: '#92400e', fontSize: '1rem', marginBottom: '2px' }}>
                  Editing as: <span style={{ color: '#b45309' }}>{formData.partnerDirectorDetails?.[editingUser as number]?.nameInBlocks || `Partner/Director ${(editingUser as number) + 2}`}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    backgroundColor: '#dc2626',
                    color: 'white',
                    padding: '3px 10px',
                    borderRadius: '12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    🔒 Other sections locked
                  </span>
                  <span style={{ fontSize: '0.8rem', color: '#a16207' }}>
                    You can only edit your own Partner section below
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowPartnerSelectionModal(true)}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '0.9rem',
                boxShadow: '0 3px 8px rgba(217, 119, 6, 0.3)',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 5px 12px rgba(217, 119, 6, 0.4)'; }}
              onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 3px 8px rgba(217, 119, 6, 0.3)'; }}
            >
              Switch User
            </button>
          </div>
        </div>
      )}
      
      <A4PageContainer ref={formRef} style={{ 
        opacity: isMainApplicantSectionLocked ? 0.85 : 1,
        pointerEvents: isMainApplicantSectionLocked ? 'none' : 'auto'
      }}>
        <HeaderSection>
          <LogoImage src={'/assets/images/logo.png'} alt="MNGL Logo" />
          <FormTitle>{formHeading}</FormTitle>
          <SubTitle>{formSubheading}</SubTitle>
          <div style={{
            marginTop: '12px',
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
            borderRadius: '8px',
            boxShadow: '0 4px 15px rgba(238, 90, 36, 0.3)',
            display: 'inline-block'
          }}>
            <span style={{
              color: 'white',
              fontWeight: 700,
              fontSize: '1em',
              letterSpacing: '0.5px',
              textShadow: '0 1px 2px rgba(0,0,0,0.2)'
            }}>
              💰 Application Fee: <span style={{ fontSize: '1.15em' }}>₹5,000/-</span> (Non-Refundable) payable after duly filling the application
            </span>
          </div>
        </HeaderSection>

        <FormContentContainer>
          <TopSectionLayout>
            <LeftFields>
              <AppNoRowContainer>
                <Label htmlFor="applicationNoOfficeUse">Application No.:</Label>
                <StyledInput 
                  type="text" 
                  id="applicationNoOfficeUse" 
                  name="applicationNoOfficeUse" 
                  value={applicationId} 
                  readOnly 
                  placeholder="Application Number"
                  style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }} 
                  disabled={isMainFieldDisabled}
                />
              </AppNoRowContainer>
              <ShakeAnimation $isShaking={isAdvertisementSourceShaking} ref={advertisementSourceRef}>
              <div style={{ 
                backgroundColor: '#f0f9ff', 
                border: '1px solid #bae6fd', 
                borderRadius: '10px', 
                padding: '16px 20px', 
                marginBottom: '15px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <Label style={{ color: '#0369a1', fontWeight: 600, marginBottom: '12px', display: 'block' }}>
                  📢 How did you find out about this advertisement?
                  {mandatoryFields.advertisementSource && !formData.advertisementSource && (
                    <span style={{ 
                      marginLeft: '8px',
                      backgroundColor: '#fef2f2', 
                      color: '#dc2626', 
                      padding: '2px 8px', 
                      borderRadius: '4px', 
                      fontSize: '0.675rem', 
                      fontWeight: 600,
                      border: '1px solid #fecaca',
                      textDecoration: 'none'
                    }}>Required</span>
                  )}
                </Label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {['Newspaper', 'Website', 'Social Media', 'Friend Ref.', 'Radio', 'Other'].map((source) => {
                    const sourceValue = source === 'Friend Ref.' ? 'Friend Referral' : source;
                    const isSelected = formData.advertisementSource === sourceValue;
                    return (
                      <label
                        key={source}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 14px',
                          backgroundColor: isSelected ? '#0284c7' : 'white',
                          color: isSelected ? 'white' : '#374151',
                          border: isSelected ? '2px solid #0284c7' : '1px solid #d1d5db',
                          borderRadius: '20px',
                          cursor: !isOnline || isViewMode ? 'not-allowed' : 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: isSelected ? 600 : 400,
                          transition: 'all 0.2s ease',
                          opacity: !isOnline || isViewMode ? 0.6 : 1
                        }}
                      >
                        <input
                          type="radio"
                          name="advertisementSource"
                          value={sourceValue}
                          checked={isSelected}
                          onChange={handleChange}
                          disabled={isMainFieldDisabled}
                          style={{ display: 'none' }}
                        />
                        {isSelected && <span>✓</span>}
                        {source}
                      </label>
                    );
                  })}
                </div>
              </div>
              </ShakeAnimation>

              {/* Warning message when advertisement source is not selected */}
              {!formData.advertisementSource && (
                <div style={{ 
                  marginTop: '10px',
                  marginBottom: '10px',
                  padding: '15px', 
                  backgroundColor: '#fff3cd', 
                  borderRadius: '8px', 
                  border: '1px solid #ffc107',
                  textAlign: 'center'
                }}>
                  <span style={{ fontSize: '1.2em', marginRight: '8px' }}>⚠️</span>
                  <span style={{ fontWeight: 600, color: '#856404', fontSize: '0.9em' }}>
                    Please select how you found out about this advertisement to continue.
                  </span>
                </div>
              )}
              
              {/* Joint Application Section (controlled by admin's "Show Linked Applications" setting) */}
              {!isViewMode && showLinkedApplicationsNav && (
                <div style={{ 
                  margin: '1.5rem 0',
                  padding: '1.25rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <h3 style={{ 
                    margin: '0 0 0.5rem 0',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#2d3748',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    🔗 Joint Application (Multiple Owners)
                  </h3>
                  <p style={{ 
                    margin: '0 0 1rem 0',
                    fontSize: '0.85rem',
                    color: '#666',
                    lineHeight: '1.4'
                  }}>
                    If multiple people are applying for the same property/dealership, link this application to an existing one.
                  </p>
                  
                  {linkedApplications.length === 0 ? (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                      <input
                        type="text"
                        placeholder="Enter Application ID to link (e.g., CNG-111120251234567)"
                        value={linkInputValue}
                        onChange={(e) => setLinkInputValue(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleLinkApplication()}
                        disabled={isLinking || !isOnline}
                        style={{
                          flex: 1,
                          padding: '0.6rem',
                          border: '1px solid #cbd5e0',
                          borderRadius: '6px',
                          fontSize: '0.9rem'
                        }}
                      />
                      <button
                        onClick={handleLinkApplication}
                        disabled={isLinking || !isOnline || !linkInputValue.trim()}
                        style={{
                          padding: '0.6rem 1.25rem',
                          background: 'linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          cursor: isLinking || !linkInputValue.trim() ? 'not-allowed' : 'pointer',
                          opacity: isLinking || !linkInputValue.trim() ? 0.6 : 1,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {isLinking ? 'Linking...' : 'Link'}
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div style={{ 
                        marginBottom: '0.75rem',
                        padding: '0.75rem',
                        backgroundColor: '#e6f7ff',
                        borderRadius: '6px',
                        border: '1px solid #91d5ff'
                      }}>
                        <p style={{ 
                          margin: 0,
                          fontSize: '0.85rem',
                          color: '#1890ff',
                          fontWeight: 500
                        }}>
                          ✓ This application is linked with {linkedApplications.length} other application{linkedApplications.length > 1 ? 's' : ''}
                        </p>
                      </div>
                      
                      <div style={{ marginBottom: '0.75rem' }}>
                        {linkedApplications.map((linked) => (
                          <div key={linked.applicationId} style={{
                            padding: '0.6rem',
                            marginBottom: '0.5rem',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div>
                              <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#2d3748' }}>
                                {linked.applicationId}
                              </span>
                              {linked.isPrimaryApplicant && (
                                <span style={{
                                  marginLeft: '0.5rem',
                                  padding: '0.15rem 0.5rem',
                                  backgroundColor: '#ffd700',
                                  color: '#7c5a00',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: 600
                                }}>
                                  PRIMARY
                                </span>
                              )}
                              <span style={{
                                marginLeft: '0.5rem',
                                fontSize: '0.8rem',
                                color: '#666'
                              }}>
                                Status: {linked.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <button
                        onClick={handleUnlinkApplication}
                        disabled={isLinking || !isOnline}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#fff5f5',
                          color: '#c53030',
                          border: '1px solid #feb2b2',
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          cursor: isLinking ? 'not-allowed' : 'pointer',
                          opacity: isLinking ? 0.6 : 1
                        }}
                      >
                        {isLinking ? 'Unlinking...' : 'Unlink from Group'}
                      </button>
                    </div>
                  )}
                  
                  {linkMessage && (
                    <p style={{
                      margin: '0.75rem 0 0 0',
                      padding: '0.5rem',
                      backgroundColor: linkMessage.startsWith('✓') ? '#f0f9ff' : '#fff5f5',
                      color: linkMessage.startsWith('✓') ? '#0c4a6e' : '#742a2a',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      fontWeight: 500
                    }}>
                      {linkMessage}
                    </p>
                  )}
                </div>
              )}

          </LeftFields>
          <ShakeAnimation $isShaking={isProfilePhotoShaking} ref={profilePhotoRef}>
          <RightPhotoPlaceholder>
            {applicationDateDisplay && (
              <>
                <ApplicationDateLabel>Application Date:</ApplicationDateLabel>
                <ApplicationDateBox>{applicationDateDisplay}</ApplicationDateBox>
              </>
            )}
            <UploadConfirmationText>{photoUploadMessage}</UploadConfirmationText>
            {mandatoryFields.profilePhoto && !photo && (
              <span style={{ 
                display: 'inline-block', 
                backgroundColor: '#fef2f2', 
                color: '#dc2626', 
                padding: '2px 8px', 
                borderRadius: '4px', 
                fontSize: '0.75rem', 
                fontWeight: 600, 
                marginBottom: '4px',
                border: '1px solid #fecaca'
              }}>Required</span>
            )}
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <PhotoBox
                onClick={() => {
                  if (!isOnline || isViewMode) return;
                  if (photo) {
                    // Toggle menu if photo exists
                    setPrimaryPhotoMenuOpen(!primaryPhotoMenuOpen);
                  } else {
                    // Open file picker if no photo
                    triggerPhotoUpload();
                  }
                }}
                disabled={isMainFieldDisabled}
              >
                {photo && (
                  <AnimatedPhotoImage
                    key={photo} // Force re-render with initial clip-path when photo changes
                    src={photo}
                    alt="Applicant"
                    className={isRevealing ? 'reveal' : ''}
                  />
                )}
                {!photo && !isRevealing && ( // Show placeholder only if no photo and not in the process of revealing
                  <PhotoBoxText>Upload your recent passport size Photograph</PhotoBoxText>
                )}
                <input 
                  type="file" 
                  accept=".jpg,.jpeg,.png" 
                  onChange={handlePhotoChange} 
                  ref={photoInputRef} 
                  style={{ display: 'none' }} 
                  disabled={isMainFieldDisabled}
                />
              </PhotoBox>
              {/* Photo Options Menu - Right Side */}
              {primaryPhotoMenuOpen && photo && !isViewMode && isOnline && (
                <div style={{
                  position: 'absolute',
                  top: '0',
                  left: '140px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                  zIndex: 100,
                  minWidth: '160px',
                  overflow: 'hidden',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ padding: '8px 12px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '0.75em', color: '#64748b', fontWeight: 600 }}>
                    Photo Options
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleProfilePhotoDoubleClick(); setPrimaryPhotoMenuOpen(false); }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '0.9em',
                      fontWeight: 500,
                      color: '#1e40af',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#eff6ff'}
                    onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'white'}
                  >
                    <span style={{ fontSize: '1.1em' }}>✏️</span> View / Edit
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handlePrimaryPhotoDelete(); }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '0.9em',
                      fontWeight: 500,
                      color: '#dc2626',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#fef2f2'}
                    onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'white'}
                  >
                    <span style={{ fontSize: '1.1em' }}>🗑️</span> Delete
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      setPrimaryPhotoMenuOpen(false);
                      triggerPhotoUpload();
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '0.9em',
                      fontWeight: 500,
                      color: '#059669',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#ecfdf5'}
                    onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'white'}
                  >
                    <span style={{ fontSize: '1.1em' }}>📤</span> Upload New
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      setPrimaryPhotoMenuOpen(false);
                      openWebcamModal('primary');
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '0.9em',
                      fontWeight: 500,
                      color: '#7c3aed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f3ff'}
                    onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'white'}
                  >
                    <span style={{ fontSize: '1.1em' }}>📷</span> Take Selfie
                  </button>
                </div>
              )}
            </div>
            {!photo && !isViewMode && isOnline && (
              <button
                type="button"
                onClick={() => openWebcamModal('primary')}
                style={{
                  marginTop: '8px',
                  padding: '8px 16px',
                  background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.8em',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 8px rgba(124,58,237,0.3)',
                  transition: 'transform 0.15s, box-shadow 0.15s'
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.transform = 'scale(1.02)';
                  (e.target as HTMLElement).style.boxShadow = '0 4px 12px rgba(124,58,237,0.4)';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.transform = 'scale(1)';
                  (e.target as HTMLElement).style.boxShadow = '0 2px 8px rgba(124,58,237,0.3)';
                }}
              >
                <span>📷</span> Take Selfie
              </button>
            )}
            {photo && !isViewMode && isOnline && (
              <span style={{ fontSize: '0.7em', color: '#94a3b8', marginTop: '4px', display: 'block', textAlign: 'center' }}>
                Click photo for options
              </span>
            )}
          </RightPhotoPlaceholder>
          </ShakeAnimation>
        </TopSectionLayout>

        <ShakeAnimation $isShaking={isCngLocationShaking} ref={cngLocationRef}>
        <LocationCard style={{ 
          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', 
          border: '1px solid #86efac', 
          borderRadius: '12px', 
          padding: '16px 20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
            <Label style={{ color: '#166534', fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              📍 CNG Station Location:
              {mandatoryFields.cngStationLocation && !formData.location && (
                <span style={{
                  backgroundColor: '#fef2f2',
                  color: '#dc2626',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '0.675rem',
                  fontWeight: 600,
                  border: '1px solid #fecaca',
                  textDecoration: 'none'
                }}>Required</span>
              )}
            </Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0px', position: 'relative' }}>
              {/* Pointing finger animation — shown when Sr.No is empty to guide user */}
              {!formData.locationSrNo && (
                <span style={{
                  fontSize: '1.5rem',
                  animation: 'pointingBounce 1.2s ease-in-out infinite',
                  display: 'inline-block',
                  marginRight: '2px',
                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))',
                  lineHeight: 1,
                }}>
                  👉
                </span>
              )}
              <a
                href="/area-stretch-info"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#ffffff',
                  fontSize: '0.88em',
                  textDecoration: 'none',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #1e40af 100%)',
                  padding: '7px 18px',
                  borderRadius: '20px',
                  border: '2px solid #3b82f6',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  letterSpacing: '0.3px',
                  boxShadow: '0 2px 10px rgba(37, 99, 235, 0.35), 0 0 16px rgba(37, 99, 235, 0.15)',
                  animation: !formData.locationSrNo ? 'pulseRing 1.5s ease-out infinite, advertLocationGlow 2s ease-in-out infinite' : 'advertLocationGlow 2s ease-in-out infinite',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(37, 99, 235, 0.5), 0 0 24px rgba(37, 99, 235, 0.25)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 10px rgba(37, 99, 235, 0.35), 0 0 16px rgba(37, 99, 235, 0.15)'; }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                List of Advertised Locations
              </a>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {availableLocations.length > 0 ? (
              availableLocations.map((location) => {
                const isSelected = formData.location === location.name;
                return (
                  <label
                    key={location.id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      backgroundColor: isSelected ? '#16a34a' : 'white',
                      color: isSelected ? 'white' : '#374151',
                      border: isSelected ? '2px solid #16a34a' : '1px solid #d1d5db',
                      borderRadius: '20px',
                      cursor: !isOnline || isViewMode ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: isSelected ? 600 : 400,
                      transition: 'all 0.2s ease',
                      opacity: !isOnline || isViewMode ? 0.6 : 1
                    }}
                  >
                    <input
                      type="radio"
                      id={`location-${location.id}`}
                      name="location"
                      value={location.name}
                      checked={isSelected}
                      onChange={handleChange}
                      disabled={isMainFieldDisabled}
                      style={{ display: 'none' }}
                    />
                    {isSelected && <span>✓</span>}
                    {location.name}
                  </label>
                );
              })
            ) : (
              <span style={{ color: '#c53030', fontSize: '0.9rem' }}>
                No locations available. Please contact administrator.
              </span>
            )}
          </div>

          {/* New detailed location block mapped from admin XLSX */}
          {/* Row 1: Sr.No + wide Location */}
          <FormFieldRow>
            <div style={{ display: 'flex', width: '100%', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '120px', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <Label htmlFor="locationSrNo">Sr.No</Label>
                <StyledInput
                  type="text"
                  id="locationSrNo"
                  name="locationSrNo"
                  value={formData.locationSrNo}
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      locationSrNo: e.target.value,
                    }))
                  }
                  onKeyDown={handleLocationSrNoKeyDown}
                  onBlur={handleLocationSrNoBlur}
                  list="stretch-suggestions"
                  placeholder="Sr.No"
                  disabled={isMainFieldDisabled}
                  style={!formData.locationSrNo ? {
                    animation: 'srNoFieldPulse 2s ease-in-out infinite',
                    borderColor: '#fca5a5',
                  } : undefined}
                />
                <datalist id="stretch-suggestions">
                  {stretchSuggestions.map((stretch) => (
                    <option key={stretch} value={stretch} />
                  ))}
                </datalist>
                {/* Hint below Sr.No field when empty */}
                {!formData.locationSrNo && (
                  <div style={{
                    marginTop: '4px',
                    padding: '4px 8px',
                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                    border: '1px solid #93c5fd',
                    borderRadius: '6px',
                    fontSize: '0.68rem',
                    color: '#1e40af',
                    fontWeight: 600,
                    lineHeight: 1.4,
                    animation: 'tooltipFadeIn 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                    <span style={{ fontSize: '0.8rem' }}>ℹ️</span>
                    Click "List of Advertised Locations" above to find your Sr.No
                  </div>
                )}
              </div>
              <div style={{ flex: 3, minWidth: '260px', display: 'flex', flexDirection: 'column' }}>
                <Label htmlFor="locationLocality">Location</Label>
                <StyledInput
                  type="text"
                  id="locationLocality"
                  name="locationLocality"
                  value={formData.locationLocality}
                  onChange={handleChange}
                  onBlur={handleLocationNameBlur}
                  list="location-suggestions"
                  placeholder="Location"
                  disabled={true}
                  style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                />
                <datalist id="location-suggestions">
                  {locationSuggestions.map((loc) => (
                    <option key={loc} value={loc} />
                  ))}
                </datalist>
              </div>
            </div>
          </FormFieldRow>

            {/* Row 2: CA, Area, Stretch Side */}
            <FormFieldRow>
              <div style={{ display: 'flex', width: '100%', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '140px', display: 'flex', flexDirection: 'column' }}>
                  <Label htmlFor="locationCA">CA</Label>
                  <StyledInput
                    type="text"
                    id="locationCA"
                    name="locationCA"
                    value={formData.locationCA}
                    onChange={handleChange}
                    placeholder="CA"
                    disabled={true}
                    style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                  />
                </div>
                <div style={{ flex: 1.3, minWidth: '160px', display: 'flex', flexDirection: 'column' }}>
                  <Label htmlFor="locationArea">Area</Label>
                  <StyledInput
                    type="text"
                    id="locationArea"
                    name="locationArea"
                    value={formData.locationArea}
                    onChange={handleChange}
                    placeholder="Area"
                    disabled={true}
                    style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                  />
                </div>
                <div style={{ flex: 1.2, minWidth: '160px', display: 'flex', flexDirection: 'column' }}>
                  <Label htmlFor="locationStretchSide">Strech Side(LHS/RHS)</Label>
                  <StyledInput
                    type="text"
                    id="locationStretchSide"
                    name="locationStretchSide"
                    value={formData.locationStretchSide}
                    onChange={handleChange}
                    placeholder="LHS / RHS"
                    disabled={true}
                    style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                  />
                </div>
              </div>
            </FormFieldRow>

            {/* Row 3: From and To in same row with Lat/Long as subscripts */}
            <FormFieldRow>
              <div style={{ display: 'flex', width: '100%', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column' }}>
                  <Label htmlFor="locationFrom">From</Label>
                  <StyledInput
                    type="text"
                    id="locationFrom"
                    name="locationFrom"
                    value={formData.locationFrom}
                    onChange={handleChange}
                    placeholder="From"
                    disabled={true}
                    style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                  />
                  {formData.locationFromLatLong && (
                    <span style={{ fontSize: '0.75em', color: '#666', marginTop: '2px', fontStyle: 'italic' }}>
                      Lat/Long: {formData.locationFromLatLong}
                    </span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column' }}>
                  <Label htmlFor="locationTo">To</Label>
                  <StyledInput
                    type="text"
                    id="locationTo"
                    name="locationTo"
                    value={formData.locationTo}
                    onChange={handleChange}
                    placeholder="To"
                    disabled={true}
                    style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                  />
                  {formData.locationToLatLong && (
                    <span style={{ fontSize: '0.75em', color: '#666', marginTop: '2px', fontStyle: 'italic' }}>
                      Lat/Long: {formData.locationToLatLong}
                    </span>
                  )}
                </div>
              </div>
            </FormFieldRow>

            {/* Info button for All Roads - shows minimum plot size table */}
            {formData.locationFrom === 'All Roads' && formData.locationTo === 'All Roads' && (
              <div style={{ 
                marginTop: '10px', 
                marginBottom: '10px', 
                padding: '12px', 
                backgroundColor: '#e8f4fd', 
                borderRadius: '8px', 
                border: '1px solid #3498db',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <span style={{ fontSize: '1.2em' }}>ℹ️</span>
                <span style={{ flex: 1, fontSize: '0.9em', color: '#1a5276' }}>
                  For "All Roads" locations, please refer to the minimum plot size requirements.
                </span>
                <button
                  type="button"
                  onClick={() => setShowPlotSizeModal(true)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3498db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.85em'
                  }}
                >
                  View Plot Size Requirements
                </button>
              </div>
            )}

            {/* Row 5: Min Plot Frontage, Road Type, Min Plot Depth */}
            <FormFieldRow>
              <div style={{ display: 'flex', width: '100%', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1.2, minWidth: '220px', display: 'flex', flexDirection: 'column' }}>
                  <Label htmlFor="locationMinPlotFrontage">Min Plot Size (Frontage in Meter)</Label>
                  <StyledInput
                    type="text"
                    id="locationMinPlotFrontage"
                    name="locationMinPlotFrontage"
                    value={formData.locationMinPlotFrontage}
                    onChange={handleChange}
                    placeholder="Frontage (m)"
                    disabled={true}
                    style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                  />
                  {formData.locationMinPlotFrontage && (
                    <ShakeAnimation $isShaking={isFrontageQuestionShaking} ref={frontageQuestionRef}>
                    <div style={{ marginTop: '8px', fontSize: '0.85em', padding: '8px', border: '1px solid #dc3545', borderRadius: '6px', backgroundColor: '#fff5f5' }}>
                      <span style={{ fontWeight: 500, color: '#dc3545' }}>Do you have frontage ≥ {formData.locationMinPlotFrontage} meters? <span style={{ color: 'red' }}>*</span></span>
                      <div style={{ display: 'flex', gap: '15px', marginTop: '5px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                          <input type="radio" name="hasSufficientFrontage" value="Yes" checked={formData.hasSufficientFrontage === 'Yes'} onChange={handleChange} disabled={isMainFieldDisabled} required />
                          Yes
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                          <input type="radio" name="hasSufficientFrontage" value="No" checked={formData.hasSufficientFrontage === 'No'} onChange={handleChange} disabled={isMainFieldDisabled} />
                          No
                        </label>
                      </div>
                      {!formData.hasSufficientFrontage && formData.locationMinPlotFrontage && (
                        <div style={{ color: '#dc3545', fontSize: '0.85em', marginTop: '5px' }}>⚠️ This field is required</div>
                      )}
                    </div>
                    </ShakeAnimation>
                  )}
                </div>
                <div style={{ flex: 1.4, minWidth: '220px', display: 'flex', flexDirection: 'column' }}>
                  <Label htmlFor="locationRoadType">Road NH/STATE/CITY</Label>
                  <StyledInput
                    type="text"
                    id="locationRoadType"
                    name="locationRoadType"
                    value={formData.locationRoadType}
                    onChange={handleChange}
                    placeholder="Road NH/STATE/CITY"
                    disabled={true}
                    style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                  />
                </div>
                <div style={{ flex: 1.1, minWidth: '200px', display: 'flex', flexDirection: 'column' }}>
                  <Label htmlFor="locationMinPlotDepth">Min Plot Size (Depth in Meter)</Label>
                  <StyledInput
                    type="text"
                    id="locationMinPlotDepth"
                    name="locationMinPlotDepth"
                    value={formData.locationMinPlotDepth}
                    onChange={handleChange}
                    placeholder="Depth (m)"
                    disabled={true}
                    style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                  />
                </div>
              </div>
            </FormFieldRow>
          </LocationCard>
        </ShakeAnimation>

          {/* Warning message when plot size requirements are not met */}
          {isPlotSizeInsufficient && (
            <div style={{
              backgroundColor: '#fee2e2',
              border: '2px solid #ef4444',
              borderRadius: '8px',
              padding: '16px',
              margin: '20px 0',
              textAlign: 'center'
            }}>
              <p style={{ color: '#dc2626', fontWeight: 700, fontSize: '1.1em', margin: 0 }}>
                ⚠️ You do not meet the minimum plot size requirements for this location.
              </p>
              <p style={{ color: '#7f1d1d', fontSize: '0.95em', marginTop: '8px', marginBottom: 0 }}>
                Please select a different location or ensure your plot meets the minimum frontage and depth requirements to proceed.
              </p>
            </div>
          )}

          {/* Application Type section - only visible when CNG Station Location is filled (Sr.No entered) */}
          <div style={{ 
            opacity: formData.locationSrNo ? ((isPlotSizeInsufficient || isFrontageUnanswered) ? 0.5 : 1) : 0,
            maxHeight: formData.locationSrNo ? 'none' : '0',
            overflow: formData.locationSrNo ? 'visible' : 'hidden',
            pointerEvents: (!formData.locationSrNo || isPlotSizeInsufficient || isFrontageUnanswered) ? 'none' : 'auto',
            transition: 'opacity 0.5s ease-out',
            transform: formData.locationSrNo ? 'translateY(0)' : 'translateY(20px)'
          }}>
          <ShakeAnimation $isShaking={isApplicationTypeShaking} ref={applicationTypeRef}>
          <SectionTitle as="div">
            Application Type:
            {mandatoryFields.applicationType && !formData.dealershipType && (
              <span style={{ 
                marginLeft: '8px',
                backgroundColor: '#fef2f2', 
                color: '#dc2626', 
                padding: '2px 8px', 
                borderRadius: '4px', 
                fontSize: '0.675rem', 
                fontWeight: 600,
                border: '1px solid #fecaca',
                textDecoration: 'none'
              }}>Required</span>
            )}
          </SectionTitle>
          {/* Main 5 options - always inline */}
          <RadioGroup style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 20px', alignItems: 'center' }}>
              <RadioOption>
                <input
                  type="radio"
                  id="dealership-type-individual"
                  name="dealershipType"
                  value="Individual"
                  checked={formData.dealershipType === 'Individual' || formData.dealershipType === 'OPC' || formData.dealershipType === 'Proprietorship'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      // Clear partner/director details when switching to Individual (single applicant)
                      setFormData(prev => ({
                        ...prev,
                        dealershipType: 'Individual',
                        partnerDirectorDetails: [],
                        numberOfPartners: 0,
                        entityName: ''
                      }));
                    }
                  }}
                  disabled={isMainFieldDisabled}
                />
                1) Individual
                <span title="1 person" style={{ marginLeft: '4px', cursor: 'help', color: '#4c6ef5', fontSize: '0.85em', fontWeight: 600 }}>ⓘ</span>
              </RadioOption>
              <RadioOption>
                <input
                  type="radio"
                  id="dealership-type-partnership"
                  name="dealershipType"
                  value="Partnership"
                  checked={formData.dealershipType === 'Partnership'}
                  onChange={(e) => {
                    if (e.target.checked && !formData.partnerDirectorDetails?.length) {
                      initializePartnerSetupModal('Partnership');
                    } else {
                      handleChange(e);
                    }
                  }}
                  disabled={isMainFieldDisabled}
                />
                2) Partnership
                <span title="Min 2 partners" style={{ marginLeft: '4px', cursor: 'help', color: '#4c6ef5', fontSize: '0.85em', fontWeight: 600 }}>ⓘ</span>
              </RadioOption>
              <RadioOption>
                <input
                  type="radio"
                  id="dealership-type-pvtltd"
                  name="dealershipType"
                  value="Pvt Ltd"
                  checked={formData.dealershipType === 'Pvt Ltd'}
                  onChange={(e) => {
                    if (e.target.checked && !formData.partnerDirectorDetails?.length) {
                      initializePartnerSetupModal('Pvt Ltd');
                    } else {
                      handleChange(e);
                    }
                  }}
                  disabled={isMainFieldDisabled}
                />
                3) Pvt. Ltd.
                <span title="Min 2 directors" style={{ marginLeft: '4px', cursor: 'help', color: '#4c6ef5', fontSize: '0.85em', fontWeight: 600 }}>ⓘ</span>
              </RadioOption>
              <RadioOption>
                <input
                  type="radio"
                  id="dealership-type-publicltd"
                  name="dealershipType"
                  value="Public Ltd"
                  checked={formData.dealershipType === 'Public Ltd'}
                  onChange={(e) => {
                    if (e.target.checked && !formData.partnerDirectorDetails?.length) {
                      initializePartnerSetupModal('Public Ltd');
                    } else {
                      handleChange(e);
                    }
                  }}
                  disabled={isMainFieldDisabled}
                />
                4) Public Ltd.
                <span title="Min 3 directors" style={{ marginLeft: '4px', cursor: 'help', color: '#4c6ef5', fontSize: '0.85em', fontWeight: 600 }}>ⓘ</span>
              </RadioOption>
              <RadioOption>
                <input
                  type="radio"
                  id="dealership-type-nonindividual"
                  name="dealershipType"
                  value="Non Individual applicant entity"
                  checked={formData.dealershipType === 'Non Individual applicant entity'}
                  onChange={(e) => {
                    if (e.target.checked && !formData.partnerDirectorDetails?.length) {
                      initializePartnerSetupModal('Non Individual applicant entity');
                    } else {
                      handleChange(e);
                    }
                  }}
                  disabled={isMainFieldDisabled}
                />
                5) Non-Individual
                <span title="1) Registered Society&#10;2) Charitable Trust&#10;3) Govt. Organisations/Bodies&#10;4) PSU" style={{ marginLeft: '4px', cursor: 'help', color: '#4c6ef5', fontSize: '0.85em', fontWeight: 600 }}>ⓘ</span>
              </RadioOption>
            </RadioGroup>
            </ShakeAnimation>
            
            {/* Sub-options for Non-Individual - appears directly below the main options */}
            {formData.dealershipType === 'Non Individual applicant entity' && (
              <div style={{ marginTop: '12px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                <Label>Type of Applicant</Label>
                <RadioGroup>
                  <RadioOption>
                    <input
                      type="radio"
                      name="niTypeOfApplicant"
                      value="Registered Society"
                      checked={formData.niTypeOfApplicant === 'Registered Society'}
                      onChange={handleChange}
                      disabled={isMainFieldDisabled}
                    />
                    1) Registered Society
                  </RadioOption>
                  <RadioOption>
                    <input
                      type="radio"
                      name="niTypeOfApplicant"
                      value="Charitable Trust"
                      checked={formData.niTypeOfApplicant === 'Charitable Trust'}
                      onChange={handleChange}
                      disabled={isMainFieldDisabled}
                    />
                    2) Charitable Trust
                  </RadioOption>
                  <RadioOption>
                    <input
                      type="radio"
                      name="niTypeOfApplicant"
                      value="Govt. Organisations/Bodies"
                      checked={formData.niTypeOfApplicant === 'Govt. Organisations/Bodies'}
                      onChange={handleChange}
                      disabled={isMainFieldDisabled}
                    />
                    3) Govt. Organisations/Bodies
                  </RadioOption>
                  <RadioOption>
                    <input
                      type="radio"
                      name="niTypeOfApplicant"
                      value="PSU"
                      checked={formData.niTypeOfApplicant === 'PSU'}
                      onChange={handleChange}
                      disabled={isMainFieldDisabled}
                    />
                    4) PSU
                  </RadioOption>
                </RadioGroup>
              </div>
            )}
            
            {/* Sub-options and fields appear below the main options */}
            {formData.dealershipType === 'Partnership' && (
              <div style={{ marginTop: '12px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                <Label htmlFor="entityName" style={{ fontSize: '0.9em', marginBottom: '4px' }}>Partnership Firm Name:</Label>
                <StyledInput
                  type="text"
                  id="entityName"
                  name="entityName"
                  value={formData.entityName || ''}
                  onChange={handleChange}
                  placeholder="Enter Partnership Firm Name"
                  disabled={isMainFieldDisabled}
                  style={{ maxWidth: '350px' }}
                />
              </div>
            )}
            {formData.dealershipType === 'Pvt Ltd' && (
              <div style={{ marginTop: '12px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                <Label htmlFor="entityName" style={{ fontSize: '0.9em', marginBottom: '4px' }}>Pvt. Ltd. Company Name:</Label>
                <StyledInput
                  type="text"
                  id="entityName"
                  name="entityName"
                  value={formData.entityName || ''}
                  onChange={handleChange}
                  placeholder="Enter Pvt. Ltd. Company Name"
                  disabled={isMainFieldDisabled}
                  style={{ maxWidth: '350px' }}
                />
              </div>
            )}
            {formData.dealershipType === 'Public Ltd' && (
              <div style={{ marginTop: '12px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                <Label htmlFor="entityName" style={{ fontSize: '0.9em', marginBottom: '4px' }}>Public Ltd. Company Name:</Label>
                <StyledInput
                  type="text"
                  id="entityName"
                  name="entityName"
                  value={formData.entityName || ''}
                  onChange={handleChange}
                  placeholder="Enter Public Ltd. Company Name"
                  disabled={isMainFieldDisabled}
                  style={{ maxWidth: '350px' }}
                />
              </div>
            )}
            
            {/* Individual sub-options */}
            {(formData.dealershipType === 'Individual' || formData.dealershipType === 'OPC' || formData.dealershipType === 'Proprietorship') && (
              <div style={{ marginTop: '12px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                <RadioGroup style={{ gap: '15px' }}>
                  <RadioOption>
                    <input
                      type="radio"
                      id="dealership-subtype-proprietorship"
                      name="individualSubType"
                      value="Proprietorship"
                      checked={formData.dealershipType === 'Proprietorship'}
                      onChange={() => {
                        // Ensure partner data is cleared for Proprietorship (single applicant)
                        setFormData(prev => ({
                          ...prev,
                          dealershipType: 'Proprietorship',
                          partnerDirectorDetails: [],
                          numberOfPartners: 0,
                          entityName: ''
                        }));
                      }}
                      disabled={isMainFieldDisabled}
                    />
                    a) Proprietorship
                  </RadioOption>
                  <RadioOption>
                    <input
                      type="radio"
                      id="dealership-subtype-opc"
                      name="individualSubType"
                      value="OPC"
                      checked={formData.dealershipType === 'OPC'}
                      onChange={() => {
                        // Ensure partner data is cleared for OPC (single applicant)
                        setFormData(prev => ({
                          ...prev,
                          dealershipType: 'OPC',
                          partnerDirectorDetails: [],
                          numberOfPartners: 0,
                          entityName: ''
                        }));
                      }}
                      disabled={isMainFieldDisabled}
                    />
                    b) OPC <span style={{ fontSize: '0.85em', color: '#666' }}>(One Person Company)</span>
                  </RadioOption>
                </RadioGroup>
              </div>
            )}

          {/* Company Registration Fields - shown for Pvt Ltd, Public Ltd, Non Individual */}
          {requiresCompanyRegistration && (
            <>
              <FormFieldRow style={{ marginTop: '15px', marginBottom: '10px' }}>
                <div style={{ flex: 1, marginRight: '20px' }}>
                  <Label>Company Registered in India</Label>
                  <RadioGroup>
                    <RadioOption>
                      <input
                        type="radio"
                        name="niCompanyRegisteredInIndia"
                        value="Yes"
                        checked={formData.niCompanyRegisteredInIndia === 'Yes'}
                        onChange={handleChange}
                        disabled={isMainFieldDisabled}
                      />
                      Yes
                    </RadioOption>
                    <RadioOption>
                      <input
                        type="radio"
                        name="niCompanyRegisteredInIndia"
                        value="No"
                        checked={formData.niCompanyRegisteredInIndia === 'No'}
                        onChange={handleChange}
                        disabled={isMainFieldDisabled}
                      />
                      No
                    </RadioOption>
                  </RadioGroup>
                </div>
                <div style={{ flex: 1 }}>
                  <Label htmlFor="niDateOfRegistrationIncorporation">Date Of Registration/Incorporation (DD/MM/YYYY)</Label>
                  <ModernDatePicker
                    id="niDateOfRegistrationIncorporation"
                    name="niDateOfRegistrationIncorporation"
                    value={formData.niDateOfRegistrationIncorporation}
                    onChange={(date) => handleChange({ target: { name: 'niDateOfRegistrationIncorporation', value: date } } as any)}
                    disabled={isMainFieldDisabled || isCompanyNotInIndia}
                    hasError={isCompanyTooYoung}
                    placeholder="dd-mm-yyyy"
                  />
                </div>
              </FormFieldRow>
              
              {/* Warning message for company not registered in India */}
              {isCompanyNotInIndia && (
                <div style={{ 
                  padding: '15px 20px', 
                  backgroundColor: '#fee2e2', 
                  borderRadius: '8px', 
                  border: '2px solid #ef4444',
                  marginBottom: '15px'
                }}>
                  <span style={{ fontSize: '1.3em', marginRight: '10px' }}>🚫</span>
                  <span style={{ fontWeight: 600, color: '#991b1b', fontSize: '0.95em' }}>
                    Only companies registered in India are eligible to apply. The form has been disabled.
                  </span>
                </div>
              )}
              
              {/* Warning message for company too young */}
              {isCompanyTooYoung && !isCompanyNotInIndia && (
                <div style={{ 
                  padding: '15px 20px', 
                  backgroundColor: '#fee2e2', 
                  borderRadius: '8px', 
                  border: '2px solid #ef4444',
                  marginBottom: '15px'
                }}>
                  <span style={{ fontSize: '1.3em', marginRight: '10px' }}>⏰</span>
                  <span style={{ fontWeight: 600, color: '#991b1b', fontSize: '0.95em' }}>
                    The company must be at least 3 years old from the date of registration/incorporation. The form has been disabled.
                  </span>
                </div>
              )}
            </>
          )}
          
          {/* Warning message for Non-Individual without sub-option selection */}
          {isNonIndividualWithoutSubOption && !isCompanyNotInIndia && !isCompanyTooYoung && (
            <div style={{ 
              padding: '15px 20px', 
              backgroundColor: '#fef3c7', 
              borderRadius: '8px', 
              border: '2px solid #f59e0b',
              marginTop: '15px',
              marginBottom: '15px'
            }}>
              <span style={{ fontSize: '1.3em', marginRight: '10px' }}>⚠️</span>
              <span style={{ fontWeight: 600, color: '#92400e', fontSize: '0.95em' }}>
                Please select a "Type of Applicant" sub-option above (Registered Society, Charitable Trust, Govt. Organisations/Bodies, or PSU) to continue filling the form.
              </span>
            </div>
          )}

          {/* Number of Partners/Directors input - shown for Partnership, Pvt Ltd, Public Ltd, Non Individual */}
          {/* For Pvt Ltd/Public Ltd, only show when both company registration conditions are met */}
          {requiresMultiplePartners && !isFormFrozen && isPvtLtdFormVisible && (
            <FormFieldFullWidth style={{ marginTop: '15px', marginBottom: '20px', padding: '15px', backgroundColor: '#f0f7ff', borderRadius: '8px', border: '1px solid #4A6CF7' }}>
              <Label htmlFor="numberOfPartners" style={{ fontWeight: 600, color: '#2c3e50' }}>
                Total number of {formData.dealershipType === 'Partnership' ? 'Partners' : 'Directors'} (including yourself): 
                <span style={{ color: '#e74c3c', marginLeft: '5px' }}>(Minimum: {getMinimumPartners(formData.dealershipType)})</span>
              </Label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                <StyledInput
                  type="number"
                  id="numberOfPartners"
                  name="numberOfPartners"
                  value={formData.numberOfPartners || getMinimumPartners(formData.dealershipType)}
                  onChange={(e) => handleNumberOfPartnersChange(parseInt(e.target.value) || getMinimumPartners(formData.dealershipType))}
                  min={getMinimumPartners(formData.dealershipType)}
                  max={20}
                  style={{ width: '100px', textAlign: 'center' }}
                  disabled={isMainFieldDisabled}
                />
                <span style={{ color: '#666', fontSize: '0.9em' }}>
                  total {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'partners' : 'directors'} (including you as the primary applicant)
                </span>
              </div>
              <p style={{ margin: '10px 0 0 0', fontSize: '0.85em', color: '#27ae60', fontWeight: 500 }}>
                <strong>Note:</strong> Your details (as the primary {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'partner' : 'director'}) are entered in Section 1 below. 
                {formData.numberOfPartners > 1 && ` Additional ${formData.numberOfPartners - 1} ${(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'partner' : 'director'}${formData.numberOfPartners > 2 ? 's' : ''} section${formData.numberOfPartners > 2 ? 's' : ''} will appear after Section 1.`}
              </p>
            </FormFieldFullWidth>
          )}

          {/* Message to select Type of Applicant first */}
          {!formData.dealershipType && (
            <div style={{ 
              marginTop: '20px', 
              padding: '20px', 
              backgroundColor: '#fff3cd', 
              borderRadius: '8px', 
              border: '1px solid #ffc107',
              textAlign: 'center'
            }}>
              <span style={{ fontSize: '1.5em', marginRight: '10px' }}>⚠️</span>
              <span style={{ fontWeight: 600, color: '#856404', fontSize: '1em' }}>
                Please select a "Type of Applicant" above to continue filling the form.
              </span>
            </div>
          )}

          {/* Message to select Individual sub-type (Proprietorship or OPC) */}
          {formData.dealershipType === 'Individual' && (
            <div style={{ 
              marginTop: '20px', 
              padding: '20px', 
              backgroundColor: '#fff3cd', 
              borderRadius: '8px', 
              border: '1px solid #ffc107',
              textAlign: 'center'
            }}>
              <span style={{ fontSize: '1.5em', marginRight: '10px' }}>⚠️</span>
              <span style={{ fontWeight: 600, color: '#856404', fontSize: '1em' }}>
                Please select a sub-type for Individual applicant (Proprietorship or OPC) above to continue filling the form.
              </span>
            </div>
          )}

          {/* Non Individual Applicant Entity Section - shown when "Non Individual applicant entity" or "Other" is selected */}
          {formData.dealershipType && (formData.dealershipType === 'Non Individual applicant entity' || formData.dealershipType === 'Other') ? (
            <>
              <SectionTitle>GENERAL INFORMATION (FOR NON INDIVIDUAL APPLICANT ENTITY)</SectionTitle>

              <FormFieldFullWidth>
                <Label htmlFor="niNameOfEntity">Name of Entity</Label>
                <StyledInput
                  type="text"
                  id="niNameOfEntity"
                  name="niNameOfEntity"
                  value={formData.niNameOfEntity}
                  onChange={handleChange}
                  disabled={isMainFieldDisabled || isFormFrozen}
                />
              </FormFieldFullWidth>

              <FormFieldFullWidth>
                <Label htmlFor="niNameOfAuthorizedSignatory">Name of Authorized Signatory</Label>
                <StyledInput
                  type="text"
                  id="niNameOfAuthorizedSignatory"
                  name="niNameOfAuthorizedSignatory"
                  value={formData.niNameOfAuthorizedSignatory}
                  onChange={handleChange}
                  disabled={isMainFieldDisabled || isFormFrozen}
                />
              </FormFieldFullWidth>

              {/* Contact Information Table */}
              <FormFieldFullWidth style={{ marginTop: '20px', padding: '15px', backgroundColor: isFormFrozen ? '#f1f5f9' : '#f8fafc', borderRadius: '8px', border: `1px solid ${isFormFrozen ? '#cbd5e1' : '#e2e8f0'}`, opacity: isFormFrozen ? 0.6 : 1 }}>
                <Label style={{ fontWeight: 600, marginBottom: '15px', color: '#2c3e50', display: 'block' }}>Contact Information</Label>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500, width: '25%' }}>Mobile No. (Primary)</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', width: '25%' }}>
                        <StyledInput type="text" id="niMobilePrimary" name="niMobilePrimary" value={formData.niMobilePrimary} onChange={handleChange} maxLength={10} placeholder="10-digit number" disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500, width: '25%' }}>Mobile No. (Alternate)</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', width: '25%' }}>
                        <StyledInput type="text" id="niMobileAlternate" name="niMobileAlternate" value={formData.niMobileAlternate} onChange={handleChange} maxLength={10} placeholder="10-digit number" disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Phone No. (Residential)</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niPhoneResidential" name="niPhoneResidential" value={formData.niPhoneResidential} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Phone No. (Official)</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niPhoneOfficial" name="niPhoneOfficial" value={formData.niPhoneOfficial} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Fax</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niFax" name="niFax" value={formData.niFax} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Email Id</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="email" id="niEmailId" name="niEmailId" value={formData.niEmailId} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </FormFieldFullWidth>

              {/* Profit/Loss Section - Table Style */}
              <FormFieldFullWidth style={{ marginTop: '20px', padding: '15px', backgroundColor: isFormFrozen ? '#f1f5f9' : '#f8fafc', borderRadius: '8px', border: `1px solid ${isFormFrozen ? '#cbd5e1' : '#e2e8f0'}`, opacity: isFormFrozen ? 0.6 : 1 }}>
                <Label style={{ fontWeight: 600, marginBottom: '15px', color: '#2c3e50', display: 'block' }}>Profit/Loss Made During the last 3 Financial Years as Certified by Chartered Accountant</Label>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '10px 12px', border: '1px solid #e2e8f0', backgroundColor: '#4A6CF7', color: 'white', fontWeight: 600, textAlign: 'left' }}>Year</th>
                      <th style={{ padding: '10px 12px', border: '1px solid #e2e8f0', backgroundColor: '#4A6CF7', color: 'white', fontWeight: 600, textAlign: 'left' }}>Profit After Tax In Rs.</th>
                      <th style={{ padding: '10px 12px', border: '1px solid #e2e8f0', backgroundColor: '#4A6CF7', color: 'white', fontWeight: 600, textAlign: 'left' }}>Loss In Rs.</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niFinancialYear1" name="niFinancialYear1" value={formData.niFinancialYear1} onChange={handleChange} placeholder="e.g., 2022-23" disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niProfitAfterTax1" name="niProfitAfterTax1" value={formData.niProfitAfterTax1} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niLoss1" name="niLoss1" value={formData.niLoss1} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niFinancialYear2" name="niFinancialYear2" value={formData.niFinancialYear2} onChange={handleChange} placeholder="e.g., 2023-24" disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niProfitAfterTax2" name="niProfitAfterTax2" value={formData.niProfitAfterTax2} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niLoss2" name="niLoss2" value={formData.niLoss2} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niFinancialYear3" name="niFinancialYear3" value={formData.niFinancialYear3} onChange={handleChange} placeholder="e.g., 2024-25" disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niProfitAfterTax3" name="niProfitAfterTax3" value={formData.niProfitAfterTax3} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niLoss3" name="niLoss3" value={formData.niLoss3} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </FormFieldFullWidth>

              {/* Contact Details - Current Address - Table Style */}
              <SectionTitle style={{ marginTop: '25px', opacity: isFormFrozen ? 0.6 : 1 }}>CONTACT DETAILS</SectionTitle>
              <FormFieldFullWidth style={{ padding: '15px', backgroundColor: isFormFrozen ? '#f1f5f9' : '#f8fafc', borderRadius: '8px', border: `1px solid ${isFormFrozen ? '#cbd5e1' : '#e2e8f0'}`, opacity: isFormFrozen ? 0.6 : 1 }}>
                <Label style={{ fontWeight: 600, marginBottom: '15px', color: '#2c3e50', display: 'block' }}>Full Current Address</Label>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500, width: '20%' }}>Flat/House No</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', width: '30%' }}>
                        <StyledInput type="text" id="niCurrentFlatHouseNo" name="niCurrentFlatHouseNo" value={formData.niCurrentFlatHouseNo} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500, width: '20%' }}>Building Name</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', width: '30%' }}>
                        <StyledInput type="text" id="niCurrentBuildingName" name="niCurrentBuildingName" value={formData.niCurrentBuildingName} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Plot No</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niCurrentPlotNo" name="niCurrentPlotNo" value={formData.niCurrentPlotNo} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Street Name</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niCurrentStreetName" name="niCurrentStreetName" value={formData.niCurrentStreetName} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Locality</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niCurrentLocality" name="niCurrentLocality" value={formData.niCurrentLocality} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Landmark</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niCurrentLandmark" name="niCurrentLandmark" value={formData.niCurrentLandmark} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>City/Town</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niCurrentCityTown" name="niCurrentCityTown" value={formData.niCurrentCityTown} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>District</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niCurrentDistrict" name="niCurrentDistrict" value={formData.niCurrentDistrict} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>State</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niCurrentState" name="niCurrentState" value={formData.niCurrentState} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>PIN Code</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niCurrentPinCode" name="niCurrentPinCode" value={formData.niCurrentPinCode} onChange={handleChange} maxLength={6} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Mobile No. (Primary)</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niCurrentMobilePrimary" name="niCurrentMobilePrimary" value={formData.niCurrentMobilePrimary} onChange={handleChange} maxLength={10} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Alternate Mobile No.</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niCurrentMobileAlternate" name="niCurrentMobileAlternate" value={formData.niCurrentMobileAlternate} onChange={handleChange} maxLength={10} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Phone No. (Residential)</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niCurrentPhoneResidential" name="niCurrentPhoneResidential" value={formData.niCurrentPhoneResidential} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Phone No. (Official)</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niCurrentPhoneOfficial" name="niCurrentPhoneOfficial" value={formData.niCurrentPhoneOfficial} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Fax</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niCurrentFax" name="niCurrentFax" value={formData.niCurrentFax} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Email Id</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="email" id="niCurrentEmailId" name="niCurrentEmailId" value={formData.niCurrentEmailId} onChange={handleChange} disabled={isMainFieldDisabled || isFormFrozen} style={{ margin: 0 }} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </FormFieldFullWidth>

              {/* Permanent Address - Table Style */}
              <FormFieldFullWidth style={{ marginTop: '20px', padding: '15px', backgroundColor: isFormFrozen ? '#f1f5f9' : '#f8fafc', borderRadius: '8px', border: `1px solid ${isFormFrozen ? '#cbd5e1' : '#e2e8f0'}`, opacity: (formData.niPermanentAddressSameAsCurrent || isFormFrozen) ? 0.6 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                  <input
                    type="checkbox"
                    id="niPermanentAddressSameAsCurrent"
                    name="niPermanentAddressSameAsCurrent"
                    checked={formData.niPermanentAddressSameAsCurrent}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setFormData(prev => ({
                        ...prev,
                        niPermanentAddressSameAsCurrent: isChecked,
                        niPermanentFlatHouseNo: isChecked ? prev.niCurrentFlatHouseNo : prev.niPermanentFlatHouseNo,
                        niPermanentBuildingName: isChecked ? prev.niCurrentBuildingName : prev.niPermanentBuildingName,
                        niPermanentPlotNo: isChecked ? prev.niCurrentPlotNo : prev.niPermanentPlotNo,
                        niPermanentStreetName: isChecked ? prev.niCurrentStreetName : prev.niPermanentStreetName,
                        niPermanentLocality: isChecked ? prev.niCurrentLocality : prev.niPermanentLocality,
                        niPermanentLandmark: isChecked ? prev.niCurrentLandmark : prev.niPermanentLandmark,
                        niPermanentCityTown: isChecked ? prev.niCurrentCityTown : prev.niPermanentCityTown,
                        niPermanentDistrict: isChecked ? prev.niCurrentDistrict : prev.niPermanentDistrict,
                        niPermanentState: isChecked ? prev.niCurrentState : prev.niPermanentState,
                        niPermanentPinCode: isChecked ? prev.niCurrentPinCode : prev.niPermanentPinCode,
                        niPermanentMobilePrimary: isChecked ? prev.niCurrentMobilePrimary : prev.niPermanentMobilePrimary,
                        niPermanentMobileAlternate: isChecked ? prev.niCurrentMobileAlternate : prev.niPermanentMobileAlternate,
                        niPermanentPhoneResidential: isChecked ? prev.niCurrentPhoneResidential : prev.niPermanentPhoneResidential,
                      }));
                    }}
                    disabled={isMainFieldDisabled || isFormFrozen}
                    style={{ width: '18px', height: '18px', cursor: isFormFrozen ? 'not-allowed' : 'pointer', accentColor: '#4A6CF7' }}
                  />
                  <label htmlFor="niPermanentAddressSameAsCurrent" style={{ fontSize: '0.95em', cursor: 'pointer', color: '#2c3e50', fontWeight: 500 }}>
                    IF Permanent address is same as the current address
                  </label>
                </div>

                <Label style={{ fontWeight: 600, marginBottom: '15px', color: '#2c3e50', display: 'block' }}>Full Permanent Address</Label>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500, width: '20%' }}>Flat/House No</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', width: '30%' }}>
                        <StyledInput type="text" id="niPermanentFlatHouseNo" name="niPermanentFlatHouseNo" value={formData.niPermanentFlatHouseNo} onChange={handleChange} disabled={!isOnline || isViewMode || formData.niPermanentAddressSameAsCurrent || isFormFrozen} style={{ margin: 0, backgroundColor: (formData.niPermanentAddressSameAsCurrent || isFormFrozen) ? '#f0f0f0' : 'white' }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500, width: '20%' }}>Building Name</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', width: '30%' }}>
                        <StyledInput type="text" id="niPermanentBuildingName" name="niPermanentBuildingName" value={formData.niPermanentBuildingName} onChange={handleChange} disabled={!isOnline || isViewMode || formData.niPermanentAddressSameAsCurrent || isFormFrozen} style={{ margin: 0, backgroundColor: (formData.niPermanentAddressSameAsCurrent || isFormFrozen) ? '#f0f0f0' : 'white' }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Plot No</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niPermanentPlotNo" name="niPermanentPlotNo" value={formData.niPermanentPlotNo} onChange={handleChange} disabled={!isOnline || isViewMode || formData.niPermanentAddressSameAsCurrent || isFormFrozen} style={{ margin: 0, backgroundColor: (formData.niPermanentAddressSameAsCurrent || isFormFrozen) ? '#f0f0f0' : 'white' }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Street Name</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niPermanentStreetName" name="niPermanentStreetName" value={formData.niPermanentStreetName} onChange={handleChange} disabled={!isOnline || isViewMode || formData.niPermanentAddressSameAsCurrent || isFormFrozen} style={{ margin: 0, backgroundColor: (formData.niPermanentAddressSameAsCurrent || isFormFrozen) ? '#f0f0f0' : 'white' }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Locality</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niPermanentLocality" name="niPermanentLocality" value={formData.niPermanentLocality} onChange={handleChange} disabled={!isOnline || isViewMode || formData.niPermanentAddressSameAsCurrent || isFormFrozen} style={{ margin: 0, backgroundColor: (formData.niPermanentAddressSameAsCurrent || isFormFrozen) ? '#f0f0f0' : 'white' }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Landmark</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niPermanentLandmark" name="niPermanentLandmark" value={formData.niPermanentLandmark} onChange={handleChange} disabled={!isOnline || isViewMode || formData.niPermanentAddressSameAsCurrent || isFormFrozen} style={{ margin: 0, backgroundColor: (formData.niPermanentAddressSameAsCurrent || isFormFrozen) ? '#f0f0f0' : 'white' }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>City/Town</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niPermanentCityTown" name="niPermanentCityTown" value={formData.niPermanentCityTown} onChange={handleChange} disabled={!isOnline || isViewMode || formData.niPermanentAddressSameAsCurrent || isFormFrozen} style={{ margin: 0, backgroundColor: (formData.niPermanentAddressSameAsCurrent || isFormFrozen) ? '#f0f0f0' : 'white' }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>District</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niPermanentDistrict" name="niPermanentDistrict" value={formData.niPermanentDistrict} onChange={handleChange} disabled={!isOnline || isViewMode || formData.niPermanentAddressSameAsCurrent || isFormFrozen} style={{ margin: 0, backgroundColor: (formData.niPermanentAddressSameAsCurrent || isFormFrozen) ? '#f0f0f0' : 'white' }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>State</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niPermanentState" name="niPermanentState" value={formData.niPermanentState} onChange={handleChange} disabled={!isOnline || isViewMode || formData.niPermanentAddressSameAsCurrent || isFormFrozen} style={{ margin: 0, backgroundColor: (formData.niPermanentAddressSameAsCurrent || isFormFrozen) ? '#f0f0f0' : 'white' }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>PIN Code</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niPermanentPinCode" name="niPermanentPinCode" value={formData.niPermanentPinCode} onChange={handleChange} maxLength={6} disabled={!isOnline || isViewMode || formData.niPermanentAddressSameAsCurrent || isFormFrozen} style={{ margin: 0, backgroundColor: (formData.niPermanentAddressSameAsCurrent || isFormFrozen) ? '#f0f0f0' : 'white' }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Mobile No. (Primary)</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niPermanentMobilePrimary" name="niPermanentMobilePrimary" value={formData.niPermanentMobilePrimary} onChange={handleChange} maxLength={10} disabled={!isOnline || isViewMode || formData.niPermanentAddressSameAsCurrent || isFormFrozen} style={{ margin: 0, backgroundColor: (formData.niPermanentAddressSameAsCurrent || isFormFrozen) ? '#f0f0f0' : 'white' }} />
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Mobile No. (Alternate)</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niPermanentMobileAlternate" name="niPermanentMobileAlternate" value={formData.niPermanentMobileAlternate} onChange={handleChange} maxLength={10} disabled={!isOnline || isViewMode || formData.niPermanentAddressSameAsCurrent || isFormFrozen} style={{ margin: 0, backgroundColor: (formData.niPermanentAddressSameAsCurrent || isFormFrozen) ? '#f0f0f0' : 'white' }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 500 }}>Phone No. (Residential)</td>
                      <td colSpan={3} style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <StyledInput type="text" id="niPermanentPhoneResidential" name="niPermanentPhoneResidential" value={formData.niPermanentPhoneResidential} onChange={handleChange} disabled={!isOnline || isViewMode || formData.niPermanentAddressSameAsCurrent || isFormFrozen} style={{ margin: 0, backgroundColor: (formData.niPermanentAddressSameAsCurrent || isFormFrozen) ? '#f0f0f0' : 'white' }} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </FormFieldFullWidth>

              {/* APPENDIX-B AFFIDAVIT SECTION - Conditionally shown based on admin Appendix Logic settings */}
              {visibleAppendixes.includes('B') && (
              <FormFieldFullWidth style={{ marginTop: '25px', padding: '15px', backgroundColor: isFormFrozen ? '#f1f5f9' : '#e8f6f3', borderRadius: '8px', border: `1px solid ${isFormFrozen ? '#cbd5e1' : '#a3e4d7'}`, opacity: isFormFrozen ? 0.6 : 1 }}>
                <Label style={{ fontWeight: 700, marginBottom: '15px', color: '#0e6251', display: 'block', fontSize: '0.9em', textAlign: 'center', textDecoration: 'underline', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                  APPENDIX – B: AFFIDAVIT FOR CO-OPERATIVE SOCIETIES / ORGANISED BODIES / CHARITABLE TRUSTS / CORPORATE HOUSES
                </Label>
                <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px dashed #d5dbdb' }}>
                  <p style={{ margin: '0 0 10px 0', fontWeight: 600, color: '#1a5276' }}>
                    📋 Instructions:
                  </p>
                  <ol style={{ margin: '0', paddingLeft: '20px', lineHeight: '1.6' }}>
                    <li>Download the Affidavit template below</li>
                    <li>Print it on appropriate Non-Judicial Stamp Paper of Rs. 500 value</li>
                    <li>Fill in your entity details and get it notarized by a Magistrate/Judge/Notary Public</li>
                    <li>Scan the notarized document and upload it here</li>
                  </ol>
                </div>
                
                <div style={{ padding: '12px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0', marginBottom: '15px', maxHeight: '200px', overflowY: 'auto', fontSize: '0.8em', lineHeight: '1.5' }}>
                  <p style={{ textAlign: 'center', fontWeight: 700, marginBottom: '10px' }}>AFFIDAVIT FORMAT (Preview)</p>
                  <p style={{ fontStyle: 'italic', color: '#666', textAlign: 'center', marginBottom: '10px' }}>(TO BE TYPED ON APPROPRIATE NON-JUDICIAL STAMP PAPER OF REQUIRED VALUE)</p>
                  <p style={{ fontWeight: 600 }}>AFFIDAVIT TO BE SUBMITTED BY REGISTERED CO-OPERATIVE SOCIETIES / ORGANISED BODIES / CHARITABLE TRUSTS / CORPORATE HOUSES</p>
                  <p>In the matter of offer of land for CNG Station on long term lease basis and Dealership of such CNG station of Maharashtra Natural Gas Limited at ____ (Location).</p>
                  <p>Whereas ____ (Name of the Co-operative Society / organised body) having its address at ____ has offered land and applied for Dealership...</p>
                  <ol style={{ paddingLeft: '20px', fontSize: '0.9em' }}>
                    <li>That through a resolution passed by the Co-operative Society / organised body, I have been authorised to offer the said land...</li>
                    <li>That the said Co-operative Society / organised body is duly registered...</li>
                    <li>That on behalf of the Co-operative Society / organised body, I undertake to observe all relevant guidelines...</li>
                    <li>That none of the members have been convicted for criminal offences...</li>
                    <li>...and more clauses as per the full template</li>
                  </ol>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center' }}>
                  <a
                    href="/templates/Appendix_B_Affidavit_Template.html"
                    target="_blank"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      backgroundColor: '#1abc9c',
                      color: 'white',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontSize: '0.9em',
                      fontWeight: 500
                    }}
                  >
                    📥 Download Template
                  </a>
                  
                  <FileUploadButton
                    onClick={(e) => {
                      e.preventDefault();
                      affidavitBFileInputRef.current?.click();
                    }}
                    disabled={isAffidavitBFileUploading || !isOnline || isViewMode || isFormFrozen}
                  >
                    {formData.affidavitBFile ? 'Change Affidavit' : 'Upload Notarized Affidavit'}
                  </FileUploadButton>
                  
                  {isAffidavitBFileUploading && (
                    <div style={{ flex: 1, minWidth: '150px' }}>
                      <FileUploadProgressBar>
                        <FileUploadProgressFill progress={affidavitBFileProgress} />
                      </FileUploadProgressBar>
                    </div>
                  )}
                </div>
                
                <input
                  type="file"
                  ref={affidavitBFileInputRef}
                  accept=".jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      handleProofFileUpload(e.target.files[0], 'affidavitBFile');
                    }
                  }}
                  disabled={isMainFieldDisabled}
                />
                
                {affidavitBFileUploadMessage && (
                  <FileUploadSuccess style={{ marginTop: '10px' }}>
                    ✓ {affidavitBFileUploadMessage}
                  </FileUploadSuccess>
                )}
                
                {formData.affidavitBFile && !isAffidavitBFileUploading && !affidavitBFileUploadMessage && (
                  <FileUploadStatus style={{ marginTop: '10px' }}>
                    File: <ClickableFileName onClick={() => handleOpenFilePreview(formData.affidavitBFile, 'affidavitBFile')}>
                      {typeof formData.affidavitBFile === 'string' ? formData.affidavitBFile : formData.affidavitBFile?.name}
                    </ClickableFileName>
                  </FileUploadStatus>
                )}
              </FormFieldFullWidth>
              )}
            </>
          ) : formData.dealershipType && formData.dealershipType !== 'Individual' && isPvtLtdFormVisible ? (
            <>
              {/* Individual Section - shown for all other dealership types (excluding just 'Individual' without sub-option) */}
              {/* For Pvt Ltd/Public Ltd, only show when both company registration conditions are met */}
              <SlideDownContainer>
              <SectionTitle>
                {formData.dealershipType === 'Partnership' 
                  ? `1. Partner No.1 (Main Applicant):${formData.nameInBlocks ? ` (${formData.nameInBlocks})` : ''}` 
                  : (formData.dealershipType === 'Pvt Ltd' || formData.dealershipType === 'Public Ltd')
                    ? `1. Director No.1 (Main Applicant):${formData.nameInBlocks ? ` (${formData.nameInBlocks})` : ''}`
                    : `1. Individual:${formData.nameInBlocks ? ` (${formData.nameInBlocks})` : ''}`}
              </SectionTitle>

          {/* PAN Card Autofill Section - Prominent placement to encourage upload */}
          <ShakeAnimation $isShaking={isPanCardUploadShaking} ref={panCardUploadRef}>
          <FormFieldFullWidth style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e8f4fd', borderRadius: '8px', border: '2px dashed #3498db' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <span style={{ fontSize: '1.5em' }}>🪪</span>
              <Label style={{ margin: 0, color: '#1a5276', fontWeight: 700, fontSize: '1em' }}>Upload PAN Card to Auto-fill Name & Date of Birth</Label>
              {mandatoryFields.panCardUpload && !formData.panCardFile && (
                <span style={{ 
                  backgroundColor: '#fef2f2', 
                  color: '#dc2626', 
                  padding: '2px 8px', 
                  borderRadius: '4px', 
                  fontSize: '0.675rem', 
                  fontWeight: 600,
                  border: '1px solid #fecaca',
                  textDecoration: 'none'
                }}>Required</span>
              )}
              {isMainApplicantSectionLocked && <span style={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '10px' }}>🔒 Disabled</span>}
            </div>
            <p style={{ margin: '0 0 12px 0', fontSize: '0.9em', color: '#2c3e50', lineHeight: '1.4' }}>
              <strong>Recommended:</strong> Upload your PAN Card image and the fields below (<em>Full Name, Father/Spouse Name, Date of Birth</em>) will be <span style={{ color: '#27ae60', fontWeight: 600 }}>automatically filled</span>.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  if (isMainFieldDisabled) { showDisabledTooltip(e); return; }
                  panFileInputRef.current?.click();
                }}
                disabled={isPanFileUploading || isMainFieldDisabled}
                style={{
                  padding: '14px 28px',
                  fontSize: '1.05em',
                  fontWeight: 700,
                  backgroundColor: formData.panCardFile ? '#27ae60' : '#2ecc71',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isPanFileUploading || isMainFieldDisabled ? 'not-allowed' : 'pointer',
                  opacity: isPanFileUploading || isMainFieldDisabled ? 0.6 : 1,
                  boxShadow: '0 4px 12px rgba(46, 204, 113, 0.4)',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span style={{ fontSize: '1.2em' }}>📤</span>
                {formData.panCardFile ? 'Change PAN Card' : 'Upload PAN Card'}
              </button>

              {isPanFileUploading && (
                <div style={{ flex: 1, minWidth: '160px' }}>
                  <FileUploadProgressBar>
                    <FileUploadProgressFill progress={panFileProgress} />
                  </FileUploadProgressBar>
                </div>
              )}
            </div>

            <input
              type="file"
              accept=".jpg,.jpeg,.png"
              ref={panFileInputRef}
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  handlePanCardUpload(e.target.files[0]);
                }
              }}
              disabled={isMainFieldDisabled}
            />

            {/* Skeleton loader while uploading */}
            {isPanFileUploading && (
              <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <SkeletonBox />
                <span style={{ color: '#666', fontSize: '0.9em' }}>Processing PAN Card...</span>
              </div>
            )}

            {/* File display - show immediately after upload completes */}
            {formData.panCardFile && !isPanFileUploading && (
              <FileUploadStatus style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span>File: <ClickableFileName onClick={() => handleOpenFilePreview(formData.panCardFile!, 'panCardFile')}>
                    {typeof formData.panCardFile === 'string' ? formData.panCardFile : formData.panCardFile?.name}
                  </ClickableFileName></span>
                </div>
                {/* Image preview for image files */}
                {typeof formData.panCardFile === 'string' && formData.panCardFile.match(/\.(jpeg|jpg|gif|png)$/i) && (
                  <img
                    src={(() => {
                      const rawApiBaseUrl = process.env.REACT_APP_API_URL || '/api';
                      let serverBase = window.location.origin;
                      if (rawApiBaseUrl.startsWith('http')) {
                        try {
                          const parsed = new URL(rawApiBaseUrl);
                          serverBase = `${parsed.protocol}//${parsed.host}`;
                        } catch { /* use default */ }
                      }
                      let path = formData.panCardFile as string;
                      if (path.includes('://')) {
                        try { path = new URL(path).pathname; } catch { /* keep as is */ }
                      }
                      if (!path.startsWith('/')) {
                        path = path.startsWith('uploads/') ? `/${path}` : `/uploads/${path}`;
                      }
                      return `${serverBase}${path}`;
                    })()}
                    alt="PAN Card Preview"
                    onClick={() => handleOpenFilePreview(formData.panCardFile!, 'panCardFile')}
                    style={{
                      width: '120px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
                {/* View button for PDF files */}
                {typeof formData.panCardFile === 'string' && formData.panCardFile.match(/\.pdf$/i) && (
                  <button
                    type="button"
                    onClick={() => handleOpenFilePreview(formData.panCardFile!, 'panCardFile')}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#9b59b6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.9em',
                      fontWeight: 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    📄 View PAN Card
                  </button>
                )}
              </FileUploadStatus>
            )}
          </FormFieldFullWidth>
          </ShakeAnimation>

          {/* Collapsible toggle for manual entry fields */}
          {!showPanFields && !formData.panCardFile && (
            <div 
              onClick={() => setShowPanFields(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                backgroundColor: '#fef9e7',
                border: '1px dashed #f39c12',
                borderRadius: '8px',
                cursor: 'pointer',
                marginBottom: '15px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fdebd0'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fef9e7'; }}
            >
              <span style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: '24px', 
                height: '24px', 
                borderRadius: '50%', 
                backgroundColor: '#f39c12', 
                color: 'white',
                fontSize: '14px',
                fontWeight: 'bold',
                transition: 'transform 0.3s ease'
              }}>▼</span>
              <span style={{ color: '#7f6c3e', fontSize: '0.9em' }}>
                <strong>Or enter details manually</strong> (Click to expand Name, Father's Name & DOB fields)
              </span>
            </div>
          )}

          {/* Name fields - will be auto-filled from PAN Card - Collapsible with animation */}
          <div style={{
            maxHeight: showPanFields || formData.panCardFile ? '1000px' : '0',
            overflow: 'hidden',
            transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease',
            opacity: showPanFields || formData.panCardFile ? 1 : 0
          }}>
            {(showPanFields || formData.panCardFile) && (
              <div style={{ 
                padding: '15px', 
                backgroundColor: formData.panCardFile ? '#f0fff4' : '#fffbeb', 
                borderRadius: '8px', 
                border: formData.panCardFile ? '1px solid #a3e4a3' : '1px solid #fcd34d',
                marginBottom: '15px'
              }}>
                {!formData.panCardFile && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span style={{ fontSize: '0.85em', color: '#92400e', fontStyle: 'italic' }}>
                      ⚠️ Manual entry - Consider uploading PAN card above for accurate auto-fill
                    </span>
                    <button
                      onClick={() => setShowPanFields(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#92400e',
                        cursor: 'pointer',
                        fontSize: '1.2em',
                        padding: '0 4px'
                      }}
                      title="Collapse"
                    >▲</button>
                  </div>
                )}
                <FormFieldRow style={{ margin: 0 }}>
                  <div style={{ flex: 1, marginRight: '20px', display: 'flex', flexDirection: 'column' }}>
                    <Label htmlFor="nameInBlocks" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      a) Full Name of Applicant (Block Letters)
                      {formData.panCardFile && <span style={{ fontSize: '0.75em', color: '#27ae60', fontWeight: 600 }}>✓ Auto-filled</span>}
                    </Label>
                    <StyledInput
                      type="text"
                      id="nameInBlocks"
                      name="nameInBlocks"
                      value={formData.nameInBlocks}
                      onChange={handleChange}
                      onBlur={handleNameBlur}
                      disabled={isMainFieldDisabled}
                      style={{ backgroundColor: formData.panCardFile ? '#f0fff4' : 'white' }}
                    />
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Label htmlFor="fatherOrHusbandName" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      b) Father / Mother / Husband Name
                      {formData.panCardFile && <span style={{ fontSize: '0.75em', color: '#27ae60', fontWeight: 600 }}>✓ Auto-filled</span>}
                    </Label>
                    <StyledInput
                      type="text"
                      id="fatherOrHusbandName"
                      name="fatherOrHusbandName"
                      value={formData.fatherOrHusbandName}
                      onChange={handleChange}
                      disabled={isMainFieldDisabled}
                      style={{ backgroundColor: formData.panCardFile ? '#f0fff4' : 'white' }}
                    />
                  </div>
                </FormFieldRow>
              </div>
            )}
          </div>

          {/* DOB field - also part of collapsible section */}
          <div style={{
            maxHeight: showPanFields || formData.panCardFile ? '500px' : '0',
            overflow: 'hidden',
            transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease',
            opacity: showPanFields || formData.panCardFile ? 1 : 0
          }}>
            {(showPanFields || formData.panCardFile) && (
              <div style={{ 
                padding: '15px', 
                backgroundColor: formData.panCardFile ? '#f0fff4' : '#fffbeb', 
                borderRadius: '8px', 
                border: formData.panCardFile ? '1px solid #a3e4a3' : '1px solid #fcd34d',
                marginBottom: '15px'
              }}>
                <FormFieldFullWidth style={{ margin: 0 }}>
                  <Label htmlFor="dateOfBirth" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    c) Date of Birth (Attach proof of date of birth):
                    {formData.panCardFile && <span style={{ fontSize: '0.75em', color: '#27ae60', fontWeight: 600 }}>✓ Auto-filled</span>}
                  </Label>
                  <StyledInput type="date" id="dateOfBirth" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} disabled={isMainFieldDisabled} style={{ backgroundColor: formData.panCardFile ? '#f0fff4' : 'white' }} />
                  {dobAge !== null && (
                    <p style={{ marginTop: '6px', fontSize: '0.85em', color: dobAgeError ? '#c53030' : '#2f855a' }}>
                      Current age as of today: {dobAge} years
                    </p>
                  )}
                  {dobAgeError && (
                    <p style={{ marginTop: '4px', fontSize: '0.85em', color: '#c53030' }}>
                      {dobAgeError}
                    </p>
                  )}
                  
                  {formData.panCardFile ? (
                    <FileUploadStatus style={{ marginTop: '8px' }}>
                      <span style={{ color: '#2f855a', fontWeight: 500 }}>✓ PAN Card serves as Date of Birth proof</span>
                    </FileUploadStatus>
                  ) : (
                    <>
                      <FileUploadButton 
                        onClick={(e) => {
                          e.preventDefault();
                          dobFileInputRef.current?.click();
                        }}
                        disabled={isDobFileUploading || !isOnline || isViewMode}
                      >
                        {formData.dobProofFile ? 'Change DOB Proof' : 'Upload DOB Proof'}
                      </FileUploadButton>
                      
                      <input 
                        type="file" 
                        accept=".jpg,.jpeg,.png" 
                        ref={dobFileInputRef}
                        style={{ display: 'none' }} 
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            handleProofFileUpload(e.target.files[0], 'dobProofFile');
                          }
                        }}
                        disabled={isMainFieldDisabled}
                      />
                      
                      {isDobFileUploading && (
                        <FileUploadProgressBar>
                          <FileUploadProgressFill progress={dobFileProgress} />
                        </FileUploadProgressBar>
                      )}
                      
                      {dobFileUploadMessage && (
                        <FileUploadSuccess>
                          ✓ {dobFileUploadMessage}
                        </FileUploadSuccess>
                      )}
                      
                      {formData.dobProofFile && !isDobFileUploading && !dobFileUploadMessage && (
                        <FileUploadStatus>
                          File: <ClickableFileName onClick={() => handleOpenFilePreview(formData.dobProofFile, 'dobProofFile')}>
                            {typeof formData.dobProofFile === 'string' ? formData.dobProofFile : formData.dobProofFile?.name}
                          </ClickableFileName>
                        </FileUploadStatus>
                      )}
                    </>
                  )}
                </FormFieldFullWidth>
              </div>
            )}
          </div>

          <ShakeAnimation $isShaking={isClass10Shaking} ref={class10QuestionRef}>
            <FormFieldFullWidth style={{ 
              opacity: dobAgeError ? 0.5 : 1,
              padding: '15px',
              backgroundColor: isClass10Shaking ? '#fff5f5' : (!formData.isMinimumEducationMet ? '#fffbeb' : 'transparent'),
              border: isClass10Shaking ? '2px solid #e53e3e' : (!formData.isMinimumEducationMet ? '1px dashed #f59e0b' : 'none'),
              borderRadius: '8px',
              transition: 'all 0.3s ease'
            }}>
              <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                d) Are you minimum Class 10 (High School) pass?
                {!formData.isMinimumEducationMet && (
                  <span style={{ 
                    fontSize: '0.75em', 
                    color: '#d97706', 
                    backgroundColor: '#fef3c7', 
                    padding: '2px 8px', 
                    borderRadius: '4px',
                    fontWeight: 600
                  }}>
                    Required
                  </span>
                )}
              </Label>
              <RadioGroup>
                <RadioOption>
                  <input 
                    type="radio" 
                    id="isMinimumEducationMet-yes" 
                    name="isMinimumEducationMet" 
                    value="Yes" 
                    checked={formData.isMinimumEducationMet === "Yes"}
                    onChange={handleChange} 
                    disabled={isMainFieldDisabled || !!dobAgeError} />
                  Yes
                </RadioOption>
                <RadioOption>
                  <input 
                    type="radio" 
                    id="isMinimumEducationMet-no" 
                    name="isMinimumEducationMet" 
                    value="No" 
                    checked={formData.isMinimumEducationMet === "No"}
                    onChange={handleChange} 
                    disabled={isMainFieldDisabled || !!dobAgeError} />
                  No
                </RadioOption>
              </RadioGroup>
              {formData.isMinimumEducationMet === 'No' && (
                <p style={{ marginTop: '8px', fontSize: '0.85em', color: '#c53030' }}>
                  You cannot proceed further without minimum Intermediate (Class 10) qualification.
                </p>
              )}
            </FormFieldFullWidth>
          </ShakeAnimation>

          <ShakeAnimation $isShaking={isCurrentAddressShaking} ref={currentAddressRef}>
          <FormFieldFullWidth style={{ opacity: (dobAgeError || formData.isMinimumEducationMet === 'No') ? 0.5 : 1 }}>
            <Label htmlFor="currentAddress">
              e) Current Address:
              {mandatoryFields.currentAddress && !formData.currentAddress && !formData.addressProofFile && (
                <span style={{ 
                  marginLeft: '8px',
                  backgroundColor: '#fef2f2', 
                  color: '#dc2626', 
                  padding: '2px 8px', 
                  borderRadius: '4px', 
                  fontSize: '0.675rem', 
                  fontWeight: 600,
                  border: '1px solid #fecaca',
                  textDecoration: 'none'
                }}>Required</span>
              )}
            </Label>
            
            {/* Address Proof Upload - Encouraged First */}
            <div style={{ 
              marginBottom: '15px', 
              padding: '12px 15px', 
              backgroundColor: '#e8f4fd', 
              borderRadius: '8px', 
              border: '2px dashed #3498db'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '1.3em' }}>📄</span>
                <span style={{ fontWeight: 600, color: '#1a5276', fontSize: '0.95em' }}>
                  Upload Address Proof for Auto-fill (Recommended)
                </span>
                {mandatoryFields.currentAddress && !formData.addressProofFile && (
                  <span style={{ 
                    backgroundColor: '#fef2f2', 
                    color: '#dc2626', 
                    padding: '2px 8px', 
                    borderRadius: '4px', 
                    fontSize: '0.675rem', 
                    fontWeight: 600,
                    border: '1px solid #fecaca',
                    textDecoration: 'none'
                  }}>Required</span>
                )}
                {isMainApplicantSectionLocked && <span style={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '10px' }}>🔒 Disabled</span>}
                <span
                  title="AI-Validated Address Proof Documents:&#10;&#10;✓ Aadhaar Card&#10;✓ Passport&#10;✓ Voter ID Card&#10;✓ Driving License&#10;✓ Electricity Bill (not older than 3 months)&#10;✓ Gas Connection Bill&#10;✓ Water Bill (not older than 3 months)&#10;✓ Postpaid Mobile Phone Bill (not older than 3 months)&#10;&#10;Upload any of these documents and our AI will automatically extract your address!"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #3498db, #2980b9)',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'help',
                    boxShadow: '0 2px 4px rgba(52, 152, 219, 0.3)'
                  }}
                >
                  ?
                </span>
              </div>
              <p style={{ margin: '0 0 10px 0', fontSize: '0.85em', color: '#2c3e50', lineHeight: '1.4' }}>
                Details will be autofilled
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <FileUploadButton 
                  onClick={(e) => {
                    e.preventDefault();
                    if (isMainFieldDisabled) { showDisabledTooltip(e); return; }
                    addressFileInputRef.current?.click();
                  }}
                  disabled={isAddressFileUploading || isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No'}
                  style={{ 
                    background: formData.addressProofFile ? 'linear-gradient(135deg, #27ae60, #2ecc71)' : 'linear-gradient(135deg, #3498db, #2980b9)',
                    padding: '10px 20px'
                  }}
                >
                  {formData.addressProofFile ? '✓ Change Address Proof' : '📤 Upload Address Proof'}
                </FileUploadButton>
                <span 
                  title="For Aadhaar Card: Upload a SINGLE image containing BOTH front side (with photo) and back side (with address) placed side by side. Other accepted documents: Passport, Voter ID, Driving License, Utility Bills."
                  style={{ 
                    cursor: 'help', 
                    color: '#3498db', 
                    fontSize: '1.2em',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >ⓘ</span>
                {isAddressFileUploading && (
                  <div style={{ flex: 1, minWidth: '160px' }}>
                    <FileUploadProgressBar>
                      <FileUploadProgressFill progress={addressFileProgress} />
                    </FileUploadProgressBar>
                  </div>
                )}
              </div>

              {/* Skeleton loader while uploading */}
              {isAddressFileUploading && (
                <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <SkeletonBox />
                  <span style={{ color: '#666', fontSize: '0.9em' }}>Processing Address Proof...</span>
                </div>
              )}

              {/* File display with thumbnail - show immediately after upload completes */}
              {formData.addressProofFile && !isAddressFileUploading && (
                <FileUploadStatus style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span>File: <ClickableFileName onClick={() => handleOpenFilePreview(formData.addressProofFile, 'addressProofFile')}>
                      {typeof formData.addressProofFile === 'string' ? formData.addressProofFile : formData.addressProofFile?.name}
                    </ClickableFileName></span>
                  </div>
                  {/* Image thumbnail preview for image files */}
                  {typeof formData.addressProofFile === 'string' && formData.addressProofFile.match(/\.(jpeg|jpg|gif|png)$/i) && (
                    <img
                      src={(() => {
                        const rawApiBaseUrl = process.env.REACT_APP_API_URL || '/api';
                        let serverBase = window.location.origin;
                        if (rawApiBaseUrl.startsWith('http')) {
                          try {
                            const parsed = new URL(rawApiBaseUrl);
                            serverBase = `${parsed.protocol}//${parsed.host}`;
                          } catch { /* use default */ }
                        }
                        let path = formData.addressProofFile as string;
                        if (path.includes('://')) {
                          try { path = new URL(path).pathname; } catch { /* keep as is */ }
                        }
                        if (!path.startsWith('/')) {
                          path = path.startsWith('uploads/') ? `/${path}` : `/uploads/${path}`;
                        }
                        return `${serverBase}${path}`;
                      })()}
                      alt="Address Proof Preview"
                      onClick={() => handleOpenFilePreview(formData.addressProofFile!, 'addressProofFile')}
                      style={{
                        width: '120px',
                        height: '80px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        cursor: 'pointer',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                  {/* View button for PDF files */}
                  {typeof formData.addressProofFile === 'string' && formData.addressProofFile.match(/\.pdf$/i) && (
                    <button
                      type="button"
                      onClick={() => handleOpenFilePreview(formData.addressProofFile!, 'addressProofFile')}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#9b59b6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.9em',
                        fontWeight: 500,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      📄 View Address Proof
                    </button>
                  )}
                </FileUploadStatus>
              )}
            </div>
            
            {/* Collapsible toggle for manual address entry */}
            {!showAddressFields && !formData.addressProofFile && (
              <div 
                onClick={() => setShowAddressFields(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  backgroundColor: '#fef9e7',
                  border: '1px dashed #f39c12',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  marginBottom: '15px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fdebd0'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fef9e7'; }}
              >
                <span style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%', 
                  backgroundColor: '#f39c12', 
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>▼</span>
                <span style={{ color: '#7f6c3e', fontSize: '0.9em' }}>
                  <strong>Or enter address manually</strong> (Click to expand Current & Permanent Address fields)
                </span>
              </div>
            )}

            {/* Address fields - Collapsible with animation */}
            <div style={{
              maxHeight: showAddressFields || formData.addressProofFile ? '800px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease',
              opacity: showAddressFields || formData.addressProofFile ? 1 : 0
            }}>
              {(showAddressFields || formData.addressProofFile) && (
                <div style={{ 
                  padding: '15px', 
                  backgroundColor: formData.addressProofFile ? '#f0fff4' : '#fffbeb', 
                  borderRadius: '8px', 
                  border: formData.addressProofFile ? '1px solid #a3e4a3' : '1px solid #fcd34d',
                  marginBottom: '15px'
                }}>
                  {!formData.addressProofFile && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <span style={{ fontSize: '0.85em', color: '#92400e', fontStyle: 'italic' }}>
                        ⚠️ Manual entry - Consider uploading address proof above for accurate auto-fill
                      </span>
                      <button
                        onClick={() => setShowAddressFields(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#92400e',
                          cursor: 'pointer',
                          fontSize: '1.2em',
                          padding: '0 4px'
                        }}
                        title="Collapse"
                      >▲</button>
                    </div>
                  )}
                  
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '0.9em', fontWeight: 600, color: '#2c3e50' }}>Current Address:</span>
                      {formData.addressProofFile && <span style={{ fontSize: '0.75em', color: '#27ae60', fontWeight: 600 }}>✓ Auto-filled</span>}
                    </div>
                    <StyledTextArea 
                      id="currentAddress" 
                      name="currentAddress" 
                      value={formData.currentAddress} 
                      onChange={(e) => {
                        handleChange(e);
                        if (formData.permanentAddressSameAsCurrent) {
                          setFormData(prev => ({ ...prev, permanentAddress: e.target.value }));
                        }
                      }} 
                      rows={3} 
                      placeholder="Enter your complete current address..."
                      disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No'} 
                      style={{ margin: 0, backgroundColor: formData.addressProofFile ? '#f0fff4' : 'white' }}
                    />
                  </div>
                  
                  <div style={{ margin: '12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      id="permanentAddressSameAsCurrent"
                      name="permanentAddressSameAsCurrent"
                      checked={formData.permanentAddressSameAsCurrent}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setFormData(prev => ({
                          ...prev,
                          permanentAddressSameAsCurrent: isChecked,
                          permanentAddress: isChecked ? prev.currentAddress : prev.permanentAddress
                        }));
                      }}
                      disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No'}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <label 
                      htmlFor="permanentAddressSameAsCurrent" 
                      style={{ fontSize: '0.9em', cursor: 'pointer', color: '#4a5568' }}
                    >
                      Permanent Address is same as Current Address
                    </label>
                  </div>

                  <div>
                    <span style={{ fontSize: '0.9em', fontWeight: 600, color: '#2c3e50', marginBottom: '6px', display: 'block' }}>Permanent Address:</span>
                    <StyledTextArea 
                      id="permanentAddress" 
                      name="permanentAddress" 
                      value={formData.permanentAddress} 
                      onChange={handleChange} 
                      rows={3} 
                      disabled={!isOnline || isViewMode || !!dobAgeError || formData.isMinimumEducationMet === 'No' || formData.permanentAddressSameAsCurrent}
                      style={{ 
                        margin: 0,
                        opacity: formData.permanentAddressSameAsCurrent ? 0.6 : 1,
                        backgroundColor: formData.permanentAddressSameAsCurrent ? '#f0f0f0' : (formData.addressProofFile ? '#f0fff4' : 'white')
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
            
            <input 
              type="file" 
              accept=".jpg,.jpeg,.png" 
              ref={addressFileInputRef}
              style={{ display: 'none' }} 
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  handleProofFileUpload(e.target.files[0], 'addressProofFile');
                }
              }}
              disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No'}
            />
          </FormFieldFullWidth>
          </ShakeAnimation>

          <FormFieldFullWidth style={{ opacity: (dobAgeError || formData.isMinimumEducationMet === 'No') ? 0.5 : 1 }}>
            <Label htmlFor="telNos">f) Tel. Nos:</Label>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <ShakeAnimation $isShaking={isPersonalMobileShaking} ref={personalMobileRef}>
              <div style={{ flex: 1, marginRight: '20px' }}>
                <Label htmlFor="personalMobile" style={{ fontSize: '0.85em', margin: '5px 0' }}>
                  Personal Mobile: (10 digits)
                  {mandatoryFields.personalMobile && (!formData.personalMobile || formData.personalMobile.length !== 10) && (
                    <span style={{ 
                      marginLeft: '8px',
                      backgroundColor: '#fef2f2', 
                      color: '#dc2626', 
                      padding: '2px 8px', 
                      borderRadius: '4px', 
                      fontSize: '0.675rem', 
                      fontWeight: 600,
                      border: '1px solid #fecaca',
                      textDecoration: 'none'
                    }}>Required</span>
                  )}
                </Label>
                <StyledInput 
                  type="text" 
                  id="personalMobile" 
                  name="personalMobile" 
                  value={formData.personalMobile} 
                  onChange={handleChange}
                  onBlur={handlePhoneBlur}
                  style={formData.errors?.personalMobile ? { borderColor: '#dc3545' } : {}} 
                  placeholder="10-digit mobile number"
                  maxLength={10}
                  disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No'}
                />
                {formData.errors?.personalMobile && (
                  <div style={{ color: '#dc3545', fontSize: '0.8em', marginTop: '3px' }}>
                    {formData.errors.personalMobile}
                  </div>
                )}
              </div>
              </ShakeAnimation>
              <div style={{ flex: 1 }}>
                <Label htmlFor="secondaryMobile" style={{ fontSize: '0.85em', margin: '5px 0' }}>Secondary Mobile: (10 digits)</Label>
                <StyledInput 
                  type="text" 
                  id="secondaryMobile" 
                  name="secondaryMobile" 
                  value={formData.secondaryMobile} 
                  onChange={handleChange} 
                  style={formData.errors?.secondaryMobile ? { borderColor: '#dc3545' } : {}} 
                  placeholder="Optional 10-digit number"
                  maxLength={10}
                  disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No'}
                />
                {formData.errors?.secondaryMobile && (
                  <div style={{ color: '#dc3545', fontSize: '0.8em', marginTop: '3px' }}>
                    {formData.errors.secondaryMobile}
                  </div>
                )}
              </div>
            </div>
          </FormFieldFullWidth>

          <FormFieldFullWidth>
            <Label htmlFor="emailId">g) E-mail ID:</Label>
            <StyledInput 
              type="email" 
              id="emailId" 
              name="emailId" 
              value={formData.emailId || userEmail} 
              onChange={handleChange} 
              style={formData.errors?.emailId ? { borderColor: '#dc3545' } : {}} 
              placeholder="your.email@example.com"
              disabled={true}
            />
            {formData.errors?.emailId && (
              <div style={{ color: '#dc3545', fontSize: '0.8em', marginTop: '3px' }}>
                {formData.errors.emailId}
              </div>
            )}
          </FormFieldFullWidth>

          <ShakeAnimation $isShaking={isNationalityShaking} ref={nationalityRef}>
          <FormFieldFullWidth style={{ opacity: (dobAgeError || formData.isMinimumEducationMet === 'No') ? 0.5 : 1 }}>
              <Label>
                h) Nationality:
                {mandatoryFields.nationality && !formData.nationality && (
                  <span style={{ 
                    marginLeft: '8px',
                    backgroundColor: '#fef2f2', 
                    color: '#dc2626', 
                    padding: '2px 8px', 
                    borderRadius: '4px', 
                    fontSize: '0.675rem', 
                    fontWeight: 600,
                    border: '1px solid #fecaca',
                    textDecoration: 'none'
                  }}>Required</span>
                )}
              </Label>
              <RadioGroup>
                <RadioOption>
                  <input 
                    type="radio" 
                    id="nationality-indian" 
                    name="nationality" 
                    value="Indian" 
                    checked={formData.nationality === "Indian"}
                    onChange={handleChange} 
                    disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No'} />
                  Indian
                </RadioOption>
                <RadioOption>
                  <input 
                    type="radio" 
                    id="nationality-nonindian" 
                    name="nationality" 
                    value="Non-Indian" 
                    checked={formData.nationality === "Non-Indian"}
                    onChange={handleChange} 
                    disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No'} />
                  Non-Indian
                </RadioOption>
              </RadioGroup>
          </FormFieldFullWidth>
          </ShakeAnimation>

          <ShakeAnimation $isShaking={isMaritalStatusShaking} ref={maritalStatusRef}>
          <FormFieldFullWidth style={{ opacity: (dobAgeError || formData.isMinimumEducationMet === 'No') ? 0.5 : 1 }}>
              <Label>
                i) Marital Status:
                {mandatoryFields.maritalStatus && !formData.maritalStatus && (
                  <span style={{ 
                    marginLeft: '8px',
                    backgroundColor: '#fef2f2', 
                    color: '#dc2626', 
                    padding: '2px 8px', 
                    borderRadius: '4px', 
                    fontSize: '0.675rem', 
                    fontWeight: 600,
                    border: '1px solid #fecaca',
                    textDecoration: 'none'
                  }}>Required</span>
                )}
              </Label>
              <RadioGroup>
                <RadioOption>
                  <input 
                    type="radio" 
                    id="maritalStatus-married" 
                    name="maritalStatus" 
                    value="Married" 
                    checked={formData.maritalStatus === "Married"}
                    onChange={handleChange} 
                    disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No'} />
                  Married
                </RadioOption>
                <RadioOption>
                  <input 
                    type="radio" 
                    id="maritalStatus-unmarried" 
                    name="maritalStatus" 
                    value="Unmarried" 
                    checked={formData.maritalStatus === "Unmarried"}
                    onChange={handleChange} 
                    disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No'} />
                  Unmarried
                </RadioOption>
                <RadioOption>
                  <input 
                    type="radio" 
                    id="maritalStatus-other" 
                    name="maritalStatus" 
                    value="Other" 
                    checked={formData.maritalStatus === "Other"}
                    onChange={handleChange} 
                    disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No'} />
                  Other
                </RadioOption>
              </RadioGroup>
              {formData.maritalStatus === 'Other' && (
                <input
                  type="text"
                  name="maritalStatusOther"
                  value={formData.maritalStatusOther || ''}
                  onChange={handleChange}
                  placeholder="Please specify"
                  disabled={isMainFieldDisabled}
                  style={{ marginTop: '8px', padding: '8px 12px', border: '1px solid #ccc', borderRadius: '4px', width: '100%', maxWidth: '200px' }}
                />)}
          </FormFieldFullWidth>
          </ShakeAnimation>

          <ShakeAnimation $isShaking={isGenderShaking} ref={genderRef}>
          <FormFieldFullWidth style={{ opacity: (dobAgeError || formData.isMinimumEducationMet === 'No') ? 0.5 : 1 }}>
            <Label>
              g) Gender:
              {mandatoryFields.gender && !formData.gender && (
                <span style={{ 
                  marginLeft: '8px',
                  backgroundColor: '#fef2f2', 
                  color: '#dc2626', 
                  padding: '2px 8px', 
                  borderRadius: '4px', 
                  fontSize: '0.675rem', 
                  fontWeight: 600,
                  border: '1px solid #fecaca',
                  textDecoration: 'none'
                }}>Required</span>
              )}
            </Label>
            <RadioGroup>
              <RadioOption>
                <input 
                  type="radio" 
                  id="gender-female" 
                  name="gender" 
                  value="Female" 
                  checked={formData.gender === "Female"}
                  onChange={handleChange} 
                  disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No'} />
                Female
              </RadioOption>
              <RadioOption>
                <input 
                  type="radio" 
                  id="gender-male" 
                  name="gender" 
                  value="Male" 
                  checked={formData.gender === "Male"}
                  onChange={handleChange} 
                  disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No'} />
                Male
              </RadioOption>
              <RadioOption>
                <input 
                  type="radio" 
                  id="gender-others" 
                  name="gender" 
                  value="Others" 
                  checked={formData.gender === "Others"}
                  onChange={handleChange} 
                  disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No'} />
                Others
              </RadioOption>
            </RadioGroup>
          </FormFieldFullWidth>
          </ShakeAnimation>

              {/* IDENTIFICATION & STATUTORY DETAILS - shown for all except Non Individual and Other */}
              {formData.dealershipType !== 'Non Individual applicant entity' && formData.dealershipType !== 'Other' && formData.dealershipType !== '' && (
                <ShakeAnimation $isShaking={isKycDetailsShaking} ref={kycDetailsRef}>
                <FormFieldFullWidth style={{ marginTop: '25px', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  {isIndividualOrProprietorship && formData.nameInBlocks && (
                    <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '0.9em', color: '#2c3e50', fontWeight: 500, background: 'linear-gradient(135deg, #e8f4fd, #d4edfc)', padding: '4px 12px', borderRadius: '15px', display: 'inline-block' }}>
                        {formData.nameInBlocks}
                      </span>
                    </div>
                  )}
                  <Label style={{ fontWeight: 700, marginBottom: '15px', color: '#1a5276', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.05em', textDecoration: 'underline' }}>
                    {isIndividualOrProprietorship ? 'KYC & Tax Registration Details' : `${(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #1 ${formData.nameInBlocks ? `(${formData.nameInBlocks})` : ''} - KYC & Tax Registration Details`}
                    {mandatoryFields.kycAadhaarPan && (!formData.aadhaarNumber || !formData.panNumberStatutory) && (
                      <span style={{ 
                        backgroundColor: '#fef2f2', 
                        color: '#dc2626', 
                        padding: '2px 8px', 
                        borderRadius: '4px', 
                        fontSize: '0.675rem', 
                        fontWeight: 600,
                        border: '1px solid #fecaca',
                        textDecoration: 'none'
                      }}>Required</span>
                    )}
                    {isMainApplicantSectionLocked && <span style={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '10px', textDecoration: 'none' }}>🔒 Disabled</span>}
                  </Label>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '10px 12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '30%' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>Aadhaar Number</span>
                            <button
                              type="button"
                              onClick={() => setShowAadhaarSampleModal(true)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '22px',
                                height: '22px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #e74c3c, #c0392b)',
                                color: 'white',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                border: 'none',
                                boxShadow: '0 2px 4px rgba(231, 76, 60, 0.3)',
                                transition: 'transform 0.2s'
                              }}
                              title="Click to see Aadhaar upload guidelines"
                            >
                              !
                            </button>
                          </div>
                        </td>
                        <td style={{ padding: '10px 12px', border: '1px solid #85c1e9', width: '70%' }}>
                          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <StyledInput type="text" id="aadhaarNumber" name="aadhaarNumber" value={formData.aadhaarNumber} onChange={handleChange} maxLength={14} placeholder="XXXX XXXX XXXX" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyStatutoryUploading} style={{ margin: 0, flex: 1, minWidth: '150px', opacity: isAnyStatutoryUploading ? 0.5 : 1 }} />
                            <span style={{ color: formData.aadhaarVerified === 'Yes' ? '#27ae60' : '#7f8c8d', fontWeight: 500, fontSize: '0.9em' }}>E verified</span>
                            <input type="file" accept=".jpg,.jpeg,.png" onChange={async (e) => { if (e.target.files?.[0]) { const file = e.target.files[0]; setFormData(prev => ({ ...prev, aadhaarFile: file })); setAiDetectionField('aadhaar'); try { const response = await uploadFormFile(applicationId, file, 'aadhaarFile', () => {}); if (response?.success) { setFormData(prev => ({ ...prev, aadhaarFile: (response as any).filePath || file })); const extracted = (response as any).extractedFields; if (extracted?.aadhaarNumber) { const dupCheck = checkDuplicateDocument('aadhaar', extracted.aadhaarNumber, 'main'); if (dupCheck.isDuplicate) { setFormData(prev => ({ ...prev, aadhaarFile: null })); setUploadErrorModal({ show: true, message: `This Aadhaar number is already used by ${dupCheck.duplicateOwner}. Each applicant/partner must have a unique Aadhaar card. Please upload a different Aadhaar card.`, fieldName: 'Aadhaar Card (Duplicate)' }); } else { await typewriterFill('aadhaarNumber', extracted.aadhaarNumber.replace(/\D/g, '').slice(0, 12).replace(/(\d{4})(?=\d)/g, '$1 ')); } } if (extracted?.aadhaarName) { setExtractedNames(prev => { const newNames = { ...prev, aadhaarName: extracted.aadhaarName }; if (newNames.panName) { validateDocumentNames(extracted.aadhaarName, newNames.panName); } return newNames; }); } } } catch (err: any) { console.error('Upload error:', err); setFormData(prev => ({ ...prev, aadhaarFile: null })); if (err.response?.data?.imageQualityError) { if (err.response.data.documentRejected) { setUploadErrorModal({ show: true, message: err.response.data.reason || 'This document type is not accepted.', fieldName: 'Aadhaar Card' }); } else { setImageQualityError({ show: true, reason: err.response.data.reason || 'The uploaded image is blurry, smudged, or unreadable.', fieldName: 'Aadhaar Card', isAadhaarError: err.response.data.incompleteAadhaar }); } } else { setUploadErrorModal({ show: true, message: err.response?.data?.reason || err.message || 'Upload failed. Please try again.', fieldName: 'Aadhaar Card' }); } } finally { setAiDetectionField(null); } }}} disabled={isMainFieldDisabled || isAnyStatutoryUploading} style={{ display: 'none' }} id="aadhaarFileInput" />
                            <label htmlFor="aadhaarFileInput" style={{ padding: '4px 12px', backgroundColor: formData.aadhaarFile ? '#27ae60' : '#3498db', color: 'white', borderRadius: '4px', fontSize: '0.8em', cursor: isMainFieldDisabled || isAnyStatutoryUploading ? 'not-allowed' : 'pointer', opacity: isMainFieldDisabled || isAnyStatutoryUploading ? 0.6 : 1, pointerEvents: isMainFieldDisabled ? 'none' : 'auto' }}>{formData.aadhaarFile ? 'Change' : 'Upload'}</label>
                            {formData.aadhaarFile && (<button type="button" onClick={() => !isMainFieldDisabled && handleOpenFilePreview(formData.aadhaarFile, 'aadhaarFile')} disabled={isMainFieldDisabled} style={{ padding: '4px 12px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.8em', cursor: isMainFieldDisabled ? 'not-allowed' : 'pointer', opacity: isMainFieldDisabled ? 0.6 : 1 }}>View</button>)}
                            {aiDetectionField === 'aadhaar' && <span style={{ fontSize: '0.75em', color: '#e67e22', fontStyle: 'italic' }}>Detection in progress.. please wait!</span>}
                            {formData.aadhaarFile && aiDetectionField !== 'aadhaar' && <span style={{ fontSize: '0.75em', color: '#27ae60' }}>✓</span>}
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '10px 12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '30%' }}>PAN Number</td>
                        <td style={{ padding: '10px 12px', border: '1px solid #85c1e9', width: '70%' }}>
                          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <StyledInput type="text" id="panNumberStatutory" name="panNumberStatutory" value={formData.panNumberStatutory} onChange={handleChange} maxLength={10} placeholder="PAN Number" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyStatutoryUploading} style={{ margin: 0, flex: 1, textTransform: 'uppercase', minWidth: '150px', opacity: isAnyStatutoryUploading ? 0.5 : 1 }} />
                            <span style={{ color: formData.panVerified === 'Yes' ? '#27ae60' : '#7f8c8d', fontWeight: 500, fontSize: '0.9em' }}>E verified</span>
                            <input type="file" accept=".jpg,.jpeg,.png" onChange={async (e) => { if (e.target.files?.[0]) { const file = e.target.files[0]; setFormData(prev => ({ ...prev, panStatutoryFile: file })); setAiDetectionField('panStatutory'); try { const response = await uploadFormFile(applicationId, file, 'panStatutoryFile', () => {}); if (response?.success) { setFormData(prev => ({ ...prev, panStatutoryFile: (response as any).filePath || file })); const extracted = (response as any).extractedFields; if (extracted?.panNumber) { const dupCheck = checkDuplicateDocument('pan', extracted.panNumber, 'main'); if (dupCheck.isDuplicate) { setFormData(prev => ({ ...prev, panStatutoryFile: null })); setUploadErrorModal({ show: true, message: `This PAN number is already used by ${dupCheck.duplicateOwner}. Each applicant/partner must have a unique PAN card. Please upload a different PAN card.`, fieldName: 'PAN Card (Duplicate)' }); } else { await typewriterFill('panNumberStatutory', extracted.panNumber); } } if (extracted?.panName) { setExtractedNames(prev => { const newNames = { ...prev, panName: extracted.panName }; if (newNames.aadhaarName) { validateDocumentNames(newNames.aadhaarName, extracted.panName); } return newNames; }); } } } catch (err: any) { console.error('Upload error:', err); setFormData(prev => ({ ...prev, panStatutoryFile: null })); if (err.response?.data?.imageQualityError) { if (err.response.data.documentRejected) { setUploadErrorModal({ show: true, message: err.response.data.reason || 'This document type is not accepted.', fieldName: 'PAN Card' }); } else { setImageQualityError({ show: true, reason: err.response.data.reason || 'The uploaded image is blurry, smudged, or unreadable.', fieldName: 'PAN Card' }); } } else { setUploadErrorModal({ show: true, message: err.response?.data?.reason || err.message || 'Upload failed. Please try again.', fieldName: 'PAN Card' }); } } finally { setAiDetectionField(null); } }}} disabled={isMainFieldDisabled || isAnyStatutoryUploading} style={{ display: 'none' }} id="panStatutoryFileInput" />
                            <label htmlFor="panStatutoryFileInput" style={{ padding: '4px 12px', backgroundColor: formData.panStatutoryFile ? '#27ae60' : '#3498db', color: 'white', borderRadius: '4px', fontSize: '0.8em', cursor: isMainFieldDisabled || isAnyStatutoryUploading ? 'not-allowed' : 'pointer', opacity: isMainFieldDisabled || isAnyStatutoryUploading ? 0.6 : 1, pointerEvents: isMainFieldDisabled ? 'none' : 'auto' }}>{formData.panStatutoryFile ? 'Change' : 'Upload'}</label>
                            {formData.panStatutoryFile && (<button type="button" onClick={() => !isMainFieldDisabled && handleOpenFilePreview(formData.panStatutoryFile, 'panStatutoryFile')} disabled={isMainFieldDisabled} style={{ padding: '4px 12px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.8em', cursor: isMainFieldDisabled ? 'not-allowed' : 'pointer', opacity: isMainFieldDisabled ? 0.6 : 1 }}>View</button>)}
                            {aiDetectionField === 'panStatutory' && <span style={{ fontSize: '0.75em', color: '#e67e22', fontStyle: 'italic' }}>Detection in progress.. please wait!</span>}
                            {formData.panStatutoryFile && aiDetectionField !== 'panStatutory' && <span style={{ fontSize: '0.75em', color: '#27ae60' }}>✓</span>}
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '10px 12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '30%' }}>GST No. (if applicable)</td>
                        <td style={{ padding: '10px 12px', border: '1px solid #85c1e9', width: '70%' }}>
                          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <StyledInput type="text" id="gstNumber" name="gstNumber" value={formData.gstNumber} onChange={handleChange} maxLength={15} placeholder="GST Number" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyStatutoryUploading} style={{ margin: 0, flex: 1, textTransform: 'uppercase', minWidth: '150px', opacity: isAnyStatutoryUploading ? 0.5 : 1 }} />
                            <span style={{ color: formData.gstVerified === 'Yes' ? '#27ae60' : '#7f8c8d', fontWeight: 500, fontSize: '0.9em' }}>E verified</span>
                            <input type="file" accept=".jpg,.jpeg,.png" onChange={async (e) => { if (e.target.files?.[0]) { const file = e.target.files[0]; setFormData(prev => ({ ...prev, gstFile: file })); setAiDetectionField('gst'); try { const response = await uploadFormFile(applicationId, file, 'gstFile', () => {}); if (response?.success) { setFormData(prev => ({ ...prev, gstFile: (response as any).filePath || file })); const extracted = (response as any).extractedFields; if (extracted?.gstNumber) { await typewriterFill('gstNumber', extracted.gstNumber); } } } catch (err: any) { console.error('Upload error:', err); setFormData(prev => ({ ...prev, gstFile: null })); if (err.response?.data?.imageQualityError) { if (err.response.data.documentRejected) { setUploadErrorModal({ show: true, message: err.response.data.reason || 'This document type is not accepted.', fieldName: 'GST Certificate' }); } else { setImageQualityError({ show: true, reason: err.response.data.reason || 'The uploaded image is blurry, smudged, or unreadable.', fieldName: 'GST Certificate' }); } } else { setUploadErrorModal({ show: true, message: err.response?.data?.reason || err.message || 'Upload failed. Please try again.', fieldName: 'GST Certificate' }); } } finally { setAiDetectionField(null); } }}} disabled={isMainFieldDisabled || isAnyStatutoryUploading} style={{ display: 'none' }} id="gstFileInput" />
                            <label htmlFor="gstFileInput" style={{ padding: '4px 12px', backgroundColor: formData.gstFile ? '#27ae60' : '#3498db', color: 'white', borderRadius: '4px', fontSize: '0.8em', cursor: isMainFieldDisabled || isAnyStatutoryUploading ? 'not-allowed' : 'pointer', opacity: isMainFieldDisabled || isAnyStatutoryUploading ? 0.6 : 1, pointerEvents: isMainFieldDisabled ? 'none' : 'auto' }}>{formData.gstFile ? 'Change' : 'Upload'}</label>
                            {formData.gstFile && (<button type="button" onClick={() => !isMainFieldDisabled && handleOpenFilePreview(formData.gstFile, 'gstFile')} disabled={isMainFieldDisabled} style={{ padding: '4px 12px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.8em', cursor: isMainFieldDisabled ? 'not-allowed' : 'pointer', opacity: isMainFieldDisabled ? 0.6 : 1 }}>View</button>)}
                            {aiDetectionField === 'gst' && <span style={{ fontSize: '0.75em', color: '#e67e22', fontStyle: 'italic' }}>Detection in progress.. please wait!</span>}
                            {formData.gstFile && aiDetectionField !== 'gst' && <span style={{ fontSize: '0.75em', color: '#27ae60' }}>✓</span>}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </FormFieldFullWidth>
                </ShakeAnimation>
              )}

              {/* EDUCATIONAL QUALIFICATION - shown for all except Non Individual and Other */}
              {formData.dealershipType !== 'Non Individual applicant entity' && formData.dealershipType !== 'Other' && formData.dealershipType !== '' && (
                <ShakeAnimation $isShaking={isEducationalQualShaking} ref={educationalQualRef}>
                <FormFieldFullWidth style={{ marginTop: '25px', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  {isIndividualOrProprietorship && formData.nameInBlocks && (
                    <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '0.9em', color: '#2c3e50', fontWeight: 500, background: 'linear-gradient(135deg, #e8f4fd, #d4edfc)', padding: '4px 12px', borderRadius: '15px', display: 'inline-block' }}>
                        {formData.nameInBlocks}
                      </span>
                    </div>
                  )}
                  <Label style={{ fontWeight: 700, marginBottom: '15px', color: '#1a5276', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.05em', textDecoration: 'underline' }}>
                    {isIndividualOrProprietorship ? 'EDUCATIONAL QUALIFICATION' : `${(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #1 ${formData.nameInBlocks ? `(${formData.nameInBlocks})` : ''} - EDUCATIONAL QUALIFICATION`}
                    {mandatoryFields.educationalQualification && !(
                      (formData.eduSscCertificateFile && formData.eduSscBoard) ||
                      (formData.eduHscCertificateFile && formData.eduHscBoard) ||
                      (formData.eduGraduateCertificateFile && formData.eduGraduateBoard) ||
                      (formData.eduOthersCertificateFile && formData.eduOthersBoard)
                    ) && (
                      <span style={{ 
                        backgroundColor: '#fef2f2', 
                        color: '#dc2626', 
                        padding: '2px 8px', 
                        borderRadius: '4px', 
                        fontSize: '0.675rem', 
                        fontWeight: 600,
                        border: '1px solid #fecaca',
                        textDecoration: 'none'
                      }}>Required</span>
                    )}
                    {isMainApplicantSectionLocked && <span style={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '10px', textDecoration: 'none' }}>🔒 Disabled</span>}
                  </Label>
                  <div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '8px 4px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, textAlign: 'center', width: '10%', fontSize: '0.85em' }}>Qualification <span title="Educational qualification level (SSC/HSC/Graduate/Others)" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span></th>
                          <th style={{ padding: '8px 4px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, textAlign: 'center', width: '22%', fontSize: '0.85em' }}>Board / University <span title="Name of the Board or University" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span></th>
                          <th style={{ padding: '8px 4px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, textAlign: 'center', width: '10%', fontSize: '0.85em' }}>Marks <span title="Marks Obtained in the examination" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span></th>
                          <th style={{ padding: '8px 4px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, textAlign: 'center', width: '10%', fontSize: '0.85em' }}>Out of <span title="Maximum/Total Marks" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span></th>
                          <th style={{ padding: '8px 4px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, textAlign: 'center', width: '12%', fontSize: '0.85em' }}>% / CGPA <span title="Percentage or CGPA obtained (auto-calculated, editable)" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span></th>
                          <th style={{ padding: '8px 4px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, textAlign: 'center', width: '10%', fontSize: '0.85em' }}>Year <span title="Year of Passing" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span></th>
                          <th style={{ padding: '8px 4px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, textAlign: 'center', width: '16%', fontSize: '0.85em' }}>Doc <span title="Upload marksheet/certificate" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '6px 4px', border: '1px solid #85c1e9', backgroundColor: '#eaf2f8', fontWeight: 600, textAlign: 'center', fontSize: '0.8em' }}>SSC / 10th</td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduSscBoard" value={formData.eduSscBoard} onChange={handleChange} placeholder="Board/University" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduSscMarksObtained" value={formData.eduSscMarksObtained} onChange={handleChange} placeholder="Marks" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduSscOutOfMarks" value={formData.eduSscOutOfMarks} onChange={handleChange} placeholder="Max" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduSscPercentage" value={formData.eduSscPercentage} onChange={handleChange} placeholder="%" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduSscYearOfPassing" value={formData.eduSscYearOfPassing} onChange={handleChange} placeholder="Year" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <input type="file" accept=".jpg,.jpeg,.png" onChange={(e) => { if (e.target.files?.[0]) handleEduCertificateUpload(e.target.files[0], 'Ssc', 'SSC / 10th'); }} disabled={isMainFieldDisabled || isAnyEduCertUploading} style={{ display: 'none' }} id="eduSscCertFileInput" />
                              <label htmlFor="eduSscCertFileInput" style={{ padding: '3px 8px', backgroundColor: formData.eduSscCertificateFile ? '#27ae60' : '#3498db', color: 'white', borderRadius: '4px', fontSize: '0.7em', cursor: isMainFieldDisabled || isAnyEduCertUploading ? 'not-allowed' : 'pointer', opacity: isMainFieldDisabled || isAnyEduCertUploading ? 0.6 : 1 }}>{formData.eduSscCertificateFile ? '✓' : 'Upload'}</label>
                              {formData.eduSscCertificateFile && <button type="button" onClick={() => handleOpenFilePreview(formData.eduSscCertificateFile, 'eduSscCertificateFile')} style={{ padding: '3px 6px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.7em', cursor: 'pointer' }}>View</button>}
                              {eduCertUploading['Ssc'] && <span style={{ fontSize: '0.65em', color: '#e67e22' }}>...</span>}
                              {!formData.eduSscCertificateFile && (formData.eduSscBoard || formData.eduSscMarksObtained || formData.eduSscOutOfMarks || formData.eduSscPercentage || formData.eduSscYearOfPassing) && (
                                <button type="button" onClick={() => setFormData(prev => ({ ...prev, eduSscBoard: '', eduSscMarksObtained: '', eduSscOutOfMarks: '', eduSscPercentage: '', eduSscYearOfPassing: '' }))} style={{ padding: '3px 6px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.65em', cursor: 'pointer' }} title="Clear autofilled data for this row">Clear</button>
                              )}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '6px 4px', border: '1px solid #85c1e9', backgroundColor: '#eaf2f8', fontWeight: 600, textAlign: 'center', fontSize: '0.8em' }}>HSC / 12th</td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduHscBoard" value={formData.eduHscBoard} onChange={handleChange} placeholder="Board/University" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduHscMarksObtained" value={formData.eduHscMarksObtained} onChange={handleChange} placeholder="Marks" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduHscOutOfMarks" value={formData.eduHscOutOfMarks} onChange={handleChange} placeholder="Max" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduHscPercentage" value={formData.eduHscPercentage} onChange={handleChange} placeholder="%" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduHscYearOfPassing" value={formData.eduHscYearOfPassing} onChange={handleChange} placeholder="Year" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <input type="file" accept=".jpg,.jpeg,.png" onChange={(e) => { if (e.target.files?.[0]) handleEduCertificateUpload(e.target.files[0], 'Hsc', 'HSC / 12th'); }} disabled={isMainFieldDisabled || isAnyEduCertUploading} style={{ display: 'none' }} id="eduHscCertFileInput" />
                              <label htmlFor="eduHscCertFileInput" style={{ padding: '3px 8px', backgroundColor: formData.eduHscCertificateFile ? '#27ae60' : '#3498db', color: 'white', borderRadius: '4px', fontSize: '0.7em', cursor: isMainFieldDisabled || isAnyEduCertUploading ? 'not-allowed' : 'pointer', opacity: isMainFieldDisabled || isAnyEduCertUploading ? 0.6 : 1 }}>{formData.eduHscCertificateFile ? '✓' : 'Upload'}</label>
                              {formData.eduHscCertificateFile && <button type="button" onClick={() => handleOpenFilePreview(formData.eduHscCertificateFile, 'eduHscCertificateFile')} style={{ padding: '3px 6px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.7em', cursor: 'pointer' }}>View</button>}
                              {eduCertUploading['Hsc'] && <span style={{ fontSize: '0.65em', color: '#e67e22' }}>...</span>}
                              {!formData.eduHscCertificateFile && (formData.eduHscBoard || formData.eduHscMarksObtained || formData.eduHscOutOfMarks || formData.eduHscPercentage || formData.eduHscYearOfPassing) && (
                                <button type="button" onClick={() => setFormData(prev => ({ ...prev, eduHscBoard: '', eduHscMarksObtained: '', eduHscOutOfMarks: '', eduHscPercentage: '', eduHscYearOfPassing: '' }))} style={{ padding: '3px 6px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.65em', cursor: 'pointer' }} title="Clear autofilled data for this row">Clear</button>
                              )}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '6px 4px', border: '1px solid #85c1e9', backgroundColor: '#eaf2f8', fontWeight: 600, textAlign: 'center', fontSize: '0.8em' }}>Graduate</td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduGraduateBoard" value={formData.eduGraduateBoard} onChange={handleChange} placeholder="Board/University" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduGraduateMarksObtained" value={formData.eduGraduateMarksObtained} onChange={handleChange} placeholder="Marks" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduGraduateOutOfMarks" value={formData.eduGraduateOutOfMarks} onChange={handleChange} placeholder="Max" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduGraduatePercentage" value={formData.eduGraduatePercentage} onChange={handleChange} placeholder="%" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduGraduateYearOfPassing" value={formData.eduGraduateYearOfPassing} onChange={handleChange} placeholder="Year" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <input type="file" accept=".jpg,.jpeg,.png" onChange={(e) => { if (e.target.files?.[0]) handleEduCertificateUpload(e.target.files[0], 'Graduate', 'Graduate'); }} disabled={isMainFieldDisabled || isAnyEduCertUploading} style={{ display: 'none' }} id="eduGraduateCertFileInput" />
                              <label htmlFor="eduGraduateCertFileInput" style={{ padding: '3px 8px', backgroundColor: formData.eduGraduateCertificateFile ? '#27ae60' : '#3498db', color: 'white', borderRadius: '4px', fontSize: '0.7em', cursor: isMainFieldDisabled || isAnyEduCertUploading ? 'not-allowed' : 'pointer', opacity: isMainFieldDisabled || isAnyEduCertUploading ? 0.6 : 1 }}>{formData.eduGraduateCertificateFile ? '✓' : 'Upload'}</label>
                              {formData.eduGraduateCertificateFile && <button type="button" onClick={() => handleOpenFilePreview(formData.eduGraduateCertificateFile, 'eduGraduateCertificateFile')} style={{ padding: '3px 6px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.7em', cursor: 'pointer' }}>View</button>}
                              {eduCertUploading['Graduate'] && <span style={{ fontSize: '0.65em', color: '#e67e22' }}>...</span>}
                              {!formData.eduGraduateCertificateFile && (formData.eduGraduateBoard || formData.eduGraduateMarksObtained || formData.eduGraduateOutOfMarks || formData.eduGraduatePercentage || formData.eduGraduateYearOfPassing) && (
                                <button type="button" onClick={() => setFormData(prev => ({ ...prev, eduGraduateBoard: '', eduGraduateMarksObtained: '', eduGraduateOutOfMarks: '', eduGraduatePercentage: '', eduGraduateYearOfPassing: '' }))} style={{ padding: '3px 6px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.65em', cursor: 'pointer' }} title="Clear autofilled data for this row">Clear</button>
                              )}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '6px 4px', border: '1px solid #85c1e9', backgroundColor: '#eaf2f8', fontWeight: 600, textAlign: 'center', fontSize: '0.8em' }}>Others</td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduOthersBoard" value={formData.eduOthersBoard} onChange={handleChange} placeholder="Board/University" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduOthersMarksObtained" value={formData.eduOthersMarksObtained} onChange={handleChange} placeholder="Marks" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduOthersOutOfMarks" value={formData.eduOthersOutOfMarks} onChange={handleChange} placeholder="Max" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduOthersPercentage" value={formData.eduOthersPercentage} onChange={handleChange} placeholder="%" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <StyledInput type="text" name="eduOthersYearOfPassing" value={formData.eduOthersYearOfPassing} onChange={handleChange} placeholder="Year" disabled={isMainFieldDisabled || !!dobAgeError || formData.isMinimumEducationMet === 'No' || isAnyEduCertUploading} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isAnyEduCertUploading ? 0.5 : 1 }} />
                          </td>
                          <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <input type="file" accept=".jpg,.jpeg,.png" onChange={(e) => { if (e.target.files?.[0]) handleEduCertificateUpload(e.target.files[0], 'Others', 'Other Qualification'); }} disabled={isMainFieldDisabled || isAnyEduCertUploading} style={{ display: 'none' }} id="eduOthersCertFileInput" />
                              <label htmlFor="eduOthersCertFileInput" style={{ padding: '3px 8px', backgroundColor: formData.eduOthersCertificateFile ? '#27ae60' : '#3498db', color: 'white', borderRadius: '4px', fontSize: '0.7em', cursor: isMainFieldDisabled || isAnyEduCertUploading ? 'not-allowed' : 'pointer', opacity: isMainFieldDisabled || isAnyEduCertUploading ? 0.6 : 1 }}>{formData.eduOthersCertificateFile ? '✓' : 'Upload'}</label>
                              {formData.eduOthersCertificateFile && <button type="button" onClick={() => handleOpenFilePreview(formData.eduOthersCertificateFile, 'eduOthersCertificateFile')} style={{ padding: '3px 6px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.7em', cursor: 'pointer' }}>View</button>}
                              {eduCertUploading['Others'] && <span style={{ fontSize: '0.65em', color: '#e67e22' }}>...</span>}
                              {!formData.eduOthersCertificateFile && (formData.eduOthersBoard || formData.eduOthersMarksObtained || formData.eduOthersOutOfMarks || formData.eduOthersPercentage || formData.eduOthersYearOfPassing) && (
                                <button type="button" onClick={() => setFormData(prev => ({ ...prev, eduOthersBoard: '', eduOthersMarksObtained: '', eduOthersOutOfMarks: '', eduOthersPercentage: '', eduOthersYearOfPassing: '' }))} style={{ padding: '3px 6px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.65em', cursor: 'pointer' }} title="Clear autofilled data for this row">Clear</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </FormFieldFullWidth>
                </ShakeAnimation>
              )}

              {/* BUSINESS / EMPLOYMENT DETAILS Section - Moved from Page2 */}
              {formData.dealershipType !== 'Non Individual applicant entity' && formData.dealershipType !== 'Other' && formData.dealershipType !== '' && (
                <ShakeAnimation $isShaking={isBusinessDetailsShaking} ref={businessDetailsRef}>
                <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  {isIndividualOrProprietorship && formData.nameInBlocks && (
                    <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '0.9em', color: '#2c3e50', fontWeight: 500, background: 'linear-gradient(135deg, #e8f4fd, #d4edfc)', padding: '4px 12px', borderRadius: '15px', display: 'inline-block' }}>
                        {formData.nameInBlocks}
                      </span>
                    </div>
                  )}
                  <Label style={{ fontWeight: 700, marginBottom: '20px', color: '#1a5276', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.05em', textDecoration: 'underline' }}>
                    {isIndividualOrProprietorship ? 'BUSINESS / EMPLOYMENT DETAILS' : `${(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #1 ${formData.nameInBlocks ? `(${formData.nameInBlocks})` : ''} - BUSINESS / EMPLOYMENT DETAILS`}
                    {mandatoryFields.businessEmploymentDetails && (!formData.presentOccupation || !formData.businessEmploymentDetails || !formData.totalWorkExperienceYears) && (
                      <span style={{ 
                        backgroundColor: '#fef2f2', 
                        color: '#dc2626', 
                        padding: '2px 8px', 
                        borderRadius: '4px', 
                        fontSize: '0.675rem', 
                        fontWeight: 600,
                        border: '1px solid #fecaca',
                        textDecoration: 'none'
                      }}>Required</span>
                    )}
                    {isMainApplicantSectionLocked && <span style={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '10px', textDecoration: 'none' }}>🔒 Disabled</span>}
                  </Label>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '35%' }}>Present Occupation</td>
                        <td style={{ padding: '12px', border: '1px solid #85c1e9' }}>
                          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                              <input type="radio" name="presentOccupation" value="Business" checked={formData.presentOccupation === 'Business'} onChange={handleChange} disabled={isMainFieldDisabled} />
                              Business
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                              <input type="radio" name="presentOccupation" value="Service" checked={formData.presentOccupation === 'Service'} onChange={handleChange} disabled={isMainFieldDisabled} />
                              Service
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                              <input type="radio" name="presentOccupation" value="Self-Employed" checked={formData.presentOccupation === 'Self-Employed'} onChange={handleChange} disabled={isMainFieldDisabled} />
                              Self-Employed
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                              <input type="radio" name="presentOccupation" value="Other" checked={formData.presentOccupation === 'Other'} onChange={handleChange} disabled={isMainFieldDisabled} />
                              Other
                            </label>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>Details of Business / Employment</td>
                        <td style={{ padding: '12px', border: '1px solid #85c1e9' }}>
                          <StyledInput type="text" name="businessEmploymentDetails" value={formData.businessEmploymentDetails || ''} onChange={handleChange} placeholder="" disabled={isMainFieldDisabled} style={{ margin: 0 }} />
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>Total Work Experience (Years)</td>
                        <td style={{ padding: '12px', border: '1px solid #85c1e9' }}>
                          <StyledInput type="text" name="totalWorkExperienceYears" value={formData.totalWorkExperienceYears || ''} onChange={handleChange} placeholder="" disabled={isMainFieldDisabled} style={{ margin: 0 }} />
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>Relevant Industry Experience</td>
                        <td style={{ padding: '12px', border: '1px solid #85c1e9' }}>
                          <StyledInput type="text" name="relevantIndustryExperience" value={formData.relevantIndustryExperience || ''} onChange={handleChange} placeholder="" disabled={isMainFieldDisabled} style={{ margin: 0 }} />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </FormFieldFullWidth>
                </ShakeAnimation>
              )}

              {/* FINANCIAL CAPABILITY Section - Moved from Page2 */}
              {formData.dealershipType !== 'Non Individual applicant entity' && formData.dealershipType !== 'Other' && formData.dealershipType !== '' && (
                <ShakeAnimation $isShaking={isFinancialCapabilityShaking} ref={financialCapabilityRef}>
                <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  {isIndividualOrProprietorship && formData.nameInBlocks && (
                    <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '0.9em', color: '#2c3e50', fontWeight: 500, background: 'linear-gradient(135deg, #e8f4fd, #d4edfc)', padding: '4px 12px', borderRadius: '15px', display: 'inline-block' }}>
                        {formData.nameInBlocks}
                      </span>
                    </div>
                  )}
                  <Label style={{ fontWeight: 700, marginBottom: '20px', color: '#1a5276', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.05em', textDecoration: 'underline' }}>
                    {isIndividualOrProprietorship ? 'FINANCIAL CAPABILITY' : `${(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #1 ${formData.nameInBlocks ? `(${formData.nameInBlocks})` : ''} - FINANCIAL CAPABILITY`}
                    {mandatoryFields.financialCapability && (!formData.annualIncome || !formData.netWorthAtApplication || !formData.sourceOfInvestment || !formData.abilityToMeetRequiredInvestment) && (
                      <span style={{ 
                        backgroundColor: '#fef2f2', 
                        color: '#dc2626', 
                        padding: '2px 8px', 
                        borderRadius: '4px', 
                        fontSize: '0.675rem', 
                        fontWeight: 600,
                        border: '1px solid #fecaca',
                        textDecoration: 'none'
                      }}>Required</span>
                    )}
                    {isMainApplicantSectionLocked && <span style={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '10px', textDecoration: 'none' }}>🔒 Disabled</span>}
                  </Label>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '35%' }}>Annual Income (₹) as per ITR filed</td>
                        <td style={{ padding: '12px', border: '1px solid #85c1e9' }}>
                          <StyledInput type="text" name="annualIncome" value={formData.annualIncome || ''} onChange={handleChange} placeholder="" disabled={isMainFieldDisabled} style={{ margin: 0 }} />
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>Net Worth (₹) at the time of application (Immovable assets value)</td>
                        <td style={{ padding: '12px', border: '1px solid #85c1e9' }}>
                          <StyledInput type="text" name="netWorthAtApplication" value={formData.netWorthAtApplication || ''} onChange={handleChange} placeholder="" disabled={isMainFieldDisabled} style={{ margin: 0 }} />
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>Source of Investment</td>
                        <td style={{ padding: '12px', border: '1px solid #85c1e9' }}>
                          <div style={{ display: 'flex', gap: '25px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                              <input type="radio" name="sourceOfInvestment" value="Own Funds" checked={formData.sourceOfInvestment === 'Own Funds'} onChange={handleChange} disabled={isMainFieldDisabled} />
                              Own Funds
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                              <input type="radio" name="sourceOfInvestment" value="Bank Loan" checked={formData.sourceOfInvestment === 'Bank Loan'} onChange={handleChange} disabled={isMainFieldDisabled} />
                              Bank Loan
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                              <input type="radio" name="sourceOfInvestment" value="Both" checked={formData.sourceOfInvestment === 'Both'} onChange={handleChange} disabled={isMainFieldDisabled} />
                              Both
                            </label>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>Ability to Meet Required Investment, I agree to arrange min. ₹1.5 Cr (One Crore Fifty Lakh Rupees) or more upon selection required for development of infra of CNG station (as per Scope Of Work)</td>
                        <td style={{ padding: '12px', border: '1px solid #85c1e9' }}>
                          <div style={{ display: 'flex', gap: '25px', alignItems: 'center' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                              <input type="radio" name="abilityToMeetRequiredInvestment" value="Yes" checked={formData.abilityToMeetRequiredInvestment === 'Yes'} onChange={handleChange} disabled={isMainFieldDisabled} />
                              Yes
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                              <input type="radio" name="abilityToMeetRequiredInvestment" value="No" checked={formData.abilityToMeetRequiredInvestment === 'No'} onChange={handleChange} disabled={isMainFieldDisabled} />
                              No
                            </label>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </FormFieldFullWidth>
                </ShakeAnimation>
              )}

              {/* FINANCIAL CAPABILITY DOCUMENT CHECKLIST - separate card */}
              {formData.dealershipType !== 'Non Individual applicant entity' && formData.dealershipType !== 'Other' && formData.dealershipType !== '' && (
                <ShakeAnimation $isShaking={isFinancialDocChecklistShaking} ref={financialDocChecklistRef}>
                <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <Label style={{ fontWeight: 700, marginBottom: '15px', color: '#1a5276', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1em', textDecoration: 'underline' }}>
                    FINANCIAL CAPABILITY DOCUMENT CHECKLIST
                    {mandatoryFields.financialCapabilityDocChecklist && !(
                      formData.finDocProjectReportFile ||
                      formData.finDocBankStatementFile ||
                      formData.finDocFdTdReceiptsFile ||
                      formData.finDocInvestmentCertificatesFile ||
                      formData.finDocMutualFundStatementFile ||
                      formData.finDocDematStatementFile ||
                      formData.finDocCaCertificateFile ||
                      formData.finDocDpCertificateFile ||
                      formData.finDocAttestedFinancialsFile ||
                      formData.finDocNotarizedAffidavitFile ||
                      formData.finDocBankConfirmationLetterFile ||
                      formData.finDocPropertyDocumentsFile
                    ) && (
                      <span style={{ 
                        backgroundColor: '#fef2f2', 
                        color: '#dc2626', 
                        padding: '2px 8px', 
                        borderRadius: '4px', 
                        fontSize: '0.675rem', 
                        fontWeight: 600,
                        border: '1px solid #fecaca',
                        textDecoration: 'none'
                      }}>Required</span>
                    )}
                    {isMainApplicantSectionLocked && <span style={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '10px', textDecoration: 'none' }}>🔒 Disabled</span>}
                  </Label>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '10px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '5%', textAlign: 'center' }}>Sr. No.</th>
                        <th style={{ padding: '10px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '55%', textAlign: 'left' }}>Description</th>
                        <th style={{ padding: '10px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '15%', textAlign: 'center' }}>Yes / No</th>
                        <th style={{ padding: '10px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '25%', textAlign: 'center' }}>Upload the Document</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>1</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Detailed Project Report / Business Plan covering project overview, site details, estimated cost, funding pattern, projected CNG sales, revenue, expenses, and implementation schedule</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <label><input type="radio" name="finDocProjectReport" value="Yes" checked={formData.finDocProjectReport === 'Yes'} onChange={handleChange} disabled={isMainFieldDisabled} /> Yes</label>
                            <label><input type="radio" name="finDocProjectReport" value="No" checked={formData.finDocProjectReport === 'No'} onChange={handleChange} disabled={isMainFieldDisabled} /> No</label>
                          </div>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          {formData.finDocProjectReport === 'Yes' && (
                            <FadeInContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <FileUploadButton onClick={() => document.getElementById('finDocProjectReportFile')?.click()} disabled={!isOnline || isViewMode || isMainFieldDisabled || finDocUploadStatus['finDocProjectReportFile'] === 'uploading'} style={{ fontSize: '0.8em', padding: '4px 8px', opacity: finDocUploadStatus['finDocProjectReportFile'] === 'uploading' ? 0.7 : 1 }}>
                                {finDocUploadStatus['finDocProjectReportFile'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.finDocProjectReportFile ? 'Change' : 'Upload')}
                              </FileUploadButton>
                              {finDocUploadStatus['finDocProjectReportFile'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{finDocUploadProgress['finDocProjectReportFile'] || 0}%</span>}
                              {formData.finDocProjectReportFile && <button type="button" onClick={() => handleOpenFilePreview(formData.finDocProjectReportFile, 'finDocProjectReportFile')} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                              <input type="file" id="finDocProjectReportFile" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleProofFileUpload(e.target.files[0], 'finDocProjectReportFile'); }} />
                            </FadeInContainer>
                          )}
                          {formData.finDocProjectReportFile && finDocUploadStatus['finDocProjectReportFile'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>2</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Latest Savings Bank account statement (minimum last 6 months) in applicant's name</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <label><input type="radio" name="finDocBankStatement" value="Yes" checked={formData.finDocBankStatement === 'Yes'} onChange={handleChange} disabled={isMainFieldDisabled} /> Yes</label>
                            <label><input type="radio" name="finDocBankStatement" value="No" checked={formData.finDocBankStatement === 'No'} onChange={handleChange} disabled={isMainFieldDisabled} /> No</label>
                          </div>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          {formData.finDocBankStatement === 'Yes' && (
                            <FadeInContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <FileUploadButton onClick={() => document.getElementById('finDocBankStatementFile')?.click()} disabled={!isOnline || isViewMode || isMainFieldDisabled || finDocUploadStatus['finDocBankStatementFile'] === 'uploading'} style={{ fontSize: '0.8em', padding: '4px 8px', opacity: finDocUploadStatus['finDocBankStatementFile'] === 'uploading' ? 0.7 : 1 }}>
                                {finDocUploadStatus['finDocBankStatementFile'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.finDocBankStatementFile ? 'Change' : 'Upload')}
                              </FileUploadButton>
                              {finDocUploadStatus['finDocBankStatementFile'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{finDocUploadProgress['finDocBankStatementFile'] || 0}%</span>}
                              {formData.finDocBankStatementFile && <button type="button" onClick={() => handleOpenFilePreview(formData.finDocBankStatementFile, 'finDocBankStatementFile')} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                              <input type="file" id="finDocBankStatementFile" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleProofFileUpload(e.target.files[0], 'finDocBankStatementFile'); }} />
                            </FadeInContainer>
                          )}
                          {formData.finDocBankStatementFile && finDocUploadStatus['finDocBankStatementFile'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>3</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Fixed Deposit / Term Deposit receipts held with Scheduled Bank or Post Office</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <label><input type="radio" name="finDocFdTdReceipts" value="Yes" checked={formData.finDocFdTdReceipts === 'Yes'} onChange={handleChange} disabled={isMainFieldDisabled} /> Yes</label>
                            <label><input type="radio" name="finDocFdTdReceipts" value="No" checked={formData.finDocFdTdReceipts === 'No'} onChange={handleChange} disabled={isMainFieldDisabled} /> No</label>
                          </div>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          {formData.finDocFdTdReceipts === 'Yes' && (
                            <FadeInContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <FileUploadButton onClick={() => document.getElementById('finDocFdTdReceiptsFile')?.click()} disabled={!isOnline || isViewMode || isMainFieldDisabled || finDocUploadStatus['finDocFdTdReceiptsFile'] === 'uploading'} style={{ fontSize: '0.8em', padding: '4px 8px', opacity: finDocUploadStatus['finDocFdTdReceiptsFile'] === 'uploading' ? 0.7 : 1 }}>
                                {finDocUploadStatus['finDocFdTdReceiptsFile'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.finDocFdTdReceiptsFile ? 'Change' : 'Upload')}
                              </FileUploadButton>
                              {finDocUploadStatus['finDocFdTdReceiptsFile'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{finDocUploadProgress['finDocFdTdReceiptsFile'] || 0}%</span>}
                              {formData.finDocFdTdReceiptsFile && <button type="button" onClick={() => handleOpenFilePreview(formData.finDocFdTdReceiptsFile, 'finDocFdTdReceiptsFile')} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                              <input type="file" id="finDocFdTdReceiptsFile" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleProofFileUpload(e.target.files[0], 'finDocFdTdReceiptsFile'); }} />
                            </FadeInContainer>
                          )}
                          {formData.finDocFdTdReceiptsFile && finDocUploadStatus['finDocFdTdReceiptsFile'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>4</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Investment proof in Government savings instruments (Kisan Vikas Patra, NSC, Bonds, etc.)</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <label><input type="radio" name="finDocInvestmentCertificates" value="Yes" checked={formData.finDocInvestmentCertificates === 'Yes'} onChange={handleChange} disabled={isMainFieldDisabled} /> Yes</label>
                            <label><input type="radio" name="finDocInvestmentCertificates" value="No" checked={formData.finDocInvestmentCertificates === 'No'} onChange={handleChange} disabled={isMainFieldDisabled} /> No</label>
                          </div>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          {formData.finDocInvestmentCertificates === 'Yes' && (
                            <FadeInContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <FileUploadButton onClick={() => document.getElementById('finDocInvestmentCertificatesFile')?.click()} disabled={!isOnline || isViewMode || isMainFieldDisabled || finDocUploadStatus['finDocInvestmentCertificatesFile'] === 'uploading'} style={{ fontSize: '0.8em', padding: '4px 8px', opacity: finDocUploadStatus['finDocInvestmentCertificatesFile'] === 'uploading' ? 0.7 : 1 }}>
                                {finDocUploadStatus['finDocInvestmentCertificatesFile'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.finDocInvestmentCertificatesFile ? 'Change' : 'Upload')}
                              </FileUploadButton>
                              {finDocUploadStatus['finDocInvestmentCertificatesFile'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{finDocUploadProgress['finDocInvestmentCertificatesFile'] || 0}%</span>}
                              {formData.finDocInvestmentCertificatesFile && <button type="button" onClick={() => handleOpenFilePreview(formData.finDocInvestmentCertificatesFile, 'finDocInvestmentCertificatesFile')} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                              <input type="file" id="finDocInvestmentCertificatesFile" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleProofFileUpload(e.target.files[0], 'finDocInvestmentCertificatesFile'); }} />
                            </FadeInContainer>
                          )}
                          {formData.finDocInvestmentCertificatesFile && finDocUploadStatus['finDocInvestmentCertificatesFile'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>5</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Mutual Fund holding statement showing units and current valuation</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <label><input type="radio" name="finDocMutualFundStatement" value="Yes" checked={formData.finDocMutualFundStatement === 'Yes'} onChange={handleChange} disabled={isMainFieldDisabled} /> Yes</label>
                            <label><input type="radio" name="finDocMutualFundStatement" value="No" checked={formData.finDocMutualFundStatement === 'No'} onChange={handleChange} disabled={isMainFieldDisabled} /> No</label>
                          </div>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          {formData.finDocMutualFundStatement === 'Yes' && (
                            <FadeInContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <FileUploadButton onClick={() => document.getElementById('finDocMutualFundStatementFile')?.click()} disabled={!isOnline || isViewMode || isMainFieldDisabled || finDocUploadStatus['finDocMutualFundStatementFile'] === 'uploading'} style={{ fontSize: '0.8em', padding: '4px 8px', opacity: finDocUploadStatus['finDocMutualFundStatementFile'] === 'uploading' ? 0.7 : 1 }}>
                                {finDocUploadStatus['finDocMutualFundStatementFile'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.finDocMutualFundStatementFile ? 'Change' : 'Upload')}
                              </FileUploadButton>
                              {finDocUploadStatus['finDocMutualFundStatementFile'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{finDocUploadProgress['finDocMutualFundStatementFile'] || 0}%</span>}
                              {formData.finDocMutualFundStatementFile && <button type="button" onClick={() => handleOpenFilePreview(formData.finDocMutualFundStatementFile, 'finDocMutualFundStatementFile')} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                              <input type="file" id="finDocMutualFundStatementFile" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleProofFileUpload(e.target.files[0], 'finDocMutualFundStatementFile'); }} />
                            </FadeInContainer>
                          )}
                          {formData.finDocMutualFundStatementFile && finDocUploadStatus['finDocMutualFundStatementFile'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>6</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Shareholding statement / Demat holding statement from Depository Participant</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <label><input type="radio" name="finDocDematStatement" value="Yes" checked={formData.finDocDematStatement === 'Yes'} onChange={handleChange} disabled={isMainFieldDisabled} /> Yes</label>
                            <label><input type="radio" name="finDocDematStatement" value="No" checked={formData.finDocDematStatement === 'No'} onChange={handleChange} disabled={isMainFieldDisabled} /> No</label>
                          </div>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          {formData.finDocDematStatement === 'Yes' && (
                            <FadeInContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <FileUploadButton onClick={() => document.getElementById('finDocDematStatementFile')?.click()} disabled={!isOnline || isViewMode || isMainFieldDisabled || finDocUploadStatus['finDocDematStatementFile'] === 'uploading'} style={{ fontSize: '0.8em', padding: '4px 8px', opacity: finDocUploadStatus['finDocDematStatementFile'] === 'uploading' ? 0.7 : 1 }}>
                                {finDocUploadStatus['finDocDematStatementFile'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.finDocDematStatementFile ? 'Change' : 'Upload')}
                              </FileUploadButton>
                              {finDocUploadStatus['finDocDematStatementFile'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{finDocUploadProgress['finDocDematStatementFile'] || 0}%</span>}
                              {formData.finDocDematStatementFile && <button type="button" onClick={() => handleOpenFilePreview(formData.finDocDematStatementFile, 'finDocDematStatementFile')} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                              <input type="file" id="finDocDematStatementFile" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleProofFileUpload(e.target.files[0], 'finDocDematStatementFile'); }} />
                            </FadeInContainer>
                          )}
                          {formData.finDocDematStatementFile && finDocUploadStatus['finDocDematStatementFile'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>7</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Certificate from Chartered Accountant certifying ownership and valuation of Bonds, Mutual Funds, and Shares</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <label><input type="radio" name="finDocCaCertificate" value="Yes" checked={formData.finDocCaCertificate === 'Yes'} onChange={handleChange} disabled={isMainFieldDisabled} /> Yes</label>
                            <label><input type="radio" name="finDocCaCertificate" value="No" checked={formData.finDocCaCertificate === 'No'} onChange={handleChange} disabled={isMainFieldDisabled} /> No</label>
                          </div>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          {formData.finDocCaCertificate === 'Yes' && (
                            <FadeInContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <FileUploadButton onClick={() => document.getElementById('finDocCaCertificateFile')?.click()} disabled={!isOnline || isViewMode || isMainFieldDisabled || finDocUploadStatus['finDocCaCertificateFile'] === 'uploading'} style={{ fontSize: '0.8em', padding: '4px 8px', opacity: finDocUploadStatus['finDocCaCertificateFile'] === 'uploading' ? 0.7 : 1 }}>
                                {finDocUploadStatus['finDocCaCertificateFile'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.finDocCaCertificateFile ? 'Change' : 'Upload')}
                              </FileUploadButton>
                              {finDocUploadStatus['finDocCaCertificateFile'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{finDocUploadProgress['finDocCaCertificateFile'] || 0}%</span>}
                              {formData.finDocCaCertificateFile && <button type="button" onClick={() => handleOpenFilePreview(formData.finDocCaCertificateFile, 'finDocCaCertificateFile')} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                              <input type="file" id="finDocCaCertificateFile" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleProofFileUpload(e.target.files[0], 'finDocCaCertificateFile'); }} />
                            </FadeInContainer>
                          )}
                          {formData.finDocCaCertificateFile && finDocUploadStatus['finDocCaCertificateFile'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>8</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Certificate from Depository Participant confirming shareholding and market value</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <label><input type="radio" name="finDocDpCertificate" value="Yes" checked={formData.finDocDpCertificate === 'Yes'} onChange={handleChange} disabled={isMainFieldDisabled} /> Yes</label>
                            <label><input type="radio" name="finDocDpCertificate" value="No" checked={formData.finDocDpCertificate === 'No'} onChange={handleChange} disabled={isMainFieldDisabled} /> No</label>
                          </div>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          {formData.finDocDpCertificate === 'Yes' && (
                            <FadeInContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <FileUploadButton onClick={() => document.getElementById('finDocDpCertificateFile')?.click()} disabled={!isOnline || isViewMode || isMainFieldDisabled || finDocUploadStatus['finDocDpCertificateFile'] === 'uploading'} style={{ fontSize: '0.8em', padding: '4px 8px', opacity: finDocUploadStatus['finDocDpCertificateFile'] === 'uploading' ? 0.7 : 1 }}>
                                {finDocUploadStatus['finDocDpCertificateFile'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.finDocDpCertificateFile ? 'Change' : 'Upload')}
                              </FileUploadButton>
                              {finDocUploadStatus['finDocDpCertificateFile'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{finDocUploadProgress['finDocDpCertificateFile'] || 0}%</span>}
                              {formData.finDocDpCertificateFile && <button type="button" onClick={() => handleOpenFilePreview(formData.finDocDpCertificateFile, 'finDocDpCertificateFile')} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                              <input type="file" id="finDocDpCertificateFile" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleProofFileUpload(e.target.files[0], 'finDocDpCertificateFile'); }} />
                            </FadeInContainer>
                          )}
                          {formData.finDocDpCertificateFile && finDocUploadStatus['finDocDpCertificateFile'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>9</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Duly signed by the applicant and the Chartered Accountant, along with the Balance Sheet and Profit & Loss Statement for the last three financial years. These financial statements must bear the UDIN corresponding to the date of signing of the Balance Sheet and Profit & Loss Statement.</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <label><input type="radio" name="finDocAttestedFinancials" value="Yes" checked={formData.finDocAttestedFinancials === 'Yes'} onChange={handleChange} disabled={isMainFieldDisabled} /> Yes</label>
                            <label><input type="radio" name="finDocAttestedFinancials" value="No" checked={formData.finDocAttestedFinancials === 'No'} onChange={handleChange} disabled={isMainFieldDisabled} /> No</label>
                          </div>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          {formData.finDocAttestedFinancials === 'Yes' && (
                            <FadeInContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <FileUploadButton onClick={() => document.getElementById('finDocAttestedFinancialsFile')?.click()} disabled={!isOnline || isViewMode || isMainFieldDisabled || finDocUploadStatus['finDocAttestedFinancialsFile'] === 'uploading'} style={{ fontSize: '0.8em', padding: '4px 8px', opacity: finDocUploadStatus['finDocAttestedFinancialsFile'] === 'uploading' ? 0.7 : 1 }}>
                                {finDocUploadStatus['finDocAttestedFinancialsFile'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.finDocAttestedFinancialsFile ? 'Change' : 'Upload')}
                              </FileUploadButton>
                              {finDocUploadStatus['finDocAttestedFinancialsFile'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{finDocUploadProgress['finDocAttestedFinancialsFile'] || 0}%</span>}
                              {formData.finDocAttestedFinancialsFile && <button type="button" onClick={() => handleOpenFilePreview(formData.finDocAttestedFinancialsFile, 'finDocAttestedFinancialsFile')} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                              <input type="file" id="finDocAttestedFinancialsFile" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleProofFileUpload(e.target.files[0], 'finDocAttestedFinancialsFile'); }} />
                            </FadeInContainer>
                          )}
                          {formData.finDocAttestedFinancialsFile && finDocUploadStatus['finDocAttestedFinancialsFile'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>10</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Notarized affidavit from concerned family member confirming relationship, ownership of funds, and consent to support the project</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <label><input type="radio" name="finDocNotarizedAffidavit" value="Yes" checked={formData.finDocNotarizedAffidavit === 'Yes'} onChange={handleChange} disabled={isMainFieldDisabled} /> Yes</label>
                            <label><input type="radio" name="finDocNotarizedAffidavit" value="No" checked={formData.finDocNotarizedAffidavit === 'No'} onChange={handleChange} disabled={isMainFieldDisabled} /> No</label>
                          </div>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          {formData.finDocNotarizedAffidavit === 'Yes' && (
                            <FadeInContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <FileUploadButton onClick={() => document.getElementById('finDocNotarizedAffidavitFile')?.click()} disabled={!isOnline || isViewMode || isMainFieldDisabled || finDocUploadStatus['finDocNotarizedAffidavitFile'] === 'uploading'} style={{ fontSize: '0.8em', padding: '4px 8px', opacity: finDocUploadStatus['finDocNotarizedAffidavitFile'] === 'uploading' ? 0.7 : 1 }}>
                                {finDocUploadStatus['finDocNotarizedAffidavitFile'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.finDocNotarizedAffidavitFile ? 'Change' : 'Upload')}
                              </FileUploadButton>
                              {finDocUploadStatus['finDocNotarizedAffidavitFile'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{finDocUploadProgress['finDocNotarizedAffidavitFile'] || 0}%</span>}
                              {formData.finDocNotarizedAffidavitFile && <button type="button" onClick={() => handleOpenFilePreview(formData.finDocNotarizedAffidavitFile, 'finDocNotarizedAffidavitFile')} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                              <input type="file" id="finDocNotarizedAffidavitFile" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleProofFileUpload(e.target.files[0], 'finDocNotarizedAffidavitFile'); }} />
                            </FadeInContainer>
                          )}
                          {formData.finDocNotarizedAffidavitFile && finDocUploadStatus['finDocNotarizedAffidavitFile'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>11</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Bank letter confirming availability of funds and source of financing for the proposed project</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <label><input type="radio" name="finDocBankConfirmationLetter" value="Yes" checked={formData.finDocBankConfirmationLetter === 'Yes'} onChange={handleChange} disabled={isMainFieldDisabled} /> Yes</label>
                            <label><input type="radio" name="finDocBankConfirmationLetter" value="No" checked={formData.finDocBankConfirmationLetter === 'No'} onChange={handleChange} disabled={isMainFieldDisabled} /> No</label>
                          </div>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          {formData.finDocBankConfirmationLetter === 'Yes' && (
                            <FadeInContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <FileUploadButton onClick={() => document.getElementById('finDocBankConfirmationLetterFile')?.click()} disabled={!isOnline || isViewMode || isMainFieldDisabled || finDocUploadStatus['finDocBankConfirmationLetterFile'] === 'uploading'} style={{ fontSize: '0.8em', padding: '4px 8px', opacity: finDocUploadStatus['finDocBankConfirmationLetterFile'] === 'uploading' ? 0.7 : 1 }}>
                                {finDocUploadStatus['finDocBankConfirmationLetterFile'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.finDocBankConfirmationLetterFile ? 'Change' : 'Upload')}
                              </FileUploadButton>
                              {finDocUploadStatus['finDocBankConfirmationLetterFile'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{finDocUploadProgress['finDocBankConfirmationLetterFile'] || 0}%</span>}
                              {formData.finDocBankConfirmationLetterFile && <button type="button" onClick={() => handleOpenFilePreview(formData.finDocBankConfirmationLetterFile, 'finDocBankConfirmationLetterFile')} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                              <input type="file" id="finDocBankConfirmationLetterFile" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleProofFileUpload(e.target.files[0], 'finDocBankConfirmationLetterFile'); }} />
                            </FadeInContainer>
                          )}
                          {formData.finDocBankConfirmationLetterFile && finDocUploadStatus['finDocBankConfirmationLetterFile'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>12</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Ownership documents of immovable assets (land, residential/commercial property, shops, buildings, etc.)</td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <label><input type="radio" name="finDocPropertyDocuments" value="Yes" checked={formData.finDocPropertyDocuments === 'Yes'} onChange={handleChange} disabled={isMainFieldDisabled} /> Yes</label>
                            <label><input type="radio" name="finDocPropertyDocuments" value="No" checked={formData.finDocPropertyDocuments === 'No'} onChange={handleChange} disabled={isMainFieldDisabled} /> No</label>
                          </div>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                          {formData.finDocPropertyDocuments === 'Yes' && (
                            <FadeInContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <FileUploadButton onClick={() => document.getElementById('finDocPropertyDocumentsFile')?.click()} disabled={!isOnline || isViewMode || isMainFieldDisabled || finDocUploadStatus['finDocPropertyDocumentsFile'] === 'uploading'} style={{ fontSize: '0.8em', padding: '4px 8px', opacity: finDocUploadStatus['finDocPropertyDocumentsFile'] === 'uploading' ? 0.7 : 1 }}>
                                {finDocUploadStatus['finDocPropertyDocumentsFile'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.finDocPropertyDocumentsFile ? 'Change' : 'Upload')}
                              </FileUploadButton>
                              {finDocUploadStatus['finDocPropertyDocumentsFile'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{finDocUploadProgress['finDocPropertyDocumentsFile'] || 0}%</span>}
                              {formData.finDocPropertyDocumentsFile && <button type="button" onClick={() => handleOpenFilePreview(formData.finDocPropertyDocumentsFile, 'finDocPropertyDocumentsFile')} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                              <input type="file" id="finDocPropertyDocumentsFile" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleProofFileUpload(e.target.files[0], 'finDocPropertyDocumentsFile'); }} />
                            </FadeInContainer>
                          )}
                          {formData.finDocPropertyDocumentsFile && finDocUploadStatus['finDocPropertyDocumentsFile'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </FormFieldFullWidth>
                </ShakeAnimation>
              )}

              {/* APPENDIX-A AFFIDAVIT SECTION - Conditionally shown based on admin Appendix Logic settings */}
              <ShakeAnimation $isShaking={isAppendixCardsShaking} ref={appendixCardsRef}>
              {visibleAppendixes.includes('A') && (
              <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: '#fef9e7', borderRadius: '10px', border: '2px solid #f39c12' }}>
                {!isIndividualOrProprietorship && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                  <span style={{ display: 'inline-block', padding: '6px 14px', backgroundColor: '#f39c12', color: 'white', borderRadius: '20px', fontSize: '0.85em', fontWeight: 600 }}>
                    {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #1 {formData.nameInBlocks ? `(${formData.nameInBlocks})` : ''}
                  </span>
                </div>
                )}
                <Label style={{ fontWeight: 700, marginBottom: '15px', color: '#9a7b0a', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline' }}>
                  APPENDIX – A: AFFIDAVIT CUM UNDERTAKING
                  {visibleAppendixes.includes('A') && !formData.affidavitFile && (
                    <span style={{ 
                      backgroundColor: '#fef2f2', 
                      color: '#dc2626', 
                      padding: '2px 8px', 
                      borderRadius: '4px', 
                      fontSize: '0.675rem', 
                      fontWeight: 600,
                      border: '1px solid #fecaca',
                      textDecoration: 'none'
                    }}>Required</span>
                  )}
                  {isMainApplicantSectionLocked && <span style={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '10px', textDecoration: 'none' }}>🔒 Disabled</span>}
                </Label>
                <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #f39c12' }}>
                  <p style={{ margin: '0' }}>
                    Affidavit-cum-Undertaking by the Applicant (Individual/Partner) declaring eligibility, financial capability, and non-involvement in criminal or malpractices cases.
                  </p>
                  <p style={{ margin: '10px 0 0 0', fontStyle: 'italic', color: '#888' }}>
                    📄 Download the editable template (.docx), fill details, print on Rs. 500 Stamp Paper, get notarized, and upload.
                  </p>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
                  <a href="/appendix_all/APPENDIX-A.docx" download style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#f39c12', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}>
                    📥 Download Appendix-A Template
                  </a>
                  <button type="button" onClick={() => handleOpenFilePreview('/appendix_all/APPENDIX-A.pdf', 'APPENDIX-A-Template')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#10b981', color: 'white', borderRadius: '6px', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                    View PDF
                  </button>
                  <FileUploadButton onClick={(e) => { e.preventDefault(); affidavitFileInputRef.current?.click(); }} disabled={isAffidavitFileUploading || isMainFieldDisabled}>
                    {formData.affidavitFile ? '🔄 Change Upload' : '📤 Upload Signed'}
                  </FileUploadButton>
                </div>
                <input type="file" ref={affidavitFileInputRef} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files[0]) { handleProofFileUpload(e.target.files[0], 'affidavitFile'); } }} disabled={isMainFieldDisabled} />
                {isAffidavitFileUploading && (
                  <div style={{ marginTop: '10px' }}>
                    <FileUploadProgressBar><FileUploadProgressFill progress={affidavitFileProgress} /></FileUploadProgressBar>
                  </div>
                )}
                {affidavitFileUploadMessage && <FileUploadSuccess style={{ marginTop: '10px' }}>✓ {affidavitFileUploadMessage}</FileUploadSuccess>}
                {formData.affidavitFile && !isAffidavitFileUploading && !affidavitFileUploadMessage && (
                  <FileUploadStatus style={{ marginTop: '10px' }}>
                    ✅ Uploaded: <ClickableFileName onClick={() => handleOpenFilePreview(formData.affidavitFile, 'affidavitFile')}>{typeof formData.affidavitFile === 'string' ? formData.affidavitFile : formData.affidavitFile?.name}</ClickableFileName>
                  </FileUploadStatus>
                )}
              </FormFieldFullWidth>
              )}

              {/* APPENDIX-B SECTION - Conditionally shown based on admin Appendix Logic settings */}
              {visibleAppendixes.includes('B') && (
              <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: '#ebf5fb', borderRadius: '10px', border: '2px solid #3498db' }}>
                {!isIndividualOrProprietorship && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                  <span style={{ display: 'inline-block', padding: '6px 14px', backgroundColor: '#3498db', color: 'white', borderRadius: '20px', fontSize: '0.85em', fontWeight: 600 }}>
                    {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #1 {formData.nameInBlocks ? `(${formData.nameInBlocks})` : ''}
                  </span>
                </div>
                )}
                <Label style={{ fontWeight: 700, marginBottom: '15px', color: '#2874a6', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline' }}>
                  APPENDIX – B: AFFIDAVIT FOR INDIVIDUAL & PARTNERS
                  {mandatoryFields.appendixCards && !formData.appendixBFile && (
                    <span style={{ 
                      backgroundColor: '#fef2f2', 
                      color: '#dc2626', 
                      padding: '2px 8px', 
                      borderRadius: '4px', 
                      fontSize: '0.675rem', 
                      fontWeight: 600,
                      border: '1px solid #fecaca',
                      textDecoration: 'none'
                    }}>Required</span>
                  )}
                  {isMainApplicantSectionLocked && <span style={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '10px', textDecoration: 'none' }}>🔒 Disabled</span>}
                </Label>
                <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #3498db' }}>
                  <p style={{ margin: '0' }}>Affidavit by Individual / Partner declaring eligibility, financial capability, and non-involvement in criminal or malpractices cases.</p>
                  <p style={{ margin: '10px 0 0 0', fontStyle: 'italic', color: '#888' }}>📄 Download the editable template (.docx), fill details, print on Rs. 500 Stamp Paper, get notarized, and upload.</p>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
                  <a href="/appendix_all/APPENDIX-B.docx" download style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#3498db', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}>📥 Download Appendix-B Template</a>
                  <button type="button" onClick={() => handleOpenFilePreview('/appendix_all/APPENDIX-B.pdf', 'APPENDIX-B-Template')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#10b981', color: 'white', borderRadius: '6px', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg> View PDF</button>
                  <FileUploadButton onClick={(e) => { e.preventDefault(); document.getElementById('appendixBFileInput')?.click(); }} disabled={isMainFieldDisabled}>{formData.appendixBFile ? '🔄 Change Upload' : '📤 Upload Signed'}</FileUploadButton>
                </div>
                <input type="file" id="appendixBFileInput" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files[0]) { handleProofFileUpload(e.target.files[0], 'appendixBFile'); } }} disabled={isMainFieldDisabled} />
                {isAffidavitBFileUploading && (
                  <div style={{ marginTop: '10px' }}>
                    <FileUploadProgressBar><FileUploadProgressFill progress={affidavitBFileProgress} /></FileUploadProgressBar>
                  </div>
                )}
                {affidavitBFileUploadMessage && <FileUploadSuccess style={{ marginTop: '10px' }}>✓ {affidavitBFileUploadMessage}</FileUploadSuccess>}
                {formData.appendixBFile && !isAffidavitBFileUploading && !affidavitBFileUploadMessage && (
                  <FileUploadStatus style={{ marginTop: '10px' }}>✅ Uploaded: <ClickableFileName onClick={() => handleOpenFilePreview(formData.appendixBFile, 'appendixBFile')}>{typeof formData.appendixBFile === 'string' ? formData.appendixBFile : formData.appendixBFile?.name}</ClickableFileName></FileUploadStatus>
                )}
              </FormFieldFullWidth>
              )}

              {/* APPENDIX-C SECTION - Conditionally shown based on admin Appendix Logic settings */}
              {visibleAppendixes.includes('C') && (
              <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: '#e8f8f5', borderRadius: '10px', border: '2px solid #1abc9c' }}>
                {!isIndividualOrProprietorship && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                  <span style={{ display: 'inline-block', padding: '6px 14px', backgroundColor: '#1abc9c', color: 'white', borderRadius: '20px', fontSize: '0.85em', fontWeight: 600 }}>
                    {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #1 {formData.nameInBlocks ? `(${formData.nameInBlocks})` : ''}
                  </span>
                </div>
                )}
                <Label style={{ fontWeight: 700, marginBottom: '15px', color: '#148f77', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline' }}>
                  APPENDIX – C: AFFIDAVIT FOR NON-INDIVIDUAL ENTITY APPLICANT
                  {mandatoryFields.appendixCards && !formData.appendixCFile && (
                    <span style={{ 
                      backgroundColor: '#fef2f2', 
                      color: '#dc2626', 
                      padding: '2px 8px', 
                      borderRadius: '4px', 
                      fontSize: '0.675rem', 
                      fontWeight: 600,
                      border: '1px solid #fecaca',
                      textDecoration: 'none'
                    }}>Required</span>
                  )}
                </Label>
                <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #1abc9c' }}>
                  <p style={{ margin: '0' }}>Affidavit by Authorized Signatory of a Non-Individual Entity declaring authorization, legal status, eligibility, financial capability, and non-involvement in criminal or malpractices cases.</p>
                  <p style={{ margin: '10px 0 0 0', fontStyle: 'italic', color: '#888' }}>📄 Download the editable template (.docx), fill details, print on Rs. 500 Stamp Paper, get notarized, and upload.</p>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
                  <a href="/appendix_all/APPENDIX-C.docx" download style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#1abc9c', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}>📥 Download Appendix-C Template</a>
                  <button type="button" onClick={() => handleOpenFilePreview('/appendix_all/APPENDIX-C.pdf', 'APPENDIX-C-Template')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#10b981', color: 'white', borderRadius: '6px', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg> View PDF</button>
                  <FileUploadButton onClick={(e) => { e.preventDefault(); document.getElementById('appendixCFileInput')?.click(); }} disabled={isMainFieldDisabled}>{formData.appendixCFile ? '🔄 Change Upload' : '📤 Upload Signed'}</FileUploadButton>
                </div>
                <input type="file" id="appendixCFileInput" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files[0]) { handleProofFileUpload(e.target.files[0], 'appendixCFile'); } }} disabled={isMainFieldDisabled} />
                {isAppendixCFileUploading && (
                  <div style={{ marginTop: '10px' }}>
                    <FileUploadProgressBar><FileUploadProgressFill progress={appendixCFileProgress} /></FileUploadProgressBar>
                  </div>
                )}
                {appendixCFileUploadMessage && <FileUploadSuccess style={{ marginTop: '10px' }}>✓ {appendixCFileUploadMessage}</FileUploadSuccess>}
                {formData.appendixCFile && !isAppendixCFileUploading && !appendixCFileUploadMessage && (
                  <FileUploadStatus style={{ marginTop: '10px' }}>✅ Uploaded: <ClickableFileName onClick={() => handleOpenFilePreview(formData.appendixCFile, 'appendixCFile')}>{typeof formData.appendixCFile === 'string' ? formData.appendixCFile : formData.appendixCFile?.name}</ClickableFileName></FileUploadStatus>
                )}
              </FormFieldFullWidth>
              )}

              {/* APPENDIX-D SECTION - Conditionally shown based on admin Appendix Logic settings */}
              {visibleAppendixes.includes('D') && (
              <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: '#fdf2e9', borderRadius: '10px', border: '2px solid #e67e22' }}>
                {!isIndividualOrProprietorship && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                  <span style={{ display: 'inline-block', padding: '6px 14px', backgroundColor: '#e67e22', color: 'white', borderRadius: '20px', fontSize: '0.85em', fontWeight: 600 }}>
                    {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #1 {formData.nameInBlocks ? `(${formData.nameInBlocks})` : ''}
                  </span>
                </div>
                )}
                <Label style={{ fontWeight: 700, marginBottom: '15px', color: '#ca6f1e', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline' }}>
                  APPENDIX – D: OWNERSHIP OF PROPERTY / OFFERED LAND
                  {mandatoryFields.appendixCards && !formData.appendixDFile && (
                    <span style={{ 
                      backgroundColor: '#fef2f2', 
                      color: '#dc2626', 
                      padding: '2px 8px', 
                      borderRadius: '4px', 
                      fontSize: '0.675rem', 
                      fontWeight: 600,
                      border: '1px solid #fecaca',
                      textDecoration: 'none'
                    }}>Required</span>
                  )}
                </Label>
                <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #e67e22' }}>
                  <p style={{ margin: '0' }}>Affidavit-cum-Undertaking by the Original Owner declaring ownership, clear and marketable title, encumbrance-free status, and consent for use of the offered land for MNGL DODO CNG Station.</p>
                  <p style={{ margin: '10px 0 0 0', fontStyle: 'italic', color: '#888' }}>📄 Download the editable template (.docx), fill details, print on Rs. 500 Stamp Paper, get notarized, and upload.</p>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
                  <a href="/appendix_all/APPENDIX-D.docx" download style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#e67e22', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}>📥 Download Appendix-D Template</a>
                  <button type="button" onClick={() => handleOpenFilePreview('/appendix_all/APPENDIX-D.pdf', 'APPENDIX-D-Template')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#10b981', color: 'white', borderRadius: '6px', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg> View PDF</button>
                  <FileUploadButton onClick={(e) => { e.preventDefault(); document.getElementById('appendixDFileInput')?.click(); }} disabled={isMainFieldDisabled}>{formData.appendixDFile ? '🔄 Change Upload' : '📤 Upload Signed'}</FileUploadButton>
                </div>
                <input type="file" id="appendixDFileInput" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files[0]) { handleProofFileUpload(e.target.files[0], 'appendixDFile'); } }} disabled={isMainFieldDisabled} />
                {isAppendixDFileUploading && (
                  <div style={{ marginTop: '10px' }}>
                    <FileUploadProgressBar><FileUploadProgressFill progress={appendixDFileProgress} /></FileUploadProgressBar>
                  </div>
                )}
                {appendixDFileUploadMessage && <FileUploadSuccess style={{ marginTop: '10px' }}>✓ {appendixDFileUploadMessage}</FileUploadSuccess>}
                {formData.appendixDFile && !isAppendixDFileUploading && !appendixDFileUploadMessage && (
                  <FileUploadStatus style={{ marginTop: '10px' }}>✅ Uploaded: <ClickableFileName onClick={() => handleOpenFilePreview(formData.appendixDFile, 'appendixDFile')}>{typeof formData.appendixDFile === 'string' ? formData.appendixDFile : formData.appendixDFile?.name}</ClickableFileName></FileUploadStatus>
                )}
              </FormFieldFullWidth>
              )}

              {/* APPENDIX-E SECTION - Conditionally shown based on admin Appendix Logic settings */}
              {visibleAppendixes.includes('E') && (
              <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: '#f4ecf7', borderRadius: '10px', border: '2px solid #9b59b6' }}>
                {!isIndividualOrProprietorship && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                  <span style={{ display: 'inline-block', padding: '6px 14px', backgroundColor: '#9b59b6', color: 'white', borderRadius: '20px', fontSize: '0.85em', fontWeight: 600 }}>
                    {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #1 {formData.nameInBlocks ? `(${formData.nameInBlocks})` : ''}
                  </span>
                </div>
                )}
                <Label style={{ fontWeight: 700, marginBottom: '15px', color: '#7d3c98', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline' }}>
                  APPENDIX – E: ADVOCATE'S CERTIFICATE / LEGAL OPINION
                  {mandatoryFields.appendixCards && !formData.appendixEFile && (
                    <span style={{ 
                      backgroundColor: '#fef2f2', 
                      color: '#dc2626', 
                      padding: '2px 8px', 
                      borderRadius: '4px', 
                      fontSize: '0.675rem', 
                      fontWeight: 600,
                      border: '1px solid #fecaca',
                      textDecoration: 'none'
                    }}>Required</span>
                  )}
                </Label>
                <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #9b59b6' }}>
                  <p style={{ margin: '0' }}>Advocate's Certificate / Legal Opinion on Title of Offered Land confirming clear title and legal compliance.</p>
                  <p style={{ margin: '10px 0 0 0', fontStyle: 'italic', color: '#888' }}>📄 Download the editable template (.docx), get it filled by your advocate, and upload.</p>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
                  <a href="/appendix_all/APPENDIX-E.docx" download style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#9b59b6', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}>📥 Download Appendix-E Template</a>
                  <button type="button" onClick={() => handleOpenFilePreview('/appendix_all/APPENDIX-E.pdf', 'APPENDIX-E-Template')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#10b981', color: 'white', borderRadius: '6px', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg> View PDF</button>
                  <FileUploadButton onClick={(e) => { e.preventDefault(); document.getElementById('appendixEFileInput')?.click(); }} disabled={isMainFieldDisabled}>{formData.appendixEFile ? '🔄 Change Upload' : '📤 Upload Signed'}</FileUploadButton>
                </div>
                <input type="file" id="appendixEFileInput" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files[0]) { handleProofFileUpload(e.target.files[0], 'appendixEFile'); } }} disabled={isMainFieldDisabled} />
                {isAppendixEFileUploading && (
                  <div style={{ marginTop: '10px' }}>
                    <FileUploadProgressBar><FileUploadProgressFill progress={appendixEFileProgress} /></FileUploadProgressBar>
                  </div>
                )}
                {appendixEFileUploadMessage && <FileUploadSuccess style={{ marginTop: '10px' }}>✓ {appendixEFileUploadMessage}</FileUploadSuccess>}
                {formData.appendixEFile && !isAppendixEFileUploading && !appendixEFileUploadMessage && (
                  <FileUploadStatus style={{ marginTop: '10px' }}>✅ Uploaded: <ClickableFileName onClick={() => handleOpenFilePreview(formData.appendixEFile, 'appendixEFile')}>{typeof formData.appendixEFile === 'string' ? formData.appendixEFile : formData.appendixEFile?.name}</ClickableFileName></FileUploadStatus>
                )}
              </FormFieldFullWidth>
              )}

              {/* APPENDIX-F SECTION - Conditionally shown based on admin Appendix Logic settings */}
              {visibleAppendixes.includes('F') && (
              <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: '#eafaf1', borderRadius: '10px', border: '2px solid #27ae60' }}>
                {!isIndividualOrProprietorship && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                  <span style={{ display: 'inline-block', padding: '6px 14px', backgroundColor: '#27ae60', color: 'white', borderRadius: '20px', fontSize: '0.85em', fontWeight: 600 }}>
                    {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #1 {formData.nameInBlocks ? `(${formData.nameInBlocks})` : ''}
                  </span>
                </div>
                )}
                <Label style={{ fontWeight: 700, marginBottom: '15px', color: '#1e8449', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline' }}>
                  APPENDIX – F: LAND OWNER CONSENT / NOC
                  {mandatoryFields.appendixCards && !formData.appendixFFile && (
                    <span style={{ 
                      backgroundColor: '#fef2f2', 
                      color: '#dc2626', 
                      padding: '2px 8px', 
                      borderRadius: '4px', 
                      fontSize: '0.675rem', 
                      fontWeight: 600,
                      border: '1px solid #fecaca',
                      textDecoration: 'none'
                    }}>Required</span>
                  )}
                </Label>
                <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #27ae60' }}>
                  <p style={{ margin: '0' }}>Land owner consent or No Objection Certificate for use of land for CNG Station.</p>
                  <p style={{ margin: '10px 0 0 0', fontStyle: 'italic', color: '#888' }}>📄 Download the editable template (.docx), fill details, print on Rs. 500 Stamp Paper, get notarized, and upload.</p>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
                  <a href="/appendix_all/APPENDIX-F.docx" download style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#27ae60', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}>📥 Download Appendix-F Template</a>
                  <button type="button" onClick={() => handleOpenFilePreview('/appendix_all/APPENDIX-F.pdf', 'APPENDIX-F-Template')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#10b981', color: 'white', borderRadius: '6px', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg> View PDF</button>
                  <FileUploadButton onClick={(e) => { e.preventDefault(); document.getElementById('appendixFFileInput')?.click(); }} disabled={isMainFieldDisabled}>{formData.appendixFFile ? '🔄 Change Upload' : '📤 Upload Signed'}</FileUploadButton>
                </div>
                <input type="file" id="appendixFFileInput" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files[0]) { handleProofFileUpload(e.target.files[0], 'appendixFFile'); } }} disabled={isMainFieldDisabled} />
                {isAppendixFFileUploading && (
                  <div style={{ marginTop: '10px' }}>
                    <FileUploadProgressBar><FileUploadProgressFill progress={appendixFFileProgress} /></FileUploadProgressBar>
                  </div>
                )}
                {appendixFFileUploadMessage && <FileUploadSuccess style={{ marginTop: '10px' }}>✓ {appendixFFileUploadMessage}</FileUploadSuccess>}
                {formData.appendixFFile && !isAppendixFFileUploading && !appendixFFileUploadMessage && (
                  <FileUploadStatus style={{ marginTop: '10px' }}>✅ Uploaded: <ClickableFileName onClick={() => handleOpenFilePreview(formData.appendixFFile, 'appendixFFile')}>{typeof formData.appendixFFile === 'string' ? formData.appendixFFile : formData.appendixFFile?.name}</ClickableFileName></FileUploadStatus>
                )}
              </FormFieldFullWidth>
              )}
              </ShakeAnimation>
              </SlideDownContainer>
            </>
          ) : null}

          {/* Dynamic Partner/Director Sections - shown when LLP, Pvt Ltd, Public Ltd, etc. is selected */}
          {/* Block entire section if primary applicant fails age or education check */}
          {/* For Pvt Ltd/Public Ltd, only show when both company registration conditions are met */}
          {requiresMultiplePartners && formData.partnerDirectorDetails && formData.partnerDirectorDetails.length > 0 && isPvtLtdFormVisible && (
            <SlideDownContainer style={{ 
              opacity: (dobAgeError || formData.isMinimumEducationMet === 'No') ? 0.5 : 1,
              pointerEvents: (dobAgeError || formData.isMinimumEducationMet === 'No') ? 'none' : 'auto'
            }}>
              {(dobAgeError || formData.isMinimumEducationMet === 'No') && (
                <div style={{
                  padding: '12px 15px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '8px',
                  marginBottom: '15px',
                  color: '#856404',
                  fontWeight: 500
                }}>
                  ⚠️ Partner/Director details are blocked because the primary applicant does not meet eligibility criteria.
                  {dobAgeError && ' Age must be between 21-65 years.'}
                  {formData.isMinimumEducationMet === 'No' && ' Minimum Intermediate (Class 10) education is required.'}
                </div>
              )}
              {formData.partnerDirectorDetails.map((partner, index) => {
                // Computed: Check if ANY statutory document is being uploaded for this partner
                const isPartnerStatutoryUploading = partnerAiDetection[`${index}_aadhaar`] || partnerAiDetection[`${index}_pan`] || partnerAiDetection[`${index}_gst`];
                // Computed: Check if ANY education certificate is being uploaded for this partner
                const isPartnerAnyEduCertUploading = partnerEduCertUploading[`${index}_Ssc`] || partnerEduCertUploading[`${index}_Hsc`] || partnerEduCertUploading[`${index}_Graduate`] || partnerEduCertUploading[`${index}_Others`];
                // Check if this partner section is locked for current editing user
                const isSectionLocked = isPartnerSectionLocked(index);
                
                return (
                <React.Fragment key={partner.id}>
                {/* Dotted Line Separator with Partner Name */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '40px 0 20px 0',
                  position: 'relative'
                }}>
                  <div style={{ 
                    flex: 1, 
                    height: '2px', 
                    background: 'repeating-linear-gradient(90deg, #dc2626 0px, #dc2626 8px, transparent 8px, transparent 16px)',
                    marginRight: '15px'
                  }} />
                  <span style={{ 
                    padding: '8px 20px', 
                    backgroundColor: '#fef2f2', 
                    border: '2px solid #dc2626', 
                    borderRadius: '25px', 
                    fontSize: '0.9em', 
                    fontWeight: 600, 
                    color: '#dc2626',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 8px rgba(220, 38, 38, 0.15)'
                  }}>
                    {partner.nameInBlocks ? `${partner.nameInBlocks}'s` : `Partner/Director ${index + 2}'s`} Section Starts Below
                  </span>
                  <div style={{ 
                    flex: 1, 
                    height: '2px', 
                    background: 'repeating-linear-gradient(90deg, #dc2626 0px, #dc2626 8px, transparent 8px, transparent 16px)',
                    marginLeft: '15px'
                  }} />
                </div>
                <div 
                  style={{ 
                    marginTop: '10px', 
                    padding: '20px', 
                    backgroundColor: isSectionLocked ? '#f1f5f9' : (index === 0 ? '#f8f9fa' : '#fff7ed'), 
                    borderRadius: '10px', 
                    border: isSectionLocked ? '2px solid #94a3b8' : (index === 0 ? '2px solid #4A6CF7' : '2px solid #f97316'),
                    position: 'relative',
                    opacity: isSectionLocked ? 0.7 : 1,
                    pointerEvents: isSectionLocked ? 'none' : 'auto'
                  }}
                >
                  {/* Locked Section Banner */}
                  {isSectionLocked && (
                    <LockedSectionBanner>
                      <span style={{ fontSize: '1.2rem' }}>🔒</span>
                      <span>This section is locked. Only <strong>{partner.nameInBlocks || `Partner/Director ${index + 2}`}</strong> can edit this section.</span>
                    </LockedSectionBanner>
                  )}
                  <SectionTitle style={{ marginBottom: '15px', color: isSectionLocked ? '#64748b' : (index === 0 ? '#4A6CF7' : '#ea580c') }}>
                    {index + 2}. {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? `Partner No.${index + 2}` : `Director No.${index + 2}`}:{partner.nameInBlocks ? ` (${partner.nameInBlocks})` : ''}
                  </SectionTitle>

                  {/* PAN Card Autofill Section - Prominent placement to encourage upload */}
                  <FormFieldFullWidth style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e8f4fd', borderRadius: '8px', border: '2px dashed #3498db' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <span style={{ fontSize: '1.5em' }}>🪪</span>
                      <Label style={{ margin: 0, color: '#1a5276', fontWeight: 700, fontSize: '1em' }}>Upload PAN Card to Auto-fill Name & Date of Birth</Label>
                    </div>
                    <p style={{ margin: '0 0 12px 0', fontSize: '0.9em', color: '#2c3e50', lineHeight: '1.4' }}>
                      <strong>Recommended:</strong> Upload PAN Card image and the fields below (<em>Full Name, Father/Spouse Name, Date of Birth</em>) will be <span style={{ color: '#27ae60', fontWeight: 600 }}>automatically filled</span>.
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          const input = document.getElementById(`partner-pan-${index}`) as HTMLInputElement;
                          input?.click();
                        }}
                        disabled={partnerFileUploading[`${index}_panCardFile`] || !isOnline || isViewMode}
                        style={{
                          padding: '14px 28px',
                          fontSize: '1.05em',
                          fontWeight: 700,
                          backgroundColor: partner.panCardFile ? '#27ae60' : '#2ecc71',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: partnerFileUploading[`${index}_panCardFile`] || !isOnline || isViewMode ? 'not-allowed' : 'pointer',
                          opacity: partnerFileUploading[`${index}_panCardFile`] || !isOnline || isViewMode ? 0.6 : 1,
                          boxShadow: '0 4px 12px rgba(46, 204, 113, 0.4)',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <span style={{ fontSize: '1.2em' }}>📤</span>
                        {partner.panCardFile ? 'Change PAN Card' : 'Upload PAN Card'}
                      </button>
                      {partnerFileUploading[`${index}_panCardFile`] && (
                        <div style={{ flex: 1, minWidth: '160px' }}>
                          <FileUploadProgressBar>
                            <FileUploadProgressFill progress={partnerFileProgress[`${index}_panCardFile`] || 0} />
                          </FileUploadProgressBar>
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      id={`partner-pan-${index}`}
                      accept=".jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handlePartnerFileUpload(index, e.target.files[0], 'panCardFile');
                        }
                      }}
                      disabled={!isOnline || isViewMode || isSectionLocked}
                    />
                    {/* Skeleton loader while uploading */}
                    {partnerFileUploading[`${index}_panCardFile`] && (
                      <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <SkeletonBox />
                        <span style={{ color: '#666', fontSize: '0.9em' }}>Processing PAN Card...</span>
                      </div>
                    )}
                    {/* File display */}
                    {partner.panCardFile && !partnerFileUploading[`${index}_panCardFile`] && (
                      <FileUploadStatus style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
                        <span>File: <ClickableFileName onClick={() => handleOpenFilePreview(partner.panCardFile, `partner_${index}_pan_card`)}>
                          {typeof partner.panCardFile === 'string' ? partner.panCardFile : (partner.panCardFile as File)?.name}
                        </ClickableFileName></span>
                        <button
                          type="button"
                          onClick={() => handleOpenFilePreview(partner.panCardFile, `partner_${index}_pan_card`)}
                          style={{ padding: '8px 16px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.9em', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                        >
                          📄 View PAN Card
                        </button>
                      </FileUploadStatus>
                    )}
                    {partnerFileMessage[`${index}_panCardFile`] && (
                      <FileUploadSuccess style={{ marginTop: '8px' }}>✓ {partnerFileMessage[`${index}_panCardFile`]}</FileUploadSuccess>
                    )}
                  </FormFieldFullWidth>

                  {/* Partner/Director Photo Upload */}
                  <ShakeAnimation $isShaking={partnerPhotoShaking[index] || false}>
                  <div 
                    ref={(el) => { partnerPhotoRefs.current[index] = el; }}
                    style={{ display: 'flex', gap: '20px', marginBottom: '20px', alignItems: 'flex-start', flexWrap: 'wrap', backgroundColor: partnerPhotoShaking[index] ? '#fff5f5' : 'transparent', border: partnerPhotoShaking[index] ? '2px solid #e53e3e' : 'none', borderRadius: '8px', padding: partnerPhotoShaking[index] ? '10px' : '0', transition: 'all 0.3s ease' }}>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      minWidth: '150px',
                      position: 'relative'
                    }}>
                      <div 
                        onClick={() => {
                          if (!isViewMode && isOnline) {
                            if (partner.photo) {
                              // Toggle menu if photo exists
                              setPartnerPhotoMenuOpen(partnerPhotoMenuOpen === index ? null : index);
                            } else {
                              // Open file picker if no photo
                              const input = document.getElementById(`partner-photo-${index}`) as HTMLInputElement;
                              input?.click();
                            }
                          }
                        }}
                        style={{ 
                          width: '130px', 
                          height: '160px', 
                          border: index === 0 ? '2px dashed #4A6CF7' : '2px dashed #f97316', 
                          borderRadius: '8px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          cursor: !isViewMode && isOnline ? 'pointer' : 'not-allowed',
                          backgroundColor: '#f8fafc',
                          overflow: 'hidden',
                          position: 'relative'
                        }}
                      >
                        {partner.photo ? (
                          <img 
                            key={typeof partner.photo === 'string' ? partner.photo : 'file-upload'}
                            src={(() => {
                              const photoVal = partner.photo;
                              if (typeof photoVal !== 'string') {
                                return URL.createObjectURL(photoVal);
                              }
                              if (photoVal.startsWith('data:') || photoVal.startsWith('http')) {
                                return photoVal;
                              }
                              // Server returns path like /uploads/012026/email/PARTNER_0_PHOTO_...
                              // Use relative path (same as primary photo) to avoid CORS issues
                              let imgPath = photoVal;
                              if (!imgPath.startsWith('/')) {
                                imgPath = imgPath.startsWith('uploads/') ? `/${imgPath}` : `/uploads/${imgPath}`;
                              }
                              return imgPath;
                            })()} 
                            alt={`${partner.nameInBlocks || 'Partner'} Photo`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => { 
                              console.error('[Partner Photo] Load error:', partner.photo);
                            }}
                          />
                        ) : (
                          <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.85em', padding: '10px' }}>
                            <div style={{ fontSize: '2em', marginBottom: '5px' }}>📷</div>
                            <div>Upload Photo</div>
                          </div>
                        )}
                      </div>
                      {/* Photo Options Menu - Right Side */}
                      {partnerPhotoMenuOpen === index && partner.photo && !isViewMode && isOnline && (
                        <div style={{
                          position: 'absolute',
                          top: '0',
                          left: '140px',
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                          zIndex: 100,
                          minWidth: '160px',
                          overflow: 'hidden',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ padding: '8px 12px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '0.75em', color: '#64748b', fontWeight: 600 }}>
                            Photo Options
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handlePartnerPhotoEdit(index); setPartnerPhotoMenuOpen(null); }}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: 'none',
                              background: 'white',
                              cursor: 'pointer',
                              textAlign: 'left',
                              fontSize: '0.9em',
                              fontWeight: 500,
                              color: '#1e40af',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              transition: 'background-color 0.15s'
                            }}
                            onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#eff6ff'}
                            onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'white'}
                          >
                            <span style={{ fontSize: '1.1em' }}>✏️</span> View / Edit
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handlePartnerPhotoDelete(index); }}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: 'none',
                              background: 'white',
                              cursor: 'pointer',
                              textAlign: 'left',
                              fontSize: '0.9em',
                              fontWeight: 500,
                              color: '#dc2626',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              transition: 'background-color 0.15s'
                            }}
                            onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#fef2f2'}
                            onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'white'}
                          >
                            <span style={{ fontSize: '1.1em' }}>🗑️</span> Delete
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setPartnerPhotoMenuOpen(null);
                              const input = document.getElementById(`partner-photo-${index}`) as HTMLInputElement;
                              input?.click();
                            }}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: 'none',
                              background: 'white',
                              cursor: 'pointer',
                              textAlign: 'left',
                              fontSize: '0.9em',
                              fontWeight: 500,
                              color: '#059669',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              transition: 'background-color 0.15s'
                            }}
                            onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#ecfdf5'}
                            onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'white'}
                          >
                            <span style={{ fontSize: '1.1em' }}>📤</span> Upload New
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setPartnerPhotoMenuOpen(null);
                              openWebcamModal(index);
                            }}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: 'none',
                              background: 'white',
                              cursor: 'pointer',
                              textAlign: 'left',
                              fontSize: '0.9em',
                              fontWeight: 500,
                              color: '#7c3aed',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              transition: 'background-color 0.15s'
                            }}
                            onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f3ff'}
                            onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'white'}
                          >
                            <span style={{ fontSize: '1.1em' }}>📷</span> Take Selfie
                          </button>
                        </div>
                      )}
                      <input
                        type="file"
                        id={`partner-photo-${index}`}
                        accept=".jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          if (e.target.files && e.target.files[0]) {
                            const file = e.target.files[0];
                            // Show immediate preview with base64
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              handlePartnerDetailChange(index, 'photo', reader.result as string);
                            };
                            reader.readAsDataURL(file);
                            // Upload to server and update with file path
                            try {
                              const response = await uploadFormFile(applicationId, file, `partner_${index}_photo`, () => {});
                              if (response && response.success && response.filePath) {
                                // Update with server file path for persistence
                                handlePartnerDetailChange(index, 'photo', response.filePath);
                              }
                            } catch (err: any) {
                              console.error('Photo upload error:', err);
                              // Revert photo on error
                              handlePartnerDetailChange(index, 'photo', null);
                              alert('Failed to upload photo. Please try again.');
                            }
                          }
                        }}
                        disabled={!isOnline || isViewMode || isSectionLocked}
                      />
                      <span style={{ fontSize: '0.75em', color: '#64748b', marginTop: '5px', textAlign: 'center' }}>
                        {partner.nameInBlocks || ((formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? `Partner #${index + 2}` : `Director #${index + 2}`)}
                      </span>
                      {!partner.photo && !isViewMode && isOnline && !isSectionLocked && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openWebcamModal(index);
                          }}
                          style={{
                            marginTop: '6px',
                            padding: '6px 12px',
                            background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.7em',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            boxShadow: '0 2px 6px rgba(124,58,237,0.3)'
                          }}
                        >
                          <span>📷</span> Selfie
                        </button>
                      )}
                      {partner.photo && !isViewMode && isOnline && (
                        <span style={{ fontSize: '0.7em', color: '#94a3b8', marginTop: '4px' }}>
                          Click photo for options
                        </span>
                      )}
                    </div>
                  </div>
                  </ShakeAnimation>

                  <FormFieldRow>
                    <div style={{ flex: 1, marginRight: '20px', display: 'flex', flexDirection: 'column' }}>
                      <Label>a) Full Name (Block Letters)</Label>
                      <StyledInput
                        type="text"
                        value={partner.nameInBlocks}
                        onChange={(e) => handlePartnerDetailChange(index, 'nameInBlocks', e.target.value.toUpperCase())}
                        disabled={!isOnline || isViewMode || isSectionLocked}
                      />
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Label>b) Father / Mother / Husband Name</Label>
                      <StyledInput
                        type="text"
                        value={partner.fatherOrHusbandName}
                        onChange={(e) => handlePartnerDetailChange(index, 'fatherOrHusbandName', e.target.value.toUpperCase())}
                        disabled={!isOnline || isViewMode || isSectionLocked}
                      />
                    </div>
                  </FormFieldRow>

                  <FormFieldRow>
                    <div style={{ flex: 1, marginRight: '20px', display: 'flex', flexDirection: 'column' }}>
                      <Label>c) Date of Birth</Label>
                      <StyledInput
                        type="date"
                        value={partner.dateOfBirth}
                        onChange={(e) => handlePartnerDetailChange(index, 'dateOfBirth', e.target.value)}
                        disabled={!isOnline || isViewMode || isSectionLocked}
                      />
                      {/* Age verification for partner */}
                      {partner.dateOfBirth && (() => {
                        const partnerAge = calculateAgeFromDob(partner.dateOfBirth);
                        const isAgeValid = partnerAge !== null && partnerAge >= 21 && partnerAge <= 65;
                        return (
                          <>
                            {partnerAge !== null && (
                              <span style={{ fontSize: '0.85em', color: isAgeValid ? '#27ae60' : '#e74c3c', marginTop: '5px' }}>
                                Current age as of today: {partnerAge} years
                              </span>
                            )}
                            {partnerAge !== null && !isAgeValid && (
                              <span style={{ fontSize: '0.85em', color: '#e74c3c', marginTop: '3px', display: 'block' }}>
                                ⚠️ Age must be between 21 and 65 years for dealership eligibility.
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <ShakeAnimation $isShaking={partnerClass10Shaking[index] || false}>
                    <div 
                      ref={(el) => { partnerClass10Refs.current[index] = el; }}
                      style={{ 
                        flex: 1, 
                        display: 'flex', 
                        flexDirection: 'column',
                        padding: '12px',
                        backgroundColor: partnerClass10Shaking[index] ? '#fff5f5' : (!partner.isMinimumEducationMet ? '#fffbeb' : 'transparent'),
                        border: partnerClass10Shaking[index] ? '2px solid #e53e3e' : (!partner.isMinimumEducationMet ? '1px dashed #f59e0b' : 'none'),
                        borderRadius: '8px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        d) Are you minimum Class 10 (High School) pass?
                        {!partner.isMinimumEducationMet && (
                          <span style={{ 
                            fontSize: '0.75em', 
                            color: '#d97706', 
                            backgroundColor: '#fef3c7', 
                            padding: '2px 8px', 
                            borderRadius: '4px',
                            fontWeight: 600
                          }}>
                            Required
                          </span>
                        )}
                      </Label>
                      <RadioGroup>
                        <RadioOption>
                          <input
                            type="radio"
                            checked={partner.isMinimumEducationMet === 'Yes'}
                            onChange={() => handlePartnerDetailChange(index, 'isMinimumEducationMet', 'Yes')}
                            disabled={!isOnline || isViewMode || isSectionLocked}
                          />
                          Yes
                        </RadioOption>
                        <RadioOption>
                          <input
                            type="radio"
                            checked={partner.isMinimumEducationMet === 'No'}
                            onChange={() => handlePartnerDetailChange(index, 'isMinimumEducationMet', 'No')}
                            disabled={!isOnline || isViewMode || isSectionLocked}
                          />
                          No
                        </RadioOption>
                      </RadioGroup>
                      {partner.isMinimumEducationMet === 'No' && (
                        <span style={{ fontSize: '0.85em', color: '#e74c3c', marginTop: '5px' }}>
                          ⚠️ Minimum Intermediate (Class 10) pass is required for dealership eligibility.
                        </span>
                      )}
                    </div>
                    </ShakeAnimation>
                  </FormFieldRow>


                  {/* Check if partner is eligible based on age and education */}
                  {(() => {
                    const partnerAge = partner.dateOfBirth ? calculateAgeFromDob(partner.dateOfBirth) : null;
                    const isAgeInvalid = partnerAge !== null && (partnerAge < 21 || partnerAge > 65);
                    const isEducationNo = partner.isMinimumEducationMet === 'No';
                    const isPartnerIneligible = isAgeInvalid || isEducationNo;
                    
                    return (
                      <>
                        {isPartnerIneligible && (
                          <div style={{ 
                            padding: '10px 15px', 
                            backgroundColor: '#fff3cd', 
                            border: '1px solid #ffc107', 
                            borderRadius: '5px', 
                            marginBottom: '15px',
                            color: '#856404'
                          }}>
                            ⚠️ This {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'partner' : 'director'} does not meet eligibility criteria. 
                            {isAgeInvalid && ' Age must be between 21-65 years.'}
                            {isEducationNo && ' Minimum Intermediate (Class 10) education is required.'}
                          </div>
                        )}
                        
                        <FormFieldFullWidth>
                          <Label>e) Current Address:</Label>
                          
                          {/* Address Proof Upload with AI Autofill - Placed first to encourage upload */}
                          <div style={{ 
                            marginBottom: '15px',
                            padding: '12px 15px', 
                            backgroundColor: '#e8f4fd', 
                            borderRadius: '8px', 
                            border: '2px dashed #3498db'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <span style={{ fontSize: '1.3em' }}>📄</span>
                              <span style={{ fontWeight: 600, color: '#1a5276', fontSize: '0.95em' }}>
                                Upload Address Proof for Auto-fill (Recommended)
                              </span>
                              <span
                                title="AI-Validated Address Proof Documents:&#10;&#10;✓ Aadhaar Card&#10;✓ Passport&#10;✓ Voter ID Card&#10;✓ Driving License&#10;✓ Electricity Bill (not older than 3 months)&#10;✓ Gas Connection Bill&#10;✓ Water Bill (not older than 3 months)&#10;✓ Postpaid Mobile Phone Bill (not older than 3 months)&#10;&#10;Upload any of these documents and our AI will automatically extract your address!"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #3498db, #2980b9)',
                                  color: 'white',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  cursor: 'help',
                                  boxShadow: '0 2px 4px rgba(52, 152, 219, 0.3)'
                                }}
                              >
                                ?
                              </span>
                            </div>
                            <p style={{ margin: '0 0 10px 0', fontSize: '0.85em', color: '#2c3e50', lineHeight: '1.4' }}>
                              Details will be autofilled
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                              <FileUploadButton 
                                onClick={(e) => {
                                  e.preventDefault();
                                  const input = document.getElementById(`partner-address-${index}`) as HTMLInputElement;
                                  input?.click();
                                }}
                                disabled={partnerFileUploading[`${index}_addressProofFile`] || !isOnline || isViewMode || isPartnerIneligible}
                                style={{ 
                                  background: partner.addressProofFile ? 'linear-gradient(135deg, #27ae60, #2ecc71)' : 'linear-gradient(135deg, #3498db, #2980b9)',
                                  padding: '10px 20px'
                                }}
                              >
                                {partner.addressProofFile ? '✓ Change Address Proof' : '📤 Upload Address Proof'}
                              </FileUploadButton>
                              <span 
                                title="For Aadhaar Card: Upload a SINGLE image containing BOTH front side (with photo) and back side (with address) placed side by side. Other accepted documents: Passport, Voter ID, Driving License, Utility Bills."
                                style={{ 
                                  cursor: 'help', 
                                  color: '#3498db', 
                                  fontSize: '1.2em',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >ⓘ</span>
                              {partnerFileUploading[`${index}_addressProofFile`] && (
                                <div style={{ flex: 1, minWidth: '160px' }}>
                                  <FileUploadProgressBar>
                                    <FileUploadProgressFill progress={partnerFileProgress[`${index}_addressProofFile`] || 0} />
                                  </FileUploadProgressBar>
                                </div>
                              )}
                            </div>
                            <input
                              type="file"
                              id={`partner-address-${index}`}
                              accept=".jpg,.jpeg,.png"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  handlePartnerFileUpload(index, e.target.files[0], 'addressProofFile');
                                }
                              }}
                              disabled={!isOnline || isViewMode || isSectionLocked}
                            />
                            {/* Skeleton loader while uploading */}
                            {partnerFileUploading[`${index}_addressProofFile`] && (
                              <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <SkeletonBox />
                                <span style={{ color: '#666', fontSize: '0.9em' }}>Processing Address Proof...</span>
                              </div>
                            )}
                            {/* File display with thumbnail */}
                            {partner.addressProofFile && !partnerFileUploading[`${index}_addressProofFile`] && (
                              <FileUploadStatus style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span>File: <ClickableFileName onClick={() => handleOpenFilePreview(partner.addressProofFile, `partner_${index}_addressProofFile`)}>
                                    {typeof partner.addressProofFile === 'string' ? partner.addressProofFile : (partner.addressProofFile as File)?.name}
                                  </ClickableFileName></span>
                                </div>
                                {/* Image thumbnail preview */}
                                {typeof partner.addressProofFile === 'string' && partner.addressProofFile.match(/\.(jpeg|jpg|gif|png)$/i) && (
                                  <img
                                    src={(() => {
                                      const rawApiBaseUrl = process.env.REACT_APP_API_URL || '/api';
                                      let serverBase = window.location.origin;
                                      if (rawApiBaseUrl.startsWith('http')) {
                                        try {
                                          const parsed = new URL(rawApiBaseUrl);
                                          serverBase = `${parsed.protocol}//${parsed.host}`;
                                        } catch { /* use default */ }
                                      }
                                      let p = partner.addressProofFile as string;
                                      if (p.includes('://')) {
                                        try { p = new URL(p).pathname; } catch { /* keep as is */ }
                                      }
                                      if (!p.startsWith('/')) {
                                        p = p.startsWith('uploads/') ? `/${p}` : `/uploads/${p}`;
                                      }
                                      return `${serverBase}${p}`;
                                    })()}
                                    alt="Address Proof Preview"
                                    onClick={() => handleOpenFilePreview(partner.addressProofFile!, `partner_${index}_addressProofFile`)}
                                    style={{
                                      width: '120px',
                                      height: '80px',
                                      objectFit: 'cover',
                                      borderRadius: '4px',
                                      border: '1px solid #ddd',
                                      cursor: 'pointer',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                  />
                                )}
                                {/* View button for PDF files */}
                                {typeof partner.addressProofFile === 'string' && partner.addressProofFile.match(/\.pdf$/i) && (
                                  <button
                                    type="button"
                                    onClick={() => handleOpenFilePreview(partner.addressProofFile!, `partner_${index}_addressProofFile`)}
                                    style={{ padding: '8px 16px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.9em', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                                  >
                                    📄 View Address Proof
                                  </button>
                                )}
                              </FileUploadStatus>
                            )}
                            {partnerFileMessage[`${index}_addressProofFile`] && (
                              <FileUploadSuccess style={{ marginTop: '8px' }}>✓ {partnerFileMessage[`${index}_addressProofFile`]}</FileUploadSuccess>
                            )}
                          </div>

                          <StyledTextArea
                            value={partner.currentAddress}
                            onChange={(e) => handlePartnerDetailChange(index, 'currentAddress', e.target.value)}
                            rows={2}
                            disabled={!isOnline || isViewMode || isPartnerIneligible}
                          />
                          
                          <div style={{ margin: '10px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              checked={partner.permanentAddressSameAsCurrent}
                              onChange={(e) => handlePartnerDetailChange(index, 'permanentAddressSameAsCurrent', e.target.checked)}
                              disabled={!isOnline || isViewMode || isPartnerIneligible}
                              style={{ width: '16px', height: '16px' }}
                            />
                            <label style={{ fontSize: '0.9em', color: '#4a5568' }}>
                              Permanent Address is same as Current Address
                            </label>
                          </div>

                          <Label>Permanent Address:</Label>
                          <StyledTextArea
                            value={partner.permanentAddress}
                            onChange={(e) => handlePartnerDetailChange(index, 'permanentAddress', e.target.value)}
                            rows={2}
                            disabled={!isOnline || isViewMode || partner.permanentAddressSameAsCurrent || isPartnerIneligible}
                            style={{ 
                              opacity: partner.permanentAddressSameAsCurrent ? 0.6 : 1,
                              backgroundColor: partner.permanentAddressSameAsCurrent ? '#f0f0f0' : 'white'
                            }}
                          />
                        </FormFieldFullWidth>
                      </>
                    );
                  })()}

                  <FormFieldRow>
                    <div style={{ flex: 1, marginRight: '20px' }}>
                      <Label>Personal Mobile (10 digits):</Label>
                      <StyledInput
                        type="text"
                        value={partner.personalMobile}
                        onChange={(e) => handlePartnerDetailChange(index, 'personalMobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="10-digit mobile number"
                        maxLength={10}
                        disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Label>E-mail ID:</Label>
                      <StyledInput
                        type="email"
                        value={partner.emailId}
                        onChange={(e) => handlePartnerDetailChange(index, 'emailId', e.target.value)}
                        disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())}
                      />
                    </div>
                  </FormFieldRow>

                  <FormFieldRow>
                    <ShakeAnimation $isShaking={partnerNationalityShaking[index] || false}>
                    <div 
                      ref={(el) => { partnerNationalityRefs.current[index] = el; }}
                      style={{ flex: 1, marginRight: '20px', backgroundColor: partnerNationalityShaking[index] ? '#fff5f5' : 'transparent', border: partnerNationalityShaking[index] ? '2px solid #e53e3e' : 'none', borderRadius: '8px', padding: partnerNationalityShaking[index] ? '10px' : '0', transition: 'all 0.3s ease' }}>
                      <Label>f) Nationality:</Label>
                      <RadioGroup>
                        <RadioOption>
                          <input
                            type="radio"
                            checked={partner.nationality === 'Indian'}
                            onChange={() => handlePartnerDetailChange(index, 'nationality', 'Indian')}
                            disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())}
                          />
                          Indian
                        </RadioOption>
                        <RadioOption>
                          <input
                            type="radio"
                            checked={partner.nationality === 'Non-Indian'}
                            onChange={() => handlePartnerDetailChange(index, 'nationality', 'Non-Indian')}
                            disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())}
                          />
                          Non-Indian
                        </RadioOption>
                      </RadioGroup>
                    </div>
                    </ShakeAnimation>
                    <ShakeAnimation $isShaking={partnerGenderShaking[index] || false}>
                    <div 
                      ref={(el) => { partnerGenderRefs.current[index] = el; }}
                      style={{ flex: 1, backgroundColor: partnerGenderShaking[index] ? '#fff5f5' : 'transparent', border: partnerGenderShaking[index] ? '2px solid #e53e3e' : 'none', borderRadius: '8px', padding: partnerGenderShaking[index] ? '10px' : '0', transition: 'all 0.3s ease' }}>
                      <Label>
                        g) Gender:
                        {mandatoryFields.gender && !partner.gender && (
                          <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px', fontSize: '0.675rem', fontWeight: 600, border: '1px solid #fecaca', textDecoration: 'none' }}>Required</span>
                        )}
                      </Label>
                      <RadioGroup>
                        <RadioOption>
                          <input
                            type="radio"
                            checked={partner.gender === 'Female'}
                            onChange={() => handlePartnerDetailChange(index, 'gender', 'Female')}
                            disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())}
                          />
                          Female
                        </RadioOption>
                        <RadioOption>
                          <input
                            type="radio"
                            checked={partner.gender === 'Male'}
                            onChange={() => handlePartnerDetailChange(index, 'gender', 'Male')}
                            disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())}
                          />
                          Male
                        </RadioOption>
                        <RadioOption>
                          <input
                            type="radio"
                            checked={partner.gender === 'Others'}
                            onChange={() => handlePartnerDetailChange(index, 'gender', 'Others')}
                            disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())}
                          />
                          Others
                        </RadioOption>
                      </RadioGroup>
                    </div>
                    </ShakeAnimation>
                  </FormFieldRow>

                  <ShakeAnimation $isShaking={partnerMaritalStatusShaking[index] || false}>
                  <FormFieldFullWidth
                    ref={(el: HTMLDivElement | null) => { partnerMaritalStatusRefs.current[index] = el; }}
                    style={{ backgroundColor: partnerMaritalStatusShaking[index] ? '#fff5f5' : 'transparent', border: partnerMaritalStatusShaking[index] ? '2px solid #e53e3e' : 'none', borderRadius: '8px', padding: partnerMaritalStatusShaking[index] ? '10px' : undefined, transition: 'all 0.3s ease' }}>
                    <Label>h) Marital Status:</Label>
                    <RadioGroup>
                      <RadioOption>
                        <input
                          type="radio"
                          checked={partner.maritalStatus === 'Married'}
                          onChange={() => handlePartnerDetailChange(index, 'maritalStatus', 'Married')}
                          disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())}
                        />
                        Married
                      </RadioOption>
                      <RadioOption>
                        <input
                          type="radio"
                          checked={partner.maritalStatus === 'Unmarried'}
                          onChange={() => handlePartnerDetailChange(index, 'maritalStatus', 'Unmarried')}
                          disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())}
                        />
                        Unmarried
                      </RadioOption>
                      <RadioOption>
                        <input
                          type="radio"
                          checked={partner.maritalStatus === 'Other'}
                          onChange={() => handlePartnerDetailChange(index, 'maritalStatus', 'Other')}
                          disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())}
                        />
                        Other
                      </RadioOption>
                    </RadioGroup>
                    {partner.maritalStatus === 'Other' && (
                      <input
                        type="text"
                        value={partner.maritalStatusOther || ''}
                        onChange={(e) => handlePartnerDetailChange(index, 'maritalStatusOther', e.target.value)}
                        placeholder="Please specify"
                        disabled={!isOnline || isViewMode || isSectionLocked}
                        style={{ marginTop: '8px', padding: '8px 12px', border: '1px solid #ccc', borderRadius: '4px', width: '100%', maxWidth: '200px' }}
                      />
                    )}
                  </FormFieldFullWidth>
                  </ShakeAnimation>

                  {/* IDENTIFICATION & STATUTORY DETAILS for Partner/Director */}
                  <ShakeAnimation $isShaking={partnerKycShaking[index] || false}>
                  <FormFieldFullWidth 
                    ref={(el: HTMLDivElement | null) => { partnerKycRefs.current[index] = el; }}
                    style={{ marginTop: '25px', padding: '15px', backgroundColor: partnerKycShaking[index] ? '#fff5f5' : (index === 0 ? '#f8fafc' : '#fff7ed'), borderRadius: '8px', border: partnerKycShaking[index] ? '2px solid #e53e3e' : (index === 0 ? '1px solid #e2e8f0' : '1px solid #fed7aa'), transition: 'all 0.3s ease' }}>
                    <Label style={{ fontWeight: 700, marginBottom: '15px', color: index === 0 ? '#1a5276' : '#ea580c', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.05em', textDecoration: 'underline' }}>
                      {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #{index + 2} {partner.nameInBlocks ? `(${partner.nameInBlocks})` : ''} - KYC & Tax Registration Details
                      {mandatoryFields.kycAadhaarPan && (!partner.aadhaarNumber || !partner.panNumberStatutory) && (
                        <span style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px', fontSize: '0.675rem', fontWeight: 600, border: '1px solid #fecaca', textDecoration: 'none' }}>Required</span>
                      )}
                    </Label>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '10px 12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '30%' }}>Aadhaar Number</td>
                          <td style={{ padding: '10px 12px', border: '1px solid #85c1e9', width: '70%' }}>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <StyledInput type="text" value={partner.aadhaarNumber || ''} onChange={(e) => handlePartnerDetailChange(index, 'aadhaarNumber', e.target.value)} maxLength={14} placeholder="XXXX XXXX XXXX" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })()) || isPartnerStatutoryUploading} style={{ margin: 0, flex: 1, minWidth: '150px', opacity: isPartnerStatutoryUploading ? 0.5 : 1 }} />
                              <span style={{ color: partner.aadhaarVerified === 'Yes' ? '#27ae60' : '#7f8c8d', fontWeight: 500, fontSize: '0.9em' }}>E verified</span>
                              <input type="file" id={`partner_${index}_aadhaarFileInput`} accept=".jpg,.jpeg,.png" style={{ display: 'none' }} onChange={async (e) => { if (e.target.files?.[0]) { const file = e.target.files[0]; handlePartnerDetailChange(index, 'aadhaarFile', file); setPartnerAiDetection(prev => ({ ...prev, [`${index}_aadhaar`]: true })); try { const response = await uploadFormFile(applicationId, file, `partner_${index}_aadhaarFile`, () => {}); if (response?.success) { handlePartnerDetailChange(index, 'aadhaarFile', (response as any).filePath || file); const extracted = (response as any).extractedFields; if (extracted?.aadhaarNumber) { const dupCheck = checkDuplicateDocument('aadhaar', extracted.aadhaarNumber, { partnerIndex: index }); if (dupCheck.isDuplicate) { handlePartnerDetailChange(index, 'aadhaarFile', null); setUploadErrorModal({ show: true, message: `This Aadhaar number is already used by ${dupCheck.duplicateOwner}. Each applicant/partner must have a unique Aadhaar card. Please upload a different Aadhaar card.`, fieldName: `Partner ${index + 2} Aadhaar (Duplicate)` }); } else { handlePartnerDetailChange(index, 'aadhaarNumber', extracted.aadhaarNumber.replace(/\D/g, '').slice(0, 12).replace(/(\d{4})(?=\d)/g, '$1 ')); } } if (extracted?.aadhaarName) { setExtractedNames(prev => { const partners = { ...(prev.partners || {}) }; partners[index] = { ...(partners[index] || {}), aadhaarName: extracted.aadhaarName }; if (partners[index]?.panName) { validateDocumentNames(extracted.aadhaarName, partners[index].panName, index); } return { ...prev, partners }; }); } } } catch (err: any) { console.error('Upload error:', err); handlePartnerDetailChange(index, 'aadhaarFile', null); if (err.response?.data?.imageQualityError) { if (err.response.data.documentRejected) { setUploadErrorModal({ show: true, message: err.response.data.reason || 'This document type is not accepted.', fieldName: `Partner ${index + 2} Aadhaar` }); } else { setImageQualityError({ show: true, reason: err.response.data.reason || 'The uploaded image is blurry or unreadable.', fieldName: `Partner ${index + 2} Aadhaar`, isAadhaarError: err.response.data.incompleteAadhaar }); } } else { setUploadErrorModal({ show: true, message: err.response?.data?.reason || err.message || 'Upload failed. Please try again.', fieldName: `Partner ${index + 2} Aadhaar` }); } } finally { setPartnerAiDetection(prev => ({ ...prev, [`${index}_aadhaar`]: false })); } }}} disabled={!isOnline || isViewMode || isPartnerStatutoryUploading} />
                              <label htmlFor={`partner_${index}_aadhaarFileInput`} style={{ padding: '4px 12px', backgroundColor: partner.aadhaarFile ? '#27ae60' : '#3498db', color: 'white', borderRadius: '4px', fontSize: '0.8em', cursor: !isOnline || isViewMode || isPartnerStatutoryUploading ? 'not-allowed' : 'pointer', opacity: !isOnline || isViewMode || isPartnerStatutoryUploading ? 0.6 : 1 }}>{partner.aadhaarFile ? 'Change' : 'Upload'}</label>
                              {partner.aadhaarFile && (<button type="button" onClick={() => handleOpenFilePreview(partner.aadhaarFile, `Partner ${index + 2} Aadhaar`)} style={{ padding: '4px 12px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.8em', cursor: 'pointer' }}>View</button>)}
                              {partnerAiDetection[`${index}_aadhaar`] && <span style={{ fontSize: '0.75em', color: '#e67e22', fontStyle: 'italic' }}>Detection in progress.. please wait!</span>}
                              {partner.aadhaarFile && !partnerAiDetection[`${index}_aadhaar`] && <span style={{ fontSize: '0.75em', color: '#27ae60' }}>✓</span>}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '10px 12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '30%' }}>PAN Number</td>
                          <td style={{ padding: '10px 12px', border: '1px solid #85c1e9', width: '70%' }}>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <StyledInput type="text" value={partner.panNumberStatutory || ''} onChange={(e) => handlePartnerDetailChange(index, 'panNumberStatutory', e.target.value)} maxLength={10} placeholder="PAN Number" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })()) || isPartnerStatutoryUploading} style={{ margin: 0, flex: 1, textTransform: 'uppercase', minWidth: '150px', opacity: isPartnerStatutoryUploading ? 0.5 : 1 }} />
                              <span style={{ color: partner.panVerified === 'Yes' ? '#27ae60' : '#7f8c8d', fontWeight: 500, fontSize: '0.9em' }}>E verified</span>
                              <input type="file" accept=".jpg,.jpeg,.png" onChange={async (e) => { if (e.target.files?.[0]) { const file = e.target.files[0]; handlePartnerDetailChange(index, 'panStatutoryFile', file); setPartnerAiDetection(prev => ({ ...prev, [`${index}_pan`]: true })); try { const response = await uploadFormFile(applicationId, file, `partner_${index}_panStatutoryFile`, () => {}); if (response?.success) { handlePartnerDetailChange(index, 'panStatutoryFile', (response as any).filePath || file); const extracted = (response as any).extractedFields; if (extracted?.panNumber) { const dupCheck = checkDuplicateDocument('pan', extracted.panNumber, { partnerIndex: index }); if (dupCheck.isDuplicate) { handlePartnerDetailChange(index, 'panStatutoryFile', null); setUploadErrorModal({ show: true, message: `This PAN number is already used by ${dupCheck.duplicateOwner}. Each applicant/partner must have a unique PAN card. Please upload a different PAN card.`, fieldName: `Partner ${index + 2} PAN (Duplicate)` }); } else { handlePartnerDetailChange(index, 'panNumberStatutory', extracted.panNumber); } } if (extracted?.panName) { setExtractedNames(prev => { const partners = { ...(prev.partners || {}) }; partners[index] = { ...(partners[index] || {}), panName: extracted.panName }; if (partners[index]?.aadhaarName) { validateDocumentNames(partners[index].aadhaarName, extracted.panName, index); } return { ...prev, partners }; }); } } } catch (err: any) { console.error('Upload error:', err); handlePartnerDetailChange(index, 'panStatutoryFile', null); if (err.response?.data?.imageQualityError) { if (err.response.data.documentRejected) { setUploadErrorModal({ show: true, message: err.response.data.reason || 'This document type is not accepted.', fieldName: `Partner ${index + 2} PAN` }); } else { setImageQualityError({ show: true, reason: err.response.data.reason || 'The uploaded image is blurry or unreadable.', fieldName: `Partner ${index + 2} PAN` }); } } else { setUploadErrorModal({ show: true, message: err.response?.data?.reason || err.message || 'Upload failed. Please try again.', fieldName: `Partner ${index + 2} PAN` }); } } finally { setPartnerAiDetection(prev => ({ ...prev, [`${index}_pan`]: false })); } }}} disabled={!isOnline || isViewMode || isPartnerStatutoryUploading} style={{ display: 'none' }} id={`partner_${index}_panStatutoryFileInput`} />
                              <label htmlFor={`partner_${index}_panStatutoryFileInput`} style={{ padding: '4px 12px', backgroundColor: partner.panStatutoryFile ? '#27ae60' : '#3498db', color: 'white', borderRadius: '4px', fontSize: '0.8em', cursor: !isOnline || isViewMode || isPartnerStatutoryUploading ? 'not-allowed' : 'pointer', opacity: !isOnline || isViewMode || isPartnerStatutoryUploading ? 0.6 : 1 }}>{partner.panStatutoryFile ? 'Change' : 'Upload'}</label>
                              {partner.panStatutoryFile && (<button type="button" onClick={() => handleOpenFilePreview(partner.panStatutoryFile, `Partner ${index + 2} PAN`)} style={{ padding: '4px 12px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.8em', cursor: 'pointer' }}>View</button>)}
                              {partnerAiDetection[`${index}_pan`] && <span style={{ fontSize: '0.75em', color: '#e67e22', fontStyle: 'italic' }}>Detection in progress.. please wait!</span>}
                              {partner.panStatutoryFile && !partnerAiDetection[`${index}_pan`] && <span style={{ fontSize: '0.75em', color: '#27ae60' }}>✓</span>}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '10px 12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '30%' }}>GST No. (if applicable)</td>
                          <td style={{ padding: '10px 12px', border: '1px solid #85c1e9', width: '70%' }}>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <StyledInput type="text" value={partner.gstNumber || ''} onChange={(e) => handlePartnerDetailChange(index, 'gstNumber', e.target.value)} maxLength={15} placeholder="GST Number" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })()) || isPartnerStatutoryUploading} style={{ margin: 0, flex: 1, textTransform: 'uppercase', minWidth: '150px', opacity: isPartnerStatutoryUploading ? 0.5 : 1 }} />
                              <span style={{ color: partner.gstVerified === 'Yes' ? '#27ae60' : '#7f8c8d', fontWeight: 500, fontSize: '0.9em' }}>E verified</span>
                              <input type="file" accept=".jpg,.jpeg,.png" onChange={async (e) => { if (e.target.files?.[0]) { const file = e.target.files[0]; handlePartnerDetailChange(index, 'gstFile', file); setPartnerAiDetection(prev => ({ ...prev, [`${index}_gst`]: true })); try { const response = await uploadFormFile(applicationId, file, `partner_${index}_gstFile`, () => {}); if (response?.success) { handlePartnerDetailChange(index, 'gstFile', (response as any).filePath || file); const extracted = (response as any).extractedFields; if (extracted?.gstNumber) { handlePartnerDetailChange(index, 'gstNumber', extracted.gstNumber); } } } catch (err: any) { console.error('Upload error:', err); handlePartnerDetailChange(index, 'gstFile', null); if (err.response?.data?.imageQualityError) { if (err.response.data.documentRejected) { setUploadErrorModal({ show: true, message: err.response.data.reason || 'This document type is not accepted.', fieldName: `Partner ${index + 2} GST` }); } else { setImageQualityError({ show: true, reason: err.response.data.reason || 'The uploaded image is blurry or unreadable.', fieldName: `Partner ${index + 2} GST` }); } } else { setUploadErrorModal({ show: true, message: err.response?.data?.reason || err.message || 'Upload failed. Please try again.', fieldName: `Partner ${index + 2} GST` }); } } finally { setPartnerAiDetection(prev => ({ ...prev, [`${index}_gst`]: false })); } }}} disabled={!isOnline || isViewMode || isPartnerStatutoryUploading} style={{ display: 'none' }} id={`partner_${index}_gstFileInput`} />
                              <label htmlFor={`partner_${index}_gstFileInput`} style={{ padding: '4px 12px', backgroundColor: partner.gstFile ? '#27ae60' : '#3498db', color: 'white', borderRadius: '4px', fontSize: '0.8em', cursor: !isOnline || isViewMode || isPartnerStatutoryUploading ? 'not-allowed' : 'pointer', opacity: !isOnline || isViewMode || isPartnerStatutoryUploading ? 0.6 : 1 }}>{partner.gstFile ? 'Change' : 'Upload'}</label>
                              {partner.gstFile && (<button type="button" onClick={() => handleOpenFilePreview(partner.gstFile, `Partner ${index + 2} GST`)} style={{ padding: '4px 12px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.8em', cursor: 'pointer' }}>View</button>)}
                              {partnerAiDetection[`${index}_gst`] && <span style={{ fontSize: '0.75em', color: '#e67e22', fontStyle: 'italic' }}>Detection in progress.. please wait!</span>}
                              {partner.gstFile && !partnerAiDetection[`${index}_gst`] && <span style={{ fontSize: '0.75em', color: '#27ae60' }}>✓</span>}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </FormFieldFullWidth>
                  </ShakeAnimation>

                  {/* EDUCATIONAL QUALIFICATION for Partner/Director */}
                  <ShakeAnimation $isShaking={partnerEduQualShaking[index] || false}>
                  <FormFieldFullWidth 
                    ref={(el: HTMLDivElement | null) => { partnerEduQualRefs.current[index] = el; }}
                    style={{ marginTop: '25px', padding: '15px', backgroundColor: partnerEduQualShaking[index] ? '#fff5f5' : (index === 0 ? '#f8fafc' : '#fff7ed'), borderRadius: '8px', border: partnerEduQualShaking[index] ? '2px solid #e53e3e' : (index === 0 ? '1px solid #e2e8f0' : '1px solid #fed7aa'), transition: 'all 0.3s ease' }}>
                    <Label style={{ fontWeight: 700, marginBottom: '15px', color: index === 0 ? '#1a5276' : '#ea580c', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.05em', textDecoration: 'underline' }}>
                      {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #{index + 2} {partner.nameInBlocks ? `(${partner.nameInBlocks})` : ''} - EDUCATIONAL QUALIFICATION
                      {mandatoryFields.educationalQualification && !(
                        (partner.eduSscCertificateFile && partner.eduSscBoard) ||
                        (partner.eduHscCertificateFile && partner.eduHscBoard) ||
                        (partner.eduGraduateCertificateFile && partner.eduGraduateBoard) ||
                        (partner.eduOthersCertificateFile && partner.eduOthersBoard)
                      ) && (
                        <span style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px', fontSize: '0.675rem', fontWeight: 600, border: '1px solid #fecaca', textDecoration: 'none' }}>Required</span>
                      )}
                    </Label>
                    <div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '8px 4px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, textAlign: 'center', width: '10%', fontSize: '0.85em' }}>Qualification <span title="Educational qualification level (SSC/HSC/Graduate/Others)" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span></th>
                            <th style={{ padding: '8px 4px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, textAlign: 'center', width: '22%', fontSize: '0.85em' }}>Board / University <span title="Name of the Board or University" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span></th>
                            <th style={{ padding: '8px 4px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, textAlign: 'center', width: '10%', fontSize: '0.85em' }}>Marks <span title="Marks Obtained in the examination" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span></th>
                            <th style={{ padding: '8px 4px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, textAlign: 'center', width: '10%', fontSize: '0.85em' }}>Out of <span title="Maximum/Total Marks" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span></th>
                            <th style={{ padding: '8px 4px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, textAlign: 'center', width: '12%', fontSize: '0.85em' }}>% / CGPA <span title="Percentage or CGPA obtained (auto-calculated, editable)" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span></th>
                            <th style={{ padding: '8px 4px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, textAlign: 'center', width: '10%', fontSize: '0.85em' }}>Year <span title="Year of Passing" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span></th>
                            <th style={{ padding: '8px 4px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, textAlign: 'center', width: '16%', fontSize: '0.85em' }}>Doc <span title="Upload marksheet/certificate" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td style={{ padding: '6px 4px', border: '1px solid #85c1e9', backgroundColor: '#eaf2f8', fontWeight: 600, textAlign: 'center', fontSize: '0.8em' }}>SSC / 10th</td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduSscBoard || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduSscBoard', e.target.value)} placeholder="Board/University" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduSscMarksObtained || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduSscMarksObtained', e.target.value)} placeholder="Marks" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduSscOutOfMarks || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduSscOutOfMarks', e.target.value)} placeholder="Max" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduSscPercentage || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduSscPercentage', e.target.value)} placeholder="%" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduSscYearOfPassing || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduSscYearOfPassing', e.target.value)} placeholder="Year" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                {partnerEduCertUploading[`${index}_Ssc`] ? (
                                  <span style={{ fontSize: '0.7em', color: '#e67e22', fontWeight: 600, animation: 'pulse 1.5s infinite' }}>Please wait...Processing!</span>
                                ) : (
                                  <>
                                    <input type="file" accept=".jpg,.jpeg,.png" onChange={(e) => { if (e.target.files?.[0]) handlePartnerEduCertificateUpload(index, e.target.files[0], 'Ssc', 'SSC / 10th'); }} disabled={!isOnline || isViewMode || isPartnerAnyEduCertUploading} style={{ display: 'none' }} id={`partner-${index}-eduSscCertFileInput`} />
                                    <label htmlFor={`partner-${index}-eduSscCertFileInput`} style={{ padding: '3px 8px', backgroundColor: partner.eduSscCertificateFile ? '#27ae60' : '#3498db', color: 'white', borderRadius: '4px', fontSize: '0.7em', cursor: !isOnline || isViewMode || isPartnerAnyEduCertUploading ? 'not-allowed' : 'pointer', opacity: !isOnline || isViewMode || isPartnerAnyEduCertUploading ? 0.6 : 1 }}>{partner.eduSscCertificateFile ? '✓' : 'Upload'}</label>
                                    {partner.eduSscCertificateFile && <button type="button" onClick={() => handleOpenFilePreview(partner.eduSscCertificateFile, `partner_${index}_eduSscCertificateFile`)} style={{ padding: '3px 6px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.7em', cursor: 'pointer' }}>View</button>}
                                    {!partner.eduSscCertificateFile && (partner.eduSscBoard || partner.eduSscMarksObtained || partner.eduSscOutOfMarks || partner.eduSscPercentage || partner.eduSscYearOfPassing) && (
                                      <button type="button" onClick={() => { (['eduSscBoard', 'eduSscMarksObtained', 'eduSscOutOfMarks', 'eduSscPercentage', 'eduSscYearOfPassing'] as const).forEach(f => handlePartnerDetailChange(index, f, '')); }} style={{ padding: '3px 6px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.65em', cursor: 'pointer' }} title="Clear autofilled data for this row">Clear</button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: '6px 4px', border: '1px solid #85c1e9', backgroundColor: '#eaf2f8', fontWeight: 600, textAlign: 'center', fontSize: '0.8em' }}>HSC / 12th</td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduHscBoard || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduHscBoard', e.target.value)} placeholder="Board/University" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduHscMarksObtained || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduHscMarksObtained', e.target.value)} placeholder="Marks" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduHscOutOfMarks || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduHscOutOfMarks', e.target.value)} placeholder="Max" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduHscPercentage || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduHscPercentage', e.target.value)} placeholder="%" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduHscYearOfPassing || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduHscYearOfPassing', e.target.value)} placeholder="Year" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                {partnerEduCertUploading[`${index}_Hsc`] ? (
                                  <span style={{ fontSize: '0.7em', color: '#e67e22', fontWeight: 600, animation: 'pulse 1.5s infinite' }}>Please wait...Processing!</span>
                                ) : (
                                  <>
                                    <input type="file" accept=".jpg,.jpeg,.png" onChange={(e) => { if (e.target.files?.[0]) handlePartnerEduCertificateUpload(index, e.target.files[0], 'Hsc', 'HSC / 12th'); }} disabled={!isOnline || isViewMode || isPartnerAnyEduCertUploading} style={{ display: 'none' }} id={`partner-${index}-eduHscCertFileInput`} />
                                    <label htmlFor={`partner-${index}-eduHscCertFileInput`} style={{ padding: '3px 8px', backgroundColor: partner.eduHscCertificateFile ? '#27ae60' : '#3498db', color: 'white', borderRadius: '4px', fontSize: '0.7em', cursor: !isOnline || isViewMode || isPartnerAnyEduCertUploading ? 'not-allowed' : 'pointer', opacity: !isOnline || isViewMode || isPartnerAnyEduCertUploading ? 0.6 : 1 }}>{partner.eduHscCertificateFile ? '✓' : 'Upload'}</label>
                                    {partner.eduHscCertificateFile && <button type="button" onClick={() => handleOpenFilePreview(partner.eduHscCertificateFile, `partner_${index}_eduHscCertificateFile`)} style={{ padding: '3px 6px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.7em', cursor: 'pointer' }}>View</button>}
                                    {!partner.eduHscCertificateFile && (partner.eduHscBoard || partner.eduHscMarksObtained || partner.eduHscOutOfMarks || partner.eduHscPercentage || partner.eduHscYearOfPassing) && (
                                      <button type="button" onClick={() => { (['eduHscBoard', 'eduHscMarksObtained', 'eduHscOutOfMarks', 'eduHscPercentage', 'eduHscYearOfPassing'] as const).forEach(f => handlePartnerDetailChange(index, f, '')); }} style={{ padding: '3px 6px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.65em', cursor: 'pointer' }} title="Clear autofilled data for this row">Clear</button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: '6px 4px', border: '1px solid #85c1e9', backgroundColor: '#eaf2f8', fontWeight: 600, textAlign: 'center', fontSize: '0.8em' }}>Graduate</td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduGraduateBoard || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduGraduateBoard', e.target.value)} placeholder="Board/University" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduGraduateMarksObtained || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduGraduateMarksObtained', e.target.value)} placeholder="Marks" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduGraduateOutOfMarks || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduGraduateOutOfMarks', e.target.value)} placeholder="Max" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduGraduatePercentage || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduGraduatePercentage', e.target.value)} placeholder="%" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduGraduateYearOfPassing || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduGraduateYearOfPassing', e.target.value)} placeholder="Year" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                {partnerEduCertUploading[`${index}_Graduate`] ? (
                                  <span style={{ fontSize: '0.7em', color: '#e67e22', fontWeight: 600, animation: 'pulse 1.5s infinite' }}>Please wait...Processing!</span>
                                ) : (
                                  <>
                                    <input type="file" accept=".jpg,.jpeg,.png" onChange={(e) => { if (e.target.files?.[0]) handlePartnerEduCertificateUpload(index, e.target.files[0], 'Graduate', 'Graduate'); }} disabled={!isOnline || isViewMode || isPartnerAnyEduCertUploading} style={{ display: 'none' }} id={`partner-${index}-eduGraduateCertFileInput`} />
                                    <label htmlFor={`partner-${index}-eduGraduateCertFileInput`} style={{ padding: '3px 8px', backgroundColor: partner.eduGraduateCertificateFile ? '#27ae60' : '#3498db', color: 'white', borderRadius: '4px', fontSize: '0.7em', cursor: !isOnline || isViewMode || isPartnerAnyEduCertUploading ? 'not-allowed' : 'pointer', opacity: !isOnline || isViewMode || isPartnerAnyEduCertUploading ? 0.6 : 1 }}>{partner.eduGraduateCertificateFile ? '✓' : 'Upload'}</label>
                                    {partner.eduGraduateCertificateFile && <button type="button" onClick={() => handleOpenFilePreview(partner.eduGraduateCertificateFile, `partner_${index}_eduGraduateCertificateFile`)} style={{ padding: '3px 6px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.7em', cursor: 'pointer' }}>View</button>}
                                    {!partner.eduGraduateCertificateFile && (partner.eduGraduateBoard || partner.eduGraduateMarksObtained || partner.eduGraduateOutOfMarks || partner.eduGraduatePercentage || partner.eduGraduateYearOfPassing) && (
                                      <button type="button" onClick={() => { (['eduGraduateBoard', 'eduGraduateMarksObtained', 'eduGraduateOutOfMarks', 'eduGraduatePercentage', 'eduGraduateYearOfPassing'] as const).forEach(f => handlePartnerDetailChange(index, f, '')); }} style={{ padding: '3px 6px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.65em', cursor: 'pointer' }} title="Clear autofilled data for this row">Clear</button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: '6px 4px', border: '1px solid #85c1e9', backgroundColor: '#eaf2f8', fontWeight: 600, textAlign: 'center', fontSize: '0.8em' }}>Others</td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduOthersBoard || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduOthersBoard', e.target.value)} placeholder="Board/University" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduOthersMarksObtained || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduOthersMarksObtained', e.target.value)} placeholder="Marks" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduOthersOutOfMarks || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduOthersOutOfMarks', e.target.value)} placeholder="Max" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduOthersPercentage || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduOthersPercentage', e.target.value)} placeholder="%" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <StyledInput type="text" value={partner.eduOthersYearOfPassing || ''} onChange={(e) => handlePartnerDetailChange(index, 'eduOthersYearOfPassing', e.target.value)} placeholder="Year" disabled={!isOnline || isViewMode || partner.isMinimumEducationMet === 'No' || isPartnerAnyEduCertUploading || (partner.dateOfBirth && (() => { const age = calculateAgeFromDob(partner.dateOfBirth); return age !== null && (age < 21 || age > 65); })())} style={{ margin: 0, fontSize: '0.8em', padding: '6px 4px', width: '100%', boxSizing: 'border-box', opacity: isPartnerAnyEduCertUploading ? 0.5 : 1 }} />
                            </td>
                            <td style={{ padding: '4px', border: '1px solid #85c1e9' }}>
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                {partnerEduCertUploading[`${index}_Others`] ? (
                                  <span style={{ fontSize: '0.7em', color: '#e67e22', fontWeight: 600, animation: 'pulse 1.5s infinite' }}>Please wait...Processing!</span>
                                ) : (
                                  <>
                                    <input type="file" accept=".jpg,.jpeg,.png" onChange={(e) => { if (e.target.files?.[0]) handlePartnerEduCertificateUpload(index, e.target.files[0], 'Others', 'Other Qualification'); }} disabled={!isOnline || isViewMode || isPartnerAnyEduCertUploading} style={{ display: 'none' }} id={`partner-${index}-eduOthersCertFileInput`} />
                                    <label htmlFor={`partner-${index}-eduOthersCertFileInput`} style={{ padding: '3px 8px', backgroundColor: partner.eduOthersCertificateFile ? '#27ae60' : '#3498db', color: 'white', borderRadius: '4px', fontSize: '0.7em', cursor: !isOnline || isViewMode || isPartnerAnyEduCertUploading ? 'not-allowed' : 'pointer', opacity: !isOnline || isViewMode || isPartnerAnyEduCertUploading ? 0.6 : 1 }}>{partner.eduOthersCertificateFile ? '✓' : 'Upload'}</label>
                                    {partner.eduOthersCertificateFile && <button type="button" onClick={() => handleOpenFilePreview(partner.eduOthersCertificateFile, `partner_${index}_eduOthersCertificateFile`)} style={{ padding: '3px 6px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.7em', cursor: 'pointer' }}>View</button>}
                                    {!partner.eduOthersCertificateFile && (partner.eduOthersBoard || partner.eduOthersMarksObtained || partner.eduOthersOutOfMarks || partner.eduOthersPercentage || partner.eduOthersYearOfPassing) && (
                                      <button type="button" onClick={() => { (['eduOthersBoard', 'eduOthersMarksObtained', 'eduOthersOutOfMarks', 'eduOthersPercentage', 'eduOthersYearOfPassing'] as const).forEach(f => handlePartnerDetailChange(index, f, '')); }} style={{ padding: '3px 6px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.65em', cursor: 'pointer' }} title="Clear autofilled data for this row">Clear</button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </FormFieldFullWidth>
                  </ShakeAnimation>

                  {/* BUSINESS / EMPLOYMENT DETAILS for Partner/Director */}
                  <ShakeAnimation $isShaking={partnerBusinessDetailsShaking[index] || false}>
                  <FormFieldFullWidth 
                    ref={(el: HTMLDivElement | null) => { partnerBusinessDetailsRefs.current[index] = el; }}
                    style={{ marginTop: '25px', padding: '20px', backgroundColor: partnerBusinessDetailsShaking[index] ? '#fff5f5' : (index === 0 ? '#f8fafc' : '#fff7ed'), borderRadius: '8px', border: partnerBusinessDetailsShaking[index] ? '2px solid #e53e3e' : (index === 0 ? '1px solid #e2e8f0' : '1px solid #fed7aa'), transition: 'all 0.3s ease' }}>
                    <Label style={{ fontWeight: 700, marginBottom: '20px', color: index === 0 ? '#1a5276' : '#ea580c', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.05em', textDecoration: 'underline' }}>
                      {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #{index + 2} {partner.nameInBlocks ? `(${partner.nameInBlocks})` : ''} - BUSINESS / EMPLOYMENT DETAILS
                      {mandatoryFields.businessEmploymentDetails && (!partner.presentOccupation || !partner.businessEmploymentDetails || !partner.totalWorkExperienceYears) && (
                        <span style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px', fontSize: '0.675rem', fontWeight: 600, border: '1px solid #fecaca', textDecoration: 'none' }}>Required</span>
                      )}
                    </Label>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '35%' }}>Present Occupation</td>
                          <td style={{ padding: '12px', border: '1px solid #85c1e9' }}>
                            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}><input type="radio" checked={partner.presentOccupation === 'Business'} onChange={() => handlePartnerDetailChange(index, 'presentOccupation', 'Business')} disabled={!isOnline || isViewMode || isSectionLocked} /> Business</label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}><input type="radio" checked={partner.presentOccupation === 'Service'} onChange={() => handlePartnerDetailChange(index, 'presentOccupation', 'Service')} disabled={!isOnline || isViewMode || isSectionLocked} /> Service</label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}><input type="radio" checked={partner.presentOccupation === 'Self-Employed'} onChange={() => handlePartnerDetailChange(index, 'presentOccupation', 'Self-Employed')} disabled={!isOnline || isViewMode || isSectionLocked} /> Self-Employed</label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}><input type="radio" checked={partner.presentOccupation === 'Other'} onChange={() => handlePartnerDetailChange(index, 'presentOccupation', 'Other')} disabled={!isOnline || isViewMode || isSectionLocked} /> Other</label>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>Details of Business / Employment</td>
                          <td style={{ padding: '12px', border: '1px solid #85c1e9' }}><StyledInput type="text" value={partner.businessEmploymentDetails || ''} onChange={(e) => handlePartnerDetailChange(index, 'businessEmploymentDetails', e.target.value)} disabled={!isOnline || isViewMode || isSectionLocked} style={{ margin: 0 }} /></td>
                        </tr>
                        <tr>
                          <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>Total Work Experience (Years)</td>
                          <td style={{ padding: '12px', border: '1px solid #85c1e9' }}><StyledInput type="text" value={partner.totalWorkExperienceYears || ''} onChange={(e) => handlePartnerDetailChange(index, 'totalWorkExperienceYears', e.target.value)} disabled={!isOnline || isViewMode || isSectionLocked} style={{ margin: 0 }} /></td>
                        </tr>
                        <tr>
                          <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>Relevant Industry Experience</td>
                          <td style={{ padding: '12px', border: '1px solid #85c1e9' }}><StyledInput type="text" value={partner.relevantIndustryExperience || ''} onChange={(e) => handlePartnerDetailChange(index, 'relevantIndustryExperience', e.target.value)} disabled={!isOnline || isViewMode || isSectionLocked} style={{ margin: 0 }} /></td>
                        </tr>
                      </tbody>
                    </table>
                  </FormFieldFullWidth>
                  </ShakeAnimation>

                  {/* FINANCIAL CAPABILITY for Partner/Director */}
                  <ShakeAnimation $isShaking={partnerFinancialCapabilityShaking[index] || false}>
                  <FormFieldFullWidth 
                    ref={(el: HTMLDivElement | null) => { partnerFinancialCapabilityRefs.current[index] = el; }}
                    style={{ marginTop: '25px', padding: '20px', backgroundColor: partnerFinancialCapabilityShaking[index] ? '#fff5f5' : (index === 0 ? '#f8fafc' : '#fff7ed'), borderRadius: '8px', border: partnerFinancialCapabilityShaking[index] ? '2px solid #e53e3e' : (index === 0 ? '1px solid #e2e8f0' : '1px solid #fed7aa'), transition: 'all 0.3s ease' }}>
                    <Label style={{ fontWeight: 700, marginBottom: '20px', color: index === 0 ? '#1a5276' : '#ea580c', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.05em', textDecoration: 'underline' }}>
                      {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #{index + 2} {partner.nameInBlocks ? `(${partner.nameInBlocks})` : ''} - FINANCIAL CAPABILITY
                      {mandatoryFields.financialCapability && (!partner.annualIncome || !partner.netWorthAtApplication || !partner.sourceOfInvestment || !partner.abilityToMeetRequiredInvestment) && (
                        <span style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px', fontSize: '0.675rem', fontWeight: 600, border: '1px solid #fecaca', textDecoration: 'none' }}>Required</span>
                      )}
                    </Label>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '35%' }}>Annual Income (₹) as per ITR filed</td>
                          <td style={{ padding: '12px', border: '1px solid #85c1e9' }}><StyledInput type="text" value={partner.annualIncome || ''} onChange={(e) => handlePartnerDetailChange(index, 'annualIncome', e.target.value)} disabled={!isOnline || isViewMode || isSectionLocked} style={{ margin: 0 }} /></td>
                        </tr>
                        <tr>
                          <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>Net Worth (₹) at the time of application (Immovable assets value)</td>
                          <td style={{ padding: '12px', border: '1px solid #85c1e9' }}><StyledInput type="text" value={partner.netWorthAtApplication || ''} onChange={(e) => handlePartnerDetailChange(index, 'netWorthAtApplication', e.target.value)} disabled={!isOnline || isViewMode || isSectionLocked} style={{ margin: 0 }} /></td>
                        </tr>
                        <tr>
                          <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>Source of Investment</td>
                          <td style={{ padding: '12px', border: '1px solid #85c1e9' }}>
                            <div style={{ display: 'flex', gap: '25px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}><input type="radio" checked={partner.sourceOfInvestment === 'Own Funds'} onChange={() => handlePartnerDetailChange(index, 'sourceOfInvestment', 'Own Funds')} disabled={!isOnline || isViewMode || isSectionLocked} /> Own Funds</label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}><input type="radio" checked={partner.sourceOfInvestment === 'Bank Loan'} onChange={() => handlePartnerDetailChange(index, 'sourceOfInvestment', 'Bank Loan')} disabled={!isOnline || isViewMode || isSectionLocked} /> Bank Loan</label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}><input type="radio" checked={partner.sourceOfInvestment === 'Both'} onChange={() => handlePartnerDetailChange(index, 'sourceOfInvestment', 'Both')} disabled={!isOnline || isViewMode || isSectionLocked} /> Both</label>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>Ability to Meet Required Investment, I agree to arrange min. ₹1.5 Cr (One Crore Fifty Lakh Rupees) or more upon selection required for development of infra of CNG station (as per Scope Of Work)</td>
                          <td style={{ padding: '12px', border: '1px solid #85c1e9' }}>
                            <div style={{ display: 'flex', gap: '25px', alignItems: 'center' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}><input type="radio" checked={partner.abilityToMeetRequiredInvestment === 'Yes'} onChange={() => handlePartnerDetailChange(index, 'abilityToMeetRequiredInvestment', 'Yes')} disabled={!isOnline || isViewMode || isSectionLocked} /> Yes</label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}><input type="radio" checked={partner.abilityToMeetRequiredInvestment === 'No'} onChange={() => handlePartnerDetailChange(index, 'abilityToMeetRequiredInvestment', 'No')} disabled={!isOnline || isViewMode || isSectionLocked} /> No</label>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    {/* FINANCIAL CAPABILITY DOCUMENT CHECKLIST - merged within same card */}
                    <Label style={{ fontWeight: 700, marginTop: '25px', marginBottom: '15px', color: index === 0 ? '#1a5276' : '#ea580c', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1em', textDecoration: 'underline' }}>
                      FINANCIAL CAPABILITY DOCUMENT CHECKLIST
                      {mandatoryFields.financialCapabilityDocChecklist && !(
                        partner.finDocProjectReportFile ||
                        partner.finDocBankStatementFile ||
                        partner.finDocFdTdReceiptsFile ||
                        partner.finDocInvestmentCertificatesFile ||
                        partner.finDocMutualFundStatementFile ||
                        partner.finDocDematStatementFile ||
                        partner.finDocCaCertificateFile ||
                        partner.finDocDpCertificateFile ||
                        partner.finDocAttestedFinancialsFile ||
                        partner.finDocNotarizedAffidavitFile ||
                        partner.finDocBankLetterFile ||
                        partner.finDocOwnershipDocsFile
                      ) && (
                        <span style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px', fontSize: '0.675rem', fontWeight: 600, border: '1px solid #fecaca', textDecoration: 'none' }}>Required</span>
                      )}
                    </Label>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '10px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '5%', textAlign: 'center' }}>Sr. No.</th>
                          <th style={{ padding: '10px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '55%', textAlign: 'left' }}>Description</th>
                          <th style={{ padding: '10px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '15%', textAlign: 'center' }}>Yes / No</th>
                          <th style={{ padding: '10px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '25%', textAlign: 'center' }}>Upload the Document</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>1</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Detailed Project Report / Business Plan covering project overview, site details, estimated cost, funding pattern, projected CNG sales, revenue, expenses, and implementation schedule</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                              <label><input type="radio" checked={partner.finDocProjectReport === 'Yes'} onChange={() => handlePartnerDetailChange(index, 'finDocProjectReport', 'Yes')} disabled={!isOnline || isViewMode || isSectionLocked} /> Yes</label>
                              <label><input type="radio" checked={partner.finDocProjectReport === 'No'} onChange={() => handlePartnerDetailChange(index, 'finDocProjectReport', 'No')} disabled={!isOnline || isViewMode || isSectionLocked} /> No</label>
                            </div>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            {partner.finDocProjectReport === 'Yes' && (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <FileUploadButton onClick={() => document.getElementById(`partner-${index}-finDocProjectReportFile`)?.click()} disabled={!isOnline || isViewMode || partnerFileUploading[`${index}_finDocProjectReportFile`]} style={{ fontSize: '0.8em', padding: '4px 8px' }}>
                                  {partnerFileUploading[`${index}_finDocProjectReportFile`] ? 'Uploading...' : (partner.finDocProjectReportFile ? 'Change' : 'Upload')}
                                </FileUploadButton>
                                {partner.finDocProjectReportFile && <button type="button" onClick={() => handleOpenFilePreview(partner.finDocProjectReportFile, `partner_${index}_finDocProjectReportFile`)} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                                <input type="file" id={`partner-${index}-finDocProjectReportFile`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handlePartnerFileUpload(index, e.target.files[0], 'finDocProjectReportFile' as any); }} />
                              </div>
                            )}
                            {partner.finDocProjectReportFile && !partnerFileUploading[`${index}_finDocProjectReportFile`] && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>2</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Latest Savings Bank account statement (minimum last 6 months) in applicant's name</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                              <label><input type="radio" checked={partner.finDocBankStatement === 'Yes'} onChange={() => handlePartnerDetailChange(index, 'finDocBankStatement', 'Yes')} disabled={!isOnline || isViewMode || isSectionLocked} /> Yes</label>
                              <label><input type="radio" checked={partner.finDocBankStatement === 'No'} onChange={() => handlePartnerDetailChange(index, 'finDocBankStatement', 'No')} disabled={!isOnline || isViewMode || isSectionLocked} /> No</label>
                            </div>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            {partner.finDocBankStatement === 'Yes' && (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <FileUploadButton onClick={() => document.getElementById(`partner-${index}-finDocBankStatementFile`)?.click()} disabled={!isOnline || isViewMode || partnerFileUploading[`${index}_finDocBankStatementFile`]} style={{ fontSize: '0.8em', padding: '4px 8px' }}>
                                  {partnerFileUploading[`${index}_finDocBankStatementFile`] ? 'Uploading...' : (partner.finDocBankStatementFile ? 'Change' : 'Upload')}
                                </FileUploadButton>
                                {partner.finDocBankStatementFile && <button type="button" onClick={() => handleOpenFilePreview(partner.finDocBankStatementFile, `partner_${index}_finDocBankStatementFile`)} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                                <input type="file" id={`partner-${index}-finDocBankStatementFile`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handlePartnerFileUpload(index, e.target.files[0], 'finDocBankStatementFile' as any); }} />
                              </div>
                            )}
                            {partner.finDocBankStatementFile && !partnerFileUploading[`${index}_finDocBankStatementFile`] && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>3</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Fixed Deposit / Term Deposit receipts held with Scheduled Bank or Post Office</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                              <label><input type="radio" checked={partner.finDocFdTdReceipts === 'Yes'} onChange={() => handlePartnerDetailChange(index, 'finDocFdTdReceipts', 'Yes')} disabled={!isOnline || isViewMode || isSectionLocked} /> Yes</label>
                              <label><input type="radio" checked={partner.finDocFdTdReceipts === 'No'} onChange={() => handlePartnerDetailChange(index, 'finDocFdTdReceipts', 'No')} disabled={!isOnline || isViewMode || isSectionLocked} /> No</label>
                            </div>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            {partner.finDocFdTdReceipts === 'Yes' && (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <FileUploadButton onClick={() => document.getElementById(`partner-${index}-finDocFdTdReceiptsFile`)?.click()} disabled={!isOnline || isViewMode || partnerFileUploading[`${index}_finDocFdTdReceiptsFile`]} style={{ fontSize: '0.8em', padding: '4px 8px' }}>
                                  {partnerFileUploading[`${index}_finDocFdTdReceiptsFile`] ? 'Uploading...' : (partner.finDocFdTdReceiptsFile ? 'Change' : 'Upload')}
                                </FileUploadButton>
                                {partner.finDocFdTdReceiptsFile && <button type="button" onClick={() => handleOpenFilePreview(partner.finDocFdTdReceiptsFile, `partner_${index}_finDocFdTdReceiptsFile`)} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                                <input type="file" id={`partner-${index}-finDocFdTdReceiptsFile`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handlePartnerFileUpload(index, e.target.files[0], 'finDocFdTdReceiptsFile' as any); }} />
                              </div>
                            )}
                            {partner.finDocFdTdReceiptsFile && !partnerFileUploading[`${index}_finDocFdTdReceiptsFile`] && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>4</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Investment proof in Government savings instruments (Kisan Vikas Patra, NSC, Bonds, etc.)</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                              <label><input type="radio" checked={partner.finDocInvestmentCertificates === 'Yes'} onChange={() => handlePartnerDetailChange(index, 'finDocInvestmentCertificates', 'Yes')} disabled={!isOnline || isViewMode || isSectionLocked} /> Yes</label>
                              <label><input type="radio" checked={partner.finDocInvestmentCertificates === 'No'} onChange={() => handlePartnerDetailChange(index, 'finDocInvestmentCertificates', 'No')} disabled={!isOnline || isViewMode || isSectionLocked} /> No</label>
                            </div>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            {partner.finDocInvestmentCertificates === 'Yes' && (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <FileUploadButton onClick={() => document.getElementById(`partner-${index}-finDocInvestmentCertificatesFile`)?.click()} disabled={!isOnline || isViewMode || partnerFileUploading[`${index}_finDocInvestmentCertificatesFile`]} style={{ fontSize: '0.8em', padding: '4px 8px' }}>
                                  {partnerFileUploading[`${index}_finDocInvestmentCertificatesFile`] ? 'Uploading...' : (partner.finDocInvestmentCertificatesFile ? 'Change' : 'Upload')}
                                </FileUploadButton>
                                {partner.finDocInvestmentCertificatesFile && <button type="button" onClick={() => handleOpenFilePreview(partner.finDocInvestmentCertificatesFile, `partner_${index}_finDocInvestmentCertificatesFile`)} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                                <input type="file" id={`partner-${index}-finDocInvestmentCertificatesFile`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handlePartnerFileUpload(index, e.target.files[0], 'finDocInvestmentCertificatesFile' as any); }} />
                              </div>
                            )}
                            {partner.finDocInvestmentCertificatesFile && !partnerFileUploading[`${index}_finDocInvestmentCertificatesFile`] && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>5</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Mutual Fund holding statement showing units and current valuation</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                              <label><input type="radio" checked={partner.finDocMutualFundStatement === 'Yes'} onChange={() => handlePartnerDetailChange(index, 'finDocMutualFundStatement', 'Yes')} disabled={!isOnline || isViewMode || isSectionLocked} /> Yes</label>
                              <label><input type="radio" checked={partner.finDocMutualFundStatement === 'No'} onChange={() => handlePartnerDetailChange(index, 'finDocMutualFundStatement', 'No')} disabled={!isOnline || isViewMode || isSectionLocked} /> No</label>
                            </div>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            {partner.finDocMutualFundStatement === 'Yes' && (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <FileUploadButton onClick={() => document.getElementById(`partner-${index}-finDocMutualFundStatementFile`)?.click()} disabled={!isOnline || isViewMode || partnerFileUploading[`${index}_finDocMutualFundStatementFile`]} style={{ fontSize: '0.8em', padding: '4px 8px' }}>
                                  {partnerFileUploading[`${index}_finDocMutualFundStatementFile`] ? 'Uploading...' : (partner.finDocMutualFundStatementFile ? 'Change' : 'Upload')}
                                </FileUploadButton>
                                {partner.finDocMutualFundStatementFile && <button type="button" onClick={() => handleOpenFilePreview(partner.finDocMutualFundStatementFile, `partner_${index}_finDocMutualFundStatementFile`)} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                                <input type="file" id={`partner-${index}-finDocMutualFundStatementFile`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handlePartnerFileUpload(index, e.target.files[0], 'finDocMutualFundStatementFile' as any); }} />
                              </div>
                            )}
                            {partner.finDocMutualFundStatementFile && !partnerFileUploading[`${index}_finDocMutualFundStatementFile`] && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>6</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Shareholding statement / Demat holding statement from Depository Participant</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                              <label><input type="radio" checked={partner.finDocDematStatement === 'Yes'} onChange={() => handlePartnerDetailChange(index, 'finDocDematStatement', 'Yes')} disabled={!isOnline || isViewMode || isSectionLocked} /> Yes</label>
                              <label><input type="radio" checked={partner.finDocDematStatement === 'No'} onChange={() => handlePartnerDetailChange(index, 'finDocDematStatement', 'No')} disabled={!isOnline || isViewMode || isSectionLocked} /> No</label>
                            </div>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            {partner.finDocDematStatement === 'Yes' && (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <FileUploadButton onClick={() => document.getElementById(`partner-${index}-finDocDematStatementFile`)?.click()} disabled={!isOnline || isViewMode || partnerFileUploading[`${index}_finDocDematStatementFile`]} style={{ fontSize: '0.8em', padding: '4px 8px' }}>
                                  {partnerFileUploading[`${index}_finDocDematStatementFile`] ? 'Uploading...' : (partner.finDocDematStatementFile ? 'Change' : 'Upload')}
                                </FileUploadButton>
                                {partner.finDocDematStatementFile && <button type="button" onClick={() => handleOpenFilePreview(partner.finDocDematStatementFile, `partner_${index}_finDocDematStatementFile`)} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                                <input type="file" id={`partner-${index}-finDocDematStatementFile`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handlePartnerFileUpload(index, e.target.files[0], 'finDocDematStatementFile' as any); }} />
                              </div>
                            )}
                            {partner.finDocDematStatementFile && !partnerFileUploading[`${index}_finDocDematStatementFile`] && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>7</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Certificate from Chartered Accountant certifying ownership and valuation of Bonds, Mutual Funds, and Shares</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                              <label><input type="radio" checked={partner.finDocCaCertificate === 'Yes'} onChange={() => handlePartnerDetailChange(index, 'finDocCaCertificate', 'Yes')} disabled={!isOnline || isViewMode || isSectionLocked} /> Yes</label>
                              <label><input type="radio" checked={partner.finDocCaCertificate === 'No'} onChange={() => handlePartnerDetailChange(index, 'finDocCaCertificate', 'No')} disabled={!isOnline || isViewMode || isSectionLocked} /> No</label>
                            </div>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            {partner.finDocCaCertificate === 'Yes' && (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <FileUploadButton onClick={() => document.getElementById(`partner-${index}-finDocCaCertificateFile`)?.click()} disabled={!isOnline || isViewMode || partnerFileUploading[`${index}_finDocCaCertificateFile`]} style={{ fontSize: '0.8em', padding: '4px 8px' }}>
                                  {partnerFileUploading[`${index}_finDocCaCertificateFile`] ? 'Uploading...' : (partner.finDocCaCertificateFile ? 'Change' : 'Upload')}
                                </FileUploadButton>
                                {partner.finDocCaCertificateFile && <button type="button" onClick={() => handleOpenFilePreview(partner.finDocCaCertificateFile, `partner_${index}_finDocCaCertificateFile`)} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                                <input type="file" id={`partner-${index}-finDocCaCertificateFile`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handlePartnerFileUpload(index, e.target.files[0], 'finDocCaCertificateFile' as any); }} />
                              </div>
                            )}
                            {partner.finDocCaCertificateFile && !partnerFileUploading[`${index}_finDocCaCertificateFile`] && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>8</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Certificate from Depository Participant confirming shareholding and market value</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                              <label><input type="radio" checked={partner.finDocDpCertificate === 'Yes'} onChange={() => handlePartnerDetailChange(index, 'finDocDpCertificate', 'Yes')} disabled={!isOnline || isViewMode || isSectionLocked} /> Yes</label>
                              <label><input type="radio" checked={partner.finDocDpCertificate === 'No'} onChange={() => handlePartnerDetailChange(index, 'finDocDpCertificate', 'No')} disabled={!isOnline || isViewMode || isSectionLocked} /> No</label>
                            </div>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            {partner.finDocDpCertificate === 'Yes' && (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <FileUploadButton onClick={() => document.getElementById(`partner-${index}-finDocDpCertificateFile`)?.click()} disabled={!isOnline || isViewMode || partnerFileUploading[`${index}_finDocDpCertificateFile`]} style={{ fontSize: '0.8em', padding: '4px 8px' }}>
                                  {partnerFileUploading[`${index}_finDocDpCertificateFile`] ? 'Uploading...' : (partner.finDocDpCertificateFile ? 'Change' : 'Upload')}
                                </FileUploadButton>
                                {partner.finDocDpCertificateFile && <button type="button" onClick={() => handleOpenFilePreview(partner.finDocDpCertificateFile, `partner_${index}_finDocDpCertificateFile`)} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                                <input type="file" id={`partner-${index}-finDocDpCertificateFile`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handlePartnerFileUpload(index, e.target.files[0], 'finDocDpCertificateFile' as any); }} />
                              </div>
                            )}
                            {partner.finDocDpCertificateFile && !partnerFileUploading[`${index}_finDocDpCertificateFile`] && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>9</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Duly signed by the applicant and the Chartered Accountant, along with the Balance Sheet and Profit & Loss Statement for the last three financial years. These financial statements must bear the UDIN corresponding to the date of signing of the Balance Sheet and Profit & Loss Statement.</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                              <label><input type="radio" checked={partner.finDocAttestedFinancials === 'Yes'} onChange={() => handlePartnerDetailChange(index, 'finDocAttestedFinancials', 'Yes')} disabled={!isOnline || isViewMode || isSectionLocked} /> Yes</label>
                              <label><input type="radio" checked={partner.finDocAttestedFinancials === 'No'} onChange={() => handlePartnerDetailChange(index, 'finDocAttestedFinancials', 'No')} disabled={!isOnline || isViewMode || isSectionLocked} /> No</label>
                            </div>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            {partner.finDocAttestedFinancials === 'Yes' && (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <FileUploadButton onClick={() => document.getElementById(`partner-${index}-finDocAttestedFinancialsFile`)?.click()} disabled={!isOnline || isViewMode || partnerFileUploading[`${index}_finDocAttestedFinancialsFile`]} style={{ fontSize: '0.8em', padding: '4px 8px' }}>
                                  {partnerFileUploading[`${index}_finDocAttestedFinancialsFile`] ? 'Uploading...' : (partner.finDocAttestedFinancialsFile ? 'Change' : 'Upload')}
                                </FileUploadButton>
                                {partner.finDocAttestedFinancialsFile && <button type="button" onClick={() => handleOpenFilePreview(partner.finDocAttestedFinancialsFile, `partner_${index}_finDocAttestedFinancialsFile`)} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                                <input type="file" id={`partner-${index}-finDocAttestedFinancialsFile`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handlePartnerFileUpload(index, e.target.files[0], 'finDocAttestedFinancialsFile' as any); }} />
                              </div>
                            )}
                            {partner.finDocAttestedFinancialsFile && !partnerFileUploading[`${index}_finDocAttestedFinancialsFile`] && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>10</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Notarized affidavit from concerned family member confirming relationship, ownership of funds, and consent to support the project</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                              <label><input type="radio" checked={partner.finDocNotarizedAffidavit === 'Yes'} onChange={() => handlePartnerDetailChange(index, 'finDocNotarizedAffidavit', 'Yes')} disabled={!isOnline || isViewMode || isSectionLocked} /> Yes</label>
                              <label><input type="radio" checked={partner.finDocNotarizedAffidavit === 'No'} onChange={() => handlePartnerDetailChange(index, 'finDocNotarizedAffidavit', 'No')} disabled={!isOnline || isViewMode || isSectionLocked} /> No</label>
                            </div>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            {partner.finDocNotarizedAffidavit === 'Yes' && (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <FileUploadButton onClick={() => document.getElementById(`partner-${index}-finDocNotarizedAffidavitFile`)?.click()} disabled={!isOnline || isViewMode || partnerFileUploading[`${index}_finDocNotarizedAffidavitFile`]} style={{ fontSize: '0.8em', padding: '4px 8px' }}>
                                  {partnerFileUploading[`${index}_finDocNotarizedAffidavitFile`] ? 'Uploading...' : (partner.finDocNotarizedAffidavitFile ? 'Change' : 'Upload')}
                                </FileUploadButton>
                                {partner.finDocNotarizedAffidavitFile && <button type="button" onClick={() => handleOpenFilePreview(partner.finDocNotarizedAffidavitFile, `partner_${index}_finDocNotarizedAffidavitFile`)} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                                <input type="file" id={`partner-${index}-finDocNotarizedAffidavitFile`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handlePartnerFileUpload(index, e.target.files[0], 'finDocNotarizedAffidavitFile' as any); }} />
                              </div>
                            )}
                            {partner.finDocNotarizedAffidavitFile && !partnerFileUploading[`${index}_finDocNotarizedAffidavitFile`] && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>11</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Bank letter confirming availability of funds and source of financing for the proposed project</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                              <label><input type="radio" checked={partner.finDocBankLetter === 'Yes'} onChange={() => handlePartnerDetailChange(index, 'finDocBankLetter', 'Yes')} disabled={!isOnline || isViewMode || isSectionLocked} /> Yes</label>
                              <label><input type="radio" checked={partner.finDocBankLetter === 'No'} onChange={() => handlePartnerDetailChange(index, 'finDocBankLetter', 'No')} disabled={!isOnline || isViewMode || isSectionLocked} /> No</label>
                            </div>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            {partner.finDocBankLetter === 'Yes' && (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <FileUploadButton onClick={() => document.getElementById(`partner-${index}-finDocBankLetterFile`)?.click()} disabled={!isOnline || isViewMode || partnerFileUploading[`${index}_finDocBankLetterFile`]} style={{ fontSize: '0.8em', padding: '4px 8px' }}>
                                  {partnerFileUploading[`${index}_finDocBankLetterFile`] ? 'Uploading...' : (partner.finDocBankLetterFile ? 'Change' : 'Upload')}
                                </FileUploadButton>
                                {partner.finDocBankLetterFile && <button type="button" onClick={() => handleOpenFilePreview(partner.finDocBankLetterFile, `partner_${index}_finDocBankLetterFile`)} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                                <input type="file" id={`partner-${index}-finDocBankLetterFile`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handlePartnerFileUpload(index, e.target.files[0], 'finDocBankLetterFile' as any); }} />
                              </div>
                            )}
                            {partner.finDocBankLetterFile && !partnerFileUploading[`${index}_finDocBankLetterFile`] && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>12</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', fontSize: '0.9em' }}>Ownership documents of immovable assets (land, residential/commercial property, shops, buildings, etc.)</td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                              <label><input type="radio" checked={partner.finDocOwnershipDocs === 'Yes'} onChange={() => handlePartnerDetailChange(index, 'finDocOwnershipDocs', 'Yes')} disabled={!isOnline || isViewMode || isSectionLocked} /> Yes</label>
                              <label><input type="radio" checked={partner.finDocOwnershipDocs === 'No'} onChange={() => handlePartnerDetailChange(index, 'finDocOwnershipDocs', 'No')} disabled={!isOnline || isViewMode || isSectionLocked} /> No</label>
                            </div>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                            {partner.finDocOwnershipDocs === 'Yes' && (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <FileUploadButton onClick={() => document.getElementById(`partner-${index}-finDocOwnershipDocsFile`)?.click()} disabled={!isOnline || isViewMode || partnerFileUploading[`${index}_finDocOwnershipDocsFile`]} style={{ fontSize: '0.8em', padding: '4px 8px' }}>
                                  {partnerFileUploading[`${index}_finDocOwnershipDocsFile`] ? 'Uploading...' : (partner.finDocOwnershipDocsFile ? 'Change' : 'Upload')}
                                </FileUploadButton>
                                {partner.finDocOwnershipDocsFile && <button type="button" onClick={() => handleOpenFilePreview(partner.finDocOwnershipDocsFile, `partner_${index}_finDocOwnershipDocsFile`)} style={{ padding: '4px 8px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}>View</button>}
                                <input type="file" id={`partner-${index}-finDocOwnershipDocsFile`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handlePartnerFileUpload(index, e.target.files[0], 'finDocOwnershipDocsFile' as any); }} />
                              </div>
                            )}
                            {partner.finDocOwnershipDocsFile && !partnerFileUploading[`${index}_finDocOwnershipDocsFile`] && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </FormFieldFullWidth>
                  </ShakeAnimation>

                  {/* APPENDIX-A AFFIDAVIT SECTION for Partner/Director - Conditionally shown based on admin Appendix Logic settings */}
                  {visibleAppendixes.includes('A') && (
                  <ShakeAnimation $isShaking={partnerAppendixAShaking[index] || false} ref={(el: HTMLDivElement | null) => { partnerAppendixARefs.current[index] = el; }}>
                  <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: partnerAppendixAShaking[index] ? '#fff5f5' : '#fef9e7', borderRadius: '10px', border: partnerAppendixAShaking[index] ? '2px solid #e53e3e' : '2px solid #f39c12', transition: 'all 0.3s ease' }}>
                    <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                      <span style={{ display: 'inline-block', padding: '6px 16px', backgroundColor: '#f39c12', color: 'white', borderRadius: '20px', fontSize: '0.85em', fontWeight: 600 }}>
                        {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? `Partner #${index + 2}` : `Director #${index + 2}`} {partner.nameInBlocks ? `(${partner.nameInBlocks})` : ''}
                      </span>
                    </div>
                    <Label style={{ fontWeight: 700, marginBottom: '15px', color: '#9a7b0a', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline' }}>
                      APPENDIX – A: AFFIDAVIT CUM UNDERTAKING
                      {visibleAppendixes.includes('A') && !partner.affidavitFile && (
                        <span style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px', fontSize: '0.675rem', fontWeight: 600, border: '1px solid #fecaca', textDecoration: 'none' }}>Required</span>
                      )}
                    </Label>
                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #f39c12' }}>
                      <p style={{ margin: '0' }}>Affidavit-cum-Undertaking declaring eligibility, financial capability, and non-involvement in criminal or malpractices cases.</p>
                      <p style={{ margin: '10px 0 0 0', fontStyle: 'italic', color: '#888' }}>📄 Download the editable template (.docx), fill details, print on Rs. 500 Stamp Paper, get notarized, and upload.</p>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', justifyContent: 'center', marginBottom: '15px' }}>
                      <a href="/appendix_all/APPENDIX-A.docx" download style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#f39c12', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}>📥 Download Appendix-A Template</a>
                      <button type="button" onClick={() => handleOpenFilePreview('/appendix_all/APPENDIX-A.pdf', 'APPENDIX-A-Template')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#10b981', color: 'white', borderRadius: '6px', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg> View PDF</button>
                      <FileUploadButton onClick={(e) => { e.preventDefault(); document.getElementById(`partner-affidavit-${index}`)?.click(); }} disabled={partnerFileUploading[`${index}_affidavitFile`] || !isOnline || isViewMode}>{partner.affidavitFile ? '🔄 Change Upload' : '📤 Upload Signed'}</FileUploadButton>
                    </div>
                    <input type="file" id={`partner-affidavit-${index}`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files[0]) { handlePartnerFileUpload(index, e.target.files[0], 'affidavitFile'); } }} disabled={!isOnline || isViewMode || isSectionLocked} />
                    {partnerFileUploading[`${index}_affidavitFile`] && (
                      <div style={{ marginTop: '10px' }}><FileUploadProgressBar><FileUploadProgressFill progress={partnerFileProgress[`${index}_affidavitFile`] || 0} /></FileUploadProgressBar></div>
                    )}
                    {partnerFileMessage[`${index}_affidavitFile`] && <FileUploadSuccess style={{ marginTop: '10px' }}>✓ {partnerFileMessage[`${index}_affidavitFile`]}</FileUploadSuccess>}
                    {partner.affidavitFile && typeof partner.affidavitFile === 'string' && !partnerFileUploading[`${index}_affidavitFile`] && !partnerFileMessage[`${index}_affidavitFile`] && (
                      <FileUploadStatus style={{ marginTop: '10px' }}>✅ Uploaded: <ClickableFileName onClick={() => handleOpenFilePreview(partner.affidavitFile, `partner_${index}_affidavitFile`)}>{partner.affidavitFile}</ClickableFileName></FileUploadStatus>
                    )}
                  </FormFieldFullWidth>
                  </ShakeAnimation>
                  )}

                  {/* APPENDIX-B for Partner/Director */}
                  {visibleAppendixes.includes('B') && (
                  <ShakeAnimation $isShaking={partnerAppendixBShaking[index] || false} ref={(el: HTMLDivElement | null) => { partnerAppendixBRefs.current[index] = el; }}>
                  <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: partnerAppendixBShaking[index] ? '#fff5f5' : '#ebf5fb', borderRadius: '10px', border: partnerAppendixBShaking[index] ? '2px solid #e53e3e' : '2px solid #3498db', transition: 'all 0.3s ease' }}>
                    <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                      <span style={{ display: 'inline-block', padding: '6px 16px', backgroundColor: '#3498db', color: 'white', borderRadius: '20px', fontSize: '0.85em', fontWeight: 600 }}>
                        {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? `Partner #${index + 2}` : `Director #${index + 2}`} {partner.nameInBlocks ? `(${partner.nameInBlocks})` : ''}
                      </span>
                    </div>
                    <Label style={{ fontWeight: 700, marginBottom: '15px', color: '#2874a6', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline' }}>
                      APPENDIX – B: AFFIDAVIT FOR INDIVIDUAL & PARTNERS
                      {visibleAppendixes.includes('B') && !partner.appendixBFile && (
                        <span style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px', fontSize: '0.675rem', fontWeight: 600, border: '1px solid #fecaca', textDecoration: 'none' }}>Required</span>
                      )}
                    </Label>
                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #3498db' }}>
                      <p style={{ margin: '0' }}>Affidavit by Individual / Partner declaring eligibility, financial capability, and non-involvement in criminal or malpractices cases.</p>
                      <p style={{ margin: '10px 0 0 0', fontStyle: 'italic', color: '#888' }}>📄 Download the editable template (.docx), fill details, print on Rs. 500 Stamp Paper, get notarized, and upload.</p>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', justifyContent: 'center', marginBottom: '15px' }}>
                      <a href="/appendix_all/APPENDIX-B.docx" download style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#3498db', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}>📥 Download Appendix-B Template</a>
                      <button type="button" onClick={() => handleOpenFilePreview('/appendix_all/APPENDIX-B.pdf', 'APPENDIX-B-Template')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#10b981', color: 'white', borderRadius: '6px', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg> View PDF</button>
                      <FileUploadButton onClick={(e) => { e.preventDefault(); document.getElementById(`partner-appendixB-${index}`)?.click(); }} disabled={!isOnline || isViewMode || isSectionLocked}>{partner.appendixBFile ? '🔄 Change Upload' : '📤 Upload Signed'}</FileUploadButton>
                    </div>
                    <input type="file" id={`partner-appendixB-${index}`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files[0]) { handlePartnerFileUpload(index, e.target.files[0], 'appendixBFile' as any); } }} disabled={!isOnline || isViewMode || isSectionLocked} />
                    {partner.appendixBFile && (
                      <FileUploadStatus style={{ marginTop: '10px' }}>✅ Uploaded: <ClickableFileName onClick={() => handleOpenFilePreview(partner.appendixBFile, `partner_${index}_appendixBFile`)}>{typeof partner.appendixBFile === 'string' ? partner.appendixBFile : ''}</ClickableFileName></FileUploadStatus>
                    )}
                  </FormFieldFullWidth>
                  </ShakeAnimation>
                  )}

                  {/* APPENDIX-C for Partner/Director */}
                  {visibleAppendixes.includes('C') && (
                  <ShakeAnimation $isShaking={partnerAppendixCShaking[index] || false} ref={(el: HTMLDivElement | null) => { partnerAppendixCRefs.current[index] = el; }}>
                  <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: partnerAppendixCShaking[index] ? '#fff5f5' : '#e8f8f5', borderRadius: '10px', border: partnerAppendixCShaking[index] ? '2px solid #e53e3e' : '2px solid #1abc9c', transition: 'all 0.3s ease' }}>
                    <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                      <span style={{ display: 'inline-block', padding: '6px 16px', backgroundColor: '#1abc9c', color: 'white', borderRadius: '20px', fontSize: '0.85em', fontWeight: 600 }}>
                        {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? `Partner #${index + 2}` : `Director #${index + 2}`} {partner.nameInBlocks ? `(${partner.nameInBlocks})` : ''}
                      </span>
                    </div>
                    <Label style={{ fontWeight: 700, marginBottom: '15px', color: '#148f77', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline' }}>
                      APPENDIX – C: AFFIDAVIT FOR NON-INDIVIDUAL ENTITY
                      {visibleAppendixes.includes('C') && !partner.appendixCFile && (
                        <span style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px', fontSize: '0.675rem', fontWeight: 600, border: '1px solid #fecaca', textDecoration: 'none' }}>Required</span>
                      )}
                    </Label>
                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #1abc9c' }}>
                      <p style={{ margin: '0' }}>Affidavit by Authorized Signatory of a Non-Individual Entity.</p>
                      <p style={{ margin: '10px 0 0 0', fontStyle: 'italic', color: '#888' }}>📄 Download the editable template (.docx), fill details, print on Rs. 500 Stamp Paper, get notarized, and upload.</p>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', justifyContent: 'center', marginBottom: '15px' }}>
                      <a href="/appendix_all/APPENDIX-C.docx" download style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#1abc9c', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}>📥 Download Appendix-C Template</a>
                      <button type="button" onClick={() => handleOpenFilePreview('/appendix_all/APPENDIX-C.pdf', 'APPENDIX-C-Template')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#10b981', color: 'white', borderRadius: '6px', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg> View PDF</button>
                      <FileUploadButton onClick={(e) => { e.preventDefault(); document.getElementById(`partner-appendixC-${index}`)?.click(); }} disabled={!isOnline || isViewMode || isSectionLocked}>{partner.appendixCFile ? '🔄 Change Upload' : '📤 Upload Signed'}</FileUploadButton>
                    </div>
                    <input type="file" id={`partner-appendixC-${index}`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files[0]) { handlePartnerFileUpload(index, e.target.files[0], 'appendixCFile' as any); } }} disabled={!isOnline || isViewMode || isSectionLocked} />
                    {partner.appendixCFile && (
                      <FileUploadStatus style={{ marginTop: '10px' }}>✅ Uploaded: <ClickableFileName onClick={() => handleOpenFilePreview(partner.appendixCFile, `partner_${index}_appendixCFile`)}>{typeof partner.appendixCFile === 'string' ? partner.appendixCFile : ''}</ClickableFileName></FileUploadStatus>
                    )}
                  </FormFieldFullWidth>
                  </ShakeAnimation>
                  )}

                  {/* APPENDIX-D for Partner/Director */}
                  {visibleAppendixes.includes('D') && (
                  <ShakeAnimation $isShaking={partnerAppendixDShaking[index] || false} ref={(el: HTMLDivElement | null) => { partnerAppendixDRefs.current[index] = el; }}>
                  <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: partnerAppendixDShaking[index] ? '#fff5f5' : '#fdf2e9', borderRadius: '10px', border: partnerAppendixDShaking[index] ? '2px solid #e53e3e' : '2px solid #e67e22', transition: 'all 0.3s ease' }}>
                    <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                      <span style={{ display: 'inline-block', padding: '6px 16px', backgroundColor: '#e67e22', color: 'white', borderRadius: '20px', fontSize: '0.85em', fontWeight: 600 }}>
                        {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? `Partner #${index + 2}` : `Director #${index + 2}`} {partner.nameInBlocks ? `(${partner.nameInBlocks})` : ''}
                      </span>
                    </div>
                    <Label style={{ fontWeight: 700, marginBottom: '15px', color: '#ca6f1e', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline' }}>
                      APPENDIX – D: OWNERSHIP OF PROPERTY / OFFERED LAND
                      {visibleAppendixes.includes('D') && !partner.appendixDFile && (
                        <span style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px', fontSize: '0.675rem', fontWeight: 600, border: '1px solid #fecaca', textDecoration: 'none' }}>Required</span>
                      )}
                    </Label>
                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #e67e22' }}>
                      <p style={{ margin: '0' }}>Affidavit-cum-Undertaking declaring ownership and consent for use of the offered land.</p>
                      <p style={{ margin: '10px 0 0 0', fontStyle: 'italic', color: '#888' }}>📄 Download the editable template (.docx), fill details, print on Rs. 500 Stamp Paper, get notarized, and upload.</p>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', justifyContent: 'center', marginBottom: '15px' }}>
                      <a href="/appendix_all/APPENDIX-D.docx" download style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#e67e22', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}>📥 Download Appendix-D Template</a>
                      <button type="button" onClick={() => handleOpenFilePreview('/appendix_all/APPENDIX-D.pdf', 'APPENDIX-D-Template')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#10b981', color: 'white', borderRadius: '6px', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg> View PDF</button>
                      <FileUploadButton onClick={(e) => { e.preventDefault(); document.getElementById(`partner-appendixD-${index}`)?.click(); }} disabled={!isOnline || isViewMode || isSectionLocked}>{partner.appendixDFile ? '🔄 Change Upload' : '📤 Upload Signed'}</FileUploadButton>
                    </div>
                    <input type="file" id={`partner-appendixD-${index}`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files[0]) { handlePartnerFileUpload(index, e.target.files[0], 'appendixDFile' as any); } }} disabled={!isOnline || isViewMode || isSectionLocked} />
                    {partner.appendixDFile && (
                      <FileUploadStatus style={{ marginTop: '10px' }}>✅ Uploaded: <ClickableFileName onClick={() => handleOpenFilePreview(partner.appendixDFile, `partner_${index}_appendixDFile`)}>{typeof partner.appendixDFile === 'string' ? partner.appendixDFile : ''}</ClickableFileName></FileUploadStatus>
                    )}
                  </FormFieldFullWidth>
                  </ShakeAnimation>
                  )}

                  {/* APPENDIX-E for Partner/Director */}
                  {visibleAppendixes.includes('E') && (
                  <ShakeAnimation $isShaking={partnerAppendixEShaking[index] || false} ref={(el: HTMLDivElement | null) => { partnerAppendixERefs.current[index] = el; }}>
                  <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: partnerAppendixEShaking[index] ? '#fff5f5' : '#f4ecf7', borderRadius: '10px', border: partnerAppendixEShaking[index] ? '2px solid #e53e3e' : '2px solid #9b59b6', transition: 'all 0.3s ease' }}>
                    <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                      <span style={{ display: 'inline-block', padding: '6px 16px', backgroundColor: '#9b59b6', color: 'white', borderRadius: '20px', fontSize: '0.85em', fontWeight: 600 }}>
                        {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? `Partner #${index + 2}` : `Director #${index + 2}`} {partner.nameInBlocks ? `(${partner.nameInBlocks})` : ''}
                      </span>
                    </div>
                    <Label style={{ fontWeight: 700, marginBottom: '15px', color: '#7d3c98', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline' }}>
                      APPENDIX – E: ADVOCATE'S CERTIFICATE / LEGAL OPINION
                      {visibleAppendixes.includes('E') && !partner.appendixEFile && (
                        <span style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px', fontSize: '0.675rem', fontWeight: 600, border: '1px solid #fecaca', textDecoration: 'none' }}>Required</span>
                      )}
                    </Label>
                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #9b59b6' }}>
                      <p style={{ margin: '0' }}>Advocate's Certificate / Legal Opinion on Title of Offered Land.</p>
                      <p style={{ margin: '10px 0 0 0', fontStyle: 'italic', color: '#888' }}>📄 Download the editable template (.docx), get it filled by your advocate, and upload.</p>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', justifyContent: 'center', marginBottom: '15px' }}>
                      <a href="/appendix_all/APPENDIX-E.docx" download style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#9b59b6', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}>📥 Download Appendix-E Template</a>
                      <button type="button" onClick={() => handleOpenFilePreview('/appendix_all/APPENDIX-E.pdf', 'APPENDIX-E-Template')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#10b981', color: 'white', borderRadius: '6px', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg> View PDF</button>
                      <FileUploadButton onClick={(e) => { e.preventDefault(); document.getElementById(`partner-appendixE-${index}`)?.click(); }} disabled={!isOnline || isViewMode || isSectionLocked}>{partner.appendixEFile ? '🔄 Change Upload' : '📤 Upload Signed'}</FileUploadButton>
                    </div>
                    <input type="file" id={`partner-appendixE-${index}`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files[0]) { handlePartnerFileUpload(index, e.target.files[0], 'appendixEFile' as any); } }} disabled={!isOnline || isViewMode || isSectionLocked} />
                    {partner.appendixEFile && (
                      <FileUploadStatus style={{ marginTop: '10px' }}>✅ Uploaded: <ClickableFileName onClick={() => handleOpenFilePreview(partner.appendixEFile, `partner_${index}_appendixEFile`)}>{typeof partner.appendixEFile === 'string' ? partner.appendixEFile : ''}</ClickableFileName></FileUploadStatus>
                    )}
                  </FormFieldFullWidth>
                  </ShakeAnimation>
                  )}

                  {/* APPENDIX-F for Partner/Director */}
                  {visibleAppendixes.includes('F') && (
                  <ShakeAnimation $isShaking={partnerAppendixFShaking[index] || false} ref={(el: HTMLDivElement | null) => { partnerAppendixFRefs.current[index] = el; }}>
                  <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: partnerAppendixFShaking[index] ? '#fff5f5' : '#eafaf1', borderRadius: '10px', border: partnerAppendixFShaking[index] ? '2px solid #e53e3e' : '2px solid #27ae60', transition: 'all 0.3s ease' }}>
                    <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                      <span style={{ display: 'inline-block', padding: '6px 16px', backgroundColor: '#27ae60', color: 'white', borderRadius: '20px', fontSize: '0.85em', fontWeight: 600 }}>
                        {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? `Partner #${index + 2}` : `Director #${index + 2}`} {partner.nameInBlocks ? `(${partner.nameInBlocks})` : ''}
                      </span>
                    </div>
                    <Label style={{ fontWeight: 700, marginBottom: '15px', color: '#1e8449', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline' }}>
                      APPENDIX – F: LAND OWNER CONSENT / NOC
                      {visibleAppendixes.includes('F') && !partner.appendixFFile && (
                        <span style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px', fontSize: '0.675rem', fontWeight: 600, border: '1px solid #fecaca', textDecoration: 'none' }}>Required</span>
                      )}
                    </Label>
                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #27ae60' }}>
                      <p style={{ margin: '0' }}>Land owner consent or No Objection Certificate for use of land for CNG Station.</p>
                      <p style={{ margin: '10px 0 0 0', fontStyle: 'italic', color: '#888' }}>📄 Download the editable template (.docx), fill details, print on Rs. 500 Stamp Paper, get notarized, and upload.</p>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', justifyContent: 'center', marginBottom: '15px' }}>
                      <a href="/appendix_all/APPENDIX-F.docx" download style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#27ae60', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}>📥 Download Appendix-F Template</a>
                      <button type="button" onClick={() => handleOpenFilePreview('/appendix_all/APPENDIX-F.pdf', 'APPENDIX-F-Template')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: '#10b981', color: 'white', borderRadius: '6px', fontSize: '0.9em', fontWeight: 600, border: 'none', cursor: 'pointer' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg> View PDF</button>
                      <FileUploadButton onClick={(e) => { e.preventDefault(); document.getElementById(`partner-appendixF-${index}`)?.click(); }} disabled={!isOnline || isViewMode || isSectionLocked}>{partner.appendixFFile ? '🔄 Change Upload' : '📤 Upload Signed'}</FileUploadButton>
                    </div>
                    <input type="file" id={`partner-appendixF-${index}`} accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files[0]) { handlePartnerFileUpload(index, e.target.files[0], 'appendixFFile' as any); } }} disabled={!isOnline || isViewMode || isSectionLocked} />
                    {partner.appendixFFile && (
                      <FileUploadStatus style={{ marginTop: '10px' }}>✅ Uploaded: <ClickableFileName onClick={() => handleOpenFilePreview(partner.appendixFFile, `partner_${index}_appendixFFile`)}>{typeof partner.appendixFFile === 'string' ? partner.appendixFFile : ''}</ClickableFileName></FileUploadStatus>
                    )}
                  </FormFieldFullWidth>
                  </ShakeAnimation>
                  )}
                </div>
                </React.Fragment>
              );
              })}
            </SlideDownContainer>
          )}

          {/* Bottom action buttons moved to floating container outside the form */}

          </div>{/* End of plot size insufficient wrapper */}
        </FormContentContainer>
        <PageNumber>1</PageNumber>
      </A4PageContainer>

      <FilePreviewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        fileUrl={modalFileUrl}
        fileName={modalFileName}
        fileType={modalFileType}
        applicationId={modalMode === 'document' ? applicationId : undefined}
        fieldName={modalMode === 'document' ? (modalFieldName || undefined) : undefined}
        onSaveSuccess={(newFilePath) => {
          if (modalMode === 'document' && modalFieldName) {
            // Check if this is a partner field
            const partnerMatch = modalFieldName.match(/^partner_(\d+)_(.+)$/);
            if (partnerMatch) {
              const partnerIndex = parseInt(partnerMatch[1], 10);
              const actualFieldName = partnerMatch[2];
              setFormData(prev => {
                const updatedDetails = [...(prev.partnerDirectorDetails || [])];
                if (updatedDetails[partnerIndex]) {
                  updatedDetails[partnerIndex] = {
                    ...updatedDetails[partnerIndex],
                    [actualFieldName]: newFilePath
                  };
                }
                return { ...prev, partnerDirectorDetails: updatedDetails };
              });
              // Save to database
              const updatedDetails = [...(formData.partnerDirectorDetails || [])];
              if (updatedDetails[partnerIndex]) {
                updatedDetails[partnerIndex] = { ...updatedDetails[partnerIndex], [actualFieldName]: newFilePath };
              }
              saveToDatabaseCallback({ ...formData, partnerDirectorDetails: updatedDetails }, true);
            } else {
              setFormData(prev => ({ ...prev, [modalFieldName]: newFilePath }));
              saveToDatabaseCallback({ ...formData, [modalFieldName]: newFilePath }, true);
            }
          }
        }}
        onSaveEditedImage={
          modalMode === 'profile'
            ? async (dataUrl: string) => {
                try {
                  // Convert data URL to File
                  const parts = dataUrl.split(',');
                  const mimeMatch = parts[0].match(/:(.*?);/);
                  const mime = (mimeMatch && mimeMatch[1]) ? mimeMatch[1] : 'image/jpeg';
                  const bstr = atob(parts[1]);
                  let n = bstr.length;
                  const u8arr = new Uint8Array(n);
                  while (n--) {
                    u8arr[n] = bstr.charCodeAt(n);
                  }
                  const editedFile = new File([u8arr], 'profile-photo-edited.jpg', { type: mime });

                  // Start animation with edited image
                  const objectUrl = URL.createObjectURL(editedFile);
                  startImageUploadAnimation(objectUrl);

                  const editTargetEmail = isAdminMode ? formData.emailId : undefined;
                  const success = await processImageUpload(editedFile, objectUrl, editTargetEmail);
                  if (success) {
                    // Update local preview to edited version
                    isPhotoFreshlyUploadedRef.current = true; // Mark as re-uploaded for animation
                    setPhoto(dataUrl);
                  }
                } catch (err) {
                  console.error('[Profile Photo Edit] Failed to save edited image:', err);
                  alert('Failed to save edited profile photo. Please try again.');
                }
              }
            : undefined
        }
      />

      {/* Appendix PDF Preview Modal */}
      {appendixPdfModal.isOpen && (
        <div 
          onClick={() => setAppendixPdfModal({ isOpen: false, pdfUrl: '', title: '' })}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
          }}>
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              width: '90%',
              maxWidth: '900px',
              height: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: '#8e44ad',
              color: 'white',
            }}>
              <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 600 }}>{appendixPdfModal.title}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '0.8em', opacity: 0.8 }}>Press ESC or click outside to close</span>
                <button
                  onClick={() => setAppendixPdfModal({ isOpen: false, pdfUrl: '', title: '' })}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: 'white',
                    fontSize: '1.5em',
                    cursor: 'pointer',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <iframe
                src={appendixPdfModal.pdfUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                title={appendixPdfModal.title}
              />
            </div>
          </div>
        </div>
      )}

      {/* Image Quality Error Modal */}
      {imageQualityError.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2100,
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            maxWidth: imageQualityError.isAadhaarError ? '600px' : '420px',
            width: '90%',
            textAlign: 'center',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h3 style={{ margin: '0 0 12px 0', color: '#c0392b', fontSize: '1.2em' }}>
              {imageQualityError.isAadhaarError ? 'Incomplete Aadhaar Card' : 'Image Quality Issue'}
            </h3>
            <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#2c3e50' }}>
              {imageQualityError.fieldName}
            </p>
            <p style={{ margin: '0 0 20px 0', color: '#7f8c8d', fontSize: '0.95em', lineHeight: '1.5' }}>
              {imageQualityError.reason}
            </p>
            
            {/* Show sample Aadhaar images when it's an Aadhaar error */}
            {imageQualityError.isAadhaarError && (
              <div style={{ 
                margin: '0 0 20px 0', 
                padding: '16px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <p style={{ margin: '0 0 12px 0', fontWeight: 600, color: '#27ae60', fontSize: '0.95em' }}>
                  ✅ Acceptable Aadhaar Format (Both Sides in Single Image):
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}>
                    <img 
                      src="/reference/Upload image Formats/Aadhar_Front_back_Both.png" 
                      alt="Aadhaar Front & Back Combined" 
                      style={{ 
                        maxWidth: '200px', 
                        maxHeight: '280px', 
                        borderRadius: '6px', 
                        border: '2px solid #27ae60',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }} 
                    />
                    <p style={{ margin: '8px 0 0 0', fontSize: '0.8em', color: '#27ae60', fontWeight: 500 }}>
                      Vertical Layout
                    </p>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <img 
                      src="/reference/Upload image Formats/Aadhar samples/1.png" 
                      alt="Aadhaar Side by Side" 
                      style={{ 
                        maxWidth: '280px', 
                        maxHeight: '200px', 
                        borderRadius: '6px', 
                        border: '2px solid #27ae60',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }} 
                    />
                    <p style={{ margin: '8px 0 0 0', fontSize: '0.8em', color: '#27ae60', fontWeight: 500 }}>
                      Horizontal Layout
                    </p>
                  </div>
                </div>
                <p style={{ margin: '12px 0 0 0', fontSize: '0.85em', color: '#7f8c8d' }}>
                  Both front side (with photo) and back side (with address & QR code) must be visible in a single image.
                </p>
              </div>
            )}
            
            <p style={{ margin: '0 0 20px 0', color: '#34495e', fontSize: '0.9em' }}>
              {imageQualityError.isAadhaarError 
                ? 'Please scan or photograph both sides of your Aadhaar card together and upload as a single image.'
                : 'Please upload a clearer image with good lighting and ensure the document is fully visible.'}
            </p>
            <button
              onClick={() => setImageQualityError({ show: false, reason: '', fieldName: '', isAadhaarError: false })}
              style={{
                padding: '10px 32px',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1em',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              OK, I'll Upload Again
            </button>
          </div>
        </div>
      )}

      {/* Document Mismatch Error Modal */}
      {documentMismatchError.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2100,
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            maxWidth: '450px',
            width: '90%',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄❌</div>
            <h3 style={{ margin: '0 0 12px 0', color: '#e67e22', fontSize: '1.2em' }}>
              Document Mismatch
            </h3>
            <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#2c3e50' }}>
              {documentMismatchError.fieldName}
            </p>
            <p style={{ margin: '0 0 12px 0', color: '#7f8c8d', fontSize: '0.95em', lineHeight: '1.5' }}>
              {documentMismatchError.reason}
            </p>
            <div style={{ margin: '0 0 20px 0', padding: '12px', backgroundColor: '#fef9e7', borderRadius: '8px', border: '1px solid #f9e79f' }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '0.85em', color: '#7f8c8d' }}>
                <strong>Expected:</strong> {documentMismatchError.expectedType}
              </p>
              <p style={{ margin: 0, fontSize: '0.85em', color: '#7f8c8d' }}>
                <strong>Detected:</strong> {documentMismatchError.detectedType}
              </p>
            </div>
            <button
              onClick={() => setDocumentMismatchError({ show: false, reason: '', fieldName: '', detectedType: '', expectedType: '' })}
              style={{
                padding: '10px 32px',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1em',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              OK, I'll Upload the Correct Document
            </button>
          </div>
        </div>
      )}

      {/* Name Mismatch Modal (PAN vs Aadhaar) */}
      {nameMismatchModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2100,
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h3 style={{ margin: '0 0 12px 0', color: '#c0392b', fontSize: '1.2em' }}>
              Name Mismatch Detected
            </h3>
            <p style={{ margin: '0 0 16px 0', color: '#7f8c8d', fontSize: '0.95em', lineHeight: '1.5' }}>
              The names on your Aadhaar card and PAN card do not match. Please ensure both documents belong to the same person.
            </p>
            <div style={{ margin: '0 0 16px 0', padding: '12px', backgroundColor: '#fef9e7', borderRadius: '8px', border: '1px solid #f9e79f', textAlign: 'left' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '0.9em', color: '#2c3e50' }}>
                <strong>Name on Aadhaar:</strong> <span style={{ color: '#e74c3c' }}>{nameMismatchModal.aadhaarName}</span>
              </p>
              <p style={{ margin: 0, fontSize: '0.9em', color: '#2c3e50' }}>
                <strong>Name on PAN:</strong> <span style={{ color: '#e74c3c' }}>{nameMismatchModal.panName}</span>
              </p>
            </div>
            <p style={{ margin: '0 0 16px 0', color: '#7f8c8d', fontSize: '0.85em', lineHeight: '1.4' }}>
              <strong>AI Analysis:</strong> {nameMismatchModal.reason}
            </p>
            <p style={{ margin: '0 0 20px 0', color: '#34495e', fontSize: '0.9em', backgroundColor: '#ebf5fb', padding: '10px', borderRadius: '6px' }}>
              Please re-upload documents with matching names, or contact support if you believe this is an error.
            </p>
            <button
              onClick={() => setNameMismatchModal({ show: false, aadhaarName: '', panName: '', reason: '' })}
              style={{
                padding: '10px 32px',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1em',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              I Understand
            </button>
          </div>
        </div>
      )}

      {/* Education Auto-Detection Info Modal - Using Portal for proper fixed positioning */}
      {eduAutoDetectInfo.show && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            maxWidth: '420px',
            width: '90%',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '42px', marginBottom: '12px' }}>🎓✅</div>
            <h3 style={{ margin: '0 0 12px 0', color: '#27ae60', fontSize: '1.15em' }}>
              Document Detected
            </h3>
            <p style={{ margin: '0 0 16px 0', color: '#2c3e50', fontSize: '0.95em', lineHeight: '1.6' }}>
              The uploaded document is a <strong style={{ color: '#2980b9' }}>{eduAutoDetectInfo.detectedLabel}</strong> certificate.
            </p>
            <p style={{ margin: '0 0 20px 0', color: '#7f8c8d', fontSize: '0.9em', lineHeight: '1.5' }}>
              It has been placed in the correct row and details have been extracted.
              {eduAutoDetectInfo.partnerIndex !== undefined && (
                <span> (Partner #{eduAutoDetectInfo.partnerIndex + 2})</span>
              )}
            </p>
            <button
              onClick={() => setEduAutoDetectInfo({ show: false, detectedType: '', detectedLabel: '' })}
              autoFocus
              style={{
                padding: '10px 32px',
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1em',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              OK
            </button>
            <p style={{ margin: '10px 0 0 0', color: '#bdc3c7', fontSize: '0.75em' }}>
              Press ESC to close
            </p>
          </div>
        </div>,
        document.body
      )}

      {/* Upload Error Modal (AI Validation) - Using Portal for proper positioning */}
      {uploadErrorModal.show && ReactDOM.createPortal(
        <UploadErrorModal onClick={() => setUploadErrorModal({ show: false, message: '', fieldName: '' })}>
          <UploadErrorContent onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h3 style={{ color: '#e74c3c', marginBottom: '12px' }}>Document Upload Failed</h3>
            <p style={{ color: '#666', marginBottom: '8px', fontWeight: 500 }}>
              Field: {uploadErrorModal.fieldName}
            </p>
            <p style={{ color: '#333', marginBottom: '20px', lineHeight: '1.5' }}>
              {uploadErrorModal.message}
            </p>
            <button
              onClick={() => setUploadErrorModal({ show: false, message: '', fieldName: '' })}
              style={{
                padding: '10px 24px',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1em',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              OK, I'll Upload the Correct Document
            </button>
          </UploadErrorContent>
        </UploadErrorModal>,
        document.body
      )}

      {/* Minimum Plot Size Requirements Modal */}
      {/* Plot Size Modal moved outside PageWrapper — see below */}

      {/* PDF Generation Progress Modal is now rendered outside PageWrapper to ensure correct positioning */}
      
      {/* Hidden containers for PDF generation - not visible but used for capturing all pages */}
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden', overflow: 'hidden', pointerEvents: 'none' }}>
        {/* Page 1 is captured from the main formRef, so we only need to render pages 2-14 here */}
        <HiddenPageContainer ref={page2Ref}><ApplicationFormPage2 key={`pdf-p2-${pdfRenderKey}`} isViewMode={true} formData={pdfFormData || formData} /></HiddenPageContainer>
        <HiddenPageContainer ref={page3Ref}><ApplicationFormPage3 key={`pdf-p3-${pdfRenderKey}`} isViewMode={true} formData={pdfFormData || formData} /></HiddenPageContainer>
        <HiddenPageContainer ref={page4Ref}><ApplicationFormPage4 key={`pdf-p4-${pdfRenderKey}`} isViewMode={true} formData={pdfFormData || formData} /></HiddenPageContainer>
        <HiddenPageContainer ref={page5Ref}><ApplicationFormPage5 key={`pdf-p5-${pdfRenderKey}`} isViewMode={true} formData={pdfFormData || formData} /></HiddenPageContainer>
        <HiddenPageContainer ref={page6Ref}><ApplicationFormPage6 key={`pdf-p6-${pdfRenderKey}`} isViewMode={true} formData={pdfFormData || formData} /></HiddenPageContainer>
        <HiddenPageContainer ref={page7Ref}><ApplicationFormPage7 key={`pdf-p7-${pdfRenderKey}`} isViewMode={true} formData={pdfFormData || formData} /></HiddenPageContainer>
        <HiddenPageContainer ref={page8Ref}><ApplicationFormPage8 key={`pdf-p8-${pdfRenderKey}`} isViewMode={true} formData={pdfFormData || formData} /></HiddenPageContainer>
        <HiddenPageContainer ref={page9Ref}><ApplicationFormPage9 key={`pdf-p9-${pdfRenderKey}`} isViewMode={true} formData={pdfFormData || formData} /></HiddenPageContainer>
        <HiddenPageContainer ref={page10Ref}><ApplicationFormPage10 key={`pdf-p10-${pdfRenderKey}`} isViewMode={true} formData={pdfFormData || formData} /></HiddenPageContainer>
        <HiddenPageContainer ref={page11Ref}><ApplicationFormPage11 key={`pdf-p11-${pdfRenderKey}`} isViewMode={true} formData={pdfFormData || formData} /></HiddenPageContainer>
        <HiddenPageContainer ref={page12Ref}><ApplicationFormPage12 key={`pdf-p12-${pdfRenderKey}`} isViewMode={true} formData={pdfFormData || formData} /></HiddenPageContainer>
        <HiddenPageContainer ref={page13Ref}><ApplicationFormPage13 key={`pdf-p13-${pdfRenderKey}`} isViewMode={true} formData={pdfFormData || formData} /></HiddenPageContainer>
        <HiddenPageContainer ref={page14Ref}><ApplicationFormPage14 key={`pdf-p14-${pdfRenderKey}`} isViewMode={true} formData={pdfFormData || formData} /></HiddenPageContainer>
      </div>
    </PageWrapper>

      {/* Plot Size Requirements Modal - placed outside PageWrapper to avoid transform scaling issues */}
      {showPlotSizeModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2100,
            backdropFilter: 'blur(3px)',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowPlotSizeModal(false); }}
          onKeyDown={(e) => { if (e.key === 'Escape') setShowPlotSizeModal(false); }}
          tabIndex={-1}
          ref={(el) => el?.focus()}
        >
          <div style={{
            backgroundColor: 'white',
            padding: '28px',
            borderRadius: '16px',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
            maxWidth: '700px',
            width: '92%',
            maxHeight: '85vh',
            overflowY: 'auto',
            animation: 'plotModalIn 0.25s ease',
          }}>
            <style>{`@keyframes plotModalIn { from { opacity:0; transform:scale(0.92) translateY(12px); } to { opacity:1; transform:scale(1) translateY(0); } }`}</style>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.1em', fontWeight: 700 }}>
                📐 Minimum Plot Size Requirements
              </h3>
              <button
                type="button"
                onClick={() => setShowPlotSizeModal(false)}
                style={{
                  background: 'none', border: 'none', fontSize: '1.5rem', color: '#94a3b8',
                  cursor: 'pointer', padding: '4px 8px', lineHeight: 1, borderRadius: '6px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#475569'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#94a3b8'; }}
                title="Close (Esc)"
              >×</button>
            </div>
            <p style={{ margin: '0 0 14px 0', fontSize: '0.88em', color: '#64748b', lineHeight: 1.5 }}>
              Minimum Plot size of offered land should be as per following table:
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85em', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              <thead>
                <tr style={{ background: 'linear-gradient(135deg, #1e3a5f, #2563eb)' }}>
                  <th style={{ padding: '11px 10px', textAlign: 'left', fontWeight: 600, color: '#fff', fontSize: '0.82em' }}>Location</th>
                  <th style={{ padding: '11px 10px', textAlign: 'center', fontWeight: 600, color: '#fff', fontSize: '0.82em' }}>Frontage<br/>Meter (feet)</th>
                  <th style={{ padding: '11px 10px', textAlign: 'center', fontWeight: 600, color: '#fff', fontSize: '0.82em' }}>Depth<br/>Meter (feet)</th>
                  <th style={{ padding: '11px 10px', textAlign: 'center', fontWeight: 600, color: '#fff', fontSize: '0.82em' }}>Total Area<br/>Sq.M (Sq.Ft)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ border: '1px solid #e2e8f0', padding: '10px', fontWeight: 500 }}>Within city</td>
                  <td style={{ border: '1px solid #e2e8f0', padding: '10px', textAlign: 'center' }}>20 (66)</td>
                  <td style={{ border: '1px solid #e2e8f0', padding: '10px', textAlign: 'center' }}>31 (102)</td>
                  <td style={{ border: '1px solid #e2e8f0', padding: '10px', textAlign: 'center', fontWeight: 600 }}>620 (6732)</td>
                </tr>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <td style={{ border: '1px solid #e2e8f0', padding: '10px', fontWeight: 500 }}>Other road (non-NH)</td>
                  <td style={{ border: '1px solid #e2e8f0', padding: '10px', textAlign: 'center' }}>31 (102)</td>
                  <td style={{ border: '1px solid #e2e8f0', padding: '10px', textAlign: 'center' }}>31 (102)</td>
                  <td style={{ border: '1px solid #e2e8f0', padding: '10px', textAlign: 'center', fontWeight: 600 }}>961 (10404)</td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #e2e8f0', padding: '10px', fontWeight: 500 }}>NH (Undivided)</td>
                  <td style={{ border: '1px solid #e2e8f0', padding: '10px', textAlign: 'center' }}>35 (115)</td>
                  <td style={{ border: '1px solid #e2e8f0', padding: '10px', textAlign: 'center' }}>35 (115)</td>
                  <td style={{ border: '1px solid #e2e8f0', padding: '10px', textAlign: 'center', fontWeight: 600 }}>1225 (13225)</td>
                </tr>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <td style={{ border: '1px solid #e2e8f0', padding: '10px', fontWeight: 500 }}>NH (Divided)</td>
                  <td style={{ border: '1px solid #e2e8f0', padding: '10px', textAlign: 'center' }}>35 (115)</td>
                  <td style={{ border: '1px solid #e2e8f0', padding: '10px', textAlign: 'center' }}>45 (148)</td>
                  <td style={{ border: '1px solid #e2e8f0', padding: '10px', textAlign: 'center', fontWeight: 600 }}>1575 (17020)</td>
                </tr>
              </tbody>
            </table>
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => setShowPlotSizeModal(false)}
                style={{
                  padding: '10px 32px',
                  background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.95em',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                  transition: 'all 0.2s',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Progress Modal - placed outside PageWrapper to avoid transform issues */}
      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2200,
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '28px 32px',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            maxWidth: '380px',
            width: '90%',
            textAlign: 'center',
            position: 'relative',
          }}>
            <button
              type="button"
              onClick={handleCancelSaveModal}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                fontSize: '1.5em',
                color: '#999',
                cursor: 'pointer',
                padding: '4px',
                lineHeight: 1,
                borderRadius: '4px',
                transition: 'color 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.color = '#333')}
              onMouseOut={(e) => (e.currentTarget.style.color = '#999')}
              aria-label="Close"
            >
              ×
            </button>
            <div style={{ fontSize: '2.5em', marginBottom: '12px' }}>💾</div>
            <h3 style={{ margin: '0 0 10px 0', color: '#2c3e50', fontSize: '1.15em', fontWeight: 600 }}>
              Save your progress?
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '0.9em', color: '#666', lineHeight: 1.5 }}>
              Would you like to save your form before continuing?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={handleSkipSaveAndNavigate}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f1f1f1',
                  color: '#555',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9em',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Skip
              </button>
              <button
                type="button"
                onClick={handleSaveAndNavigate}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9em',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Save & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Generation Progress Modal */}
      {isPdfGenerating && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: '2000'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 5px 20px rgba(0, 0, 0, 0.2)',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>Generating PDF</h3>
            <p style={{ margin: '0 0 15px 0', color: '#555' }}>Please wait while your PDF is being created...</p>
            <div style={{ width: '100%', height: '10px', backgroundColor: '#e0e0e0', borderRadius: '5px', overflow: 'hidden' }}>
              <div style={{ width: `${compressionProgress}%`, height: '100%', backgroundColor: '#4CAF50', transition: 'width 0.3s ease-in-out' }} />
            </div>
            <p style={{ fontSize: '14px', color: '#777', marginTop: '10px' }}>{Math.round(compressionProgress)}% complete</p>
          </div>
        </div>
      )}
      
      {/* Countdown Modal before PDF generation */}
      {showCountdownModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2001,
          backdropFilter: 'blur(4px)'
        }}>
          <style>{`
            @keyframes countdownPulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.08); }
            }
            @keyframes countdownFadeIn {
              from { opacity: 0; transform: scale(0.9); }
              to { opacity: 1; transform: scale(1); }
            }
          `}</style>
          <div style={{
            backgroundColor: 'white',
            padding: '40px 50px',
            borderRadius: '20px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            textAlign: 'center',
            maxWidth: '420px',
            width: '90%',
            animation: 'countdownFadeIn 0.3s ease-out'
          }}>
            <div style={{
              width: '100px',
              height: '100px',
              margin: '0 auto 25px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)',
              animation: 'countdownPulse 1s ease-in-out infinite'
            }}>
              <span style={{
                fontSize: '48px',
                fontWeight: 'bold',
                color: 'white',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}>
                {countdownValue > 0 ? countdownValue : '✓'}
              </span>
            </div>
            <h3 style={{
              margin: '0 0 12px 0',
              color: '#1a1a2e',
              fontSize: '22px',
              fontWeight: '600'
            }}>
              Preparing Your Download
            </h3>
            <p style={{
              margin: '0 0 20px 0',
              color: '#666',
              fontSize: '15px',
              lineHeight: '1.5'
            }}>
              {countdownValue > 0 
                ? 'Please wait while we load your application data...'
                : 'Starting download now...'}
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              {[3, 2, 1].map((num) => (
                <div
                  key={num}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    backgroundColor: countdownValue >= num ? '#667eea' : '#e8e8e8',
                    color: countdownValue >= num ? 'white' : '#999',
                    transform: countdownValue === num ? 'scale(1.2)' : 'scale(1)',
                    boxShadow: countdownValue === num ? '0 4px 15px rgba(102, 126, 234, 0.5)' : 'none'
                  }}
                >
                  {num}
                </div>
              ))}
            </div>
            <p style={{
              margin: 0,
              color: '#999',
              fontSize: '13px'
            }}>
              {countdownValue > 0 
                ? `Starting in ${countdownValue} second${countdownValue > 1 ? 's' : ''}...`
                : 'Generating PDF...'}
            </p>
          </div>
        </div>
      )}
      
      {/* Full Application PDF Generation Progress Modal */}
      {isFullPdfGenerating && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: '2000'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 5px 20px rgba(0, 0, 0, 0.2)',
            textAlign: 'center',
            maxWidth: '400px',
            width: '100%'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>Generating Full Application PDF</h3>
            <p style={{ margin: '0 0 15px 0', color: '#555' }}>Processing page {currentPageGenerating} of 5...</p>
            <div style={{ width: '100%', height: '10px', backgroundColor: '#e0e0e0', borderRadius: '5px', overflow: 'hidden' }}>
              <div style={{ width: `${fullPdfProgress}%`, height: '100%', backgroundColor: '#4CAF50', transition: 'width 0.3s ease-in-out' }} />
            </div>
            <p style={{ fontSize: '14px', color: '#777', marginTop: '10px' }}>{Math.round(fullPdfProgress)}% complete</p>
            <p style={{ fontSize: '12px', marginTop: '15px', color: '#666' }}>This may take a moment. Please don't close the browser.</p>
          </div>
        </div>
      )}
    
    {/* Session Timer - hidden on mobile when toggle is active */}
    {!isViewMode && !(isMobile && mobileFloatingHidden) && (
      <SessionTimer 
        sessionDuration={30}
        warningTime={5}
        onSessionExpired={() => navigate('/')}
        onSaveState={async () => {
          if (applicationId) {
            await saveToDatabaseCallback(formData, true, applicationId);
          }
        }}
      />
    )}

    {/* Application ID Display - hidden on mobile when toggle is active */}
    {applicationId && !(isMobile && mobileFloatingHidden) && <ApplicationIdDisplay applicationId={applicationId} />}

    {/* Home Icon Button - hidden on mobile when toggle is active */}
    {!(isMobile && mobileFloatingHidden) && (
      <button
        onClick={() => {
          // Skip save modal if application is locked
          if (applicationId && formData.emailId && formData.emailId.trim() !== '' && isOnline && !isApplicationLocked) {
            setPendingNavigation('/user-dashboard');
            setShowSaveModal(true);
          } else {
            navigate('/user-dashboard');
          }
        }}
        title="Go to Dashboard"
        style={{
          position: 'fixed',
          left: '20px',
          bottom: '150px',
          zIndex: 1000,
          height: '44px',
          padding: '0 16px 0 12px',
          borderRadius: '22px',
          border: 'none',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: 600,
          boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
          transition: 'all 0.25s ease'
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
        Home
      </button>
    )}

    {/* Save Icon Button - hidden on mobile when toggle is active, also hidden when application is locked */}
    {!(isMobile && mobileFloatingHidden) && !isApplicationLocked && (
      <button
        onClick={() => saveToDatabaseCallback(formData, true, applicationId)}
        disabled={isSaving || !applicationId || !isOnline}
        title="Save Draft"
        style={{
          position: 'fixed',
          left: '20px',
          bottom: '98px',
          zIndex: 1000,
          height: '44px',
          padding: '0 16px 0 12px',
          borderRadius: '22px',
          border: 'none',
          background: isSaving ? '#86efac' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
          color: 'white',
          cursor: isSaving || !applicationId || !isOnline ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: 600,
          boxShadow: '0 4px 14px rgba(34, 197, 94, 0.35)',
          transition: 'all 0.25s ease',
          opacity: isSaving || !applicationId || !isOnline ? 0.6 : 1
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
          <polyline points="17 21 17 13 7 13 7 21"/>
          <polyline points="7 3 7 8 15 8"/>
        </svg>
        {isSaving ? 'Saving...' : 'Save'}
      </button>
    )}
    
    {/* Mobile Toggle Button to hide/show floating elements */}
    <MobileFloatingToggle 
      isHidden={mobileFloatingHidden}
      onClick={() => setMobileFloatingHidden(!mobileFloatingHidden)}
      title={mobileFloatingHidden ? 'Show UI elements' : 'Hide UI elements'}
    >
      {mobileFloatingHidden ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14" />
        </svg>
      )}
    </MobileFloatingToggle>
    
    {/* Link Confirmation Modal */}
    {showLinkConfirm && (
      <ModalOverlay onClick={() => setShowLinkConfirm(false)}>
        <ConfirmModalContent onClick={(e) => e.stopPropagation()}>
          <ConfirmModalIcon>🔗</ConfirmModalIcon>
          <ConfirmModalTitle>Link Applications?</ConfirmModalTitle>
          <ConfirmModalText>
            Are you sure you want to link your application with <strong>{linkInputValue}</strong> for joint dealership ownership?
          </ConfirmModalText>
          <ConfirmModalButtons>
            <ConfirmButtonSecondary onClick={() => setShowLinkConfirm(false)}>
              Cancel
            </ConfirmButtonSecondary>
            <ConfirmButtonPrimary onClick={confirmLinkApplication} disabled={isLinking}>
              {isLinking ? 'Linking...' : 'Yes, Link'}
            </ConfirmButtonPrimary>
          </ConfirmModalButtons>
        </ConfirmModalContent>
      </ModalOverlay>
    )}
    
    {/* Unlink Confirmation Modal */}
    {showUnlinkConfirm && (
      <ModalOverlay onClick={() => setShowUnlinkConfirm(false)}>
        <ConfirmModalContent onClick={(e) => e.stopPropagation()}>
          <ConfirmModalIcon>⚠️</ConfirmModalIcon>
          <ConfirmModalTitle>Unlink Application?</ConfirmModalTitle>
          <ConfirmModalText>
            Are you sure you want to unlink your application from the dealership group? This will remove the connection with other co-applicants.
          </ConfirmModalText>
          <ConfirmModalButtons>
            <ConfirmButtonSecondary onClick={() => setShowUnlinkConfirm(false)}>
              Cancel
            </ConfirmButtonSecondary>
            <ConfirmButtonDanger onClick={confirmUnlinkApplication} disabled={isLinking}>
              {isLinking ? 'Unlinking...' : 'Yes, Unlink'}
            </ConfirmButtonDanger>
          </ConfirmModalButtons>
        </ConfirmModalContent>
      </ModalOverlay>
    )}

    {/* Aadhaar Sample Guidelines Modal */}
    {showAadhaarSampleModal && (
      <ModalOverlay onClick={() => setShowAadhaarSampleModal(false)}>
        <div 
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90vw',
            maxWidth: '800px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#fef2f2'
          }}>
            <h3 style={{ margin: 0, color: '#dc2626', fontWeight: 600 }}>⚠️ Aadhaar Card Upload Guidelines</h3>
            <button
              onClick={() => setShowAadhaarSampleModal(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#6b7280',
                padding: '4px 8px',
                borderRadius: '4px'
              }}
            >
              ✕
            </button>
          </div>
          <div style={{ padding: '20px', overflowY: 'auto' }}>
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #f59e0b' }}>
              <p style={{ margin: 0, fontWeight: 600, color: '#92400e', fontSize: '0.95em' }}>
                📋 <strong>Important:</strong> Please upload a clear, unmasked Aadhaar card image. Masked Aadhaar cards (with hidden digits) will NOT be accepted.
              </p>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Accepted Sample */}
              <div style={{ padding: '15px', backgroundColor: '#ecfdf5', borderRadius: '8px', border: '2px solid #10b981' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '1.5em' }}>✅</span>
                  <h4 style={{ margin: 0, color: '#059669', fontWeight: 700 }}>ACCEPTED</h4>
                </div>
                <img 
                  src="/images/accepted_aadhar_sample.png" 
                  alt="Accepted Aadhaar Sample"
                  style={{ width: '100%', borderRadius: '6px', border: '1px solid #a7f3d0' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                <ul style={{ margin: '12px 0 0 0', paddingLeft: '20px', fontSize: '0.85em', color: '#065f46' }}>
                  <li>Full Aadhaar number visible</li>
                  <li>Clear photo and QR code</li>
                  <li>Both front and back side visible</li>
                  <li>All details readable</li>
                </ul>
              </div>
              
              {/* Not Accepted Sample */}
              <div style={{ padding: '15px', backgroundColor: '#fef2f2', borderRadius: '8px', border: '2px solid #ef4444' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '1.5em' }}>❌</span>
                  <h4 style={{ margin: 0, color: '#dc2626', fontWeight: 700 }}>NOT ACCEPTED</h4>
                </div>
                <img 
                  src="/images/masked_aadhar_not_accepted.png" 
                  alt="Masked Aadhaar - Not Accepted"
                  style={{ width: '100%', borderRadius: '6px', border: '1px solid #fecaca' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                <ul style={{ margin: '12px 0 0 0', paddingLeft: '20px', fontSize: '0.85em', color: '#991b1b' }}>
                  <li>Masked Aadhaar (XXXX XXXX pattern)</li>
                  <li>Hidden digits not acceptable</li>
                  <li>Blurry or unreadable images</li>
                  <li>Cropped or partial Aadhaar</li>
                </ul>
              </div>
            </div>
            
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #3b82f6' }}>
              <p style={{ margin: 0, fontSize: '0.9em', color: '#1e40af' }}>
                💡 <strong>Tip:</strong> For best results, place both front and back of your Aadhaar card side by side on a flat surface and take a single clear photo in good lighting.
              </p>
            </div>
          </div>
          <div style={{ padding: '15px 20px', borderTop: '1px solid #e5e7eb', backgroundColor: '#f8fafc', textAlign: 'right' }}>
            <button
              onClick={() => setShowAadhaarSampleModal(false)}
              style={{
                padding: '10px 24px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Got it!
            </button>
          </div>
        </div>
      </ModalOverlay>
    )}

    {/* Partner Selection Modal - Who is filling the form? */}
    {showPartnerSelectionModal && (
      <ModalOverlay>
        <PartnerSelectionModal onClick={(e) => e.stopPropagation()}>
          <PartnerSelectionTitle>👋 Who is filling the form today?</PartnerSelectionTitle>
          <PartnerSelectionSubtitle>
            Select your role to ensure you can only edit your own section. This prevents accidental changes to other partners' information.
          </PartnerSelectionSubtitle>
          <PartnerOptionsList>
            {/* Main Applicant Option - Full Access */}
            <PartnerOption isMain onClick={() => handleSelectEditingUser('main')}>
              <PartnerOptionIcon>👑</PartnerOptionIcon>
              <PartnerOptionInfo>
                <PartnerOptionName>{formData.nameInBlocks || 'Main Applicant'}</PartnerOptionName>
                <PartnerOptionRole isMain>Main Applicant (Can edit all sections)</PartnerOptionRole>
              </PartnerOptionInfo>
            </PartnerOption>
            
            {/* Partner Options */}
            {formData.partnerDirectorDetails?.map((partner, index) => (
              <PartnerOption key={index} onClick={() => handleSelectEditingUser(index)}>
                <PartnerOptionIcon>👤</PartnerOptionIcon>
                <PartnerOptionInfo>
                  <PartnerOptionName>{partner.nameInBlocks || `Partner/Director ${index + 2}`}</PartnerOptionName>
                  <PartnerOptionRole>Partner/Director {index + 2} (Can edit own section only)</PartnerOptionRole>
                </PartnerOptionInfo>
              </PartnerOption>
            ))}
          </PartnerOptionsList>
        </PartnerSelectionModal>
      </ModalOverlay>
    )}

    {/* Partner Setup Modal - Initial setup when non-Individual type is selected */}
    {showPartnerSetupModal && (
      <ModalOverlay>
        <PartnerSetupModalContent onClick={(e) => e.stopPropagation()}>
          <PartnerSetupTitle>
            👥 Set Up Your {partnerSetupType === 'Partnership' ? 'Partners' : 'Directors'}
          </PartnerSetupTitle>
          <PartnerSetupSubtitle>
            Enter details for all {partnerSetupType === 'Partnership' ? 'partners' : 'directors'}.
          </PartnerSetupSubtitle>

          {/* Partner Count Selector */}
          <PartnerSetupCountSelector>
            <span style={{ fontSize: '0.7rem', color: '#86868b', fontWeight: 500 }}>
              Total {partnerSetupType === 'Partnership' ? 'Partners' : 'Directors'}:
            </span>
            <PartnerCountBtn 
              onClick={() => handlePartnerSetupCountChange(-1)}
              disabled={partnerSetupCount <= (partnerSetupType === 'Public Ltd' ? 3 : 2)}
            >
              −
            </PartnerCountBtn>
            <PartnerCountDisplay>{partnerSetupCount}</PartnerCountDisplay>
            <PartnerCountBtn 
              onClick={() => handlePartnerSetupCountChange(1)}
              disabled={partnerSetupCount >= 20}
            >
              +
            </PartnerCountBtn>
          </PartnerSetupCountSelector>

          {/* Horizontal Cards Container */}
          <PartnerSetupCardsContainer>
            {/* Main Applicant Card */}
            <PartnerSetupRow isMain index={0}>
              <PartnerSetupRowHeader>
                <PartnerSetupRowTitle>
                  <span style={{ fontSize: '0.85rem' }}>👑</span>
                  {partnerSetupType === 'Partnership' ? 'Partner' : 'Director'} #1
                </PartnerSetupRowTitle>
              </PartnerSetupRowHeader>
              <div style={{ 
                background: 'rgba(0, 122, 255, 0.08)', 
                borderRadius: '6px', 
                padding: '0.4rem', 
                fontSize: '0.65rem', 
                color: '#007aff',
                textAlign: 'center'
              }}>
                <strong>You (Main Applicant)</strong>
                <div style={{ fontSize: '0.6rem', marginTop: '0.15rem', color: '#5856d6' }}>
                  Your details will be entered in Section 1
                </div>
              </div>
            </PartnerSetupRow>

            {/* Additional Partners/Directors Cards */}
            {partnerSetupData.map((partner, index) => (
              <PartnerSetupRow key={index} index={index + 1}>
                <PartnerSetupRowHeader>
                  <PartnerSetupRowTitle>
                    <span style={{ fontSize: '0.75rem' }}>👤</span>
                    {partnerSetupType === 'Partnership' ? 'Partner' : 'Director'} #{index + 2}
                  </PartnerSetupRowTitle>
                </PartnerSetupRowHeader>
                
                <PartnerSetupInputRow>
                  <PartnerSetupInputGroup>
                    <label>Full Name</label>
                    <input
                      type="text"
                      value={partner.name}
                      onChange={(e) => handlePartnerSetupInputChange(index, 'name', e.target.value)}
                      placeholder="FULL NAME"
                    />
                  </PartnerSetupInputGroup>
                  
                  <PartnerSetupInputGroup hasError={partner.email.length > 0 && !isValidEmail(partner.email)}>
                    <label>Email Address *</label>
                    <input
                      type="email"
                      value={partner.email}
                      onChange={(e) => handlePartnerSetupInputChange(index, 'email', e.target.value)}
                      placeholder="email@example.com"
                    />
                  </PartnerSetupInputGroup>
                  
                  <PartnerSetupInputGroup hasError={partner.phone.length > 0 && (partner.phone.length === 10 ? !isValidIndianMobile(partner.phone) : false)}>
                    <label>Mobile Number</label>
                    <input
                      type="text"
                      value={partner.phone}
                      onChange={(e) => handlePartnerSetupInputChange(index, 'phone', e.target.value)}
                      placeholder="10-digit (6/7/8/9...)"
                      maxLength={10}
                    />
                  </PartnerSetupInputGroup>
                </PartnerSetupInputRow>

                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {partner.email && !isValidEmail(partner.email) && (
                    <span style={{ fontSize: '0.55rem', color: '#ff3b30' }}>Invalid email</span>
                  )}
                  {partner.phone.length === 10 && !isValidIndianMobile(partner.phone) && (
                    <span style={{ fontSize: '0.55rem', color: '#ff3b30' }}>Must start with 6-9</span>
                  )}
                  {partner.phone.length === 10 && isValidIndianMobile(partner.phone) && getDuplicateMobiles().includes(partner.phone) && (
                    <span style={{ fontSize: '0.55rem', color: '#ff9500' }}>Duplicate number</span>
                  )}
                </div>
              </PartnerSetupRow>
            ))}
          </PartnerSetupCardsContainer>

          <PartnerSetupActions>
            <PartnerSetupBtn onClick={handlePartnerSetupCancel}>
              Cancel
            </PartnerSetupBtn>
            <PartnerSetupBtn 
              variant="primary" 
              onClick={handlePartnerSetupSubmit}
              disabled={isPartnerSetupSubmitting || partnerSetupData.some(p => !isValidEmail(p.email)) || partnerSetupData.some(p => p.phone && !isValidIndianMobile(p.phone)) || hasDuplicateMobiles()}
            >
              {isPartnerSetupSubmitting ? 'Setting up...' : 'Continue to Form →'}
            </PartnerSetupBtn>
          </PartnerSetupActions>
        </PartnerSetupModalContent>
      </ModalOverlay>
    )}

    {/* Webcam Selfie Capture Modal */}
    {showWebcamModal && (
      <ModalOverlay onClick={closeWebcamModal}>
        <div 
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '90vw',
            maxWidth: '500px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '1.1em', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>📷</span> Take Selfie
            </h3>
            <button
              type="button"
              onClick={closeWebcamModal}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5em',
                cursor: 'pointer',
                color: '#64748b',
                padding: '4px'
              }}
            >
              ×
            </button>
          </div>
          
          {webcamError ? (
            <div style={{
              padding: '40px 20px',
              textAlign: 'center',
              backgroundColor: '#fef2f2',
              borderRadius: '12px',
              color: '#dc2626'
            }}>
              <span style={{ fontSize: '2em', display: 'block', marginBottom: '12px' }}>⚠️</span>
              {webcamError}
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <video
                ref={webcamVideoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  borderRadius: '12px',
                  transform: 'scaleX(-1)',
                  backgroundColor: '#1e293b'
                }}
              />
              <canvas ref={webcamCanvasRef} style={{ display: 'none' }} />
              
              {/* Face detection guide frame - changes color based on detection */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '150px',
                height: '180px',
                border: faceInFrame 
                  ? '4px solid #22c55e' 
                  : faceDetected 
                  ? '3px solid #f59e0b' 
                  : '2px dashed rgba(255,255,255,0.6)',
                borderRadius: '50% 50% 45% 45%',
                pointerEvents: 'none',
                transition: 'all 0.3s ease',
                boxShadow: faceInFrame 
                  ? '0 0 30px rgba(34,197,94,0.6), inset 0 0 30px rgba(34,197,94,0.15)' 
                  : faceDetected 
                  ? '0 0 15px rgba(245,158,11,0.4)'
                  : 'none'
              }} />
              
              {/* Countdown overlay */}
              {captureCountdown !== null && captureCountdown > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(34,197,94,0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '3em',
                  fontWeight: 700,
                  color: 'white',
                  animation: 'pulse 1s ease-in-out infinite',
                  boxShadow: '0 0 30px rgba(34,197,94,0.6)'
                }}>
                  {captureCountdown}
                </div>
              )}
              
              {/* AI Status indicators - top right */}
              <div style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                fontSize: '0.65em',
                fontWeight: 500
              }}>
                <div style={{
                  padding: '3px 8px',
                  borderRadius: '12px',
                  backgroundColor: detectionStatus.isFrontal ? 'rgba(34,197,94,0.9)' : 'rgba(239,68,68,0.85)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {detectionStatus.isFrontal ? '✓' : '✗'} Front facing
                </div>
                <div style={{
                  padding: '3px 8px',
                  borderRadius: '12px',
                  backgroundColor: detectionStatus.isCentered ? 'rgba(34,197,94,0.9)' : 'rgba(239,68,68,0.85)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {detectionStatus.isCentered ? '✓' : '✗'} Centered
                </div>
                <div style={{
                  padding: '3px 8px',
                  borderRadius: '12px',
                  backgroundColor: detectionStatus.sizeOk ? 'rgba(34,197,94,0.9)' : 'rgba(239,68,68,0.85)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {detectionStatus.sizeOk ? '✓' : '✗'} Distance
                </div>
                <div style={{
                  padding: '3px 8px',
                  borderRadius: '12px',
                  backgroundColor: detectionStatus.lightingOk ? 'rgba(34,197,94,0.9)' : 'rgba(239,68,68,0.85)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {detectionStatus.lightingOk ? '✓' : '✗'} Lighting
                </div>
              </div>
              
              {/* AI Guidance message - bottom */}
              <div style={{
                position: 'absolute',
                bottom: '12px',
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '0.8em',
                fontWeight: 600,
                maxWidth: '90%',
                textAlign: 'center',
                backgroundColor: faceInFrame 
                  ? 'rgba(34,197,94,0.95)' 
                  : faceDetected 
                  ? 'rgba(245,158,11,0.95)' 
                  : 'rgba(30,41,59,0.9)',
                color: 'white',
                backdropFilter: 'blur(4px)',
                transition: 'all 0.3s',
                boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
              }}>
                {captureCountdown !== null && captureCountdown > 0 
                  ? `📸 Capturing in ${captureCountdown}...`
                  : faceInFrame 
                  ? '✓ Perfect! Capturing...'
                  : aiGuidance}
              </div>
            </div>
          )}
          
          <p style={{ 
            fontSize: '0.85em', 
            color: faceInFrame ? '#22c55e' : '#64748b', 
            textAlign: 'center', 
            margin: '12px 0 8px',
            lineHeight: 1.4,
            fontWeight: faceInFrame ? 600 : 400,
            transition: 'color 0.3s'
          }}>
            {faceDetectionSupported 
              ? (faceInFrame 
                ? '✓ All checks passed! Auto-capturing...' 
                : 'AI is analyzing - follow the guidance above')
              : 'AI detection unavailable - use manual capture'}
          </p>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={closeWebcamModal}
              style={{
                padding: '12px 24px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                backgroundColor: 'white',
                color: '#64748b',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={captureWebcamPhoto}
              disabled={!webcamStream || !!webcamError || autoCapturing}
              style={{
                padding: '12px 32px',
                border: 'none',
                borderRadius: '8px',
                background: webcamStream && !webcamError && !autoCapturing
                  ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)' 
                  : '#e2e8f0',
                color: webcamStream && !webcamError && !autoCapturing ? 'white' : '#94a3b8',
                fontWeight: 600,
                cursor: webcamStream && !webcamError && !autoCapturing ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: webcamStream && !webcamError && !autoCapturing ? '0 4px 12px rgba(124,58,237,0.3)' : 'none'
              }}
            >
              <span>📸</span> {autoCapturing ? 'Auto-capturing...' : 'Manual Capture'}
            </button>
          </div>
        </div>
      </ModalOverlay>
    )}

    {/* Brochure PDF Modal */}
    {showBrochureModal && (
      <ModalOverlay onClick={() => setShowBrochureModal(false)}>
        <div 
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90vw',
            maxWidth: '900px',
            height: '85vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f8fafc'
          }}>
            <h3 style={{ margin: 0, color: '#1e40af', fontWeight: 600 }}>📄 MNGL DODO Brochure</h3>
            <button
              onClick={() => setShowBrochureModal(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#6b7280',
                padding: '4px 8px',
                borderRadius: '4px'
              }}
            >
              ✕
            </button>
          </div>
          <iframe
            src="/brochures/mngl-dodo-brochure.pdf"
            style={{
              flex: 1,
              width: '100%',
              border: 'none'
            }}
            title="MNGL DODO Brochure"
          />
        </div>
      </ModalOverlay>
    )}
    {/* Sr.No Guidance Modal — trilingual (English / Hindi / Marathi) */}
    {showSrNoGuide && ReactDOM.createPortal(
      <div
        onClick={() => setShowSrNoGuide(false)}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(15, 23, 42, 0.5)',
          backdropFilter: 'blur(4px)',
          padding: '16px',
          animation: 'srNoOverlayFade 0.2s ease',
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            background: '#ffffff',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '440px',
            padding: '28px 24px 24px',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.2)',
            animation: 'srNoGuideEnter 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            position: 'relative',
            textAlign: 'center',
          }}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={() => setShowSrNoGuide(false)}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              border: '1.5px solid #e2e8f0',
              background: '#f8fafc',
              color: '#64748b',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1rem',
              lineHeight: 1,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.borderColor = '#fca5a5'; e.currentTarget.style.color = '#ef4444'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; }}
          >
            &times;
          </button>

          {/* Icon */}
          <div style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 14px',
          }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>

          <h3 style={{ margin: '0 0 6px', fontSize: '1.05rem', fontWeight: 800, color: '#1e293b' }}>
            Invalid Sr. No
          </h3>

          {/* English */}
          <p style={{ margin: '0 0 10px', fontSize: '0.8rem', color: '#475569', lineHeight: 1.55 }}>
            The Sr. No you entered does not match any advertised location. Please refer to the
            <strong> List of Advertised Locations</strong> to find the correct Sr. No for your desired area.
          </p>

          {/* Hindi */}
          <p style={{ margin: '0 0 10px', fontSize: '0.78rem', color: '#64748b', lineHeight: 1.55, fontStyle: 'italic' }}>
            आपने जो क्रमांक दर्ज किया है वह किसी भी विज्ञापित स्थान से मेल नहीं खाता। कृपया सही क्रमांक जानने के लिए
            <strong> विज्ञापित स्थानों की सूची</strong> देखें।
          </p>

          {/* Marathi */}
          <p style={{ margin: '0 0 16px', fontSize: '0.78rem', color: '#64748b', lineHeight: 1.55, fontStyle: 'italic' }}>
            आपण प्रविष्ट केलेला अनुक्रमांक कोणत्याही जाहिरात केलेल्या ठिकाणाशी जुळत नाही. कृपया योग्य अनुक्रमांक शोधण्यासाठी
            <strong> जाहिरात केलेल्या ठिकाणांची यादी</strong> पहा.
          </p>

          {/* CTA */}
          <a
            href="/area-stretch-info"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setShowSrNoGuide(false)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 24px',
              border: 'none',
              borderRadius: '10px',
              fontSize: '0.85rem',
              fontWeight: 700,
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              color: '#fff',
              textDecoration: 'none',
              transition: 'all 0.2s',
              boxShadow: '0 4px 14px rgba(37, 99, 235, 0.3)',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(37, 99, 235, 0.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(37, 99, 235, 0.3)'; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            View Advertised Locations
          </a>
        </div>
      </div>,
      document.body
    )}

    {/* Keyframe injection for advertised locations button glow + pointing finger */}
    <style>{`
      @keyframes advertLocationGlow {
        0%, 100% { box-shadow: 0 0 4px rgba(37, 99, 235, 0.3), 0 0 12px rgba(37, 99, 235, 0.1); }
        50% { box-shadow: 0 0 8px rgba(37, 99, 235, 0.5), 0 0 20px rgba(37, 99, 235, 0.2); }
      }
      @keyframes srNoOverlayFade {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes srNoGuideEnter {
        from { opacity: 0; transform: scale(0.92) translateY(8px); }
        to { opacity: 1; transform: scale(1) translateY(0); }
      }
      @keyframes pointingBounce {
        0%, 100% { transform: translateX(0px) rotate(-10deg) scale(1); }
        30% { transform: translateX(6px) rotate(-5deg) scale(1.12); }
        60% { transform: translateX(2px) rotate(-8deg) scale(1.05); }
      }
      @keyframes tooltipFadeIn {
        from { opacity: 0; transform: translateY(4px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes pulseRing {
        0% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.4); }
        70% { box-shadow: 0 0 0 10px rgba(37, 99, 235, 0); }
        100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
      }
      @keyframes srNoFieldPulse {
        0%, 100% { border-color: #fca5a5; box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        50% { border-color: #ef4444; box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15); }
      }
    `}</style>
    </>
  );
};

export default ApplicationFormPage1;
